import React, {createRef, useContext, useState} from "react";
import {createPortal, flushSync} from "react-dom";
import { RoughCanvas } from "roughjs/bin/canvas";
import rough from "roughjs/bin/rough";
import clsx from "clsx";
import { nanoid } from "nanoid";
import {
  actionAddToLibrary,
  actionBringForward,
  actionBringToFront,
  actionCopy,
  actionCopyAsPng,
  actionCopyAsSvg,
  copyText,
  actionCopyStyles,
  actionCut,
  actionDeleteSelected,
  actionReplaceSelection,
  actionDuplicateSelection,
  actionFinalize,
  actionFlipHorizontal,
  actionFlipVertical,
  actionGroup,
  actionPasteStyles,
  actionSelectAll,
  actionSendBackward,
  actionSendToBack,
  actionToggleGridMode,
  actionToggleStats,
  actionToggleZenMode,
  actionUnbindText,
  actionBindText,
  actionUngroup,
  actionLink,
  actionToggleElementLock,
  actionToggleLinearEditor,
  actionToggleObjectsSnapMode,
  actionChangeBackgroundImage, actionLoadScene
} from "../actions";
import { createRedoAction, createUndoAction } from "../actions/actionHistory";
import { ActionManager } from "../actions/manager";
import { actions } from "../actions/register";
import { Action, ActionResult } from "../actions/types";
import { trackEvent } from "../analytics";
import {
  getDefaultAppState,
  isEraserActive,
  isHandToolActive,
} from "../appState";
import {
  PastedMixedContent,
  copyTextToSystemClipboard,
  parseClipboard,
} from "../clipboard";
import {
  APP_NAME,
  CURSOR_TYPE,
  DEFAULT_MAX_IMAGE_WIDTH_OR_HEIGHT,
  DEFAULT_VERTICAL_ALIGN,
  DRAGGING_THRESHOLD,
  ELEMENT_READY_TO_ERASE_OPACITY,
  ELEMENT_SHIFT_TRANSLATE_AMOUNT,
  ELEMENT_TRANSLATE_AMOUNT,
  ENV,
  EVENT,
  FRAME_STYLE,
  EXPORT_IMAGE_TYPES,
  GRID_SIZE,
  IMAGE_MIME_TYPES,
  IMAGE_RENDER_TIMEOUT,
  isBrave,
  LINE_CONFIRM_THRESHOLD,
  MAX_ALLOWED_FILE_BYTES,
  MIME_TYPES,
  MQ_MAX_HEIGHT_LANDSCAPE,
  MQ_MAX_WIDTH_LANDSCAPE,
  MQ_MAX_WIDTH_PORTRAIT,
  MQ_RIGHT_SIDEBAR_MIN_WIDTH,
  POINTER_BUTTON,
  ROUNDNESS,
  SCROLL_TIMEOUT,
  TAP_TWICE_TIMEOUT,
  TEXT_TO_CENTER_SNAP_THRESHOLD,
  THEME,
  THEME_FILTER,
  TOUCH_CTX_MENU_TIMEOUT,
  VERTICAL_ALIGN,
  YOUTUBE_STATES,
  ZOOM_STEP,
  POINTER_EVENTS,
  TOOL_TYPE,
  EDITOR_LS_KEYS,
  isIOS, DEFAULT_ARROW_STROKE_WIDTH, DEFAULT_FONT_FAMILY, SHIFT_LOCKING_ANGLE, DEFAULT_FONT_SIZE, FONT_FAMILY,
} from "../constants";
import { ExportedElements, exportCanvas, loadFromBlob } from "../data";
import Library, { distributeLibraryItemsOnSquareGrid } from "../data/library";
import { restore, restoreElements } from "../data/restore";
import {
  dragNewElement,
  dragSelectedElements,
  duplicateElement,
  getCommonBounds,
  getCursorForResizingElement,
  getDragOffsetXY,
  getElementWithTransformHandleType,
  getNormalizedDimensions,
  getResizeArrowDirection,
  getResizeOffsetXY,
  getLockedLinearCursorAlignSize,
  getTransformHandleTypeFromCoords,
  hitTest,
  isHittingElementBoundingBoxWithoutHittingElement,
  isInvisiblySmallElement,
  isNonDeletedElement,
  isTextElement,
  newElement,
  newLinearElement,
  newTextElement,
  newImageElement,
  textWysiwyg,
  textAreaWysiwyg,
  transformElements,
  updateTextElement,
  redrawTextBoundingBox, refreshTextDimensions, getElementAbsoluteCoords,
} from "../element";
import {
  indLinearElement,
  bindOrUnbindLinearElement,
  bindOrUnbindSelectedElements,
  fixBindingsAfterDeletion,
  fixBindingsAfterDuplication,
  getEligibleElementsForBinding,
  getHoveredElementForBinding,
  isBindingEnabled,
  isLinearElementSimpleAndAlreadyBound,
  maybeBindLinearElement,
  shouldEnableBindingForPointerEvent,
  unbindLinearElements, updateBindingStartDirection,
  updateBoundElements, updatedraggingElementPoint, bindLinearElement,
} from "../element/binding";
import { LinearElementEditor } from "../element/linearElementEditor";
import { mutateElement, newElementWith } from "../element/mutateElement";
import {
  deepCopyElement,
  duplicateElements,
  newFrameElement,
  newFreeDrawElement,
  newEmbeddableElement,
  newMagicFrameElement,
  newIframeElement,
  newMindmapElement, newStickyNotesElement, newTextTiptapElement,
} from "../element/newElement";
import {
  hasBoundTextElement,
  isArrowElement,
  isBindingElement,
  isBindingElementType,
  isBoundToContainer,
  isFrameLikeElement,
  isImageElement,
  isEmbeddableElement,
  isInitializedImageElement,
  isLinearElement,
  isLinearElementType,
  isUsingAdaptiveRadius,
  isFrameElement,
  isIframeElement,
  isIframeLikeElement,
  isMagicFrameElement,
  isFreeDrawElement,
  isMindmapElement,
  isProjectLinkElement,
  isFreeDrawElementType,
  isStickyNotesElement, isHoverAbleElement, isShapeElement, isBindableElement, isEmbedableBookmarkElement,
} from "../element/typeChecks";
import {
  ExcalidrawBindableElement,
  ExcalidrawElement,
  ExcalidrawFreeDrawElement,
  ExcalidrawGenericElement,
  ExcalidrawLinearElement,
  ExcalidrawTextElement,
  NonDeleted,
  InitializedExcalidrawImageElement,
  ExcalidrawImageElement,
  FileId,
  NonDeletedExcalidrawElement,
  ExcalidrawTextContainer,
  ExcalidrawFrameLikeElement,
  ExcalidrawMagicFrameElement,
  ExcalidrawIframeLikeElement,
  IframeData,
  ExcalidrawIframeElement,
  ExcalidrawEmbeddableElement,
  ExcalidrawMindmapElement,
  RichText, ExcalidrawStickyNotesElement,
} from "../element/types";
import { getCenter, getDistance } from "../gesture";
import {
  editGroupForSelectedElement,
  getElementsInGroup,
  getSelectedGroupIdForElement,
  getSelectedGroupIds,
  isElementInGroup,
  isSelectedViaGroup,
  selectGroupsForSelectedElements,
} from "../groups";
import History from "../history";
import { defaultLang, getLanguage, languages, setLanguage, t } from "../i18n";
import {
  CODES,
  shouldResizeFromCenter,
  shouldMaintainAspectRatio,
  shouldRotateWithDiscreteAngle,
  isArrowKey,
  KEYS,
} from "../keys";
import { calculateScaledRectSize,isElementInViewport } from "../element/sizeHelpers";
import {
  distance2d,
  getCornerRadius,
  getGridPoint,
  isPathALoop,
  rotate,
} from "../math";
import {
  calculateScrollCenter,
  getElementsAtPosition,
  getElementsWithinSelection,
  getNormalizedZoom,
  getSelectedElements,
  hasBackground,
  isOverScrollBars,
  isSomeElementSelected,
} from "../scene";
import Scene from "../scene/Scene";
import { RenderInteractiveSceneCallback, ScrollBars } from "../scene/types";
import { getStateForZoom } from "../scene/zoom";
import { findShapeByKey } from "../shapes";
import {
  AppClassProperties,
  AppProps,
  AppState,
  BinaryFileData,
  DataURL,
  ExcalidrawImperativeAPI,
  BinaryFiles,
  Gesture,
  GestureEvent,
  LibraryItems,
  PointerDownState,
  SceneData,
  Device,
  FrameNameBoundsCache,
  SidebarName,
  SidebarTabName,
  KeyboardModifiersObject,
  CollaboratorPointer,
  ToolType,
  OnUserFollowedPayload,
  UnsubscribeCallback,
  CroppedImageSize,
  DisplayMode, ElementWithQuickFlowHandle, QuickFlowCount,
  ElementWithHoverHandle, VideoNotesType,
} from "../types";
import {
  debounce,
  distance,
  getFontString,
  getNearestScrollableContainer,
  isInputLike,
  isToolIcon,
  isWritableElement,
  sceneCoordsToViewportCoords,
  tupleToCoors,
  viewportCoordsToSceneCoords,
  wrapEvent,
  updateObject,
  updateActiveTool,
  getShortcutKey,
  isTransparent,
  easeToValuesRAF,
  muteFSAbortError,
  isTestEnv,
  easeOut,
  updateStable,
  addEventListener,
  normalizeEOL, getUpdatedTimestamp,
} from "../utils";
import {
  createSrcDoc,
  embeddableURLValidator,
  maybeParseEmbedSrc,
  getEmbedLink,
} from "../element/embeddable";
import {
  ContextMenu,
  ContextMenuItems,
  CONTEXT_MENU_SEPARATOR, getArrayExportMarkdown,
} from "./ContextMenu";
import LayerUI from "./LayerUI";
import { Toast } from "./Toast";
import { actionToggleViewMode } from "../actions/actionToggleViewMode";
import {
  dataURLToFile, downsampleImage,
  generateIdFromFile,
  getDataURL,
  getFileFromEvent,
  ImageURLToFile,
  isImageFileHandle,
  isSupportedImageFile,
  loadSceneOrLibraryFromBlob,
  normalizeFile,
  parseLibraryJSON,
  resizeImageFile,
  SVGStringToFile,
} from "../data/blob";
import {
  getInitializedImageElements,
  loadHTMLImageElement,
  normalizeSVG,
  updateImageCache as _updateImageCache,
} from "../element/image";
import throttle from "lodash.throttle";
import { fileOpen, FileSystemHandle } from "../data/filesystem";
import {
  bindTextToShapeAfterDuplication, computeContainerDimensionForBoundText,
  getApproxFontSizeByContainerHeight,
  getApproxMinLineHeight,
  getApproxMinLineWidth,
  getBoundTextElement,
  getContainerCenter,
  getContainerElement,
  getDefaultLineHeight,
  getLineHeightInPx,
  getTextBindableContainerAtPosition,
  isMeasureTextSupported,
  isValidTextContainer,
} from "../element/textElement";
import { isHittingElementNotConsideringBoundingBox } from "../element/collision";
import {
  showHyperlinkTooltip,
  hideHyperlinkToolip,
  Hyperlink,
  isPointHittingLink,
  isPointHittingLinkIcon, isPointHittingVideoNotes,
} from "../element/Hyperlink";
import { isLocalLink, normalizeLink, toValidURL } from "../data/url";
import { shouldShowBoundingBox } from "../element/transformHandles";
import { actionUnlockAllElements } from "../actions/actionElementLock";
import { Fonts } from "../scene/Fonts";
import {
  getFrameChildren,
  isCursorInFrame,
  bindElementsToFramesAfterDuplication,
  addElementsToFrame,
  replaceAllElementsInFrame,
  removeElementsFromFrame,
  getElementsInResizingFrame,
  getElementsInNewFrame,
  getContainingFrame,
  elementOverlapsWithFrame,
  updateFrameMembershipOfSelectedElements,
  isElementInFrame,
  getFrameLikeTitle,
  expandRect2ToContainRect1,
} from "../frame";
import {
  excludeElementsInFramesFromSelection,
  makeNextSelectedElementIds,
} from "../scene/selection";
import {
  actionAddImage,
  actionAddLink,
  actionChangeMindmapLineColor,
  actionDeleteNode,
  actionEditLink,
  actionMindmapAddCard,
  actionMindmapAddFile,
  actionMindmapAddHighlight,
  actionMindmapAddPdf,
  actionMindmapAddVideo,
  actionMindmapCopylink,actionMindmapCopyToClipboardAsMarkdown,
  actionMindmapDeleteCard,
  actionMindmapRemoveFile,
  actionMindmapRemoveHighlight,
    actionMindmapExportAsMarkdown,
    actionMindmapExportToMarkdown,
    actionMindmapImportFromMarkdown,
  actionMindmapRemoveLink,
  actionMindmapRemoveVideo,
  actionMoveDownNode,
  actionMoveUpNode,
  actionPaste,
} from "../actions/actionClipboard";
import {
  actionRemoveAllElementsFromFrame,
  actionSelectAllElementsInFrame,
} from "../actions/actionFrame";
import { actionToggleHandTool, zoomToFit } from "../actions/actionCanvas";
import {jotaiScope, jotaiStore} from "../jotai";
import { activeConfirmDialogAtom } from "./ActiveConfirmDialog";
import { ImageSceneDataError } from "../errors";
import {
  getSnapLinesAtPointer,
  snapDraggedElements,
  isActiveToolNonLinearSnappable,
  snapNewElement,
  snapResizingElements,
  isSnappingEnabled,
  getVisibleGaps,
  getReferenceSnapPoints,
  SnapCache,
} from "../snapping";
import { actionWrapTextInContainer } from "../actions/actionBoundText";
import BraveMeasureTextError from "./BraveMeasureTextError";
import {activeEyeDropperAtom, EyeDropper} from "./EyeDropper";
import {
  ExcalidrawElementSkeleton,
  convertToExcalidrawElements,
} from "../data/transform";
import { MarkOptional,ValueOf } from "../utility-types";
import { isSidebarDockedAtom } from "./Sidebar/Sidebar";
import { StaticCanvas, InteractiveCanvas } from "./canvases";
import { Renderer } from "../scene/Renderer";
import { ShapeCache } from "../scene/ShapeCache";
import { LaserToolOverlay } from "./LaserTool/LaserTool";
import { LaserPathManager } from "./LaserTool/LaserPathManager";
import {
  setEraserCursor,
  setCursor,
  resetCursor,
  setCursorForShape, getCursor, forceSetCursor,
} from "../cursor";
import { Emitter } from "../emitter";
import { ElementCanvasButtons } from "../element/ElementCanvasButtons";
import { MagicCacheData, diagramToHTML } from "../data/magic";
import { elementsOverlappingBBox, exportToBlob } from "../../utils/export";
import { COLOR_PALETTE } from "../colors";
import { ElementCanvasButton } from "./MagicButton";
import { MagicIcon, copyIcon, fullscreenIcon } from "./icons";
import { EditorLocalStorage } from "../data/EditorLocalStorage";
import FollowMode from "./FollowMode/FollowMode";
import { withBatchedUpdates, withBatchedUpdatesThrottled } from "../reactUtils";
import {
  base64ToBlob, blobToBase64,
  calcTextAreaHeight,
  calcTextHeight, calcVideoAnnoHeight,
  changeSvgColor,
  createFileElementHtml,
  createNoteElementByNote,
  createPdfAnnoTextElement,
  decodeDeeplink, downloadImage, downloadImageBlob,
  extFromMineType, firstLetterUpper,
  formatDate,
  generateRandomPageKey,
  generateUUID2,
  getBase64ImageSize, getFileIcon,
  getFileImportMode,
  getFileNameExt,
  getFileNameWithoutExt, getImageExt,
  getImagePath,
  getImagePathWithTheme, getImagePathWithThemeSvg,
  getLocalFilePath,
  getPdfImageThumb, getSafeFloat, getSafeInt,
  getSvgColor,
  getSvgHeight,
  getSvgWidth,
  getVideoImageThumb,
  getYoutubeThumbUrl, hasBeyondBoardLimit, hasBeyondTextLimit, hasDesktopBaseLicense,
  hasLicense,
  imgPathPerfix, isAnnoElement, isAnnoType, isArray,
  isAudio,
  isAudioAnnoElement,
  isAudioFileElement, isBaiduVideoUrl,
  isBilibiliUrl,
  isCardAnnoElement, isCardBlockDeeplink,
  isCardDeeplink,
  isDarkMode,
  isDeepLink, isFileDeeplink,
  isFileElement,
  isFileVideoElement,
  isFreeCreateCardMode, isHighlightDeeplink, isHTML,
  isImage,
  isLinkAndNotExist, isLiveVideoElement, isMdFileElement,
  isNoteElement,
  isPdf,
  isPdfAnnoElement,
  isPdfFileElement,
  isStringNullOrEmpty,
  isSupportVideoAnnoUrl,
  isSvg,
  isVideo,
  isVideoAnnoElement, isVideoDeeplink,
  isYoutubeUrl,
} from "../../../../src/utils";
import ImageCropper from "./ImageEditor/ImageCropper";
import {
  BASE64_CANVAS_IMAGE_ERROR_PLACEHOLDER,
  BASE64_GIF,
  BASE64_PLACEHOLDER,
  DEFAULT_ARROW_COLOR,
  DEFAULT_AUDIO_HEIGHT,
  DEFAULT_AUDIO_WIDTH,
  DEFAULT_CANVAS_BACKGROUND,
  DEFAULT_CANVAS_BACKGROUND_DARK,
  DEFAULT_DRAW_ST_COLOR,
  DEFAULT_ELEMENT_STROKE_WITHD,
  DEFAULT_EMBEDABLE_BOOKMARK_HEIGHT,
  DEFAULT_EMBEDABLE_BOOKMARK_WIDTH,
  DEFAULT_EMBEDABLE_ELEMENT_HEIGHT,
  DEFAULT_EMBEDABLE_ELEMENT_WIDTH,
  DEFAULT_NOTE_HEIGHT,
  DEFAULT_NOTE_HEIGHT_1,
  DEFAULT_NOTE_WIDTH,
  DEFAULT_STICK_NOTE_HEIGHT,
  DEFAULT_STICK_NOTE_WIDTH,
  DEFAULT_STICKYNOTE_BACKGROUND_COLOR,
  DEFAULT_TEXT_HEIGHT,
  DEFAULT_TEXT_WIDTH,
  EVENT_ADD_NOTE_BY_CARD,
  EVENT_CANVAS_REFRESH_ELEMENT_COUNT,
  EVENT_CANVAS_SELECTION_CHANGED,
  EVENT_CLEAR_CANVAS,
  EVENT_COPY_DOC_NOTE_FROM_ELEMENT,
  EVENT_COPY_MINDMAP_FROM_ELEMENT,
  EVENT_DELETE_ELEMENT_BY_ID,
  EVENT_FORCE_UPDATE_MINDMAP,
  EVENT_FRAME_POINTER_UP,
  EVENT_HIDDEN_FLOAT_MENU,
  EVENT_MAX_DRAG_IMAGE,
  EVENT_MINDMAP_ADD_LINK,
  EVENT_NAVIGATE_DEEPLINK,
  EVENT_ON_DOUBLE_CLICK,
  EVENT_OPEN_VIDEO_IN_POPUP,
  EVENT_PASTE_DEEP_LINK,
  EVENT_PASTE_FILE_MESSAGE, EVENT_PASTE_MINDMAP_PLAINT_TEXT,
  EVENT_PASTE_URL,
  EVENT_REFRESH_LIBRARY_LIST,
  EVENT_REFRESH_PDF_ANNO_COMMENT,
  EVENT_RESIZING_CARD,
  EVENT_SAVE_TO_SERVER,
  EVENT_SHOW_FONT_CHOOSE,
  EVENT_SHOW_IMPORT_FILE_VIP,
  EVENT_TEST_ONDRAG_NOTE,
  EVENT_UPDATE_FRAME_LIST,
  EVENT_UPDATE_HIGHLIGHT_ANNO,
  EVENT_UPDATE_MEDIA_ELEMENT_SRC,
  FROM_CANVAS,
  FROM_PAGE_CARD,
  getBookmarkBackgroundColor,
  getElementEditorBorderColor,
  getElementSelectionBorderColor,
  getInputBorderColor,
  getMindmapRootTextColor,
  getMindmapSecondTextColor,
  getNoteElementBackgroundColor,
  getNoteElementStrokeColor,
  getNoteElementStrokeColor250724,
  getProjectLinkElementBackground,
  getProjectLinkElementStrokeColor250728,
  getShapeElementBackgroundColor,
  getShapeElementStrokeColor,
  HAS_ENABLE_TIPTAP_TEXT,
  HEADER_HEIGHT, HOVER_CIRCLE_ELEMENT_SIZE,
  IMPORT_FILE_MODE_ASKME,
  IMPORT_FILE_MODE_IMPORTMODE,
  IMPORT_FILE_MODE_LINKMODE,
  KEY_CARD_ELEMENT_DEFAULT_STYLE,
  KEY_DEFAULT_STICKYNOTE_BACKGROUND_COLOR,
  KEY_DEFAULT_TEXT_BACKGROUND_COLOR,
  KEY_DEFAULT_TEXT_FONT_FAMILY,
  KEY_DEFAULT_TEXT_FONT_SIZE,
  KEY_DONOT_SHOW_REMOVE_IMAGE_TIP, KEY_DRAW_ARROW_LINE_STYLE, KEY_DRAW_ARROW_STROKE_COLOR, KEY_DRAW_ARROW_STROKE_WIDTH,
  KEY_FILE_DIR,
  KEY_FILE_IMPORT_MODE,
  KEY_FREE_DRAW_STROKE_WIDTH,
  KEY_GLOBAL_CONNECTOR_TYPE,
  KEY_GLOBAL_DOUBLE_CLICK_ELEMENT, KEY_STICKYNOTES_HAS_BORDER, KEY_TEXT_HAS_BORDER,
  KEY_USE_SCROLL_WHEEL_ZOOM,
  LINK_INFO_TYPE_URL,
  MAX_CARD_COUNT,
  MAX_IMPORT_FILE_SIZE,
  MAX_SIZE,
  MAX_Z_INDEX, PROJECT_THUMB_DEFAULT_HEIGHT,
  PROJECT_THUMB_W_H,
  usedColorToShowColor
} from "../../../../src/Const";
import Global from "../../../../src/global";
import SlidesBar from "./SlidesBar/SlidesBar";
import { cloneDeep } from "lodash";
import {ipcInvoke, ipcSend} from "../../../../src/common/ipc";
import {postMessage, recvMessage, unSubscribe} from "../../../../src/common/event";
import ImageSimpleCropper from "./ImageEditor/ImageSimpleCropper";
import {
  activeNode,
  clearMindmapActiveNode,
  createMindMap,
  mindMapCache,
  mindMapPngCache,
  mindMapFormatInfo,
  updateMindmapCallback,
  mindMapId_Data,
  mindMapUid_Text,
  mindMapControls,
  fontFamilyList,
  fontSizeList,
  addMindMapControls,
  OFFSET,
  hiddenToolbar,
  DEFAULT_MINDMAP_CONFIG,
  renderNodesToolBar, hideMindmapEditBox,
} from "../element/mindMap";
import mindmap from "../assets/mindmap.svg";
import mindmapWhite from "../assets/mindmap_white.svg";
import {ColorPicker, Dropdown, message, Modal, Popover, Spin, Tooltip} from "antd";
import MindMap from "./MindMap/MindMap";
import global from "../../../../src/global";
import {APP_WEB, isOfflineWeb, isWeb, isWebApp} from "../../../../src/electron_interactive";
import {getShareDataUrl} from "../../../../src/api/Aws";
import { updateRichText } from "../element/textWysiwyg";
import { Op } from "quill/core";
import {
  copyNewLibrary,
  dbGetHighlight, dbGetHighlightByLibraryId,
  getCardById,
  getCardCount, getCardList, getLibraryByFileName,
  renameCard,
  renameCardById,
  updateCard, updateHighlight
} from "../../../../model";
import {uploadS3File} from "../../../../src/api/AwsUpload";
import {fileToBase64} from "../../../../src/components/EditorV2/ImagePaste";
import i18next from "i18next";

import { actionChangeQuickFlow } from "../actions/actionQuickFlow";
import {
  QUICKFLOW_MARGIN,
  getElementWithQuickFlowHandles,
  hideQuickFlowToolip,
  showQuickFlowTooltip, getElementWithHoverHandles,
} from "../element/resizeTest";
import CardEdit from "./card/CardEdit";
import VideoNotes from "./VideoNotes/VideoNotes";
import {beyondFreeVideoLimit, getScheme, isMac, isSupportLink, pauseAllVideo} from "../../../../src/Utility";
import {isZh} from "../../../../src/language";
import EmptyElement from "./EmptyElement/EmptyElement";
import EmbedProject from "./ExtendView/EmbedProject";
import Input from "antd/es/input/Input";
import CustomAudio from "./Audio/CustomAudio";
import ColorChoose from "../../../../src/views/Draw/ColorChoose";
import {apiBilibiliCover, fetchPageTitle} from "../../../../src/api/ThirdApi";
import cardLink from "../element/cardLink";
import { LoadingOutlined } from "@ant-design/icons";
import {actionShowSnapLines} from "../actions/actionToggleGridMode";
import {HoveredElementCircle} from "./HoveredElementCircle/HoveredElementCircle";
import {PROJECT_ID} from "../../../../src/views/Draw/DrawIndex";
import {calculateScrollTopLeft} from "../scene/scroll";
import CardEditBubbleMenu from "./card/CardEditBubbleMenu";
import WebviewExt from "./ExtendView/WebviewExt";
import {getBaiduVideoNameByUrl} from "../../../../src/Utility0";
import ChooseFont from "./ExtendView/ChooseFont";
import ChooseFontSize from "./ExtendView/ChooseFontSize";
import addIconWhite from "../assets/add_white.svg";
import addIcon from "../assets/add.svg";
import ChooseTextStyle from "./ExtendView/ChooseTextStyle";
import {randomInteger} from "../random";
import {releaseCanvasCache} from "../renderer/renderElement";
import RenderCanvasNote from "./ExtendView/RenderCanvasNote";
import StickyNoteTiptap from "./StickyNote/StickyNoteTiptap";
import TextTiptap from "./TextTiptap/TextTiptap";
import {
  getTiptapHandleText,
  handleClipboard,
  parseHtmlFromPasteTiptap,
  parseHtmlFromPasteTiptapWithDownload
} from "../../../../src/components/EditorV2/TiptapCommonHandle";
import markdown, {transformMarkdownTo} from "./MindMap/Plus/markdownTo.js";
import {LinearElementTypeMenu} from "./LinearElementTypeMenu";
import {actionChangeLineType} from "../actions/actionProperties";
import MyMdEditor from "./MdEditor/MyMdEditor";


const AppContext = React.createContext<AppClassProperties>(null!);
const AppPropsContext = React.createContext<AppProps>(null!);

const deviceContextInitialValue = {
  viewport: {
    isMobile: false,
    isLandscape: false,
  },
  editor: {
    isMobile: false,
    canFitSidebar: false,
  },
  isTouchScreen: false,
};
const DeviceContext = React.createContext<Device>(deviceContextInitialValue);
DeviceContext.displayName = "DeviceContext";

export const ExcalidrawContainerContext = React.createContext<{
  container: HTMLDivElement | null;
  id: string | null;
}>({ container: null, id: null });
ExcalidrawContainerContext.displayName = "ExcalidrawContainerContext";

const ExcalidrawElementsContext = React.createContext<
  readonly NonDeletedExcalidrawElement[]
>([]);
ExcalidrawElementsContext.displayName = "ExcalidrawElementsContext";

const ExcalidrawAppStateContext = React.createContext<AppState>({
  ...getDefaultAppState(),
  width: 0,
  height: 0,
  offsetLeft: 0,
  offsetTop: 0,
});
ExcalidrawAppStateContext.displayName = "ExcalidrawAppStateContext";

const ExcalidrawSetAppStateContext = React.createContext<
  React.Component<any, AppState>["setState"]
>(() => {
  console.warn("Uninitialized ExcalidrawSetAppStateContext context!");
});
ExcalidrawSetAppStateContext.displayName = "ExcalidrawSetAppStateContext";

const ExcalidrawActionManagerContext = React.createContext<ActionManager>(
  null!,
);
ExcalidrawActionManagerContext.displayName = "ExcalidrawActionManagerContext";


export const useApp = () => useContext(AppContext);
export const useAppProps = () => useContext(AppPropsContext);
export const useDevice = () => useContext<Device>(DeviceContext);
export const useExcalidrawContainer = () =>
  useContext(ExcalidrawContainerContext);
export const useExcalidrawElements = () =>
  useContext(ExcalidrawElementsContext);
export const useExcalidrawAppState = () =>
  useContext(ExcalidrawAppStateContext);
export const useExcalidrawSetAppState = () =>
  useContext(ExcalidrawSetAppStateContext);
export const useExcalidrawActionManager = () =>
  useContext(ExcalidrawActionManagerContext);


const supportsResizeObserver =
  typeof window !== "undefined" && "ResizeObserver" in window;

let didTapTwice: boolean = false;
let tappedTwiceTimer = 0;
let isHoldingSpace: boolean = false;
let isPanning: boolean = false;
let isDraggingScrollBar: boolean = false;
let currentScrollBars: ScrollBars = { horizontal: null, vertical: null };
let touchTimeout = 0;
let invalidateContextMenu = false;
let previousMindMapElement: ExcalidrawMindmapElement | null = null;
let previousMindMapElementIndex = 0;
let mMoveHitElement:any = null;//当前移动在的元素
let imageChoosePromise:any;
let imageChoosePromiseResolve:any;

let textSelectRange: {
  selectionStart: number;
  selectionEnd: number;
  id: string;
} | null = null;
let copyTextSelectRange: {
  selectionStart: number;
  selectionEnd: number;
  id: string;
  element: ExcalidrawTextElement;
} | null = null;

export let resizeHandleType: any = false;

/**
 * Map of youtube embed video states
 */
const YOUTUBE_VIDEO_STATES = new Map<
  ExcalidrawElement["id"],
  ValueOf<typeof YOUTUBE_STATES>
>();

let IS_PLAIN_PASTE = false;
let IS_PLAIN_PASTE_TIMER = 0;
let PLAIN_PASTE_TOAST_SHOWN = false;

let SELECTED_FRAME_INDEX = 0;

let lastPointerUp: (() => void) | null = null;
const gesture: Gesture = {
  pointers: new Map(),
  lastCenter: null,
  initialDistance: null,
  initialScale: null,
};
export const videoCache: Map<string, VideoNoteType[]> = new Map();
let mOnPointDownCallback: (( activeTool: AppState["activeTool"],
                             pointerDownState: PointerDownState) => void) | null = null;

let mIsPointerRightDown = false,mIsPointerRightMove = false;

class App extends React.Component<AppProps, AppState> {
  canvas: AppClassProperties["canvas"];
  interactiveCanvas: AppClassProperties["interactiveCanvas"] = null;
  rc: RoughCanvas;
  unmounted: boolean = false;
  actionManager: ActionManager;
  device: Device = deviceContextInitialValue;

  private excalidrawContainerRef = React.createRef<HTMLDivElement>();
  private staticCanvasRef = React.createRef<HTMLDivElement>();

  public scene: Scene;
  public renderer: Renderer;
  private fonts: Fonts;
  private resizeObserver: ResizeObserver | undefined;
  private nearestScrollableContainer: HTMLElement | Document | undefined;
  public library: AppClassProperties["library"];
  public libraryItemsFromStorage: LibraryItems | undefined;
  public id: string;
  private history: History;
  private excalidrawContainerValue: {
    container: HTMLDivElement | null;
    id: string;
  };

  public visibleElements:any = [];
  public files: BinaryFiles = {};
  public imageCache: AppClassProperties["imageCache"] = new Map();
  public quickFlowCache: AppClassProperties["quickFlowCache"] = new Map();
  public mIsMouseDown = false;
  public mIsMouseMoving = false;
  private mIsMouseMoved = false;//是否有过移动.
  private iFrameRefs = new Map<ExcalidrawElement["id"], HTMLIFrameElement>();
  private initializedEmbeds = new Set<ExcalidrawIframeLikeElement["id"]>();

  hitLinkElement?: NonDeletedExcalidrawElement;
  hitVideoNotesElement?: NonDeletedExcalidrawElement;
  elementWithQuickFlowHandle: ElementWithQuickFlowHandle | false = false;
  elementWithHoverHandle: ElementWithHoverHandle | false = false;
  lastPointerDownEvent: React.PointerEvent<HTMLElement> | null = null;
  lastPointerUpEvent: React.PointerEvent<HTMLElement> | PointerEvent | null =
    null;
  lastPointerMoveEvent: PointerEvent | null = null;
  lastViewportPosition = { x: 0, y: 0 };

  laserPathManager: LaserPathManager = new LaserPathManager(this);

  onChangeEmitter = new Emitter<
    [
      elements: readonly ExcalidrawElement[],
      appState: AppState,
      files: BinaryFiles,
    ]
  >();

  onUpdateDisplayMode = new Emitter();

  onPointerDownEmitter = new Emitter<
    [
      activeTool: AppState["activeTool"],
      pointerDownState: PointerDownState,
      event: React.PointerEvent<HTMLElement>,
    ]
  >();

  onPointerUpEmitter = new Emitter<
    [
      activeTool: AppState["activeTool"],
      pointerDownState: PointerDownState,
      event: PointerEvent,
    ]
  >();
  onUserFollowEmitter = new Emitter<[payload: OnUserFollowedPayload]>();
  onScrollChangeEmitter = new Emitter<
    [scrollX: number, scrollY: number, zoom: AppState["zoom"]]
  >();

  missingPointerEventCleanupEmitter = new Emitter<
    [event: PointerEvent | null]
  >();
  onRemoveEventListenersEmitter = new Emitter<[]>();

  constructor(props: AppProps) {
    super(props);
    const defaultAppState = getDefaultAppState();
    const {
      excalidrawAPI,
      viewModeEnabled = false,
      zenModeEnabled = false,
      gridModeEnabled = false,
      objectsSnapModeEnabled = false,
      theme = defaultAppState.theme,
      name = defaultAppState.name
    } = props;
    this.state = {
      ...defaultAppState,
      theme,
      isLoading: true,
      ...this.getCanvasOffsets(),
      viewModeEnabled,
      zenModeEnabled,
      objectsSnapModeEnabled,
      gridSize: gridModeEnabled ? GRID_SIZE : null,
      name,
      width: window.innerWidth,
      height: window.innerHeight,
    };

    this.id = nanoid();
    this.library = new Library(this);
    this.actionManager = new ActionManager(
      this.syncActionResult,
      () => this.state,
      () => this.scene.getElementsIncludingDeleted(),
      this,
    );
    this.scene = new Scene();
    this.fileImageInputRef = createRef();

    this.canvas = document.createElement("canvas");
    this.rc = rough.canvas(this.canvas);
    this.renderer = new Renderer(this.scene);
    if (excalidrawAPI) {
      const api: ExcalidrawImperativeAPI = {
        updateScene: this.updateScene,
        updateDisplayMode: this.updateDisplayMode,
        updateLibrary: this.library.updateLibrary,
        addFiles: this.addFiles,
        resetScene: this.resetScene,
        getSceneElementsIncludingDeleted: this.getSceneElementsIncludingDeleted,
        history: {
          clear: this.resetHistory,
        },
        scrollToContent: this.scrollToContent,
        getSceneElements: this.getSceneElements,
        getAppState: () => this.state,
        getFiles: () => this.files,
        registerAction: (action: Action) => {
          this.actionManager.registerAction(action);
        },
        refresh: this.refresh,
        setToast: this.setToast,
        id: this.id,
        setActiveTool: this.setActiveTool,
        setCursor: this.setCursor,
        resetCursor: this.resetCursor,
        updateFrameRendering: this.updateFrameRendering,
        toggleSidebar: this.toggleSidebar,
        getApp:()=> this,
        onChange: (cb) => this.onChangeEmitter.on(cb),
        onPointerDown: (cb) => this.onPointerDownEmitter.on(cb),
        onPointerUp: (cb) => this.onPointerUpEmitter.on(cb),
        onScrollChange: (cb) => this.onScrollChangeEmitter.on(cb),
        onUserFollow: (cb) => this.onUserFollowEmitter.on(cb),
        onUpdateDisplayMode: () => this.onUpdateDisplayMode.on(),
      } as const;
      if (typeof excalidrawAPI === "function") {
        excalidrawAPI(api);
      } else {
        console.error("excalidrawAPI should be a function!");
      }
    }

    this.excalidrawContainerValue = {
      container: this.excalidrawContainerRef.current,
      id: this.id,
    };

    this.fonts = new Fonts({
      scene: this.scene,
      onSceneUpdated: this.onSceneUpdated,
    });
    this.history = new History();
    this.actionManager.registerAll(actions);

    this.actionManager.registerAction(createUndoAction(this.history));
    this.actionManager.registerAction(createRedoAction(this.history));
  }

  private onWindowMessage(event: MessageEvent) {
    if (
      event.origin !== "https://player.vimeo.com" &&
      event.origin !== "https://www.youtube.com"
    ) {
      return;
    }

    let data = null;
    try {
      data = JSON.parse(event.data);
    } catch (e) {}
    if (!data) {
      return;
    }

    switch (event.origin) {
      case "https://player.vimeo.com":
        //Allowing for multiple instances of Excalidraw running in the window
        if (data.method === "paused") {
          let source: Window | null = null;
          const iframes = document.body.querySelectorAll(
            "iframe.excalidraw__embeddable",
          );
          if (!iframes) {
            break;
          }
          for (const iframe of iframes as NodeListOf<HTMLIFrameElement>) {
            if (iframe.contentWindow === event.source) {
              source = iframe.contentWindow;
            }
          }
          source?.postMessage(
            JSON.stringify({
              method: data.value ? "play" : "pause",
              value: true,
            }),
            "*",
          );
        }
        break;
      case "https://www.youtube.com":
        if (
          data.event === "infoDelivery" &&
          data.info &&
          data.id &&
          typeof data.info.playerState === "number"
        ) {
          const id = data.id;
          const playerState = data.info.playerState as number;
          if (
            (Object.values(YOUTUBE_STATES) as number[]).includes(playerState)
          ) {
            YOUTUBE_VIDEO_STATES.set(
              id,
              playerState as ValueOf<typeof YOUTUBE_STATES>,
            );
          }
        }
        break;
    }
  }

  private cacheEmbeddableRef(
    element: ExcalidrawIframeLikeElement,
    ref: HTMLIFrameElement | null,
  ) {
    if (ref) {
      this.iFrameRefs.set(element.id, ref);
    }

    // ref?.style.backgroundColor = "red";
    //set style....
    // let cssLink = document.createElement("link");
    // cssLink.href = "IFrameNote.css";
    // cssLink.rel = "stylesheet";
    // cssLink.type = "text/css";
    // var head = ref?.getElementsByTagName('head')[0];
    // head.append('<link rel="stylesheet" type="text/css" href="style.css">')
  }

  private getHTMLIFrameElement(
    element: ExcalidrawIframeLikeElement,
  ): HTMLIFrameElement | undefined {
    return this.iFrameRefs.get(element.id);
  }

  private handleEmbeddableCenterClick(element: ExcalidrawIframeLikeElement) {
    if (
      this.state.activeEmbeddable?.element === element &&
      this.state.activeEmbeddable?.state === "active"
    ) {
      return;
    }

    if (isIframeElement(element)) {
      return;
    }

    // The delay serves two purposes
    // 1. To prevent first click propagating to iframe on mobile,
    //    else the click will immediately start and stop the video
    // 2. If the user double clicks the frame center to activate it
    //    without the delay youtube will immediately open the video
    //    in fullscreen mode
    //
    // ****************************************************************************
    // 激活内部的元素。如果要使用iframe的编辑状态，上面不能直接返回，这个要启用焦点..............
    // ****************************************************************************
    setTimeout(() => {
      this.setState({
        activeEmbeddable: { element, state: "active" },
        selectedElementIds: { [element.id]: true },
        draggingElement: null,
        selectionElement: null,
      });
    }, 100);

    const iframe = this.getHTMLIFrameElement(element);

    if (!iframe?.contentWindow) {
      return;
    }

    if (iframe.src.includes("youtube")) {
      const state = YOUTUBE_VIDEO_STATES.get(element.id);
      if (!state) {
        YOUTUBE_VIDEO_STATES.set(element.id, YOUTUBE_STATES.UNSTARTED);
        iframe.contentWindow.postMessage(
          JSON.stringify({
            event: "listening",
            id: element.id,
          }),
          "*",
        );
      }
      switch (state) {
        case YOUTUBE_STATES.PLAYING:
        case YOUTUBE_STATES.BUFFERING:
          iframe.contentWindow?.postMessage(
            JSON.stringify({
              event: "command",
              func: "pauseVideo",
              args: "",
            }),
            "*",
          );
          break;
        default:
          iframe.contentWindow?.postMessage(
            JSON.stringify({
              event: "command",
              func: "playVideo",
              args: "",
            }),
            "*",
          );
      }
    }

    if (iframe.src.includes("player.vimeo.com")) {
      iframe.contentWindow.postMessage(
        JSON.stringify({
          method: "paused", //video play/pause in onWindowMessage handler
        }),
        "*",
      );
    }
  }


  private handleStickyNotesCenterClick(element: ExcalidrawStickyNotesElement) {
    if (
        this.state.activeStickyNotes?.element === element &&
        this.state.activeStickyNotes?.state === "active"
    ) {
      return;
    }

    this.setState({
      activeStickyNotes: { element, state: "active" },
      selectedElementIds: { [element.id]: true },
      draggingElement: null,
      selectionElement: null,
    });
  }

  private isIframeLikeElementCenter(
    el: ExcalidrawIframeLikeElement | null,
    event: React.PointerEvent<HTMLElement> | PointerEvent,
    sceneX: number,
    sceneY: number,
  ) {
    return (
      el &&
      !event.altKey &&
      !event.shiftKey &&
      !event.metaKey &&
      !event.ctrlKey &&
      (this.state.activeEmbeddable?.element !== el ||
        this.state.activeEmbeddable?.state === "hover" ||
        !this.state.activeEmbeddable) &&
      sceneX >= el.x + el.width / 3 &&
      sceneX <= el.x + (2 * el.width) / 3 &&
      sceneY >= el.y + el.height / 3 &&
      sceneY <= el.y + (2 * el.height) / 3
    );
  }

  private isStickyNotesElementCenter(
      el: ExcalidrawIframeLikeElement | null,
      event: React.PointerEvent<HTMLElement> | PointerEvent,
      sceneX: number,
      sceneY: number,
  ) {
    // return true;
    return (
        el &&
        !event.altKey &&
        !event.shiftKey &&
        !event.metaKey &&
        !event.ctrlKey &&
        (this.state.activeStickyNotes?.element !== el ||
            this.state.activeStickyNotes?.state === "hover" ||
            !this.state.activeStickyNotes) &&
        sceneX >= el.x + el.width / 3 &&
        sceneX <= el.x + (2 * el.width) / 3 &&
        sceneY >= el.y + el.height / 3 &&
        sceneY <= el.y + (2 * el.height) / 3
    );
  }

  private handleMindMapToolbar = ({
                                    type,
                                    color,
                                    fontFamily,
                                    fontSize,
                                  }: {
    type: string;
    color?: string;
    fontFamily?: string;
    fontSize?: string;
  }) => {
    if (!mindMapFormatInfo.bold){
      mindMapFormatInfo.bold = false;
    }
    if (!mindMapFormatInfo.italic){
      mindMapFormatInfo.italic = false;
    }
    if (!mindMapFormatInfo.underline){
      mindMapFormatInfo.underline = false;
    }
    if (!mindMapFormatInfo.strike){
      mindMapFormatInfo.strike = false;
    }
    const { id, isNodes,bold, italic, underline, strike } = mindMapFormatInfo;

    const mindMap = mindMapCache.get(id)!;

    if (isNodes) {
      const firstNode = mindMap.renderer.activeNodeList[0];
      const data = cloneDeep(firstNode?.nodeData?.data);
      const config = this.state.mindmap?.selected?.element?.config || DEFAULT_MINDMAP_CONFIG;
      mindMap.renderer.activeNodeList.forEach((node: any) => {
        if (type === "remove") {
          node.setStyles({
            fontWeight: node.isRoot ? "bold" : "none",
            fontStyle: "none",
            underline: "none",
            strike: "none",
            color: node.isRoot?getMindmapRootTextColor():getMindmapSecondTextColor(),
            fillColor: "transparent", // 节点颜色
            background: "transparent", // 文字背景颜色
            fontFamily: 'OpenSans',//config.fontFamily,//"微软雅黑, Microsoft YaHei",
            fontSize: node.isRoot ? config.fontSize : config.subnodeFontSize,
          });

          return;
        }
        switch (type) {
          case "bold":
            node.setStyle(
                "fontWeight",
                data.fontWeight === "bold" ? "none" : "bold",
            );
            break;
          case "italic":
            node.setStyle(
                "fontStyle",
                data.fontStyle === "italic" ? "none" : "italic",
            );
            break;
          case "underline":
            node.setStyle(
                "underline",
                data.underline === "underline" ? "none" : "underline",
            );
            break;
          case "strike":
            node.setStyle(
                "strike",
                data.strike === "strike" ? "none" : "strike",
            );
            break;
          case "color":
            node.setStyle("color", color);
            break;
          case "backgroundColor":
            node.setStyle("fillColor", color);
            break;
          case "fontFamily":
            node.setStyle("fontFamily", fontFamily);

            break;
          case "fontSize":
            node.setStyle("fontSize", fontSize);
            break;
        }
      });

      if (mindMap.renderer.activeNodeList.length === 1) {
        mindMapControls(
            this.state.zoom.value,
            mindMap.renderer.activeNodeList[0],
            mindMap,
        );
      }

      return;
    }

    if (type === "remove") {
      // @ts-ignore
      mindMap.richText.removeFormat();
      return;
    }

    switch (type) {
      case "bold":
        // @ts-ignore
        mindMap.richText.formatText({
          bold: !bold,
        });
        mindMapFormatInfo.bold = !bold;
        break;
      case "italic":
        // @ts-ignore
        mindMap.richText.formatText({
          italic: !italic,
        });
        mindMapFormatInfo.italic = !italic;
        break;
      case "underline":
        // @ts-ignore
        mindMap.richText.formatText({
          underline: !underline,
        });
        mindMapFormatInfo.underline = !underline;
        break;

      case "strike":
        // @ts-ignore
        mindMap.richText.formatText({
          strike: !strike,
        });
        mindMapFormatInfo.strike = !strike;
        break;
      case "color":
        // @ts-ignore
        mindMap.richText.formatText({
          color,
        });
        break;
      case "backgroundColor":
        // @ts-ignore
        mindMap.richText.formatText({
          background: color,
        });
        break;
      case "fontFamily":
        // @ts-ignore
        mindMap.richText.formatText({
          font: fontFamily,
        });
        break;
      case "fontSize":
        // @ts-ignore
        mindMap.richText.formatText({
          size: `${fontSize}px`,
        });
        break;
    }
  };

  private updateEmbeddables = () => {
    const iframeLikes = new Set<ExcalidrawIframeLikeElement["id"]>();

    let updated = false;
    this.scene.getNonDeletedElements().filter((element) => {
      if (isEmbeddableElement(element)) {
        iframeLikes.add(element.id);
        if (element.validated == null) {
          updated = true;

          const validated = embeddableURLValidator(
            element.link,
            this.props.validateEmbeddable,
          );

          mutateElement(element, { validated }, false);
          ShapeCache.delete(element);
        }
      } else if (isIframeElement(element)) {
        iframeLikes.add(element.id);
      }
      return false;
    });

    if (updated) {
      this.scene.informMutation();
    }

    // GC
    this.iFrameRefs.forEach((ref, id) => {
      if (!iframeLikes.has(id)) {
        this.iFrameRefs.delete(id);
      }
    });
  };

  /**
   * 是否渲染cardEdit
   * @param element
   * @private
   */
  private isRenderNoteCardEdit(element:any){
    return isNoteElement(element);
  }

  /**
   * 元素是否被选中.
   * @param element
   * @private
   */
  private isElementSelect(element:any){
    return this.state.selectedElementIds[element.id];
  }

  /**
   * 是否有选中某元素
   * @param element
   */
  private isSelectedElement(element){
    return this.state.selectedElementIds[element.id];
  }

  private renderEmbeddables() {
    const scale = this.state.zoom.value;
    const normalizedWidth = this.state.width;
    const normalizedHeight = this.state.height;

    const embeddableElements = this.scene
        .getNonDeletedElements()
        .filter(
            (el): el is NonDeleted<ExcalidrawIframeLikeElement> =>
                (isEmbeddableElement(el) && !!el.validated) || isIframeElement(el),
        );

    /**
     * 获取视频地址的路径。
     * WEBSHARE 的。
     * WEB的
     * 本地路径的
     * 本地绝对路径的
     * 各种
     * @param el
     */
    const getVideoPath = (el)=>{
      if(APP_WEB){
        return (getShareDataUrl()+global.g_projId +"/notesfile/")+el.customData?.generationData.fileName;
      }
      if (isWebApp()){
        return el.customData?.generationData.fileName;
      }

      return getLocalFilePath(el.customData?.generationData?.fileName,el.customData?.generationData?.isAbsolutePath)
    }

      /**
       * 音频的路径....
       * @param el
       */
      const getAudioPath = (el)=>{
          if(APP_WEB){
              return (getShareDataUrl()+global.g_projId +"/notesfile/")+el.customData?.generationData.fileName;
          }
          if (isWebApp()){
              return el.customData?.generationData.fileName;
          }
          return getLocalFilePath(el.customData?.generationData?.fileName,el.customData?.generationData?.isAbsolutePath)
      }

      /**
     * pdf缩略图.
     * @param el
     */
    const getPdfThumb = (el)=> {
      // (APP_WEB?AWS_S3_PRE_DATA_URL:'localfile://')+(el.customData?.generationData.id + '.png')
      if (APP_WEB) {
        return getShareDataUrl() + global.g_projId + "/notesfile/" + el.customData?.generationData.id + '.png';
      }
      return 'localfile://' + el.customData?.generationData.id + '.png'
    }

    /**
     * 视频缩略图.
     * @param el
     */
    const getVideoThumb = (el)=> {
      // (APP_WEB?AWS_S3_PRE_DATA_URL:'localfile://')+(el.customData?.generationData.id + '.png')
      if (APP_WEB) {
        return getShareDataUrl() + global.g_projId + "/notesfile/" + el.customData?.generationData.id + '.png';
      }
      return 'localfile://' + el.customData?.generationData.id + '.png'
    }



    return (
        <>
          {embeddableElements.map((el) => {
            const { x, y } = sceneCoordsToViewportCoords(
                { sceneX: el.x, sceneY: el.y },
                this.state,
            );

            let  isVisible = isElementInViewport(
                el,
                normalizedWidth,
                normalizedHeight,
                this.state,
            );
            if (isMindmapElement(el) || isEmbeddableElement(el)){
              isVisible = true;
            }

            const hasBeenInitialized = this.initializedEmbeds.has(el.id);

            if (el.isHidden){
              isVisible = false;
            }

            // console.log('isVisibleisVisibleisVisibleisVisible:'+isVisible)

            if (isVisible && !hasBeenInitialized) {
              this.initializedEmbeds.add(el.id);
            }
            const shouldRender = isVisible || hasBeenInitialized;

            if (!shouldRender) {
              return null;
            }

            let src: IframeData | null;

            if (isIframeElement(el)) {
              src = null;

              const data: MagicCacheData = (el.customData?.generationData ??
                  this.magicGenerations.get(el.id)) || {
                status: "error",
                message: "No generation data",
                code: "ERR_NO_GENERATION_DATA",
              };

              if (data.status === "done") {
                let style = '';//"<head><link rel='stylesheet' href='IFrameNote.css' type='text/css'>";
                // style += "</head>";
                const html = style + data.html;
                src = {
                  intrinsicSize: { w: el.width, h: el.height },
                  type: "document",
                  srcdoc: () => {
                    return html;
                  },
                } as const;
              } else if (data.status === "pending") {
                src = {
                  intrinsicSize: { w: el.width, h: el.height },
                  type: "document",
                  srcdoc: () => {
                    return createSrcDoc(`
                    <style>
                      html, body {
                        width: 100%;
                        height: 100%;
                        color: ${
                        this.state.theme === "dark" ? "white" : "black"
                    };
                      }
                      body {
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        flex-direction: column;
                        gap: 1rem;
                      }

                      .Spinner {
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        margin-left: auto;
                        margin-right: auto;
                      }

                      .Spinner svg {
                        animation: rotate 1.6s linear infinite;
                        transform-origin: center center;
                        width: 40px;
                        height: 40px;
                      }

                      .Spinner circle {
                        stroke: currentColor;
                        animation: dash 1.6s linear 0s infinite;
                        stroke-linecap: round;
                      }

                      @keyframes rotate {
                        100% {
                          transform: rotate(360deg);
                        }
                      }

                      @keyframes dash {
                        0% {
                          stroke-dasharray: 1, 300;
                          stroke-dashoffset: 0;
                        }
                        50% {
                          stroke-dasharray: 150, 300;
                          stroke-dashoffset: -200;
                        }
                        100% {
                          stroke-dasharray: 1, 300;
                          stroke-dashoffset: -280;
                        }
                      }
                    </style>
                    <div class="Spinner">
                      <svg
                        viewBox="0 0 100 100"
                      >
                        <circle
                          cx="50"
                          cy="50"
                          r="46"
                          strokeWidth="8"
                          fill="none"
                          stroke-miter-limit="10"
                        />
                      </svg>
                    </div>
                    <div>Generating...</div>
                  `);
                  },
                } as const;
              } else {
                let message: string;
                if (data.code === "ERR_GENERATION_INTERRUPTED") {
                  message = "Generation was interrupted...";
                } else {
                  message = data.message || "Generation failed";
                }
                src = {
                  intrinsicSize: { w: el.width, h: el.height },
                  type: "document",
                  srcdoc: () => {
                    return createSrcDoc(`
                    <style>
                    html, body {
                      height: 100%;
                    }
                      body {
                        display: flex;
                        flex-direction: column;
                        align-items: center;
                        justify-content: center;
                        color: ${COLOR_PALETTE.red[3]};
                      }
                      h1, h3 {
                        margin-top: 0;
                        margin-bottom: 0.5rem;
                      }
                    </style>
                    <h1>Error!</h1>
                    <h3>${message}</h3>
                  `);
                  },
                } as const;
              }
            } else {
              src = getEmbedLink(toValidURL(el.link || ""));
            }

            // const src = getEmbedLink(toValidURL(el.link || ""));

            // let isActive = false;
            // let isHovered = false;
            // if (el.embedType === "page") {
            //   isActive =
            //       this.state.activeEmbeddable?.element === el &&
            //       this.state.activeEmbeddable?.state === "active";
            //   isHovered =
            //       this.state.activeEmbeddable?.element === el &&
            //       this.state.activeEmbeddable?.state === "hover";
            // }

            let isActive =
                this.state.activeEmbeddable?.element === el &&
                this.state.activeEmbeddable?.state === "active";
            let isHovered =
                this.state.activeEmbeddable?.element === el &&
                this.state.activeEmbeddable?.state === "hover";

            if (isEmbeddableElement(el) && el.embedType != 'page'){
              isActive = false;
            }


            // if (this.state.activeEmbeddable?.state !== "hover" && isFileVideoElement(el) ){
            //   isActive = true;
            // }
            // if(this.state.selectedElementIds[el.id] && isFileVideoElement(el)){
            //   // console.log('select and .....');
            //   // isActive = true;
            // }

            let nPadding = 0;//el.strokeWidth;
            if (el.type == "iframe" && !isAudioFileElement(el)){
              if (nPadding < 2){
                nPadding = 1;
              }
            }
            let isNoteEle = false;
            if (isNoteElement(el)){
              isNoteEle = true;
              if (this.isRenderNoteCardEdit(el)){
                // isActive = true;
              }

              /////这里是使用draw画上去的线的时候设置的padding
              // if (nPadding < 3){
              //   nPadding = 3;
              // }
              /////上面是使用draw画上去的线的时候设置的padding--配置CardEdit中的div-placeholder中的margin...使用
              nPadding = 0;
            }

            if (isFileVideoElement(el)){
              nPadding = 0;
            }

            if (isLinkAndNotExist(el)){
              nPadding = 1;
            }
            if (isEmbeddableElement(el) && el.embedType != 'page'){
              nPadding = 0;
            }

            if (isPdfFileElement(el) || isAnnoElement(el)){
              nPadding = 0;
            }

            if (isFileElement(el)){
              nPadding = 0;
            }


            const getCardEditBorder = ()=>{
              if(isEmbeddableElement(el) && el.embedType != 'page'){
                return '0px solid var(--line-color)';
              }
              if (isNoteElement(el))return 'none';//边框换成boxshadow了。
              if (!isNoteElement(el))return 'none';
              const nBorderWidth = 2.2/this.state.zoom.value;

              //边框颜色和粗细如果有变化的话也使用变化过的.
              const szDefaultStyle = localStorage.getItem(KEY_CARD_ELEMENT_DEFAULT_STYLE);
              let defaultStyle = {strokeWidth:2,strokeColor:'transparent',backgroundColor:getNoteElementBackgroundColor()};
              if (szDefaultStyle){
                const jsonDefaultStyle = JSON.parse(szDefaultStyle);
                if (jsonDefaultStyle.backgroundColor){
                  defaultStyle.backgroundColor = jsonDefaultStyle.backgroundColor;
                }
                if (jsonDefaultStyle.strokeWidth){
                  defaultStyle.strokeWidth = jsonDefaultStyle.strokeWidth;
                }
                if (jsonDefaultStyle.strokeColor){
                  defaultStyle.strokeColor = jsonDefaultStyle.strokeColor;
                }
                if (jsonDefaultStyle.corner){
                  defaultStyle.corner = jsonDefaultStyle.corner;
                }
              }
              let hasChange = el.strokeWidth != 2 || el.strokeColor != defaultStyle.strokeColor ;
              if (el.strokeColor == '#EFEFEF'){//之前的边框有这样设置的也有那样改过，兼容之前的。
                hasChange = false;
              }

              if (isNoteElement(el) && !this.isElementSelect(el) || hasChange){
                return `${el.strokeWidth/this.state.zoom.value}px solid ${el.strokeColor}`;
              }

              if (el.customData?.generationData?.isEditable){
                return `${nBorderWidth}px solid ${getElementEditorBorderColor()}`
              }

              // return `${nBorderWidth}px solid var(--primary-color)`;
              return `${nBorderWidth}px solid ${getElementSelectionBorderColor()}`;
            }

            const getBorder = ()=>{
              if (this.isSelectedElement(el)){
                if (el.customData?.generationData?.isEditable){
                  return `2px solid ${getInputBorderColor()}`
                }
                return `2px solid ${getElementSelectionBorderColor()}`
              }
              let nStrokeWidth = el.strokeWidth;
              if (nStrokeWidth <= 1){
                nStrokeWidth = 0;
              }
              //nStrokeWidth = nStrokeWidth * (1/this.state.zoom.value);
              return `${nStrokeWidth}px solid ${el.strokeColor}`;
            }

            const getBorderRadius = ()=>{
              if (isNoteElement(el)){
                return el.roundness?.value;
              }
              return 8;
            }

            const getOverFlow = ()=>{
              if (isNoteElement(el)){
                return 'hidden'
              }
              return '';
            }

            const getEmbeddableRadius = ()=>{
              if (isNoteElement(el))return `${getBorderRadius()}px`;
              if (isFileVideoElement(el) || isLinkAndNotExist(el) || isEmbeddableElement(el) || isPdfFileElement(el)){
                return '8px';
              }
              return `${getCornerRadius(
                  Math.min(el.width, el.height),
                  el,
              )}px`
            }


            /**
             * 边框颜色
             */
            // const getBorderColor = ()=>{
            //   if (this.state.selectedElementIds[el.id]){
            //     return getElementSelectionBorderColor();
            //   }
            //   return el.strokeColor;
            // }
            //
            // const getBorderWidth = ()=>{
            //   if (this.state.selectedElementIds[el.id]){
            //     return 2;
            //   }
            //   return el.strokeWidth/this.state.zoom.value;
            // }

            return (
                <div
                    key={el.id}
                    className={clsx("excalidraw__embeddable-container", {
                      "is-hovered": isHovered,
                    })}
                    id={`excalidraw__embeddable-container${el.id}`}
                    // translate要使用整数，不然card有滚动条时字体会模糊   translate要使用整数，不然card有滚动条时字体会模糊   translate要使用整数，不然card有滚动条时字体会模糊
                    style={{
                      // borderRadius:`${el.roundness?.value}px`,
                      // border:this.isElementSelect(el)?'2px solid red':'none',
                      transform: isVisible
                          ? `translate(${Math.floor(x - this.state.offsetLeft)}px, ${
                              Math.floor(y - this.state.offsetTop)
                          }px) scale(${scale}) rotate(${el.angle}rad)`
                          : "none",
                      display: isVisible ? "block" : "none",
                      // overflow:'hidden',
                      // background:isDarkMode()?DEFAULT_CANVAS_BACKGROUND_DARK:DEFAULT_CANVAS_BACKGROUND,
                      opacity: el.opacity / 100,
                      ["--embeddable-radius" as string]: getEmbeddableRadius(),
                      border:getCardEditBorder(),
                      borderRadius:`${getBorderRadius()}px`,
                      // boxShadow:`inset 0 0 0 ${getBorderWidth()}px ${getBorderColor()}`,
                      // overflow:'hidden'
                      // overflow:getOverFlow()
                    }}>
                  <div
                      //this is a hack that addresses isse with embedded excalidraw.com embeddable
                      //https://github.com/excalidraw/excalidraw/pull/6691#issuecomment-1607383938
                      /*ref={(ref) => {
                        if (!this.excalidrawContainerRef.current) {
                          return;
                        }
                        const container = this.excalidrawContainerRef.current;
                        const sh = container.scrollHeight;
                        const ch = container.clientHeight;
                        if (sh !== ch) {
                          container.style.height = `${sh}px`;
                          setTimeout(() => {
                            container.style.height = `100%`;
                          });
                        }
                      }}*/
                      className="excalidraw__embeddable-container__inner"
                      style={{
                        overflow:isNoteEle?'visible':'hidden',
                        width: isVisible ? `${el.width}px` : 0,
                        height: isVisible ? `${el.height+1}px` : 0,
                        // transform: isVisible ? `rotate(${el.angle}rad)` : "none",
                        pointerEvents: isActive
                            ? POINTER_EVENTS.enabled
                            : POINTER_EVENTS.disabled,
                        background:'var(--element-withnote-background)'
                      }}
                      id={`excalidraw__embeddable__outer${el.id}`}>
                    {/*{isHovered && (*/}
                    {/*  <div className="excalidraw__embeddable-hint">*/}
                    {/*    {t("buttons.embeddableInteractionButton")}*/}
                    {/*  </div>*/}
                    {/*)}*/}
                    <div
                        className="excalidraw__embeddable__outer"
                        style={{
                          padding: `${nPadding}px`,
                          display: "flex",
                          // alignItems: "center",
                          justifyContent: "center",
                          // fontSize: `${el.width / 20}px`,
                        }}
                        id={`excalidraw__embeddable__outer_${el.id}`}
                    >
                      {
                          // region 视频video....
                          isFileVideoElement(el) && !isLinkAndNotExist(el) &&
                          <div className='file' style={{position: 'relative', height: '100%',width:'100%',}} onMouseMove={(event) => {
                            // console.log('video moving....');
                            if (this.state.isPointerDown) {

                            }

                            this.handleCanvasPointerMove(event);
                          }}
                               onMouseDown={(event) => {
                                 if (document.activeElement?.id?.includes('txt-input-note-')){
                                   return;
                                 }

                                 this.setState({
                                   isPointerDown: true
                                 })
                                 this.handleCanvasPointerDown(event);
                               }}
                               onMouseUp={(event) => {
                                 this.setState({
                                   isPointerDown: false
                                 })
                                 this.handleCanvasPointerUp(event);
                               }}
                               onWheel={(event) => {
                                 this.handleWheel(event);
                               }}>
                            {
                                (this.state.videoPlayIds[el.id] || APP_WEB) ? <video
                                    autoPlay={!APP_WEB}
                                  id={`video_${el.id}`}
                                  style={{
                                    width: '100%',
                                    height: (el.customData?.generationData?.originHeight || el.height) + 'px',
                                    objectFit: 'cover',
                                    background: 'black',
                                    // position: 'absolute',
                                    left: '0px',
                                    top: '0px', borderRadius: '0px',
                                  }}

                                  onPlay={(event) => {
                                    pauseAllVideo(event.target);
                                  }}
                                  // src={getVideoPath(el)}
                                  controls>
                                {
                                  <source src={getVideoPath(el)}
                                          type='video/mp4'/>
                                }
                              </video>:<div style={{cursor:'pointer',width:'100%',position:'relative',height:`${el.customData?.generationData?.originHeight}px`}} onClick={(e)=>{
                                this.setState({
                                  videoPlayIds:{...this.state.videoPlayIds,[el.id]:true}
                                })
                              }}>
                                <img style={{width:'100%'}} src={getVideoThumb(el)}/>
                                <img style={{width:'60px',height:'60px',position:'absolute',top:'50%',left:'50%',transform:'translateX(-50%) translateY(-50%)'}} src={getImagePathWithTheme('video_play')}/>
                              </div>
                            }


                            <RenderCanvasNote el={el} state={this.state} app={this}
                                              onWheel={(event) => this.handleWheel(event)}/>

                            <div
                                style={{
                                  width: '100%',
                                  height: '100%',
                                  position: 'absolute',
                                  left: '0',
                                  top: '0',
                                  borderRadius: '8px',
                                  pointerEvents: 'none',
                                  border: getBorder()
                                }}>
                            </div>
                          </div>
                        //endregion
                      }
                      {
                        //region pdf 注记
                          (isPdfAnnoElement(el) || isCardAnnoElement(el)) &&
                          <div style={{
                            padding: '12px 8px 8px 8px',
                            height: `${el.height}px`,
                            width: '100%',
                            background: 'var(--canvas-iframe-item-background)'
                          }}>
                            <div
                                style={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  fontSize: '12px',
                                  paddingLeft: '6px',
                                  paddingRight: '6px',
                                  paddingBottom: '6px',
                                  height: '100%',
                                  overflow: 'hidden',
                                }}>
                              <div className={'header-part'} id={`pdf-anno-header-part-${el.id}`}>
                                <div
                                    style={{display: 'flex', alignItems: 'center', flexShrink: '0', minHeight: '20px'}}><span
                                    style={{
                                      color: usedColorToShowColor(el.customData.generationData.pdfData.color),
                                      border: `1px solid ${usedColorToShowColor(el.customData.generationData.pdfData.color)}`,
                                      width: '18px',
                                      height: '18px',
                                      display: 'flex',
                                      justifyItems: 'center',
                                      justifyContent: 'center',
                                      flexShrink: '0',
                                      minHeight: '18px',
                                    }}>A</span><span
                                    style={{
                                      marginLeft: '4px',
                                      color: usedColorToShowColor(el.customData.generationData.pdfData.color),
                                      textOverflow: 'ellipsis',//这几个东西，当缩放的时候，宽度小到一定程度 span不会自动变小。就不再变化了。。。。。
                                      whiteSpace: 'nowrap',
                                      overflow: 'hidden'
                                    }}>{"" + el.customData.generationData.pdfData.name}</span>
                                </div>
                                <div
                                    style={{
                                      height: '1px',
                                      background: '#E4E4E4',
                                      marginTop: '6px',
                                      marginLeft: '-16px',
                                      marginRight: '-16px'
                                    }}></div>
                                {
                                    (el.customData?.generationData.pdfData.text) &&
                                    <blockquote
                                        style={{
                                          marginTop: '10px',
                                          marginLeft: '0px',
                                          marginRight: '0px',
                                          paddingLeft: '8px',
                                          borderLeft: `2px solid ${usedColorToShowColor(el.customData.generationData.pdfData.color)}`,
                                          textAlign: 'left'
                                        }}>
                                  <span style={{
                                    color: isDarkMode() ? 'var(--text-color)' : 'var(--text-color)',
                                    marginTop: '4px'
                                  }}>{el.customData.generationData.pdfData.text}</span>
                                    </blockquote>
                                }
                                {
                                    !el.customData?.generationData.pdfData.text &&
                                    <div className={'flex-center'}>
                                      <img style={{
                                        maxWidth: '100%',
                                        marginTop: '12px',
                                        height: 'auto',
                                        display: 'block',
                                        // maxHeight: `${el.height - document.getElementById('pdf-anno-'+el.id).clientHeight}px`
                                      }}
                                           src={el.customData.generationData.pdfData.image || el.customData.generationData.pdfData.thumbImage}/>
                                    </div>
                                }
                              </div>

                              <textarea id={'pdf-anno-' + el.id} className={'font-family-regular'}
                                        placeholder={i18next.t('Add a note')}
                                        value={el.customData?.generationData.pdfData.comment}
                                        style={{
                                          margin: '8px 0', resize: 'none',
                                          fontSize: '12px',
                                          // flexGrow: '1',
                                          // flexShrink: '0',
                                          marginBottom: '1px',
                                          minHeight: '20px',
                                          color: 'var(--text-color)',
                                          background: '#ff000000',
                                          fontFamily: 'Popins',
                                          height: (`${document.getElementById('pdf-anno-' + el.id)?.scrollHeight}` || calcTextAreaHeight((el.width - (2 + 8 + 6) * 2), el.customData.generationData.pdfData.comment)) + 'px',
                                          // maxHeight: calcTextAreaHeight((el.width - (2 + 8 + 6) * 2), el.customData.generationData.pdfData.comment) + 'px',
                                          border: 'none', padding: '0', overflow: 'hidden',
                                          pointerEvents: 'all', textWrap: 'wrap'
                                        }}
                                        onInput={(event) => {
                                          this.pushHistory();
                                          const txtArea = document.getElementById('pdf-anno-' + el.id);
                                          // console.log('app-txtArea-');
                                          if (txtArea && isVisible) {
                                            // console.log(txtArea)

                                            let customData = {...el.customData};
                                            txtArea.style.height = '0px';
                                            // const commentHeight = calcTextAreaHeight((el.width - (2 + 8 + 6) * 2), customData.generationData.pdfData.comment);
                                            txtArea.style.height = (txtArea.scrollHeight) + 'px';
                                            //更新这个元素的高度.
                                            let nAnnoHeight = el.width;

                                            if (el.customData?.generationData.annoHeight) {
                                              nAnnoHeight = el.customData.generationData.annoHeight;
                                            } else {
                                              customData.generationData.annoHeight = el.width;
                                            }
                                            customData.generationData.pdfData.comment = event.target.value;

                                            let elOriHeight = 0;
                                            // if (el.customData.generationData.pdfData.type == 'image' || el.customData.generationData.pdfData.annoType == 'image') {
                                            //   elOriHeight = nAnnoHeight;
                                            // }else {
                                            //   const szHtml = createPdfAnnoTextElement(el.customData.generationData.pdfData);
                                            //   elOriHeight = calcTextHeight((el.width - (8 + 8 + 6) * 2), szHtml);
                                            // }
                                            const divHeader = document.getElementById(`pdf-anno-header-part-${el.id}`);
                                            if (divHeader) {
                                              elOriHeight = divHeader.offsetHeight + 8 + 8 + 8;//上下padding
                                            }

                                            const textAreaHeight = txtArea.scrollHeight;//commentHeight;
                                            let height = elOriHeight + textAreaHeight + 16;//16是上下的边距

                                            mutateElement(el, {height: height, customData: customData});

                                            //更新画布上别的
                                            const arraySameAnno = this.scene.getNonDeletedElements().filter(p=>p.customData?.generationData?.pdfData?.id ==el.customData?.generationData?.pdfData?.id);
                                            arraySameAnno.forEach(p=>{
                                              mutateElement(p,{height: height, customData: customData})
                                            })

                                            //更新iframe
                                            if (!event.nativeEvent?.isManualUpdate) {
                                              if (isPdfAnnoElement(el)) {
                                                const iframe = document.getElementById("pdf-iframe-slider") as HTMLIFrameElement;
                                                if (iframe) {
                                                  const newPdfData = {
                                                    ...customData.generationData.pdfData,
                                                    sortIndex: 1
                                                  }
                                                  iframe.contentWindow._reader?._annotationManager?.updateHighlightNoteByNodeID([newPdfData])
                                                } else {
                                                  //直接保存到文件
                                                  ipcInvoke('save-one-annotations', {data: customData.generationData.pdfData});
                                                }
                                              } else if (isCardAnnoElement(el)) {
                                                const annoData = {
                                                  id: el.customData?.generationData?.pdfData?.id || el.id,
                                                  libraryId: el.customData?.generationData.id,
                                                  comment: event.target.value
                                                }
                                                window.onUpdateComment(annoData);
                                                postMessage(EVENT_UPDATE_HIGHLIGHT_ANNO, {...annoData});
                                              }
                                            }
                                            //更新到注记库
                                            getCardById(el.customData?.generationData?.pdfData?.id || el.id).then(dbLibrary => {
                                              if (dbLibrary) {
                                                if (isPdfAnnoElement(el)) {
                                                  dbLibrary.annoType = el.customData.generationData.pdfData.annoType || el.customData.generationData.pdfData.type;
                                                  if (!dbLibrary.thumbImage) {
                                                    dbLibrary.thumbImage = el.customData.generationData.pdfData.thumbImage;
                                                  }
                                                }
                                                dbLibrary.comment = event.target.value;
                                                updateCard(dbLibrary).then(r => {
                                                  //刷新列表...
                                                  // postMessage(EVENT_REFRESH_LIBRARY_LIST);
                                                  window.globalRefreshList('refreshLibraryList_');
                                                })
                                              }
                                            });
                                          }
                                        }} onBlur={(event) => {
                                el.customData.generationData.isEditable = false;
                              }} onFocus={(event) => {
                                // const customData = {...el.customData};
                                el.customData.generationData.isEditable = true;
                                this.setSelectElements(el);
                              }} onMouseMove={(event) => {
                                this.handleCanvasPointerMove(event);
                              }} onMouseUp={(event) => {
                                this.handleCanvasPointerUp(event);
                              }} onWheel={(event) => {
                                this.handleWheel(event);
                              }}/>
                            </div>

                            <div
                                style={{
                                  width: '100%',
                                  height: '100%',
                                  position: 'absolute',
                                  left: '0',
                                  top: '0',
                                  borderRadius: '8px',
                                  pointerEvents: 'none',
                                  border: getBorder()
                                }}>
                            </div>
                          </div>
                        //endregion
                      }

                      {
                        //region 视频注记...
                          (isVideoAnnoElement(el) || isAudioAnnoElement(el)) &&
                          <div style={{
                            padding: '8px 12px',
                            overflow: 'auto',
                            color: 'var(--text-color)',
                            width: '100%',
                            height: '100%',
                            position: 'relative',
                            background:'var(--canvas-iframe-item-background)'
                          }}>
                            <div className={'flex-row-vertical-center mt-4'}>
                              <img className={'img-16'} src={getImagePath('video_anno_draw')}/>
                              <span className={'span-single-line ml-10'} style={{
                                flexGrow: '1',
                                textAlign: 'left',
                                color: '#FF9500'
                              }}>{el.customData.generationData.videoName}</span>
                              <span style={{
                                flexShrink: '0',
                                marginLeft: '6px'
                              }}>{formatDate(new Date(el.customData?.generationData?.videoData?.time), 'hh:mm:ss')}</span>
                              {
                                  el.customData?.generationData?.videoData?.endTime > 0 &&
                                  <span style={{
                                    flexShrink: '0',
                                    marginLeft: '0px'
                                  }}>-{formatDate(new Date(el.customData?.generationData.videoData.endTime), 'hh:mm:ss')}</span>
                              }
                              {
                                  el.customData?.generationData?.videoData?.loop &&
                                  <img className={'img-12 ml-4'} src={getImagePathWithTheme('video_loop')}/>
                              }
                            </div>
                            <div id={`video-anno-${el.id}`}
                                 style={{textAlign: 'left', marginTop: '10px', overflow: 'hidden'}}>
                              {
                                  el.customData?.generationData.videoData.title &&
                                  <span className={'font-family-semi-bold'} style={{
                                    fontSize: '16px',
                                    textWrap: 'wrap'
                                  }}>{el.customData?.generationData.videoData.title}</span>
                              }
                              <div style={{marginTop: '6px'}}>
                                <span style={{
                                  display: 'flex',
                                  justifyContent: 'flex-start',
                                  textAlign: 'left',
                                  whiteSpace: 'pre',
                                  textWrap: 'wrap',
                                  wordBreak: 'break-word'
                                }}>{el.customData?.generationData.videoData.content}</span>
                                {
                                    el.customData?.generationData.videoData.thumbImage &&
                                    <img style={{width: '100%', height: 'auto', marginTop: '8px', borderRadius: '8px'}}
                                         src={getLocalFilePath(el.customData?.generationData.videoData.thumbImage, false)}/>
                                }
                              </div>
                            </div>

                            <div
                                style={{
                                  width: '100%',
                                  height: '100%',
                                  position: 'absolute',
                                  left: '0',
                                  top: '0',
                                  borderRadius: '8px',
                                  pointerEvents: 'none',
                                  border: getBorder()
                                }}>
                            </div>
                          </div>
                        //endregion
                      }

                      {
                        //region 音频
                          isAudioFileElement(el) &&
                          <div id={`excalidraw__iframe__outer${el.id}`} style={{height: `100%`, width: '100%', overflow: 'hidden', position: 'relative'}}>
                            <CustomAudio from={FROM_CANVAS} onMouseDown={(event) => {
                              this.setState({
                                isPointerDown: true
                              })
                              this.handleCanvasPointerDown(event);
                            }} onMouseMove={(event)=>{
                              this.handleCanvasPointerMove(event);
                            }} onMouseUp={(event)=>{
                              this.handleCanvasPointerUp(event);
                            }} onWheel={(event)=>{
                              this.handleWheel(event);
                            }} szUrl={getAudioPath(el)}
                            height={`${el.customData?.generationData?.originHeight || el.height}px`}/>

                            <RenderCanvasNote el={el} state={this.state} app={this} onWheel={(event)=>this.handleWheel(event)}/>

                            <div style={{height:'100%',width:'100%',position:'absolute',left:'0',top:'0',border:getBorder(),borderRadius:'8px'}}>
                            </div>
                          </div>
                        //endregion
                      }

                      {
                        //region 如果文件不存在....
                        isLinkAndNotExist(el) &&
                          <EmptyElement appState={this.state} element={el} onMouseDown={(event)=>{
                            this.setState({
                              isPointerDown:true
                            })
                            this.handleCanvasPointerDown(event);
                          }} onMouseUp={(event)=>{
                            this.setState({
                              isPointerDown:false
                            })
                            this.handleCanvasPointerUp(event);
                          }} onWheel={(event)=>{
                            this.handleWheel(event);
                          }} onDelete={(element)=>{
                            postMessage(EVENT_DELETE_ELEMENT_BY_ID, {id:element.id});
                          }}/>
                        //endregion
                      }

                      {
                        // region直接在卡片上编辑 step 1
                          this.isRenderNoteCardEdit(el) &&
                          <>
                            <CardEdit app={this} state={this.state} data={el.customData?.generationData} element={el} isSelected={this.state.selectedElementIds[el.id]} isRender={isVisible}
                                      updateElementCallback={(element, updates, isExpandOrCollapse = false) => {
                                        // console.log('cardEdit--updateElementCallback',updates);
                                        //如果属性一样就不更新了。。
                                        if (Object.keys(updates).length == 1 && updates.height){
                                          if (updates.height != element.height){
                                            this.onUpdateElement(element, updates);
                                          }
                                        }else{
                                          this.onUpdateElement(element, updates);
                                        }

                                        //更新别的元素....
                                        // const arrayOtherNotes = this.scene.getNonDeletedElements().filter(p=>p.customData?.generationData?.id == element.customData?.generationData?.id)

                                        //输入的时候，如果element接触到底部，则canvas向下自动滚动.
                                        let height = this.state.height - this.state.scrollY * (this.state.zoom?.value || 1);
                                        let elementOffset = element.height + element.y;
                                        elementOffset *= (this.state.zoom?.value || 1);
                                        const offsetY = 40;

                                        const nY = el.y*this.state.zoom.value + element.height + 0*this.state.zoom.value;
                                        const nTotal = this.state.height + Math.abs(this.state.scrollY*this.state.zoom.value);

                                        if (!isExpandOrCollapse && (elementOffset + offsetY) > height && element.height < (document.body.offsetHeight - 100) && (element.height*this.state.height < document.body.offsetHeight)) {
                                          if (element.customData?.generationData?.isEditable){
                                            this.setState({
                                              scrollY: this.state.scrollY - ((elementOffset + offsetY) - height)
                                            });
                                          }
                                        }

                                        //这里处理连线问题....
                                        if (element.boundElements) {
                                          updateBoundElements(element);
                                        }
                                        // console.log('canvasHeight:'+height + '   elementHeight:'+(element.height + element.y) + ' scrollY:'+this.state.scrollY)
                                      }} onActionCallback={(action:any,element:any)=>{
                              if ('delete' == action){
                                //删除这个...
                                this.removeElementById(element.id);
                              }else if ('rename' == action){
                                const customData = element.customData;
                                customData.generationData.isEditable = true;
                                this.onUpdateElement(element,{customData})
                                this.setState({
                                  selectedElementIds:{ [element.id]: true },
                                })
                              }
                            }} onMouseDown={(event)=>{
                              this.handleCanvasPointerDown(event)
                            }} onMouseMove={(event)=>{
                              this.handleCanvasPointerMove(event)
                            }} onMouseUp={(event)=>{
                              this.handleCanvasPointerUp(event)
                            }} onWheel={(event)=>{
                              this.handleWheel(event);
                            }} onTextSelection={(isShow,editor,updateEditCardHtml,syncNoteData,el,data,onHiddenBubbleMenu,onAction)=>{
                              this.setState({
                                cardEditMenu:{
                                  visibility:isShow,
                                  editor:editor,
                                  updateEditCardHtml:updateEditCardHtml,
                                  syncNoteData:syncNoteData,
                                  element:el,
                                  data:data,
                                  onHiddenBubbleMenu:onHiddenBubbleMenu,
                                  onAction:onAction,
                                  from:'card'
                                }
                              })
                            }}/>

                            {/*//四个hoverd点*/}
                            <HoveredElementCircle state={this.state} el={el}/>
                          </>


                          //endregion
                      }

                      {
                        //region pdf 文件
                        isPdfFileElement(el) &&
                          <div id={`pdf-iframe-element--${el.id}`} className='file' style={{position: 'relative',width:'100%',height:'100%'}}>
                            <img
                                style={{width: '100%', height: `${el.customData?.generationData?.originHeight || el.height}px`, borderRadius: '0px'}}
                                src={getPdfThumb(el)}/>
                            <RenderCanvasNote el={el} state={this.state} app={this} onWheel={(event)=>this.handleWheel(event)}/>
                            <div
                                style={{
                                  width: '100%',
                                  height: '100%',
                                  position: 'absolute',
                                  left: '0',
                                  top: '0',
                                  borderRadius: '8px',
                                  pointerEvents: 'none',
                                  border: getBorder()
                                }}>
                            </div>
                          </div>
                          //endregion
                      }

                      {
                        //region md文件编辑...
                          isMdFileElement(el) &&
                          <div id={`excalidraw__iframe__outer${el.id}`}
                               style={{height: `100%`, width: '100%', overflow: 'hidden', position: 'relative'}}>
                            <MyMdEditor element={el} isActive={isActive} appState={this.state} onMouseDown={(event) => {
                              this.setState({
                                isPointerDown: true
                              })
                              this.handleCanvasPointerDown(event);
                            }} onMouseMove={(event)=>{
                              this.handleCanvasPointerMove(event);
                            }} onMouseUp={(event)=>{
                              this.handleCanvasPointerUp(event);
                            }} onWheel={(event)=>{
                              this.handleWheel(event);
                            }}/>
                            <div style={{
                              height: '100%',
                              width: '100%',
                              position: 'absolute',
                              left: '0',
                              top: '0',
                              border: getBorder(),
                              borderRadius: '8px',
                              pointerEvents:'none'
                            }}>
                            </div>
                          </div>


                        //endregion
                      }

                      {
                        //region  文件
                          isFileElement(el) && (!isPdfFileElement(el) && !isFileVideoElement(el) && !isAudioFileElement(el) && !isMdFileElement(el)) &&
                          <div id={`file-element--${el.id}`} className='file'
                               style={{position: 'relative', width: '100%', height: '100%'}}>
                            <div className={'file flex-center'} style={{height:'210px',width:'100%',borderBottom:'0px solid var(--line-color)'}}>
                              <img
                                  style={{width: '86px', height: '86px', borderRadius: '8px'}}
                                  src={imgPathPerfix() + getFileIcon(el.customData?.generationData.fileName)}/>
                            </div>


                            <RenderCanvasNote el={el} state={this.state} app={this}
                                              onWheel={(event) => this.handleWheel(event)}/>

                            <div
                                style={{
                                  width: '100%',
                                  height: '100%',
                                  position: 'absolute',
                                  left: '0',
                                  top: '0',
                                  borderRadius: '8px',
                                  pointerEvents: 'none',
                                  border: getBorder()
                                }}>
                            </div>
                          </div>
                        //endregion
                      }

                      {
                        //region <style(.*?)<\/style>/g,''  直接删除style样式
                        (this.isRenderNoteCardEdit(el) || isPdfAnnoElement(el) || isLinkAndNotExist(el) || isVideoAnnoElement(el) || isFileVideoElement(el) || isAudioFileElement(el) || isAudioAnnoElement(el) || isCardAnnoElement(el) || isPdfFileElement(el) || isFileElement(el)) ? <></> ://直接在卡片上编辑 step 2
                            (isEmbeddableElement(el) ? this.props.renderEmbeddable?.(el, this.state) : null) ?? (
                              !isFileVideoElement(el) &&
                              (isNoteElement(el) || isPdfAnnoElement(el))?<div dangerouslySetInnerHTML={{__html:isNoteElement(el)?createNoteElementByNote(el.customData?.generationData.name,el.customData?.generationData.html).replaceAll("html{", "xx{").replaceAll('line-height: 20px;',''):el.customData?.generationData.html.replace("html{", "xx{").replaceAll('line-height: 20px;','')}} className={'div-iframe-note-draw'} style={{textAlign:"left",margin:'10px 6px',fontFamily:'Popins',color:isDarkMode()?"white":'black'}}></div>:
                                  <>
                                    {
                                      (isEmbeddableElement(el) && el.embedType) === 'text' ? (
                                          <div
                                              id={`${el.id}_div`}
                                              style={{
                                                height: "100%",
                                                width: "100%",
                                                display: "flex",
                                                justifyContent: "center",
                                                // alignItems: "center",
                                                overflow: 'hidden',
                                              }}
                                          >
                                            <div style={{width:'100%'}}>
                                              <a
                                                  id={`${el.id}-card`}
                                                  href={el.link || ""}
                                                  target="_blank"
                                                  rel="noreferrer"
                                                  style={{
                                                    display: "flex",
                                                    justifyContent: "center",
                                                  }}>
                                                <Spin
                                                    indicator={
                                                      <LoadingOutlined
                                                          spin
                                                          style={{fontSize: 48,marginTop:'43px'}}
                                                      />
                                                    }
                                                />
                                              </a>

                                              <div id={`div-input-note-${el.id}`} style={{opacity:'0'}}>
                                                <RenderCanvasNote el={el} state={this.state} app={this}
                                                                  onWheel={(event) => this.handleWheel(event)}/>
                                              </div>


                                            </div>


                                            <div style={{
                                              height: '100%',
                                              width: '100%',
                                              position: 'absolute',
                                              left: '0',
                                              top: '0',
                                              border: getBorder(),
                                              borderRadius: '8px'
                                            }}></div>

                                          </div>
                                      ) : (
                                          <div style={{display:'flex',flexDirection:'column',position:'relative',width:'100%'}}>
                                            {
                                              isWeb() ? <iframe
                                                  id={`excalidraw__iframe__outer${el.id}`}
                                                  // onKeyDown={(event)=>{
                                                  //   console.log('iframe onkey down'+event.key)
                                                  // }}
                                                  // onMouseMove={(event)=>{
                                                  //   console.log('iframe move....')
                                                  // }}
                                                  onLoad={(e) => {
                                                    const iframe = e.target;
                                                    // @ts-ignore
                                                    if (iframe?.src !== el.link) {
                                                      return;
                                                    }

                                                    // @ts-ignore
                                                    // const isLoaded = iframe.contentWindow.window.length;
                                                    //
                                                    // if (!isLoaded) {
                                                    //   const parentEle = document.getElementById(
                                                    //       `excalidraw__embeddable__outer_${el.id}}`,
                                                    //   )!;
                                                    //   parentEle.innerHTML = i18next.t("ThisWebpageCanntBeEmbed");
                                                    // }
                                                  }}
                                                  ref={(ref) => this.cacheEmbeddableRef(el, ref)}
                                                  className="excalidraw__embeddable iframe-content"
                                                  srcDoc={
                                                    src?.type === "document"
                                                        ? src.srcdoc(this.state.theme)
                                                        : undefined
                                                  }
                                                  src={
                                                    src?.type !== "document" ? (src?.link || el.link) : undefined
                                                  }
                                                  style={{marginLeft: '0.6px', marginTop: '0.6px'}}
                                                  // https://stackoverflow.com/q/18470015
                                                  scrolling={isEmbeddableElement(el) ? "yes" : "no"}
                                                  referrerPolicy="no-referrer-when-downgrade"
                                                  title="Noteey Embedded Content"
                                                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                                  allowFullScreen={true}
                                                  sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-popups-to-escape-sandbox allow-presentation allow-downloads"
                                              /> : <WebviewExt el={el} src={src} source={'canvas'}/>
                                    }

                                            <RenderCanvasNote el={el} state={this.state} app={this}
                                                              onWheel={(event) => this.handleWheel(event)}/>

                                    <div
                                        style={{
                                          width: '100%',
                                          height: '100%',
                                          position: 'absolute',
                                          left: '0',
                                          top: '0',
                                          borderRadius: '8px',
                                          pointerEvents: 'none',
                                          border: getBorder()
                                        }}>
                                    </div>

                                  </div>
                            )
                      }
                    </>
                    )
                    //endregion
                    }

                  </div>
                </div>

                  {
                    // (isFileElement(el) || isEmbeddableElement(el) || isAnnoElement(el)) &&
                    <HoveredElementCircle state={this.state} el={el}/>
                  }
                </div>
          );
          })}
        </>
    );
  }

  private renderEmbeddables0() {
    const scale = this.state.zoom.value;
    const normalizedWidth = this.state.width;
    const normalizedHeight = this.state.height;

    const embeddableElements = this.scene
        .getNonDeletedElements()
        .filter(
            (el): el is NonDeleted<ExcalidrawIframeLikeElement> =>
                (isEmbeddableElement(el) && !!el.validated) || isIframeElement(el),
        );

    return (
        <>
          {embeddableElements.map((el) => {
            const {x, y} = sceneCoordsToViewportCoords(
                {sceneX: el.x, sceneY: el.y},
                this.state,
            );

            const isVisible = isElementInViewport(
                el,
                normalizedWidth,
                normalizedHeight,
                this.state,
            );
            const hasBeenInitialized = this.initializedEmbeds.has(el.id);

            if (isVisible && !hasBeenInitialized) {
              this.initializedEmbeds.add(el.id);
            }
            const shouldRender = isVisible || hasBeenInitialized;

            if (!shouldRender) {
              return null;
            }

            let src: IframeData | null;

            if (isIframeElement(el)) {
              src = null;

              const data: MagicCacheData = (el.customData?.generationData ??
                  this.magicGenerations.get(el.id)) || {
                status: "error",
                message: "No generation data",
                code: "ERR_NO_GENERATION_DATA",
              };

              if (data.status === "done") {
                let style = '';//"<head><link rel='stylesheet' href='IFrameNote.css' type='text/css'>";
                // style += "</head>";
                const html = style + data.html;
                src = {
                  intrinsicSize: {w: el.width, h: el.height},
                  type: "document",
                  srcdoc: () => {
                    return html;
                  },
                } as const;
              } else if (data.status === "pending") {
                src = {
                  intrinsicSize: {w: el.width, h: el.height},
                  type: "document",
                  srcdoc: () => {
                    return createSrcDoc(`
                    <style>
                      html, body {
                        width: 100%;
                        height: 100%;
                        color: ${
                        this.state.theme === "dark" ? "white" : "black"
                    };
                      }
                      body {
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        flex-direction: column;
                        gap: 1rem;
                      }

                      .Spinner {
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        margin-left: auto;
                        margin-right: auto;
                      }

                      .Spinner svg {
                        animation: rotate 1.6s linear infinite;
                        transform-origin: center center;
                        width: 40px;
                        height: 40px;
                      }

                      .Spinner circle {
                        stroke: currentColor;
                        animation: dash 1.6s linear 0s infinite;
                        stroke-linecap: round;
                      }

                      @keyframes rotate {
                        100% {
                          transform: rotate(360deg);
                        }
                      }

                      @keyframes dash {
                        0% {
                          stroke-dasharray: 1, 300;
                          stroke-dashoffset: 0;
                        }
                        50% {
                          stroke-dasharray: 150, 300;
                          stroke-dashoffset: -200;
                        }
                        100% {
                          stroke-dasharray: 1, 300;
                          stroke-dashoffset: -280;
                        }
                      }
                    </style>
                    <div class="Spinner">
                      <svg
                        viewBox="0 0 100 100"
                      >
                        <circle
                          cx="50"
                          cy="50"
                          r="46"
                          strokeWidth="8"
                          fill="none"
                          stroke-miter-limit="10"
                        />
                      </svg>
                    </div>
                    <div>Generating...</div>
                  `);
                },
              } as const;
            } else {
              let message: string;
              if (data.code === "ERR_GENERATION_INTERRUPTED") {
                message = "Generation was interrupted...";
              } else {
                message = data.message || "Generation failed";
              }
              src = {
                intrinsicSize: { w: el.width, h: el.height },
                type: "document",
                srcdoc: () => {
                  return createSrcDoc(`
                    <style>
                    html, body {
                      height: 100%;
                    }
                      body {
                        display: flex;
                        flex-direction: column;
                        align-items: center;
                        justify-content: center;
                        color: ${COLOR_PALETTE.red[3]};
                      }
                      h1, h3 {
                        margin-top: 0;
                        margin-bottom: 0.5rem;
                      }
                    </style>
                    <h1>Error!</h1>
                    <h3>${message}</h3>
                  `);
                },
              } as const;
            }
          } else {
            src = getEmbedLink(toValidURL(el.link || ""));
          }

          let isActive =
            this.state.activeEmbeddable?.element === el &&
            this.state.activeEmbeddable?.state === "active";
          const isHovered =
            this.state.activeEmbeddable?.element === el &&
            this.state.activeEmbeddable?.state === "hover";

          // if (this.state.activeEmbeddable?.state !== "hover" && isFileVideoElement(el) ){
          //   isActive = true;
          // }
          // if(this.state.selectedElementIds[el.id] && isFileVideoElement(el)){
          //   // console.log('select and .....');
          //   // isActive = true;
          // }

          return (
            <div
              key={el.id}
              className={clsx("excalidraw__embeddable-container", {
                "is-hovered": isHovered,
              })}
              style={{
                transform: isVisible
                  ? `translate(${x - this.state.offsetLeft}px, ${
                      y - this.state.offsetTop
                    }px) scale(${scale})`
                  : "none",
                display: isVisible ? "block" : "none",
                opacity: el.opacity / 100,
                ["--embeddable-radius" as string]: `${getCornerRadius(
                  Math.min(el.width, el.height),
                  el,
                )}px`,
              }}
            >
              <div
                //this is a hack that addresses isse with embedded excalidraw.com embeddable
                //https://github.com/excalidraw/excalidraw/pull/6691#issuecomment-1607383938
                /*ref={(ref) => {
                  if (!this.excalidrawContainerRef.current) {
                    return;
                  }
                  const container = this.excalidrawContainerRef.current;
                  const sh = container.scrollHeight;
                  const ch = container.clientHeight;
                  if (sh !== ch) {
                    container.style.height = `${sh}px`;
                    setTimeout(() => {
                      container.style.height = `100%`;
                    });
                  }
                }}*/
                className="excalidraw__embeddable-container__inner"
                style={{
                  width: isVisible ? `${el.width}px` : 0,
                  height: isVisible ? `${el.height}px` : 0,
                  transform: isVisible ? `rotate(${el.angle}rad)` : "none",
                  pointerEvents: isActive
                    ? POINTER_EVENTS.enabled
                    : POINTER_EVENTS.disabled,
                }}
                id={`excalidraw__embeddable__outer${el.id}`}
              >
                {/*{isHovered && (*/}
                {/*  <div className="excalidraw__embeddable-hint">*/}
                {/*    {t("buttons.embeddableInteractionButton")}*/}
                {/*  </div>*/}
                {/*)}*/}
                <div
                  className="excalidraw__embeddable__outer"

                  style={{
                    padding: `${el.strokeWidth}px`,
                  }}
                >
                  {(isEmbeddableElement(el)
                    ? this.props.renderEmbeddable?.(el, this.state)
                    : null) ?? (
                    <iframe
                        onKeyDown={(event)=>{
                          console.log('iframe onkey down'+event.key)
                        }}
                        onMouseMove={(event)=>{
                          console.log('iframe move....')
                        }}
                      ref={(ref) => this.cacheEmbeddableRef(el, ref)}
                      className="excalidraw__embeddable iframe-content"
                      srcDoc={
                        src?.type === "document"
                          ? src.srcdoc(this.state.theme)
                          : undefined
                      }
                      src={
                        src?.type !== "document" ? src?.link ?? "" : undefined
                      }
                      // https://stackoverflow.com/q/18470015
                      scrolling="no"
                      referrerPolicy="no-referrer-when-downgrade"
                      title="Muenzo Embedded Content"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen={true}
                      sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-popups-to-escape-sandbox allow-presentation allow-downloads"
                    />
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </>
    );
  }

  /**
   * 更新mindmap的layout
   * @param value
   * @private
   */
  private updateMindmapLayout(value:string){
    const { element, node } = this.state.mindmap.selected;
    const currentMindMap = mindMapCache.get(element.id)!;
    currentMindMap.setLayout(value);
    const zoom = this.state.zoom.value;
    addMindMapControls(node, currentMindMap, isDarkMode()?addIconWhite:addIcon, OFFSET,zoom);

    //修改配置
    const config = element?.config || DEFAULT_MINDMAP_CONFIG;
    config.layout = value;
    mutateElement(element,{config:config});
  }

  private getMindmapNodeFontSize(){
    const node = this.state.mindmap?.selected?.node;
    if (!node)return 0;
    if (node.isRoot){
      return node?.nodeData?.data?.fontSize || this.state.mindmap?.selected?.element?.config?.fontSize
    }else{
      return this.state.mindmap?.selected?.node?.nodeData?.data?.fontSize || this.state.mindmap?.selected?.element?.config?.subnodeFontSize;
    }
  }

  private shouldAllowMindmapDomInteraction = () => {
    const tool = this.state.activeTool?.type;
    const isSelectionTool = tool === TOOL_TYPE.selection;

    // IMPORTANT:
    // During connector/arrow dragging we keep mindmap DOM interactions disabled
    // so Excalidraw can handle binding/snap smoothly without mindmap doing its
    // own heavy pointermove processing.
    if (!isSelectionTool) {
      return false;
    }
    // Never in view/hand tool mode (canvas should pan/zoom smoothly)
    if (this.state.viewModeEnabled || isHandToolActive(this.state)) {
      return false;
    }
    // Never during active canvas interactions
    // - For selection tool: disable during any drag/selection to keep marquee smooth
    if (this.state.selectionElement) {
      return false;
    }
    if (this.state.draggingElement) {
      return false;
    }
    if (this.state.isResizing || this.state.resizingElement) {
      return false;
    }
    // `isRotating` exists in AppState in this codebase; guard just in case.
    // @ts-ignore
    if (this.state.isRotating) {
      return false;
    }
    return true;
  };

  private shouldAllowMindmapDomInteraction = () => {
    const tool = this.state.activeTool?.type;
    const isSelectionTool = tool === TOOL_TYPE.selection;

    // IMPORTANT:
    // During connector/arrow dragging we keep mindmap DOM interactions disabled
    // so Excalidraw can handle binding/snap smoothly without mindmap doing its
    // own heavy pointermove processing.
    if (!isSelectionTool) {
      return false;
    }
    // Never in view/hand tool mode (canvas should pan/zoom smoothly)
    if (this.state.viewModeEnabled || isHandToolActive(this.state)) {
      return false;
    }
    // Never during active canvas interactions
    // - For selection tool: disable during any drag/selection to keep marquee smooth
    if (this.state.selectionElement) {
      return false;
    }
    if (this.state.draggingElement) {
      return false;
    }
    if (this.state.isResizing || this.state.resizingElement) {
      return false;
    }
    // `isRotating` exists in AppState in this codebase; guard just in case.
    // @ts-ignore
    if (this.state.isRotating) {
      return false;
    }
    return true;
  };

  /**
   * mindmap的工具栏....
   * @private
   */
  private renderMindMapToolbar() {
    return (
        // 这里本来应该是div的，但是不知道什么影响的，里面的input不能正确点击了，input也要换成textarea才可以，不知道为什么---这里本来应该是div的，但是不知道什么影响的，里面的input不能正确点击了，input也要换成textarea才可以，不知道为什么---这里本来应该是div的，但是不知道什么影响的，里面的input不能正确点击了，input也要换成textarea才可以，不知道为什么---这里本来应该是div的，但是不知道什么影响的，里面的input不能正确点击了，input也要换成textarea才可以，不知道为什么---这里本来应该是div的，但是不知道什么影响的，里面的input不能正确点击了，input也要换成textarea才可以，不知道为什么---这里本来应该是div的，但是不知道什么影响的，里面的input不能正确点击了，input也要换成textarea才可以，不知道为什么---这里本来应该是div的，但是不知道什么影响的，里面的input不能正确点击了，input也要换成textarea才可以，不知道为什么---这里本来应该是div的，但是不知道什么影响的，里面的input不能正确点击了，input也要换成textarea才可以，不知道为什么---这里本来应该是div的，但是不知道什么影响的，里面的input不能正确点击了，input也要换成textarea才可以，不知道为什么---这里本来应该是div的，但是不知道什么影响的，里面的input不能正确点击了，input也要换成textarea才可以，不知道为什么---这里本来应该是div的，但是不知道什么影响的，里面的input不能正确点击了，input也要换成textarea才可以，不知道为什么---这里本来应该是div的，但是不知道什么影响的，里面的input不能正确点击了，input也要换成textarea才可以，不知道为什么---这里本来应该是div的，但是不知道什么影响的，里面的input不能正确点击了，input也要换成textarea才可以，不知道为什么---这里本来应该是div的，但是不知道什么影响的，里面的input不能正确点击了，input也要换成textarea才可以，不知道为什么---这里本来应该是div的，但是不知道什么影响的，里面的input不能正确点击了，input也要换成textarea才可以，不知道为什么---这里本来应该是div的，但是不知道什么影响的，里面的input不能正确点击了，input也要换成textarea才可以，不知道为什么---这里本来应该是div的，但是不知道什么影响的，里面的input不能正确点击了，input也要换成textarea才可以，不知道为什么---这里本来应该是div的，但是不知道什么影响的，里面的input不能正确点击了，input也要换成textarea才可以，不知道为什么---这里本来应该是div的，但是不知道什么影响的，里面的input不能正确点击了，input也要换成textarea才可以，不知道为什么---这里本来应该是div的，但是不知道什么影响的，里面的input不能正确点击了，input也要换成textarea才可以，不知道为什么---这里本来应该是div的，但是不知道什么影响的，里面的input不能正确点击了，input也要换成textarea才可以，不知道为什么---这里本来应该是div的，但是不知道什么影响的，里面的input不能正确点击了，input也要换成textarea才可以，不知道为什么---这里本来应该是div的，但是不知道什么影响的，里面的input不能正确点击了，input也要换成textarea才可以，不知道为什么---这里本来应该是div的，但是不知道什么影响的，里面的input不能正确点击了，input也要换成textarea才可以，不知道为什么---这里本来应该是div的，但是不知道什么影响的，里面的input不能正确点击了，input也要换成textarea才可以，不知道为什么---
        <span
            className="excalidraw__mindmap__outer__toolbar"
            id={`excalidraw__mindmap__outer__toolbar`}
            style={{display: "none"}}
            onMouseDown={(e)=>{
              e.stopPropagation();
              e.preventDefault();
            }}
            onMouseUp={(event)=>{
              event.stopPropagation();
              event.preventDefault();
            }}
            onClick={(e) => {
              e.stopPropagation()
              e.preventDefault();
            }}
        >
          <span className={'flex-row-vertical-center'}
               style={{width: '100%', height: '100%', padding: '0px 8px 0px 8px'}} onMouseDown={(e)=>{
            e.stopPropagation();
            e.preventDefault();
          }}>
            {
              this.state.mindmap?.selected?.node?.isRoot &&
              <>
                <Popover placement={'bottom'} arrow={false} overlayInnerStyle={{margin: '0', padding: '0 6px'}}
                         align={{offset: [0, 12]}} trigger={['click']}
                         content={<div className={'flex-row-vertical-center'} style={{height: '38px'}}>
                           <img className={'img-30 hovered cursor-pointer'}
                                src={getImagePathWithTheme('mindmap_layout_lr', true)} onClick={()=>{
                                this.updateMindmapLayout('logicalStructure');
                           }}/>
                           <img className={'img-30 hovered cursor-pointer mr-6 ml-6'}
                                src={getImagePathWithTheme('mindmap_layout_tb', true)} onClick={()=>{
                             this.updateMindmapLayout('organizationStructure');
                           }}/>
                           {/*<img className={'img-30 hovered cursor-pointer'}*/}
                           {/*     src={getImagePathWithTheme('mindmap_layout_double', true)} onClick={()=>{*/}
                           {/*  this.updateMindmapLayout('mindMap');*/}
                           {/*}}/>*/}
                         </div>}>
                  <Tooltip arrow={false} title={i18next.t("Layout")}>
                    <img className={'img-30 cursor-pointer hovered'}
                         src={getImagePathWithTheme('mindmap_toolbar_layout', true)}/>
                  </Tooltip>

                </Popover>

                <Tooltip arrow={false} title={i18next.t("Settings")}>
                <img className={'img-30 cursor-pointer hovered'}
                     src={getImagePathWithTheme('mindmap_toolbar_set', true)}
                     onClick={() => {
                       //设置....
                       window.showDialogMindmapStyleSetting(this.state.mindmap?.selected?.element);
                       hiddenToolbar();
                     }}/>
                </Tooltip>
                <div className={'line-split-width1'}></div>
              </>
            }

            {
              //非多选...
                !this.state.isMindmapNodeMultiply && !this.state.isMindmapEditing &&
              <>
                <Tooltip arrow={false} title={i18next.t("Add card")}>
                  <img className={'img-30 cursor-pointer hovered'} src={getImagePathWithTheme('mindmap_toolbar_card', true)}
                       onClick={() => {
                         this.actionManager.executeAction(actionMindmapAddCard);
                         hiddenToolbar();
                       }}/>
                </Tooltip>
                <Tooltip arrow={false} title={i18next.t("Add image")}>
                  <img className={'img-30 cursor-pointer hovered'} src={getImagePathWithTheme('mindmap_toolbar_image', true)}
                       onClick={() => {
                         this.actionManager.executeAction(actionAddImage);
                         hiddenToolbar();
                       }}/>
                </Tooltip>
                <Tooltip arrow={false} title={i18next.t("Add file")}>
                  <img className={'img-30 cursor-pointer hovered'} src={getImagePathWithTheme('mindmap_toolbar_file', true)}
                       onClick={() => {
                         this.actionManager.executeAction(actionMindmapAddFile);
                         hiddenToolbar();
                       }}/>
                </Tooltip>
                <Tooltip arrow={false} title={i18next.t("Add link")}>
                  <img className={'img-30 cursor-pointer hovered'} src={getImagePathWithTheme('mindmap_toolbar_link', true)}
                       onClick={() => {
                         this.actionManager.executeAction(actionAddLink);
                         hiddenToolbar();
                       }}/>
                </Tooltip>
                <div className={'line-split-width1'}></div>
              </>
            }

            {
              // !this.state.isMindmapEditing &&
              <>
                <Popover zIndex={3001} placement={"bottom"} trigger={'click'} arrow={false} align={{offset: [0, 20]}} onOpenChange={(e)=>{
                  Global.g_isKeepMindmapToolbarShow = false;
                }}
                         content={
                           <ColorChoose defaultColor={this.state.mindmap?.selected?.node?.nodeData?.data?.fillColor || "#00000000"} from={'mindmap'} onColorClicked={(szHex: string) => {
                             this.handleMindMapToolbar({
                               type: 'backgroundColor',
                               color: szHex
                             })
                           }}/>
                         }>
                  <Tooltip arrow={false} title={i18next.t("Background")}>
                    <img className={'img-30 cursor-pointer hovered'}
                         src={getImagePathWithTheme('mindmap_toolbar_background', true)}/>
                  </Tooltip>
                </Popover>
                <div className={'line-split-width1'}></div>

                <Tooltip arrow={false} title={i18next.t("Font")}>
                  <div style={{minWidth: '96px'}}>
                    <ChooseFont hasMouseOverBorder={true}
                                defaultFontFamily={this.state.mindmap?.selected?.node?.nodeData?.data?.fontFamily || this.state.mindmap?.selected?.element?.config?.fontFamily}
                                onFontFamilyChange={(szFontFamily: any) => {
                                  this.handleMindMapToolbar({
                                    type: 'fontFamily',
                                    fontFamily: szFontFamily
                                  })
                                }}/>
                  </div>
                </Tooltip>

                <Tooltip arrow={false} title={i18next.t("Font size")}>
                  <ChooseFontSize defaultFontSize={this.getMindmapNodeFontSize()} onFontSizeChange={(nFontSize) => {
                    this.handleMindMapToolbar({
                      type: 'fontSize',
                      fontSize: `${nFontSize}`
                    })
                  }}/>
                </Tooltip>

                <div className={'line-split-width1'}></div>
              </>
            }


            <Popover zIndex={3001} placement={"bottom"} trigger={'click'} arrow={false} align={{offset: [0, 20]}}
                     content={
                       <ColorChoose defaultColor={this.state.mindmap?.selected?.node?.nodeData?.data?.color || "#000000"} onColorClicked={(szHex: string) => {
                         this.handleMindMapToolbar({
                           type: 'color',
                           color: szHex
                         })
                       }}/>
                     }>
              <Tooltip arrow={false} title={i18next.t("Color")}>
              <button
                  // style={{marginLeft:this.state.isMindmapEditing?'-6px':'2px'}}
                  className="toolbar"
                  onKeyDown={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    const toolbar = document.getElementById(
                        `excalidraw__mindmap__outer__toolbar`,
                    )!;
                    toolbar.style.display = "none";
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
              >
                <div className={'flex-row-vertical-center'}>
                  <img className={'img-30 hovered'} src={getImagePathWithTheme('mindmap_layout_color.svg')}/>
                  {/*<img className={'img-12'} src={getImagePathWithTheme('home_small_favor_dropdown')}/>*/}
                </div>

              </button>
              </Tooltip>
            </Popover>


            <ChooseTextStyle handleMindMapToolbar={this.handleMindMapToolbar}/>

            <div className={'line-split-width1'}></div>

            <Tooltip arrow={false} title={i18next.t("Clear formatting")}>
            <img className={'img-30 cursor-pointer hovered'}
                 src={getImagePathWithTheme('mindmap_toolbar_clear_style', true)} onClick={()=>{
                   this.handleMindMapToolbar({type:'remove'})
            }}/>
            </Tooltip>

            <div className={'line-split-width1'}></div>
            <Popover zIndex={MAX_Z_INDEX} open={this.state.isShowMindmapMore} onOpenChange={e=>{
              this.setState({
                isShowMindmapMore:e
              })
            }} trigger={'click'} align={{offset:[12,-2]}} overlayInnerStyle={{margin: '0', padding: '6px 0'}} arrow={false} placement={"rightTop"} content={<div className={'flex-col'} style={{minWidth:'310px'}}>
              {
                this.state.mindmap?.selected?.node &&
                this.getContextMenuItems('node_contextmenu').map(p=>{
                    return(p === CONTEXT_MENU_SEPARATOR?<div className={'line'}></div>:
                        <>
                            {
                                (p.name == 'mindmapExportToMarkdown')?
                                    <Popover arrow={false} open={this.state.isShowMindmapExportMore} onOpenChange={(e)=>{
                                        this.setState({
                                            isShowMindmapExportMore:e
                                        })
                                    }} trigger={'hover'} placement={'right'} overlayInnerStyle={{margin:'0',padding:'0px'}} content={
                                        <div style={{padding:'6px 0px'}}>
                                            {
                                                getArrayExportMarkdown().map(p => {
                                                    return <div className={'dropdown-item flex-row-vertical-center'}
                                                                style={{padding: '0px 12px', height: '36px', lineHeight: '36px'}}
                                                                onClick={() => {
                                                                    this.actionManager.executeAction(p);
                                                                    this.setState({
                                                                        isShowMindmapExportMore:false
                                                                    })

                                                                    setTimeout(()=>{
                                                                        this.setState({
                                                                            isShowMindmapMore: false
                                                                        })
                                                                    },100)
                                                                }}>
                                                        <img className={'img-16'} src={getImagePathWithTheme(p.icon)}/>
                                                        <span
                                                            className={'ml-6 default-font-family default-font-size'}>{i18next.t(p.contextItemLabel)}</span>
                                                    </div>
                                                })
                                            }
                                        </div>
                                    }>
                                        <div className={'dropdown-item flex-row-vertical-center'}
                                             style={{padding: '0px 12px', height: '36px', lineHeight: '36px'}}
                                             onClick={() => {
                                             }}>
                                            <img className={'img-16'} src={getImagePathWithTheme(p.icon)}/>
                                            <span
                                                className={'ml-6 default-font-family default-font-size'}>{i18next.t(p.contextItemLabel)}</span>
                                            <img className={'img-10 margin-left-auto'}
                                                 style={{position: 'absolute', right: '10px'}}
                                                 src={getImagePathWithTheme('right_arrow')}/>
                                        </div>
                                    </Popover> :
                                    <div className={'dropdown-item flex-row-vertical-center'}
                                         style={{padding: '0px 12px', height: '36px', lineHeight: '36px'}} onClick={() => {
                                        this.actionManager.executeAction(p);
                                        this.setState({
                                            isShowMindmapMore: false
                                        })
                                        if (p.name == 'deleteNode' || p.name == 'moveUpNode' || p.name == 'moveDownNode') {
                                            hiddenToolbar();
                                        }
                                    }}>
                                        <img className={'img-16'} src={getImagePathWithTheme(p.icon)}/>
                                        <span className={'ml-6 default-font-family default-font-size'}>{i18next.t(p.contextItemLabel)}</span>
                                    </div>
                            }
                        </>)
                })
              }
            </div>}>
              <Tooltip arrow={false} title={i18next.t("More")}>
                <img className={'img-30 cursor-pointer hovered'}
                     src={getImagePathWithTheme('mindmap_toolbar_more', true)}/>
              </Tooltip>
            </Popover>

          </span>
        </span>
    );
  }

  private renderMindMap() {
    const scale = 1;
    const normalizedWidth = this.state.width;
    const normalizedHeight = this.state.height;

    const mindmapElements = this.scene
        .getNonDeletedElements()
        .filter((el): el is NonDeleted<ExcalidrawMindmapElement> =>
            isMindmapElement(el),
        );

    return (
        <>
          {mindmapElements.map((el) => {
            const {x, y} = sceneCoordsToViewportCoords(
                {sceneX: el.x, sceneY: el.y},
                this.state,
            );

            const isVisible = true;

            // isElementInViewport(
            //     el,
            //     normalizedWidth,
            //     normalizedHeight,
            //     this.state,
            // );

            // const isActive =
            //   this.state.activeMindMap?.element === el &&
            //   this.state.activeMindMap?.state === "active";
            const isHovered =
                this.state.activeMindMap?.element === el &&
                this.state.activeMindMap?.state === "hover";
            let isPointerEvents = isHovered && !this.state.draggingElement;
            if (global.g_isPresentMode) {
              isPointerEvents = true;
            }

            const zoom = this.state.zoom.value;
            const mindMap = mindMapCache.get(el.id);
            if (mindMap) {
              // @ts-ignore
              const {oldScale, view, draw, el, svg} = mindMap;

              if (oldScale !== zoom) {
                requestAnimationFrame(() => {
                  if (oldScale === zoom) {
                    return;
                  }
                  //step1: mindmap 内部设置大小，div的scale始终为1
                  // @ts-ignore
                  view.setScale(zoom);

                  // step2: 获取g区域（节点区域）信息
                  const gRect = draw.node.getBoundingClientRect();

                  // step3：设置div元素的新宽度和高度
                  const newWidth = gRect.width + 60 * zoom;
                  const newHeight = gRect.height + 100 * zoom;
                  el.style.width = `${newWidth}px`;
                  el.style.height = `${newHeight}px`;

                  // step4：div元素宽高改变后，svg元素也会改变，需要重新设置svg元素的大小
                  const elRect = el.getBoundingClientRect();
                  svg.size(elRect.width, elRect.height);

                  // step5：获取svg元素的区域信息
                  const svgElement = svg.node;
                  const svgElementRect = svgElement.getBoundingClientRect();

                  // step6：g区域（节点区域）的偏移量,保证g在svg位置保持不变
                  const offset = 3 * zoom;
                  const deltaX = svgElementRect.x - gRect.x + offset;
                  const deltaY = svgElementRect.y - gRect.y + 50 * zoom;
                  view.translateXY(deltaX, deltaY);
                  // @ts-ignore
                  mindMap.oldScale = zoom;

                  // @ts-ignore
                  if (APP_WEB){

                  }else{
                    setTimeout(()=>{
                      mindMap.render();
                    },600)
                  }

                });
              }
            }

            return (
                <div
                    key={el.id}
                    className={clsx("excalidraw__mindmap-container")}
                    id={`excalidraw__mindmap-container${el.id}`}
                    style={{
                      transform: `translate(${x - this.state.offsetLeft}px, ${
                          y - this.state.offsetTop
                      }px) scale(${scale})`,
                      display: "block",
                      opacity: el.opacity / 100,
                      ["--mindmap-radius" as string]: `${getCornerRadius(
                          Math.min(el.width, el.height),
                          el,
                      )}px`,
                    }}>
                  <div
                      className="excalidraw__mindmap-container__inner"
                      id={`excalidraw__mindmap-container__inner${el.id}`}
                      style={{
                        transform: isVisible ? `rotate(${el.angle}rad)` : "none",
                      }}
                  >
                    <div
                        className="excalidraw__mindmap__outer"
                        id={`excalidraw__mindmap__outer${el.id}`}
                        style={{
                          // width: `${el.width}px`,
                          // height: `${el.height}px`,
                          padding: `${el.strokeWidth}px`,
                          pointerEvents: (isPointerEvents)
                              ? POINTER_EVENTS.enabled
                              : POINTER_EVENTS.disabled,
                        }}
                        onPointerDown={(event) => {
                            if (event.button === POINTER_BUTTON.WHEEL) {
                                setTimeout(() => {
                                    const selfEl = document.getElementById(
                                        `excalidraw__mindmap__outer${el.id}`,
                                    )!;
                                    selfEl.style.pointerEvents = POINTER_EVENTS.disabled;
                                    this.handleCanvasPointerDown(event);
                                }, 10);
                              return;
                            }
                        }}
                        onDoubleClick={(event) => {
                          const target = event?.target instanceof Element ? event.target : null;
                          if (!target) return;
                          // Only skip selection when double-clicking on clearly interactive elements
                          // (links, expand btn, rich-text edit). Blank area and node backgrounds
                          // should select the mindmap.
                          const isNodeOrControl =
                              target.closest?.(".smm-expand-btn") ||
                              target.closest?.('[data-mindmap-control="true"]') ||
                              target.closest?.("a") ||
                              target.closest?.(".smm-richtext-node-wrap") ||
                              target.closest?.(".smm-richtext-node-edit-wrap") ||
                              ["rect", "path", "circle", "tspan", "text", "image", "foreignobject"].includes(
                                  (target as any).tagName?.toLowerCase?.() || ""
                              );
                          if (!isNodeOrControl) {
                            this.setState({
                              selectedElementIds: { [el.id]: true },
                              draggingElement: null,
                              selectionElement: null,
                            });
                          }
                        }}
                    ></div>
                  </div>
                </div>
            );
          })}
        </>
    );
  }

  private renderHoverModel() {
    return (
        <>
          {
            // this.state.isShowHoverdElementMenu &&
            <div
                id="hoverModel"
                className={'default-font-family ' + clsx("excalidraw__embeddable-container")}
                style={{
                  display: "none",
                  pointerEvents: "all",
                  borderRadius: "8px",
                  padding: '4px 0px',
                  minWidth: "200px",
                  fontSize:'12px',
                  boxShadow: 'var(--dialog-box-shadow)',
                  backgroundColor: "var(--dialog-dialog-background-color-2c)",
                }}
            >
              <div className={'cursor-pointer dropdown-item flex-row-vertical-center menu-height-38'}
                   onClick={() => this.addElementByHover("card")}>
                <img className={'img-16 ml-16 '} src={getImagePathWithTheme('dl_contextmenu_new_card')}/>
                <span className={'ml-8 mr-16 mt-2'}>{i18next.t("New card")}</span>
              </div>
                  <div className={'cursor-pointer dropdown-item  flex-row-vertical-center menu-height-38'}
                     onClick={() => this.addElementByHover("newText")}>
                    <img className={'img-16 ml-16 '} src={getImagePathWithTheme('contextmenu_create_text')}/>
                    <span className={'ml-8 mr-16'}>{i18next.t("New text")}</span>
                </div>
              <div className={'cursor-pointer dropdown-item  flex-row-vertical-center menu-height-38'}
                   onClick={() => this.addElementByHover("existCard")}>
                <img className={'img-16 ml-16 '} src={getImagePathWithTheme('dl_contextmenu_exist_card')}/>
                <span className={'ml-8 mr-16'}>{i18next.t("Existing card")}</span>
              </div>
              <div className={'cursor-pointer dropdown-item  flex-row-vertical-center menu-height-38'}
                   onClick={() => this.addElementByHover("stickyNotes")}>
                <img className={'img-16 ml-16 '} src={getImagePathWithTheme('dl_contextmenu_new_sticky')}/>
                <span className={'ml-8 mr-16'}>{i18next.t("NewStickyNotes")}</span>
              </div>
              <div className={'cursor-pointer dropdown-item flex-row-vertical-center menu-height-38'}
                   onClick={() => this.addElementByHover("roundedRectangle")}>
                <img className={'img-16 ml-16 '} src={getImagePathWithTheme('dl_contextmenu_new_shape')}/>
                <span className={'ml-8 mr-16'}>{i18next.t("New shape")}</span>
              </div>
              <div className={'cursor-pointer dropdown-item flex-row-vertical-center menu-height-38'}
                   onClick={() => this.addElementByHover("newBoard")}>
                <img className={'img-16 ml-16 '} src={getImagePathWithTheme('dl_contextmenu_new_board')}/>
                <span className={'ml-8 mr-16'}>{i18next.t("New board")}</span>
              </div>
              <div className={'cursor-pointer dropdown-item flex-row-vertical-center menu-height-38'}
                   onClick={() => this.addElementByHover("pin")}>
                <img className={'img-16 ml-16 '} src={getImagePathWithTheme('dl_contextmenu_pin_here')}/>
                <span className={'ml-8 mr-16'}>{i18next.t("Pin here")}</span>
              </div>
            </div>
          }
        </>
    );
  }

  /**
   * 项目链接
   * @private
   */
  private renderProjectLink() {
    const scale = this.state.zoom.value;
    const projectLinkElements = this.scene
        .getNonDeletedElements()
        .filter((el): el is NonDeleted<ExcalidrawMindmapElement> =>
            isProjectLinkElement(el),
        );

    return (
        <>
          {
            projectLinkElements.map(el => {
              const {x, y} = sceneCoordsToViewportCoords(
                  {sceneX: el.x, sceneY: el.y},
                  this.state,
              );
              const isVisible = true;
              const isHovered =
                  this.state.activeMindMap?.element === el &&
                  this.state.activeMindMap?.state === "hover";
              let isPointerEvents =
                  isHovered &&
                  Object.keys(this.state.selectedElementIds).length === 0;
              if (global.g_isPresentMode) {
                isPointerEvents = true;
              }


              /**
               * 是否应该显示这个边框，
               */
              const hasShowThisBorderBorderAnd = ()=>{
                return this.state.selectedElementIds[el.id] || (el.backgroundColor != '#DEDEDE' && el.backgroundColor != '#FFFFFF');
              }


              const getBorder = ()=>{
                if (this.state.selectedElementIds[el.id]){
                  return `2px solid ${getElementSelectionBorderColor()}`;
                }
                if ((el.backgroundColor != '#DEDEDE' && el.backgroundColor != '#FFFFFF')){
                  return `2px solid ${el.backgroundColor}`;
                }
                return `2px solid transparent`;
              }


              return (
                  <div
                      key={el.id}
                      className={clsx("excalidraw__embeddable-container")}
                      id={`excalidraw__projectlink-container${el.id}`}
                      style={{
                        width: `${el.width}px`,
                        height: `${el.height}px`,
                        transform: isVisible
                            ? `translate(${x - this.state.offsetLeft}px, ${
                                y - this.state.offsetTop
                            }px) scale(${scale})`
                            : "none",
                        display: isVisible ? "block" : "none",
                        opacity: el.opacity / 100,
                        // borderRadius: '8px',
                        // overflow: 'hidden',
                        // border: `1px solid ${getProjectLinkElementStrokeColor250728()}`,
                        // ["--mindmap-radius" as string]: `${getCornerRadius(
                        //     Math.min(el.width, el.height),
                        //     el,
                        // )}px`,
                      }}>
                    <div style={{position: 'relative',
                      width:'100%',height:'100%',
                      border: `1px solid ${getProjectLinkElementStrokeColor250728()}`,
                      borderRadius:'8px',overflow:'hidden'
                    }}>
                      <EmbedProject appState={this.state} element={el} onMouseDown={(event) => {
                        this.handleCanvasPointerDown(event);
                      }} onMouseUp={(event) => {
                        this.handleCanvasPointerUp(event);
                      }} onWheel={(event) => {
                        this.handleWheel(event);
                      }} onAction={(szAction: any, element: any) => {
                        if (szAction == 'doubleClick') {
                          //转到链接的条目...
                          postMessage(EVENT_NAVIGATE_DEEPLINK, {element: element})
                        }
                      }}/>

                    </div>

                    {
                        hasShowThisBorderBorderAnd() &&
                        <div style={{position: 'absolute',
                          width: '100%', height: '100%',left:'0',
                          top:'0',
                          borderRadius:'8px',
                          zIndex:'99999',
                          border:getBorder()
                        }}></div>
                    }
                    <HoveredElementCircle state={this.state} el={el}/>
                  </div>
              )
            })
          }
        </>
    )
  }

    /**
     * 渲染文本
     **/
    private renderText(){
        if (!HAS_ENABLE_TIPTAP_TEXT)return <></>;
        const scale = this.state.zoom.value;
        const normalizedWidth = this.state.width;
        const normalizedHeight = this.state.height;
        const selectedElementIds = this.state.selectedElementIds;
        // const textElements = this.scene
        //     .getNonDeletedElements()
        //     .filter((el): el is NonDeleted<ExcalidrawStickyNotesElement> =>
        //         isTextElement(el) && !el.containerId,
        //     );
        const textElements = this.visibleElements.filter(el=>isTextElement(el) && !el.containerId);

        return (
            <>
                {textElements.map((el) => {
                  const isVisible = isElementInViewport(
                      el,
                      normalizedWidth,
                      normalizedHeight,
                      this.state,
                  );

                    return (
                        <TextTiptap key={el.id} element={el} app={this} state={this.state} isVisible={isVisible} onMouseDown={(event)=>{
                            this.handleCanvasPointerDown(event)
                        }} onMouseMove={(event)=>{
                            this.handleCanvasPointerMove(event)
                        }} onMouseUp={(event)=>{
                            this.handleCanvasPointerUp(event)
                        }} onWheel={(event)=>{
                            this.handleWheel(event);
                        }}
                                    onFocus={()=>{
                                        if (!this.state.selectedElementIds[el.id]){
                                            //选中这个..
                                            this.setState({
                                                selectedElementIds:{[el.id]:true}
                                            })
                                            setTimeout(()=>{
                                                window.showCanvasFolatMenu();
                                            },100)
                                        }
                                    }}
                                    onSelectionText={(hasSelected)=>{
                                        if (hasSelected){
                                            window.hiddenCanvasFloatMenu();
                                        }else{
                                            if (el && this.isSelectedElement(el)){
                                                window.showCanvasFolatMenu();
                                            }
                                        }
                                    }}
                                    onTextSelection={(isShow,editor,updateEditCardHtml,syncNoteData,element,data,onHiddenBubbleMenu,onAction)=>{

                                        this.setState({
                                            cardEditMenu:{
                                                visibility:isShow,
                                                editor:editor,
                                                updateEditCardHtml:updateEditCardHtml,
                                                syncNoteData:syncNoteData,
                                                element:element,
                                                data:data,
                                                onHiddenBubbleMenu:onHiddenBubbleMenu,
                                                onAction:onAction,
                                                from:'stickynote'
                                            }
                                        })
                                    }}
                                    updateHeightCallback={(element, updates)=>{
                                        this.onUpdateElement(element, updates);
                                    }}
                                    updateElementCallback={(element, updates, isExpandOrCollapse = false) => {
                                        //如果属性一样就不更新了。。
                                        if (Object.keys(updates).length == 1 && updates.height){
                                            if (updates.height != element.height){
                                                this.onUpdateElement(element, updates);
                                            }
                                        }else{
                                            this.onUpdateElement(element, updates);
                                        }

                                        //更新别的元素....
                                        // const arrayOtherNotes = this.scene.getNonDeletedElements().filter(p=>p.customData?.generationData?.id == element.customData?.generationData?.id)

                                        //输入的时候，如果element接触到底部，则canvas向下自动滚动.
                                        let height = this.state.height - this.state.scrollY * (this.state.zoom?.value || 1);
                                        let elementOffset = element.height + element.y;
                                        elementOffset *= (this.state.zoom?.value || 1);
                                        const offsetY = 40;

                                        const nY = el.y*this.state.zoom.value + element.height + 0*this.state.zoom.value;
                                        const nTotal = this.state.height + Math.abs(this.state.scrollY*this.state.zoom.value);

                                        //不要自动滚动了。。。太长的文本又没有滚动条，随便在开始或中间哪里输入都会跳转到最下面....
                                        if ((elementOffset + offsetY) > height) {
                                            if (element.customData?.generationData?.isEditable)
                                            {
                                                // this.setState({
                                                //     scrollY: this.state.scrollY - ((elementOffset + offsetY) - height)
                                                // });
                                            }
                                        }

                                        //这里处理连线问题....
                                        if (element.boundElements) {
                                            updateBoundElements(element);
                                        }
                                        // console.log('canvasHeight:'+height + '   elementHeight:'+(element.height + element.y) + ' scrollY:'+this.state.scrollY)
                                    }}/>
                    );
                })}
            </>
        );
    }

  private renderStickyNotes() {
    const scale = this.state.zoom.value;
    const normalizedWidth = this.state.width;
    const normalizedHeight = this.state.height;
    const selectedElementIds = this.state.selectedElementIds;
    const stickyNotesElements = this.scene
        .getNonDeletedElements()
        .filter((el): el is NonDeleted<ExcalidrawStickyNotesElement> =>
            isStickyNotesElement(el),
        );

    return (
        <>
          {stickyNotesElements.map((el) => {
            return (
                // <StickyNote
                //     stickyNotesElement={el}
                //     appState={this.state}
                //     key={el.id}
                //     onWheel={(event)=>{
                //       this.handleWheel(event);
                //     }}
                // />
                <StickyNoteTiptap element={el} app={this} state={this.state} onMouseDown={(event)=>{
                  this.handleCanvasPointerDown(event)
                }} onMouseMove={(event)=>{
                  this.handleCanvasPointerMove(event)
                }} onMouseUp={(event)=>{
                  this.handleCanvasPointerUp(event)
                }} onWheel={(event)=>{
                  this.handleWheel(event);
                }}
                                  onFocus={()=>{
                                    if (!this.state.selectedElementIds[el.id]){
                                      //选中这个..
                                      this.setState({
                                        selectedElementIds:{[el.id]:true}
                                      })
                                      setTimeout(()=>{
                                        window.showCanvasFolatMenu();
                                      },100)
                                    }
                                  }}
                onSelectionText={(hasSelected)=>{
                  if (hasSelected){
                    window.hiddenCanvasFloatMenu();
                  }else{
                    if (el && this.isSelectedElement(el)){
                      window.showCanvasFolatMenu();
                    }
                  }
                }}
                onTextSelection={(isShow,editor,updateEditCardHtml,syncNoteData,element,data,onHiddenBubbleMenu,onAction)=>{

                  this.setState({
                    cardEditMenu:{
                      visibility:isShow,
                      editor:editor,
                      updateEditCardHtml:updateEditCardHtml,
                      syncNoteData:syncNoteData,
                      element:element,
                      data:data,
                      onHiddenBubbleMenu:onHiddenBubbleMenu,
                      onAction:onAction,
                      from:'stickynote'
                    }
                  })
                }} updateElementCallback={(element, updates, isExpandOrCollapse = false) => {
                  // console.log('cardEdit--updateElementCallback',updates);
                  //如果属性一样就不更新了。。
                  if (Object.keys(updates).length == 1 && updates.height){
                    if (updates.height != element.height){
                      this.onUpdateElement(element, updates);
                    }
                  }else{
                    this.onUpdateElement(element, updates);
                  }

                  //更新别的元素....
                  // const arrayOtherNotes = this.scene.getNonDeletedElements().filter(p=>p.customData?.generationData?.id == element.customData?.generationData?.id)

                  //输入的时候，如果element接触到底部，则canvas向下自动滚动.
                  let height = this.state.height - this.state.scrollY * (this.state.zoom?.value || 1);
                  let elementOffset = element.height + element.y;
                  elementOffset *= (this.state.zoom?.value || 1);
                  const offsetY = 40;

                  const nY = el.y*this.state.zoom.value + element.height + 0*this.state.zoom.value;
                  const nTotal = this.state.height + Math.abs(this.state.scrollY*this.state.zoom.value);

                  if (!isExpandOrCollapse && (elementOffset + offsetY) > height && element.height < (document.body.offsetHeight - 100) && (element.height*this.state.height < document.body.offsetHeight)) {
                    if (element.customData?.generationData?.isEditable){
                      this.setState({
                        scrollY: this.state.scrollY - ((elementOffset + offsetY) - height)
                      });
                    }
                  }

                  //这里处理连线问题....
                  if (element.boundElements) {
                    updateBoundElements(element);
                  }
                  // console.log('canvasHeight:'+height + '   elementHeight:'+(element.height + element.y) + ' scrollY:'+this.state.scrollY)
                }}/>
            );
          })}
        </>
    );


    // return (
    //     <>
    //       {stickyNotesElements.map((el) => {
    //         const { x, y } = sceneCoordsToViewportCoords(
    //             { sceneX: el.x, sceneY: el.y },
    //             this.state,
    //         );
    //
    //         const isVisible = isElementInViewport(
    //             el,
    //             normalizedWidth,
    //             normalizedHeight,
    //             this.state,
    //         );
    //
    //         let isActive =
    //             this.state.activeStickyNotes?.element === el &&
    //             this.state.activeStickyNotes?.state === "active";
    //         // console.log(isActive);
    //         const isSelectedCurrent =
    //             Object.keys(selectedElementIds).length === 1 &&
    //             selectedElementIds[el.id];
    //
    //         mutateElement(el, { isActive });
    //
    //         // console.log('xxxxxx',isActive)
    //
    //         // isActive = true;
    //
    //         // if (isActive) {
    //         //   const textArea = document.getElementById(`stickyNotes-${el.id}`);
    //         //   //当文字高度超过默认高度时，矩形就会保持宽度不变，高度不断变大
    //         //   if (textArea && textArea.scrollHeight > textArea.clientHeight) {
    //         //     const targetContainerHeight =
    //         //         computeContainerDimensionForBoundText(
    //         //             el.height,
    //         //             "rectangle",
    //         //         ) as number;
    //         //     if (targetContainerHeight + el.y < normalizedHeight) {
    //         //       mutateElement(el, { height: targetContainerHeight });
    //         //     } else {
    //         //       mutateElement(el, {
    //         //         y: normalizedHeight - targetContainerHeight,
    //         //         height: targetContainerHeight,
    //         //       });
    //         //     }
    //         //   }
    //         // }
    //
    //         return (
    //             <div
    //                 key={el.id}
    //                 className={clsx("excalidraw__mindmap-container")}
    //                 data-type={'stickynotes'}
    //                 id={`excalidraw__mindmap-container${el.id}`}
    //                 style={{
    //                   transform: isVisible
    //                       ? `translate(${x - this.state.offsetLeft}px, ${
    //                           y - this.state.offsetTop
    //                       }px) scale(${scale})`
    //                       : "none",
    //                   display: isVisible ? "block" : "none",
    //                   opacity: el.opacity / 100,
    //                 }}
    //             >
    //               <div
    //                   className="excalidraw__mindmap-container__inner"
    //                   id={`excalidraw__mindmap-container__inner${el.id}`}
    //                   style={{
    //                     width: `${el.width}px`,
    //                     height: `${el.height}px`,
    //                     // boxShadow:'var(--dialog-box-shadow)',
    //                     // borderRadius:'8px',
    //                     transform: isVisible ? `rotate(${el.angle}rad)` : "none",
    //                   }}
    //               >
    //                 {/* {!isActive && (
    //               <div className="excalidraw__mindmap-hint">Click to edit</div>
    //             )} */}
    //                 <div
    //                     className="excalidraw__mindmap__outer shadow"
    //                     id={`excalidraw__mindmap__outer${el.id}`}
    //                     style={{
    //                       position: 'relative',
    //                       pointerEvents: true
    //                           ? POINTER_EVENTS.enabled
    //                           : POINTER_EVENTS.disabled,
    //                       cursor: isActive ? 'text' : 'pointer',
    //
    //                     }}
    //                     onMouseMove={(event) => {
    //                       this.handleCanvasPointerMove(event);
    //                     }}
    //                     onMouseDown={(event) => {
    //                       const txt = document.getElementById('stickyNotes-' + el.id);
    //                       if (txt && txt.readOnly) {
    //                         this.setState({
    //                           isPointerDown: true
    //                         })
    //                         this.handleCanvasPointerDown(event, el);//指定选中这一个元素
    //                       }
    //                     }}
    //                     onMouseUp={(event) => {
    //                       this.setState({
    //                         isPointerDown: false
    //                       })
    //                       this.handleCanvasPointerUp(event);
    //                     }}
    //
    //                     onWheel={(event) => {
    //                       const txt = document.getElementById('stickyNotes-' + el.id);
    //                       if (txt && txt.readOnly) {
    //                         this.handleWheel(event);
    //                       }
    //                     }}
    //
    //                     onDoubleClick={(event) => {
    //                       event.preventDefault();
    //                       event.stopPropagation();
    //                       // mutateElement(el, { isActive:true });
    //                       this.setState({
    //                         activeStickyNotes: {
    //                           element: el,
    //                           state: 'active'
    //                         },
    //                         selectedElementIds: {[el.id]: true}
    //                       })
    //                       const txt = document.getElementById('stickyNotes-' + el.id);
    //                       if (txt) {
    //                         txt.readOnly = false;
    //                         // txt.style.cursor = 'text';
    //                         txt.style.pointerEvents = 'all';
    //                         txt.focus();
    //                       }
    //                     }}
    //                 >
    //                   <Input.TextArea
    //                       id={`stickyNotes-${el.id}`}
    //                       readOnly={!el.isActive}
    //                       style={{
    //                         height: `${el.height}px`,
    //                         borderRadius: "8px",
    //                         resize: "none",
    //                         backgroundColor: el.backgroundColor,
    //                         color: el.strokeColor,
    //                         border: "none",
    //                         padding: `16px`,
    //                         font: getFontString({
    //                           fontSize: el.fontSize,
    //                           fontFamily: el.fontFamily,
    //                           textareaType: "all",
    //                         }),
    //                         pointerEvents: 'none',
    //                       }}
    //
    //                       // autoSize
    //                       // placeholder={i18next.t("Add text")}
    //                       onInput={(e) => {
    //                         this.changeStickyNotesValue(el, e.target.value);
    //                         const textArea = document.getElementById(`stickyNotes-${el.id}`);
    //                         if (textArea) {
    //                           textArea.style.height = 'auto';
    //                           let nHeight = textArea.scrollHeight;
    //                           if (el.customData?.generationData?.isFixedSize) {
    //                             // if (el.height > nHeight) {
    //                               nHeight = el.height;
    //                             // }
    //                           }
    //                           if (nHeight < DEFAULT_STICK_NOTE_HEIGHT && !el.customData?.generationData?.isFixedSize) {
    //                             nHeight = DEFAULT_STICK_NOTE_HEIGHT;
    //                           }
    //                           if (nHeight > document.body.offsetHeight - 102) {
    //                             nHeight = document.body.offsetHeight - 102;
    //                           }
    //                           textArea.style.height = nHeight + 'px';
    //                           mutateElement(el, {height: nHeight})
    //
    //
    //                           if (!el.customData?.generationData?.isFixedSize) {
    //                             //如果触底了，canvas也要跟着动...
    //                             // let nY = el.y * this.state.zoom.value + nHeight + 0 * this.state.zoom.value;
    //                             // const nTotal = this.state.height + Math.abs(this.state.scrollY * this.state.zoom.value);
    //                             // if (nY >= nTotal && nHeight <= (document.body.clientHeight - 100)) {
    //                             //   this.setState({
    //                             //     scrollY: (this.state.height - nY)
    //                             //   })
    //                             // }
    //
    //
    //                             //输入的时候，如果element接触到底部，则canvas向下自动滚动.
    //                             let height = this.state.height - this.state.scrollY * (this.state.zoom?.value || 1);
    //                             let elementOffset = el.height + el.y;
    //                             elementOffset *= (this.state.zoom?.value || 1);
    //                             const offsetY = 40;
    //
    //                             const nY = el.y*this.state.zoom.value + el.height + 0*this.state.zoom.value;
    //                             const nTotal = this.state.height + Math.abs(this.state.scrollY*this.state.zoom.value);
    //
    //                             if ((elementOffset + offsetY) > height && el.height < (document.body.offsetHeight - 100)) {
    //                               this.setState({
    //                                 scrollY: this.state.scrollY - ((elementOffset + offsetY) - height)
    //                               });
    //                             }
    //
    //                             //这里处理连线问题....
    //                             if (el.boundElements) {
    //                               updateBoundElements(el);
    //                             }
    //                           }
    //                         }
    //                       }}
    //                       onFocus={() => {
    //                         // console.log("onFocus");
    //                         // this.setState({
    //                         //   selectedElementIds: {},
    //                         // });
    //
    //                         // setTimeout(()=>{
    //                         //   const txt = document.getElementById('stickyNotes-'+el.id);
    //                         //   if (txt) {
    //                         //     txt.focus();
    //                         //   }
    //                         // },100)
    //                         // console.log('onFocusonFocusonFocusonFocusonFocusonFocus')
    //
    //                         const txt = document.getElementById('stickyNotes-' + el.id);
    //                         if (txt) {
    //                           txt.readOnly = false;
    //                           // txt.style.cursor = 'text';
    //                           txt.style.pointerEvents = 'all';
    //                           txt.focus();
    //                         }
    //                       }}
    //
    //                       onBlur={(event) => {
    //
    //                         // setTimeout(() => {
    //                         //   const act = document.activeElement;
    //                         //   if (act?.className?.includes('tiptap') || act.tagName.toLowerCase().includes('textarea') || act.tagName.toLowerCase().includes('input')) {
    //                         //     this.setState({
    //                         //       selectedElementIds: {},
    //                         //       activeStickyNotes: {element: null, state: null}
    //                         //     })
    //                         //   }
    //                         // }, 10)
    //
    //                         const txt = document.getElementById('stickyNotes-' + el.id);
    //                         if (txt) {
    //                           txt.readOnly = true;
    //                           // txt.style.cursor = 'pointer';
    //                           txt.style.pointerEvents = 'none';
    //                         }
    //
    //                         //刷新左侧栏content
    //                         if (window.onRefreshElement) {
    //                           window.onRefreshElement(this.getSceneElements());
    //                         }
    //                       }}
    //
    //                       onWheel={(event) => {
    //                         if (event.currentTarget.scrollHeight <= event.currentTarget.offsetHeight) {
    //                           this.handleWheel(event);
    //                         }
    //                       }}
    //
    //                       // onMouseDown = {(event)=>{
    //                       //   var currentTime = new Date().getTime();
    //                       //   if (currentTime - lastClickTime < 300) { // 双击的时间阈值，这里设置为300毫秒
    //                       //     event.preventDefault();
    //                       //   }
    //                       //   lastClickTime = currentTime;
    //                       // }}
    //
    //                       defaultValue={el.value}
    //                       // onResize={(size) => {
    //                       //   console.log(size);
    //                       //   mutateElement(el, { height: size.height });
    //                       // }}
    //                       ref={(ref) => {
    //                         if (isSelectedCurrent && isActive && selectedElementIds[el.id]) {
    //                           setTimeout(() => {
    //                             ref?.focus();
    //                           }, 0);
    //                         }
    //                       }}
    //                   />
    //                   <div style={{
    //                     width: '100%', height: '100%', left: '0', top: '0', position: 'absolute',
    //                     border: (this.isElementSelect(el)) ? `${2.2 / this.state.zoom.value}px solid ${getElementSelectionBorderColor()}` : 'none',
    //                     borderRadius: '8px', pointerEvents: 'none'
    //                   }}></div>
    //
    //                 </div>
    //               </div>
    //
    //               {/*//四个hoverd点*/}
    //               <HoveredElementCircle state={this.state} el={el}/>
    //
    //             </div>
    //         );
    //       })}
    //     </>
    // );
  }

  private renderGIF() {

    const GIFElements = this.scene
        .getNonDeletedElements()
        .map((el): el is NonDeleted<ExcalidrawImageElement> => {
          if (!isImageElement(el)) {
            return false;
          }
          if (!el.fileId) {
            return false;
          }

          const file = this.files[el.fileId];
          const imageCache = this.imageCache.get(el.fileId);
          // if (imageCache?.mimeType === MIME_TYPES.gif || file?.mimeType == MIME_TYPES.gif) {
          //   // @ts-ignore
          //   return { ...el, ...imageCache };
          // }


          if (imageCache){
            //只有显示了infoNotes的才使用图片
            if (!el.customData?.generationData?.isShowInfoNotes && (imageCache?.mimeType !== MIME_TYPES.gif && file?.mimeType !== MIME_TYPES.gif)){
              return false;
            }

            return {...el,...imageCache}
          }
          return false;
        })
        .filter((el) => !!el) as unknown as any[];


    return (
        <>
          {GIFElements.map((el) => {
            // return <RenderCanvasImage state={this.state} el={el} onSelectedElement={()=>{
            //   this.setState({
            //     selectedElementIds:{[el.id]:true}
            //   })
            // }}/>

            const scale = this.state.zoom.value;
            const normalizedWidth = this.state.width;
            const normalizedHeight = this.state.height;


            const { x, y } = sceneCoordsToViewportCoords(
                { sceneX: el.x, sceneY: el.y },
                this.state,
            );

            const isSelectedElement = (element)=>{
              return this.state.selectedElementIds[element.id];
            }

            const getBorder = ()=>{
              if (isSelectedElement(el)){
                if (el.customData?.generationData?.isEditable){
                  return `2px solid ${getInputBorderColor()}`
                }
                return `2px solid ${getElementSelectionBorderColor()}`
              }
              let nStrokeWidth = el.strokeWidth;
              if (nStrokeWidth <= 1){
                nStrokeWidth = 0;
              }
              //nStrokeWidth = nStrokeWidth * (1/this.state.zoom.value);
              return `${nStrokeWidth}px solid ${el.strokeColor}`;
            }

            let isVisible = isElementInViewport(
                el,
                normalizedWidth,
                normalizedHeight,
                this.state,
            );

            /**
             * 计算元素区域高度.
             */
            const getHeight = ()=>{
              let nHeight = el.height;
              const txtArea = document.getElementById(`txt-input-note-${el.id}`);
              if (txtArea && el.customData?.generationData?.isShowInfoNotes){
                nHeight += el.customData?.generationData?.inputHeight;
                nHeight += 16 + 8;
              }
              return nHeight;
            }

            const getImageStyle = ()=>{
              if (el.image?.src){
                return {width:'100%',height:`auto`};
              }
              return {width:'90px',height:`${el.customData?.generationData?.originHeight || el.height}px`,marginLeft:'50%',transform:'translateX(-50%)'};
            }

            return (
                <div
                    key={el.id}
                    className={clsx("excalidraw__mindmap-container")}
                    id={`excalidraw__mindmap-container${el.id}`}
                    style={{
                      transform: isVisible
                          ? `translate(${x - this.state.offsetLeft}px, ${
                              y - this.state.offsetTop
                          }px) scale(${scale})`
                          : "none",
                      display: isVisible ? "block" : "none",
                      opacity: el.opacity / 100,
                    }}
                >
                  {/*<input className={'input-none'} style={{transform: isVisible ? `rotate(${el.angle}rad)` : "none",}} value={el.name}/>*/}
                  <div
                      className="excalidraw__mindmap-container__inner"
                      id={`excalidraw__mindmap-container__inner${el.id}`}
                      style={{
                        overflow: 'visible',//旋转后的点点显示的问题....
                        width: `${el.width}px`,
                        height: `${el.height}px`,
                        transform: isVisible ? `rotate(${el.angle}rad)` : "none",
                        borderRadius: `${el.roundness?.value || 0}px`,
                        display:'flex',
                        flexDirection:'column',
                        background: 'var(--element-withnote-background)'
                      }}
                  >
                    <div
                        className="excalidraw__mindmap__outer"
                        style={{borderRadius: `${el.roundness?.value || 0}px`, overflow: 'hidden',}}
                        id={`excalidraw__mindmap__outer${el.id}`}
                    >

                      <img
                          src={el.image?.src || BASE64_CANVAS_IMAGE_ERROR_PLACEHOLDER}
                          alt={el.image?.src}
                          onError={(event) => {
                            event.currentTarget.src = BASE64_CANVAS_IMAGE_ERROR_PLACEHOLDER;
                          }}
                          style={getImageStyle()}
                      />
                      <RenderCanvasNote el={el} state={this.state} app={this}
                                        onWheel={(event) => this.handleWheel(event)} isVisible={isVisible}/>
                    </div>

                    <div
                        style={{
                          width: '100%',
                          height: '100%',
                          position: 'absolute',
                          left: '0',
                          top: '0',
                          borderRadius: `${el.roundness?.value || 0}px`,
                          pointerEvents: 'none',
                          border: getBorder()
                        }}>
                    </div>

                    <HoveredElementCircle state={this.state} el={el}/>

                  </div>

                </div>
            );
          })}
        </>
    );
  }

  private getFrameNameDOMId = (frameElement: ExcalidrawElement) => {
    return `${this.id}-frame-name-${frameElement.id}`;
  };

  frameNameBoundsCache: FrameNameBoundsCache = {
    get: (frameElement) => {
      let bounds = this.frameNameBoundsCache._cache.get(frameElement.id);
      if (
          !bounds ||
          bounds.zoom !== this.state.zoom.value ||
        bounds.versionNonce !== frameElement.versionNonce
      ) {
        const frameNameDiv = document.getElementById(
          this.getFrameNameDOMId(frameElement),
        );

        if (frameNameDiv) {
          const box = frameNameDiv.getBoundingClientRect();
          const boxSceneTopLeft = viewportCoordsToSceneCoords(
            { clientX: box.x, clientY: box.y },
            this.state,
          );
          const boxSceneBottomRight = viewportCoordsToSceneCoords(
            { clientX: box.right, clientY: box.bottom },
            this.state,
          );

          bounds = {
            x: boxSceneTopLeft.x,
            y: boxSceneTopLeft.y,
            width: boxSceneBottomRight.x - boxSceneTopLeft.x,
            height: boxSceneBottomRight.y - boxSceneTopLeft.y,
            angle: 0,
            zoom: this.state.zoom.value,
            versionNonce: frameElement.versionNonce,
          };

          this.frameNameBoundsCache._cache.set(frameElement.id, bounds);

          return bounds;
        }
        return null;
      }

      return bounds;
    },
    /**
     * @private
     */
    _cache: new Map(),
  };

  /**
   * 名称最大宽度，不然会越过canvas
   */
  getMaxInputWidth = (element:any)=>{
    let width = element.width;
    let nPos = element.x + this.state.scrollX*this.state.zoom.value + element.width;
    if (nPos > this.state.width){
      width = element.width - (nPos - this.state.width)
    }
    return width;
  }

  private renderNames = ()=>{}
  private renderIFrameNames = ()=>{}

  /**
   * 名称名称名称名称名称名称名称名称名称名称名称名称名称名称
   */
  private renderFrameNames = () => {
    if (!this.state.frameRendering.enabled || !this.state.frameRendering.name) {
      return null;
    }

    const isDarkTheme = this.state.theme === "dark";

    let frameIndex = 0;
    let magicFrameIndex = 0;

    //getNonDeletedFramesLikes 换掉了。。
    return this.scene.getNonDeletedElementWithName(this.state.activeTool.type).map((f,index) => {
      if (isFrameElement(f)) {
        frameIndex++;
      } else {
        magicFrameIndex++;
      }
      if (
        !isElementInViewport(
          f,
          this.canvas.width / window.devicePixelRatio,
          this.canvas.height / window.devicePixelRatio,
          {
            offsetLeft: this.state.offsetLeft,
            offsetTop: this.state.offsetTop,
            scrollX: this.state.scrollX,
            scrollY: this.state.scrollY,
            zoom: this.state.zoom,
          },
        )
      ) {
        // if frame not visible, don't render its name
        return null;
      }

      const { x: x1, y: y1 } = sceneCoordsToViewportCoords(
        { sceneX: f.x, sceneY: f.y },
        this.state,
      );

      const FRAME_NAME_EDIT_PADDING = 6;

      const reset = (isShowDefault:any = false,isEnter:any) => {
        if (f.name?.trim() === "") {
          if (isImageElement(f)){
            let customData;
            if (!f.customData?.generationData){
              customData = {generationData:{originName:''}}
            }else{
              customData = {...f.customData};
            }
            customData.generationData.isShowName = isShowDefault;
            mutateElement(f,{name:f.customData?.generationData?.originName,customData});
            //更新库
            // renameCardById(customData.generationData?.id,customData.generationData.originName);
          }else{
            mutateElement(f, { name: isEnter?formatDate(f.updated):'' });
          }
          renameCardById(f.customData?.generationData?.id,f.name).then(res=>{
            postMessage(EVENT_REFRESH_LIBRARY_LIST);
          });
        }

        this.setState({ editingFrame: null });
        postMessage(EVENT_UPDATE_FRAME_LIST);
      };

      let frameNameJSX;

      const frameName = getFrameLikeTitle(
        f,
        isFrameElement(f) ? frameIndex : magicFrameIndex,
      );

      if (f.id === this.state.editingFrame) {
        const frameNameInEdit = frameName;

        frameNameJSX = (
          <input
            autoFocus
            className={'font-size-14'}
            value={frameNameInEdit}
            onChange={(e) => {
              if (f.customData?.generationData){
                const customData = {...f.customData};
                customData.generationData.name = e.target.value;
                mutateElement(f, {
                  name: e.target.value,
                  customData
                });
              }else{
                mutateElement(f, {
                  name: e.target.value,
                });
              }

              //如果是相应的库文件，更新到card
            }}
            onBlur={(e) => {
              reset(false,false);
              let szValue = e.target.value;
              if (!szValue){
                szValue = f.customData?.generationData?.originName || '';
              }

              renameCardById(f.customData?.generationData?.id,szValue).then(res=>{
                postMessage(EVENT_REFRESH_LIBRARY_LIST);
                if (window.refreshSlideRightBreadcrumb){
                  window.refreshSlideRightBreadcrumb({id:f.customData?.generationData?.id,name:szValue});
                }
                if (isEmbedableBookmarkElement(f)){
                  //这里还要改里面的标题..
                  const divTitle = document.getElementById('div-card-link-title-'+f.id);
                  if (divTitle){
                    divTitle.innerText = szValue;
                  }
                }
              });

              if (window.onRefreshElement){
                window.onRefreshElement(this.getSceneElements());
              }


              //修改自己画布上的注记一些名称。
              // const arrayElements = this.getSceneElements();
              // for (let i=0;i<arrayElements.length;i++){
              //   const ele = arrayElements[i];
              //   if (ele.customData?.generationData?.id == f.id){
              //     const customData = ele.customData;
              //     customData.generationData.name = e.target.value;
              //     if (isPdfAnnoElement(ele)){
              //       customData.generationData.pdfData.name = e.target.value;
              //     }else if (isVideoAnnoElement(ele) || isAudioAnnoElement(ele)){
              //       customData.generationData.videoName = e.target.value;
              //     }
              //
              //     mutateElement(ele,{customData})
              //   }
              // }
            }}
            onKeyDown={(event) => {
              // for some inexplicable reason, `onBlur` triggered on ESC
              // does not reset `state.editingFrame` despite being called,
              // and we need to reset it here as well
              if (event.key === KEYS.ESCAPE || event.key === KEYS.ENTER) {
                reset(true,true);
                renameCardById(f.customData?.generationData?.id,f.name).then(res=>{
                  postMessage(EVENT_REFRESH_LIBRARY_LIST);
                });
              }
            }}
            style={{
              background: 'transparent',// this.state.viewBackgroundColor,
              filter: isDarkTheme ? "none" : "none",
              zIndex: 2,
              fontFamily:'Poppins',
              border: "none",
              display: "block",
              padding: `${FRAME_NAME_EDIT_PADDING}px`,
              borderRadius: 4,
              boxShadow: "inset 0 0 0 1px var(--color-primary)",
              fontSize: "14px",
              transform: `translate(-${FRAME_NAME_EDIT_PADDING}px, ${FRAME_NAME_EDIT_PADDING}px) rotate(${f.angle}rad)`,
              color: "var(--text-color)",
              overflow: "hidden",
              fontWeight:'600',
              width:`${this.getMaxInputWidth(f)}px`,
              maxWidth: `${
                f.width*this.state.zoom.value//document.body.clientWidth - x1 - FRAME_NAME_EDIT_PADDING
              }px`,
            }}
            size={frameNameInEdit.length + 1 || 1}
            dir="auto"
            autoComplete="off"
            autoCapitalize="off"
            autoCorrect="off"
          />
        );
      } else {
        frameNameJSX = frameName;
      }

      let display = "block";
      if (
          this.state.displayMode === "preview" &&
          SELECTED_FRAME_INDEX !== index
      ) {
        display = "none";
      }

      let [x10, y10, x2, y2] = getElementAbsoluteCoords(f);
      const cx = ((x10 + x2) / 2 + this.state.scrollX);
      const cy = ((y10 + y2) / 2 + this.state.scrollY);

      // const { x, y } = sceneCoordsToViewportCoords(
      //     { sceneX: f.x, sceneY: f.y },
      //     this.state,
      // );

      return (
        <div
          id={this.getFrameNameDOMId(f)}
          key={f.id}
          style={{
            position: "absolute",
            // Positioning from bottom so that we don't to either
            // calculate text height or adjust using transform (which)
            // messes up input position when editing the frame name.
            // This makes the positioning deterministic and we can calculate
            // the same position when rendering to canvas / svg.
            bottom: `${
              this.state.height +
              FRAME_STYLE.nameOffsetY -
              y1 +
              this.state.offsetTop
            }px`,
            left: `${x1 - this.state.offsetLeft}px`,
            zIndex: 2,
            fontSize: '14px',//FRAME_STYLE.nameFontSize,
            // color: isDarkTheme
            //   ? FRAME_STYLE.nameColorDarkTheme
            //   : FRAME_STYLE.nameColorLightTheme,
            color:'var(--text-color)',
            // fontFamily:'PoppinsMedium',
            fontWeight:'600',
            fontFamily:'Poppins',
            lineHeight: FRAME_STYLE.nameLineHeight,
            width: "max-content",
            maxWidth: `${f.width*this.state.zoom.value}px`,
            overflow: f.id === this.state.editingFrame ? "visible" : "hidden",
            whiteSpace: "nowrap",
            textOverflow: "ellipsis",
            // transformOrigin: 'left top',
            // transform: `translate(${x - this.state.offsetLeft}px, ${
            //     y - this.state.offsetTop
            // }px) scale(${this.state.zoom.value}) rotate(${f.angle}rad)`,
            // transform:`rotate(${f.angle}rad)`,
            cursor:
                this.state.displayMode === "preview"
                    ? CURSOR_TYPE.AUTO
                    : CURSOR_TYPE.MOVE,
            // cursor: CURSOR_TYPE.MOVE,
            pointerEvents: this.state.viewModeEnabled
              ? POINTER_EVENTS.disabled
              : POINTER_EVENTS.enabled,
            display,
            minWidth:'50px',
            minHeight:'18px'
          }}
          onPointerDown={(event) => {
            this.setState(
                {
                  selectedElementIds: makeNextSelectedElementIds(
                      { [f.id]: true },
                      this.state,
                  ),
                },
                () => {
                  this.handleCanvasPointerDown(event);
                },
            );
          }}
          onPointerUp={()=>{
            postMessage(EVENT_FRAME_POINTER_UP);
          }}
          onWheel={(event) => this.handleWheel(event)}
          onContextMenu={(event)=>{
            this.handleCanvasContextMenu(event,true);
          }}
          onDoubleClick={() => {
            this.setState({
              editingFrame: f.id,
            });
          }}
        >
          {frameNameJSX}
        </div>
      );
    });
  };

  public render() {
    const selectedElements = this.scene.getSelectedElements(this.state);
    const { renderTopRightUI, renderCustomStats } = this.props;

    const versionNonce = this.scene.getVersionNonce();
    let { canvasElements, visibleElements } =
      this.renderer.getRenderableElements({
        versionNonce,
        zoom: this.state.zoom,
        offsetLeft: this.state.offsetLeft,
        offsetTop: this.state.offsetTop,
        scrollX: this.state.scrollX,
        scrollY: this.state.scrollY,
        height: this.state.height,
        width: this.state.width,
        editingElement: this.state.editingElement,
        pendingImageElementId: this.state.pendingImageElementId,
      });

    if (this.state.displayMode === "preview") {
      // let canvasFrameElements = canvasElements.filter((item) =>
      //     isFrameElement(item),
      // );

      //这里也要处理一下。不能使用默认的列表......
      const canvasFrameElements = this.state.sortArrayFrames;

      const currentFrameElement = canvasFrameElements[SELECTED_FRAME_INDEX];
      const canvasAttachedToCurrentFrameElements = canvasElements.filter(
          (item) => item.frameId === currentFrameElement?.id,
      );
      visibleElements = [
        currentFrameElement,
        ...canvasAttachedToCurrentFrameElements,
      ];
    }

    this.visibleElements = visibleElements;

    const shouldBlockPointerEvents =
      !(
        this.state.editingElement && isLinearElement(this.state.editingElement)
      ) &&
      (this.state.selectionElement ||
        this.state.draggingElement ||
        this.state.resizingElement ||
        (this.state.activeTool.type === "laser" &&
          // technically we can just test on this once we make it more safe
          this.state.cursorButton === "down") ||
        (this.state.editingElement &&
          !isTextElement(this.state.editingElement)));

    const firstSelectedElement = selectedElements[0];
    const braftControls:any = ['text-color','bold'];

    return (
      <div
        className={clsx("excalidraw excalidraw-container", {
          "excalidraw--view-mode": this.state.viewModeEnabled,
          "excalidraw--mobile": this.device.editor.isMobile,
        })}
        style={{
          ["--ui-pointerEvents" as any]: shouldBlockPointerEvents
            ? POINTER_EVENTS.disabled
            : POINTER_EVENTS.enabled,
          backgroundColor:'transparent',
        }}
        ref={this.excalidrawContainerRef}
        onDrop={this.handleAppOnDrop}
        tabIndex={0}
        onKeyDown={
          this.props.handleKeyboardGlobally ? undefined : this.onKeyDown
        }
      >
        <AppContext.Provider value={this}>
          <AppPropsContext.Provider value={this.props}>
            <ExcalidrawContainerContext.Provider
              value={this.excalidrawContainerValue}
            >
              <DeviceContext.Provider value={this.device}>
                <ExcalidrawSetAppStateContext.Provider value={this.setAppState}>
                  <ExcalidrawAppStateContext.Provider value={this.state}>
                    <ExcalidrawElementsContext.Provider
                        value={this.scene.getNonDeletedElements()}
                    >
                      <ExcalidrawActionManagerContext.Provider
                          value={this.actionManager}
                      >
                        <LayerUI
                            canvas={this.canvas}
                            appState={this.state}
                            files={this.files}
                            setAppState={this.setAppState}
                            actionManager={this.actionManager}
                            elements={this.scene.getNonDeletedElements()}
                            onLockToggle={this.toggleLock}
                            onPenModeToggle={this.togglePenMode}
                            onHandToolToggle={this.onHandToolToggle}
                            langCode={getLanguage().code}
                            renderTopRightUI={renderTopRightUI}
                            renderCustomStats={renderCustomStats}
                            showExitZenModeBtn={
                                typeof this.props?.zenModeEnabled === "undefined" &&
                                this.state.zenModeEnabled
                            }
                            UIOptions={this.props.UIOptions}
                            onExportImage={this.onExportImage}
                            renderWelcomeScreen={
                                !this.state.isLoading &&
                                this.state.showWelcomeScreen &&
                                this.state.activeTool.type === "selection" &&
                                !this.state.zenModeEnabled &&
                                !this.scene.getElementsIncludingDeleted().length
                            }
                            app={this}
                            isCollaborating={this.props.isCollaborating}
                            openAIKey={this.OPENAI_KEY}
                            isOpenAIKeyPersisted={this.OPENAI_KEY_IS_PERSISTED}
                            onOpenAIAPIKeyChange={this.onOpenAIKeyChange}
                            onMagicSettingsConfirm={this.onMagicSettingsConfirm}
                        >
                          {this.props.children}
                        </LayerUI>

                        <div className="excalidraw-textEditorContainer">

                        </div>
                        <div className="excalidraw-contextMenuContainer"/>
                        <div className="excalidraw-eye-dropper-container"/>
                        <LaserToolOverlay manager={this.laserPathManager}/>
                        {selectedElements.length === 1 &&
                            this.state.showHyperlinkPopup && (
                                <Hyperlink
                                    key={firstSelectedElement.id}
                                    element={firstSelectedElement}
                                    setAppState={this.setAppState}
                                    onLinkOpen={this.props.onLinkOpen}
                                    setToast={this.setToast}
                                />
                            )}
                        {this.props.aiEnabled !== false &&
                            selectedElements.length === 1 &&
                            isMagicFrameElement(firstSelectedElement) && (
                                <ElementCanvasButtons
                                    element={firstSelectedElement}
                                >
                                  <ElementCanvasButton
                                      title={t("labels.convertToCode")}
                                      icon={MagicIcon}
                                      checked={false}
                                      onChange={() =>
                                          this.onMagicFrameGenerate(
                                              firstSelectedElement,
                                              "button",
                                          )
                                      }
                                  />
                                </ElementCanvasButtons>
                            )}
                        {selectedElements.length === 1 &&
                            isIframeElement(firstSelectedElement) &&
                            firstSelectedElement.customData?.generationData
                                ?.status === "done000000000000" && (
                                <ElementCanvasButtons
                                    element={firstSelectedElement}
                                >
                                  <ElementCanvasButton
                                      title={t("labels.copySource")}
                                      icon={copyIcon}
                                      checked={false}
                                      onChange={() =>
                                          this.onIframeSrcCopy(firstSelectedElement)
                                      }
                                  />
                                  <ElementCanvasButton
                                      title="Enter fullscreen"
                                      icon={fullscreenIcon}
                                      checked={false}
                                      onChange={() => {
                                        const iframe =
                                            this.getHTMLIFrameElement(
                                                firstSelectedElement,
                                            );
                                        if (iframe) {
                                          try {
                                            iframe.requestFullscreen();
                                            this.setState({
                                              activeEmbeddable: {
                                                element: firstSelectedElement,
                                                state: "active",
                                              },
                                              selectedElementIds: {
                                                [firstSelectedElement.id]: true,
                                              },
                                              draggingElement: null,
                                              selectionElement: null,
                                            });
                                          } catch (err: any) {
                                            console.warn(err);
                                            this.setState({
                                              errorMessage:
                                                  "Couldn't enter fullscreen",
                                            });
                                          }
                                        }
                                      }}
                                  />
                                </ElementCanvasButtons>
                            )}
                        {this.state.toast !== null && (
                            <Toast
                                message={this.state.toast.message}
                                onClose={() => this.setToast(null)}
                                duration={this.state.toast.duration}
                                closable={this.state.toast.closable}
                            />
                        )}
                        {this.state.contextMenu && (
                            <ContextMenu
                                items={this.state.contextMenu.items}
                                top={this.state.contextMenu.top}
                                left={this.state.contextMenu.left}
                                actionManager={this.actionManager}
                                onClose={(callback) => {
                                  this.setState({contextMenu: null}, () => {
                                    this.focusContainer();
                                    callback?.();
                                  });
                                }}
                            />
                        )}
                        <StaticCanvas
                            ref={this.staticCanvasRef}
                            canvas={this.canvas}
                            rc={this.rc}
                            elements={canvasElements}
                            visibleElements={visibleElements}
                            versionNonce={versionNonce}
                            selectionNonce={
                              this.state.selectionElement?.versionNonce
                            }
                            scale={window.devicePixelRatio}
                            appState={this.state}
                            renderConfig={{
                              imageCache: this.imageCache,
                              isExporting: false,
                              renderGrid: true,
                              canvasBackgroundColor:
                              this.state.viewBackgroundColor,
                            }}
                        />
                        <InteractiveCanvas
                            containerRef={this.excalidrawContainerRef}
                            canvas={this.interactiveCanvas}
                            elements={canvasElements}
                            visibleElements={visibleElements}
                            selectedElements={selectedElements}
                            versionNonce={versionNonce}
                            selectionNonce={
                              this.state.selectionElement?.versionNonce
                            }
                            scale={window.devicePixelRatio}
                            appState={this.state}
                            renderInteractiveSceneCallback={
                              this.renderInteractiveSceneCallback
                            }
                            handleCanvasRef={this.handleInteractiveCanvasRef}
                            onContextMenu={this.handleCanvasContextMenu}
                            onPointerMove={this.handleCanvasPointerMove}
                            onPointerUp={this.handleCanvasPointerUp}
                            onPointerCancel={this.removePointer}
                            onTouchMove={this.handleTouchMove}
                            onPointerDown={this.handleCanvasPointerDown}
                            onDoubleClick={this.handleCanvasDoubleClick}
                        />
                        {
                            this.state.imgCropper?.image && (
                                <ImageCropper
                                    imgCropper={this.state.imgCropper}
                                    onClose={(callback: any) => {
                                      this.setState({
                                        imgCropper: null,
                                      });
                                      callback && this.replaceImage(callback);
                                    }}
                                />
                            )}
                        {this.state.imgCropper?.type === "background" &&
                            this.state.imgCropper.simpleCropper && (
                                <ImageSimpleCropper
                                    simpleCropper={
                                      this.state.imgCropper.simpleCropper
                                    }
                                    onClose={(file: File) => {
                                      this.setState({
                                        imgCropper: null,
                                      });
                                      if (!file) {
                                        return;
                                      }

                                      this.insertFrameBackgroundImage(file);
                                    }}
                                />
                            )}
                        {this.state.displayMode === "preview" && (
                            <SlidesBar
                                displayMode={this.state.displayMode}
                                frames={/*this.scene.getNonDeletedFramesLikes()*/this.state.sortArrayFrames}
                                changePreviewFrame={(frame, frameIndex) => {
                                  this.changePreviewFrame({frame, frameIndex});
                                }}
                            />
                        )}
                        {this.state.userToFollow && (
                            <FollowMode
                                width={this.state.width}
                                height={this.state.height}
                                userToFollow={this.state.userToFollow}
                                onDisconnect={this.maybeUnfollowRemoteUser}
                            />
                        )}
                        {this.state.displayMode !== "preview" &&
                            this.renderFrameNames()}
                      </ExcalidrawActionManagerContext.Provider>
                      {this.renderEmbeddables()}
                      {this.renderStickyNotes()}
                        {HAS_ENABLE_TIPTAP_TEXT && this.renderText()}
                      {this.renderGIF()}
                      {this.renderMindMap()}
                      {this.renderProjectLink()}
                      {this.renderMindMapToolbar()}

                      <MindMap
                          visibleElements={visibleElements}
                          elements={canvasElements}
                          appState={this.state}
                          modal={this.state.mindmap.modal}
                      />

                      {this.state.videoNotes.element && (
                          <VideoNotes
                              // @ts-ignore
                              videoNotes={this.state.videoNotes}
                              onVideoNotes={this.onVideoNotes}
                              setToast={this.setToast}
                          />
                      )}

                      {
                        this.state.cardEditMenu?.visibility &&
                          <CardEditBubbleMenu app={this} editor={this.state.cardEditMenu.editor} updateEditCardHtml={this.state.cardEditMenu.updateEditCardHtml} syncNoteData={this.state.cardEditMenu.syncNoteData} element={this.state.cardEditMenu.element} data={this.state.cardEditMenu.data} onHiddenBubbleMenu={this.state.cardEditMenu.onHiddenBubbleMenu} onAction={this.state.cardEditMenu.onAction} from={this.state.cardEditMenu.from}/>
                      }

                      {this.renderHoverModel()}

                      {
                          this.state.isShowEyeDrop &&
                          <EyeDropper onCancel={() => {

                          }} onSelect={() => {

                          }} onChange={() => {

                          }} colorPickerType={() => {

                          }}/>
                      }

                      {/*隐藏的文件选择*/}
                      <div className="file-selector">
                        <input
                            ref={this.fileImageInputRef}
                            type="file"
                            id="file-input-0000-image"
                            accept={getImageExt()}
                            style={{display: 'none'}}
                            multiple={true}
                            onChange={(event) => {
                              imageChoosePromiseResolve(event.target.files);
                            }}
                        />
                      </div>

                    </ExcalidrawElementsContext.Provider>
                  </ExcalidrawAppStateContext.Provider>
                </ExcalidrawSetAppStateContext.Provider>
              </DeviceContext.Provider>
            </ExcalidrawContainerContext.Provider>
          </AppPropsContext.Provider>
        </AppContext.Provider>
      </div>
    );
  }

  public focusContainer: AppClassProperties["focusContainer"] = () => {
    this.excalidrawContainerRef.current?.focus();
  };

  /*执行右键菜单*/
  public executeContextMenuAction(action, isCanvas = false) {
    var arrayMenuItems = this.getContextMenuItems(isCanvas?'canvas':'element')
    for (var i=0;i<arrayMenuItems.length;i++){
      var item = arrayMenuItems[i];
      if (action === item.name){
        this.actionManager.executeAction(item,'contextMenu');
      }
    }
  }

  /**
   * 创建图片缓存....
   * @param szFileId
   * @param szDataUrl
   * @param szMineType
   * @param fullPath
   */
  public createImageToCache = (szFileId:any,szDataUrl:string,szMineType:string,fullPath:any)=>{
    return new Promise(resolve => {
      const img0 =  this.imageCache.get(szFileId);
      if (img0 instanceof Promise){
        img0.then(res=>{
          resolve('ok');
        })
        return;
      }

      const img = new Image();
      if (APP_WEB || isOfflineWeb() || isSvg(szMineType)){
        img.src = szDataUrl;
      }else if (fullPath){
        img.src = getLocalFilePath(fullPath,true);
      }else{
        img.src = 'localfile://'+szFileId + '.'+ extFromMineType(szMineType);
      }
      // img.src = (APP_WEB || isOfflineWeb() || isSvg(szMineType))?szDataUrl:'localfile://'+szFileId + '.'+ extFromMineType(szMineType);//szDataUrl;
      img.onload = ()=>{
        this.imageCache.set(szFileId, {
          ...this.imageCache,
          image: img,
        });
        console.log('createImageToCache')
        resolve('ok');
      }
      img.onerror = (err)=>{
        resolve('ok');
      }
    })
  }

  public clearImageCache = ()=>{
    this.imageCache.clear();
  }

  public getSceneElementsIncludingDeleted = () => {
    return this.scene.getElementsIncludingDeleted();
  };



  /**
   * 更新连接线....
   * @param element
   */
  public updateBoundElements = (element)=>{
    //这里处理连线问题....
    if (element.boundElements){
      updateBoundElements(element);
    }
  }


  public getSceneElementsOrigin = () => {
    return this.scene.getNonDeletedElements();
  }

  public getSceneElements = () => {
    // return this.scene.getNonDeletedElements();
    return this.scene.getNonDeletedElements().map((element) => {
      if (isMindmapElement(element)) {
        const mindMap = mindMapCache.get(element.id);
        return {
          ...element,
          data: cloneDeep(mindMap?.getData(false)),
        };
      }
      return element;
    });
  };

  /**
   * 获取选中的元素
   */
  public getSelectedElements = ()=>{
    var arraySelectedElements:any = [];
    this.getSceneElements().forEach(p=>{
      if(this.state.selectedElementIds[p.id]){
        arraySelectedElements.push(p);
      }
    });
    return arraySelectedElements;
  }

  public removeElementById = (szId:string)=>{
    const arrayElements = this.getSceneElements();
    for (let i=0;i<arrayElements.length;i++){
      const element = arrayElements[i];
      if (element.id == szId || element.data?.data?.uid == szId || element.customData?.generationData?.id == szId || element.customData?.generationData?.pdfData?.id == szId || element.customData?.generationData?.videoData?.id == szId){
        arrayElements.splice(i,1);
        break;
      }
    }
    this.updateScene({
      elements:arrayElements
    })
  }

  public onInsertElements = (elements: readonly ExcalidrawElement[]) => {
    this.addElementsFromPasteOrLibrary({
      elements,
      position: "center",
      files: null,
    });
  };

  /**
   * 显示图片剪切
   * @param element
   */
  public onShowCropImage = (element:ExcalidrawImageElement)=>{
    let fileId = element.fullImageFileId;
    if (this.imageCache.get(fileId) == undefined){
      fileId = element.fileId;
    }
    this.setState({
      imgCropper: {
        element: element,
        image: this.imageCache.get(fileId)?.image as HTMLImageElement,
      },
    });
  }

  public onExportImage = async (
    type: keyof typeof EXPORT_IMAGE_TYPES,
    elements: ExportedElements,
    opts: { exportingFrame: ExcalidrawFrameLikeElement | null },
  ) => {
    trackEvent("export", type, "ui");
    const fileHandle = await exportCanvas(
      type,
      elements,
      this.state,
      this.files,
      {
        exportBackground: this.state.exportBackground,
        name: this.state.name,
        viewBackgroundColor: this.state.viewBackgroundColor,
        exportingFrame: opts.exportingFrame,
      },
    )
      .catch(muteFSAbortError)
      .catch((error) => {
        console.error(error);
        this.setState({ errorMessage: error.message });
      });

    if (
      this.state.exportEmbedScene &&
      fileHandle &&
      isImageFileHandle(fileHandle)
    ) {
      this.setState({ fileHandle });
    }
  };

  private magicGenerations = new Map<
    ExcalidrawIframeElement["id"],
    MagicCacheData
  >();

  private updateMagicGeneration = ({
    frameElement,
    data,
  }: {
    frameElement: ExcalidrawIframeElement;
    data: MagicCacheData;
  }) => {
    if (data.status === "pending") {
      // We don't wanna persist pending state to storage. It should be in-app
      // state only.
      // Thus reset so that we prefer local cache (if there was some
      // generationData set previously)
      mutateElement(
        frameElement,
        { customData: { generationData: undefined } },
        false,
      );
    } else {
      mutateElement(
        frameElement,
        { customData: { generationData: data } },
        false,
      );
    }
    this.magicGenerations.set(frameElement.id, data);
    this.onSceneUpdated();
  };


  private getTextFromElements(elements: readonly ExcalidrawElement[]) {
    const text = elements
      .reduce((acc: string[], element) => {
        if (isTextElement(element)) {
          acc.push(element.text);
        }
        return acc;
      }, [])
      .join("\n\n");
    return text;
  }

  private async onMagicFrameGenerate(
    magicFrame: ExcalidrawMagicFrameElement,
    source: "button" | "upstream",
  ) {
    if (!this.OPENAI_KEY) {
      this.setState({
        openDialog: {
          name: "settings",
          tab: "diagram-to-code",
          source: "generation",
        },
      });
      trackEvent("ai", "generate (missing key)", "d2c");
      return;
    }

    const magicFrameChildren = elementsOverlappingBBox({
      elements: this.scene.getNonDeletedElements(),
      bounds: magicFrame,
      type: "overlap",
    }).filter((el) => !isMagicFrameElement(el));

    if (!magicFrameChildren.length) {
      if (source === "button") {
        this.setState({ errorMessage: "Cannot generate from an empty frame" });
        trackEvent("ai", "generate (no-children)", "d2c");
      } else {
        this.setActiveTool({ type: "magicframe" });
      }
      return;
    }

    const frameElement = this.insertIframeElement({
      sceneX: magicFrame.x + magicFrame.width + 30,
      sceneY: magicFrame.y,
      width: magicFrame.width,
      height: magicFrame.height,
    });

    if (!frameElement) {
      return;
    }

    this.updateMagicGeneration({
      frameElement,
      data: { status: "pending" },
    });

    this.setState({
      selectedElementIds: { [frameElement.id]: true },
    });

    const blob = await exportToBlob({
      elements: this.scene.getNonDeletedElements(),
      appState: {
        ...this.state,
        exportBackground: true,
        viewBackgroundColor: this.state.viewBackgroundColor,
      },
      exportingFrame: magicFrame,
      files: this.files,
    });

    const dataURL = await getDataURL(blob);

    const textFromFrameChildren = this.getTextFromElements(magicFrameChildren);

    trackEvent("ai", "generate (start)", "d2c");

    const result = await diagramToHTML({
      image: dataURL,
      apiKey: this.OPENAI_KEY,
      text: textFromFrameChildren,
      theme: this.state.theme,
    });

    if (!result.ok) {
      trackEvent("ai", "generate (failed)", "d2c");
      console.error(result.error);
      this.updateMagicGeneration({
        frameElement,
        data: {
          status: "error",
          code: "ERR_OAI",
          message: result.error?.message || "Unknown error during generation",
        },
      });
      return;
    }
    trackEvent("ai", "generate (success)", "d2c");

    if (result.choices[0].message.content == null) {
      this.updateMagicGeneration({
        frameElement,
        data: {
          status: "error",
          code: "ERR_OAI",
          message: "Nothing genereated :(",
        },
      });
      return;
    }

    const message = result.choices[0].message.content;

    const html = message.slice(
      message.indexOf("<!DOCTYPE html>"),
      message.indexOf("</html>") + "</html>".length,
    );

    this.updateMagicGeneration({
      frameElement,
      data: { status: "done", html },
    });
  }

  private onIframeSrcCopy(element: ExcalidrawIframeElement) {
    if (element.customData?.generationData?.status === "done") {
      copyTextToSystemClipboard(element.customData.generationData.html);
      this.setToast({
        message: "copied to clipboard",
        closable: false,
        duration: 1500,
      });
    }
  }

  private OPENAI_KEY: string | null = EditorLocalStorage.get(
    EDITOR_LS_KEYS.OAI_API_KEY,
  );
  private OPENAI_KEY_IS_PERSISTED: boolean =
    EditorLocalStorage.has(EDITOR_LS_KEYS.OAI_API_KEY) || false;

  private onOpenAIKeyChange = (
    openAIKey: string | null,
    shouldPersist: boolean,
  ) => {
    this.OPENAI_KEY = openAIKey || null;
    if (shouldPersist) {
      const didPersist = EditorLocalStorage.set(
        EDITOR_LS_KEYS.OAI_API_KEY,
        openAIKey,
      );
      this.OPENAI_KEY_IS_PERSISTED = didPersist;
    } else {
      this.OPENAI_KEY_IS_PERSISTED = false;
    }
  };

  private onMagicSettingsConfirm = (
    apiKey: string,
    shouldPersist: boolean,
    source: "tool" | "generation" | "settings",
  ) => {
    this.OPENAI_KEY = apiKey || null;
    this.onOpenAIKeyChange(this.OPENAI_KEY, shouldPersist);

    if (source === "settings") {
      return;
    }

    const selectedElements = this.scene.getSelectedElements({
      selectedElementIds: this.state.selectedElementIds,
    });

    if (apiKey) {
      if (selectedElements.length) {
        this.onMagicframeToolSelect();
      } else {
        this.setActiveTool({ type: "magicframe" });
      }
    } else if (!isMagicFrameElement(selectedElements[0])) {
      // even if user didn't end up setting api key, let's pick the tool
      // so they can draw up a frame and move forward
      this.setActiveTool({ type: "magicframe" });
    }
  };

  public onMagicframeToolSelect = () => {
    if (!this.OPENAI_KEY) {
      this.setState({
        openDialog: {
          name: "settings",
          tab: "diagram-to-code",
          source: "tool",
        },
      });
      trackEvent("ai", "tool-select (missing key)", "d2c");
      return;
    }

    const selectedElements = this.scene.getSelectedElements({
      selectedElementIds: this.state.selectedElementIds,
    });

    if (selectedElements.length === 0) {
      this.setActiveTool({ type: TOOL_TYPE.magicframe });
      trackEvent("ai", "tool-select (empty-selection)", "d2c");
    } else {
      const selectedMagicFrame: ExcalidrawMagicFrameElement | false =
        selectedElements.length === 1 &&
        isMagicFrameElement(selectedElements[0]) &&
        selectedElements[0];

      // case: user selected elements containing frame-like(s) or are frame
      // members, we don't want to wrap into another magicframe
      // (unless the only selected element is a magic frame which we reuse)
      if (
        !selectedMagicFrame &&
        selectedElements.some((el) => isFrameLikeElement(el) || el.frameId)
      ) {
        this.setActiveTool({ type: TOOL_TYPE.magicframe });
        return;
      }

      trackEvent("ai", "tool-select (existing selection)", "d2c");

      let frame: ExcalidrawMagicFrameElement;
      if (selectedMagicFrame) {
        // a single magicframe already selected -> use it
        frame = selectedMagicFrame;
      } else {
        // selected elements aren't wrapped in magic frame yet -> wrap now

        const [minX, minY, maxX, maxY] = getCommonBounds(selectedElements);
        const padding = 50;

        frame = newMagicFrameElement({
          ...FRAME_STYLE,
          x: minX - padding,
          y: minY - padding,
          width: maxX - minX + padding * 2,
          height: maxY - minY + padding * 2,
          opacity: 100,
          locked: false,
        });

        this.scene.addNewElement(frame);

        for (const child of selectedElements) {
          mutateElement(child, { frameId: frame.id });
        }

        this.setState({
          selectedElementIds: { [frame.id]: true },
        });
      }

      this.onMagicFrameGenerate(frame, "upstream");
    }
  };

  private openEyeDropper = ({ type }: { type: "stroke" | "background" }) => {
    jotaiStore.set(activeEyeDropperAtom, {
      swapPreviewOnAlt: true,
      colorPickerType:
        type === "stroke" ? "elementStroke" : "elementBackground",
      onSelect: (color, event) => {
        const shouldUpdateStrokeColor =
          (type === "background" && event.altKey) ||
          (type === "stroke" && !event.altKey);
        const selectedElements = this.scene.getSelectedElements(this.state);
        if (
          !selectedElements.length ||
          this.state.activeTool.type !== "selection"
        ) {
          if (shouldUpdateStrokeColor) {
            this.syncActionResult({
              appState: { ...this.state, currentItemStrokeColor: color },
              commitToHistory: true,
            });
          } else {
            this.syncActionResult({
              appState: { ...this.state, currentItemBackgroundColor: color },
              commitToHistory: true,
            });
          }
        } else {
          this.updateScene({
            elements: this.scene.getElementsIncludingDeleted().map((el) => {
              if (this.state.selectedElementIds[el.id]) {
                return newElementWith(el, {
                  [shouldUpdateStrokeColor ? "strokeColor" : "backgroundColor"]:
                    color,
                });
              }
              return el;
            }),
          });
        }
      },
      keepOpenOnAlt: false,
    });
  };

  private syncActionResult = withBatchedUpdates(
    (actionResult: ActionResult) => {
      if (this.unmounted || actionResult === false) {
        return;
      }

      let editingElement: AppState["editingElement"] | null = null;
      if (actionResult.elements) {
        actionResult.elements.forEach((element) => {
          if (
            this.state.editingElement?.id === element.id &&
            this.state.editingElement !== element &&
            isNonDeletedElement(element)
          ) {
            editingElement = element;
          }

          // if (/*actionResult.isImport &&*/ isMindmapElement(element)) {
          //   setTimeout(() => {
          //     const mindMap = createMindMap(
          //         element,
          //         this.createMindMapCallBack,
          //         this.props.onLinkOpen,
          //         false,
          //         this.state,
          //     );
          //
          //     mindMap.setData(element.data);
          //   }, 1500);
          // }

          if (actionResult.isImport){
            if (isEmbeddableElement(element)) {
              setTimeout(() => {
                const node = document.getElementById(`${element.id}-card`)!;
                cardLink([node],element);
              }, 500);
            }
          }

        });
        this.scene.replaceAllElements(actionResult.elements);
        if (actionResult.commitToHistory) {
          this.history.resumeRecording();
        }
        setTimeout(() => {
          // @ts-ignore
          if (actionResult.arrange) {
            const elements =
                actionResult.elements?.filter((element) => {
                  return !!this.state.selectedElementIds[element.id];
                }) || [];

            // Only scroll to center if there are selected elements
            if (elements.length > 0) {
              this.setAppState(
                  (appState) => {
                    return {
                      ...calculateScrollCenter(elements, appState, false),
                      // @ts-ignore
                      ...actionResult.arrange,
                    };
                  },
                  () => {
                    elements.forEach((element) => {
                      if (
                          element.startConnectionDirection ||
                          element.endConnectionDirection
                      ) {
                        updateBoundElements(element, {
                          simultaneouslyUpdated: [element],
                        });
                      }
                    });
                  },
              );
            } else {
              // No selected elements, just update bound elements
              const allElements = actionResult.elements || [];
              allElements.forEach((element) => {
                if (
                    element.startConnectionDirection ||
                    element.endConnectionDirection
                ) {
                  updateBoundElements(element, {
                    simultaneouslyUpdated: [element],
                  });
                }
              });
            }
          }
          // @ts-ignore
          if (actionResult.align) {
            const elements =
                actionResult.elements?.filter((element) => {
                  return !!this.state.selectedElementIds[element.id];
                }) || [];
            elements.forEach((element) => {
              if (
                  element.startConnectionDirection ||
                  element.endConnectionDirection
              ) {
                updateBoundElements(element, {
                  simultaneouslyUpdated: [element],
                });
              }
            });
          }
        }, 100);
      }

      if (actionResult.files) {
        this.files = actionResult.replaceFiles
          ? actionResult.files
          : { ...this.files, ...actionResult.files };
        this.addNewImagesToImageCache();
      }

      if (actionResult.appState || editingElement || this.state.contextMenu) {
        if (actionResult.commitToHistory) {
          this.history.resumeRecording();
        }

        let viewModeEnabled = actionResult?.appState?.viewModeEnabled || false;
        let zenModeEnabled = actionResult?.appState?.zenModeEnabled || false;
        let gridSize = actionResult?.appState?.gridSize || null;
        const theme =
          actionResult?.appState?.theme || this.props.theme || THEME.LIGHT;
        let name = actionResult?.appState?.name ?? this.state.name;
        const errorMessage =
          actionResult?.appState?.errorMessage ?? this.state.errorMessage;
        if (typeof this.props.viewModeEnabled !== "undefined") {
          viewModeEnabled = this.props.viewModeEnabled;
        }

        if (typeof this.props.zenModeEnabled !== "undefined") {
          zenModeEnabled = this.props.zenModeEnabled;
        }

        if (typeof this.props.gridModeEnabled !== "undefined") {
          gridSize = this.props.gridModeEnabled ? GRID_SIZE : null;
        }

        if (typeof this.props.name !== "undefined") {
          name = this.props.name;
        }

        editingElement =
          editingElement || actionResult.appState?.editingElement || null;

        if (editingElement?.isDeleted) {
          editingElement = null;
        }

        // 修改背景图片
        if (
            actionResult.actionType &&
            ["changeBackgroundImage", "removeBackgroundImage"].includes(
                actionResult.actionType,
            ) &&
            actionResult?.appState?.selectedElementIds
        ) {
          const frameElement = this.scene.getSelectedElements({
            selectedElementIds: actionResult.appState.selectedElementIds,
          })[0];
          // 如果之前有背景图片，需要删除之前得背景图片
          const isBackgroundImageElement = this.scene
              .getNonDeletedElements()
              .find(
                  (item) =>
                      item.type === "image" &&
                      item.isFrameBackgroundImage &&
                      item.frameId === frameElement.id,
              );

          if (actionResult.actionType === "changeBackgroundImage") {
            // 插入新的背景图片
            this.onImageAction({
              insertOnCanvasDirectly: true,
              frameElement,
            });
          } // 如果是删除背景图
          else if (isBackgroundImageElement) {
            const elements =
                this.scene.getElementsIncludingDeleted().map((item) => {
                  if (
                      item.type === "image" &&
                      item.id === isBackgroundImageElement.id
                  ) {
                    return newElementWith(item, {
                      isDeleted: true,
                      isFrameBackgroundImage: false,
                      locked: false,
                    });
                  }
                  return item;
                }) || [];

            this.scene.replaceAllElements(elements);
          }
          this.setState({
            selectedElementIds: { [frameElement.id]: true },
          });
        }

        //修改文本的样式
        if (actionResult.actionType && actionResult.appState) {
          if (isTextElement(editingElement)) {
            const richText = editingElement.richText || [];
            const {actionType, appState} = actionResult;
            textSelectRange &&
            updateRichText({
              textSelectRange,
              richText,
              actionType,
              appState,
            });
          } else {
            try {
              const selectedTextElements = this.scene
                  .getSelectedElements({
                    selectedElementIds: this.state.selectedElementIds,
                    includeBoundTextElement: true,
                    includeElementsInFrames: true,
                  })
                  .filter((element) =>
                      isTextElement(element),
                  ) as ExcalidrawTextElement[];

              const {appState} = actionResult;
              const actionType = actionResult.actionType as
                  | "color"
                  | "bold"
                  | "italic"
                  | "underline"
                  | "strikeout";
              const areAllSameActionType = selectedTextElements.every(
                  (element) => {
                    const areOneAllSameActionType = element.richText?.every(
                        (item) =>
                            Object.prototype.hasOwnProperty.call(item, actionType)
                                ? item[actionType] === element.richText?.[0][actionType]
                                : true,
                    );

                    if (!areOneAllSameActionType) {
                      return false;
                    }
                    return (
                        selectedTextElements[0].richText?.[0][actionType] ===
                        selectedTextElements[selectedTextElements.length - 1]
                            .richText?.[0][actionType]
                    );
                  },
              );

              selectedTextElements.forEach((element) => {
                element.richText?.forEach((curText) => {
                  if (actionType === "color") {
                    curText.color = appState?.currentItemFontColor;
                  } else if (
                      ["bold", "italic", "underline", "strikeout"].includes(
                          actionType,
                      )
                  ) {
                    // 如果选中的文本样式不一致，则直接设置为true，否则取反
                    curText[actionType] =
                        !areAllSameActionType || !curText[actionType];
                  }
                });
              });
            } catch (error) {
            }
          }
        }

        this.setState(
          (state) => {
            // using Object.assign instead of spread to fool TS 4.2.2+ into
            // regarding the resulting type as not containing undefined
            // (which the following expression will never contain)
              // Preserve current selection for text color changes. We rely on
              // selection to drive panel state, and some actions return appState
              // without selectedElementIds (or with it empty), which would clear
              // multi-selection and make subsequent updates no-op.
              const preservedSelectedElementIds =
                  actionResult.actionType === "color" &&
                  (!actionResult.appState?.selectedElementIds ||
                      Object.keys(actionResult.appState.selectedElementIds).length ===
                      0)
                      ? state.selectedElementIds
                      : undefined;
            return Object.assign(actionResult.appState || {}, {
              // NOTE this will prevent opening context menu using an action
              // or programmatically from the host, so it will need to be
              // rewritten later
              contextMenu: null,
              editingElement,
              viewModeEnabled,
              zenModeEnabled,
              gridSize,
              theme,
              name,
              errorMessage,
                ...(preservedSelectedElementIds && {
                    selectedElementIds: preservedSelectedElementIds,
                }),
            });
          },
          () => {
            if (actionResult.syncHistory) {
              //todo  还原mindmap
              setTimeout(()=>{
                // this.loadMindmap();
                this.loadEmbedLink();
              },600)
              this.history.setCurrentState(
                this.state,
                this.scene.getElementsIncludingDeleted(),
              );
            }
          },
        );
      }
    },
  );

  // Lifecycle

  private onBlur = withBatchedUpdates(() => {
    isHoldingSpace = false;
    this.setState({ isBindingEnabled: true });
  });

  private onUnload = () => {
    this.onBlur();
  };

  private disableEvent: EventListener = (event) => {
    event.preventDefault();
  };

  private resetHistory = () => {
    this.history?.clear();
  };

  /**
   * 每个画布使用一个历史记录
   * @param history
   */
  public resetNewCanvasHistory = (history:any,isNew:boolean)=>{
    // if(isNew){
    //   this.actionManager.registerAction(createUndoAction(history));
    //   this.actionManager.registerAction(createRedoAction(history));
    // }
    // this.history = history;
  }

  public resetHistory0 = ()=>{
    this.history?.clear();
  }

  public getHistory = ()=>{
    return this.history;
  }

  /**
   * Resets scene & history.
   * ! Do not use to clear scene user action !
   */
  private resetScene = withBatchedUpdates(
    (opts?: { resetLoadingState: boolean }) => {
      this.scene.replaceAllElements([]);
      this.setState((state) => ({
        ...getDefaultAppState(),
        isLoading: opts?.resetLoadingState ? false : state.isLoading,
        theme: this.state.theme,
      }));
      this.resetHistory();
    },
  );

  private initializeScene = async () => {
    if ("launchQueue" in window && "LaunchParams" in window) {
      (window as any).launchQueue.setConsumer(
        async (launchParams: { files: any[] }) => {
          if (!launchParams.files.length) {
            return;
          }
          const fileHandle = launchParams.files[0];
          const blob: Blob = await fileHandle.getFile();
          this.loadFileToCanvas(
            new File([blob], blob.name || "", { type: blob.type }),
            fileHandle,
          );
        },
      );
    }

    if (this.props.theme) {
      this.setState({ theme: this.props.theme });
    }
    if (!this.state.isLoading) {
      this.setState({ isLoading: true });
    }
    let initialData = null;
    try {
      initialData = (await this.props.initialData) || null;
      if (initialData?.libraryItems) {
        this.library
          .updateLibrary({
            libraryItems: initialData.libraryItems,
            merge: true,
          })
          .catch((error) => {
            console.error(error);
          });
      }
    } catch (error: any) {
      console.error(error);
      initialData = {
        appState: {
          errorMessage:
            error.message ||
            "Encountered an error during importing or restoring scene data",
        },
      };
    }
    const scene = restore(initialData, null, null, { repairBindings: true });
    scene.appState = {
      ...scene.appState,
      theme: this.props.theme || scene.appState.theme,
      // we're falling back to current (pre-init) state when deciding
      // whether to open the library, to handle a case where we
      // update the state outside of initialData (e.g. when loading the app
      // with a library install link, which should auto-open the library)
      openSidebar: scene.appState?.openSidebar || this.state.openSidebar,
      activeTool:
        scene.appState.activeTool.type === "image"
          ? { ...scene.appState.activeTool, type: "selection" }
          : scene.appState.activeTool,
      isLoading: false,
      toast: this.state.toast,
    };
    if (initialData?.scrollToContent) {
      scene.appState = {
        ...scene.appState,
        ...calculateScrollCenter(scene.elements, {
          ...scene.appState,
          width: this.state.width,
          height: this.state.height,
          offsetTop: this.state.offsetTop,
          offsetLeft: this.state.offsetLeft,
        }),
      };
    }
    // FontFaceSet loadingdone event we listen on may not always fire
    // (looking at you Safari), so on init we manually load fonts for current
    // text elements on canvas, and rerender them once done. This also
    // seems faster even in browsers that do fire the loadingdone event.
    this.fonts.loadFontsForElements(scene.elements);

    this.resetHistory();
    this.syncActionResult({
      ...scene,
      commitToHistory: true,
    });
  };

  private isMobileBreakpoint = (width: number, height: number) => {
    return (
      width < MQ_MAX_WIDTH_PORTRAIT ||
      (height < MQ_MAX_HEIGHT_LANDSCAPE && width < MQ_MAX_WIDTH_LANDSCAPE)
    );
  };

  private refreshViewportBreakpoints = () => {
    const container = this.excalidrawContainerRef.current;
    if (!container) {
      return;
    }

    const { clientWidth: viewportWidth, clientHeight: viewportHeight } =
      document.body;

    const prevViewportState = this.device.viewport;

    const nextViewportState = updateObject(prevViewportState, {
      isLandscape: viewportWidth > viewportHeight,
      isMobile: this.isMobileBreakpoint(viewportWidth, viewportHeight),
    });

    if (prevViewportState !== nextViewportState) {
      this.device = { ...this.device, viewport: nextViewportState };
      return true;
    }
    return false;
  };

  private refreshEditorBreakpoints = () => {
    const container = this.excalidrawContainerRef.current;
    if (!container) {
      return;
    }

    const { width: editorWidth, height: editorHeight } =
      container.getBoundingClientRect();

    const sidebarBreakpoint =
      this.props.UIOptions.dockedSidebarBreakpoint != null
        ? this.props.UIOptions.dockedSidebarBreakpoint
        : MQ_RIGHT_SIDEBAR_MIN_WIDTH;

    const prevEditorState = this.device.editor;

    const nextEditorState = updateObject(prevEditorState, {
      isMobile: this.isMobileBreakpoint(editorWidth, editorHeight),
      canFitSidebar: editorWidth > sidebarBreakpoint,
    });

    if (prevEditorState !== nextEditorState) {
      this.device = { ...this.device, editor: nextEditorState };
      return true;
    }
    return false;
  };

  /**
   * 视频左右键 增加视频时间
   * @param event
   */
  private onWindowKeyDown = (event)=>{
    const code = event.keyCode;
    if (code == 37 || code == 39){
      const selectedElements = this.getSelectedElements();
      if (selectedElements.length > 0 && isFileVideoElement(selectedElements[0])){
        const video = document.getElementById('video_'+selectedElements[0].id);
        if (video){
          video.currentTime = video.currentTime + 5;
        }
        event.preventDefault;
        event.stopImmediatePropagation();
      }
    }
  }

  // private onDocumentKeyDown = (event)=>{
  //   const code = event.code;
  //   if (code == 37 || code == 39){
  //       event.preventDefault;
  //       event.stopImmediatePropagation();
  //     }
  // }

  public async componentDidMount() {
    this.unmounted = false;
    this.excalidrawContainerValue.container =
      this.excalidrawContainerRef.current;

    // document.onkeydown = this.onDocumentKeyDown;
    //
    window.addEventListener('keydown',this.onWindowKeyDown,{capture:true});

    // if (import.meta.env.MODE === ENV.TEST || import.meta.env.DEV) {
    //   const setState = this.setState.bind(this);
    //   Object.defineProperties(window.h, {
    //     state: {
    //       configurable: true,
    //       get: () => {
    //         return this.state;
    //       },
    //     },
    //     setState: {
    //       configurable: true,
    //       value: (...args: Parameters<typeof setState>) => {
    //         return this.setState(...args);
    //       },
    //     },
    //     app: {
    //       configurable: true,
    //       value: this,
    //     },
    //     history: {
    //       configurable: true,
    //       value: this.history,
    //     },
    //   });
    // }

    this.scene.addCallback(this.onSceneUpdated);
    this.scene.setOnElementChangedCallback(this.props.onSceneElementUpdate)
    this.addEventListeners();

    if (this.props.autoFocus && this.excalidrawContainerRef.current) {
      this.focusContainer();
    }

    if (
      // bounding rects don't work in tests so updating
      // the state on init would result in making the test enviro run
      // in mobile breakpoint (0 width/height), making everything fail
      !isTestEnv()
    ) {
      this.refreshViewportBreakpoints();
      this.refreshEditorBreakpoints();
    }

    if (supportsResizeObserver && this.excalidrawContainerRef.current) {
      this.resizeObserver = new ResizeObserver(() => {
        this.refreshEditorBreakpoints();
        this.updateDOMRect();
      });
      this.resizeObserver?.observe(this.excalidrawContainerRef.current);
    }

    const searchParams = new URLSearchParams(window.location.search.slice(1));

    if (searchParams.has("web-share-target")) {
      // Obtain a file that was shared via the Web Share Target API.
      this.restoreFileFromShare();
    } else {
      this.updateDOMRect(this.initializeScene);
    }

    // note that this check seems to always pass in localhost
    if (isBrave() && !isMeasureTextSupported()) {
      this.setState({
        errorMessage: <BraveMeasureTextError />,
      });
    }

    recvMessage(EVENT_COPY_MINDMAP_FROM_ELEMENT,(msg,data)=>{
      if (data?.data){
        setTimeout(()=>{
          for (let i=0;i<data.data.length;i++){
            const element = data.data[i];
            if (isMindmapElement(element)){
              const mindMap = createMindMap(
                  element,
                  this.createMindMapCallBack,
                  this.props.onLinkOpen,
                  false,
                  this.state
              );

              mindMap.setData(element.data);
            }
          }
        },50);
      }
    });

    //插入card的缩略图后，mindmap的高度不会自动调整.....
    recvMessage(EVENT_FORCE_UPDATE_MINDMAP,(msg,data)=>{
      let arrayElements = this.getSceneElements();
      var arrayNewEle = [...arrayElements];
      for (let i=0;i<arrayNewEle.length;i++){
        let ele = arrayNewEle[i];
        if (ele.id == data.data.element.id){
          var newEle = {
            ...ele,
            width: data.data.width,
            height: data.data.height,
            data:data.data.data,
            config:data.data.element?.config,
            versionNonce : randomInteger(),
            updated : Date.now()
          }
          arrayNewEle.splice(i,1,newEle);
          this.updateScene({
            elements:arrayNewEle
          })
          // mutateElement(ele,{width:data.data.width,height:data.data.height});
          break;
        }
      }

    })

    //打开视频弹窗
    recvMessage(EVENT_OPEN_VIDEO_IN_POPUP,(msg,data)=>{
      // this.setState({
      //   videoNotes: {
      //     visibility: true,
      //     type: data.data.isFullScreen?"fullScreen":"model",
      //     element: data.data.element,
      //   },
      // })

      window.showVideoNotesWithElement(data.data.element,data.data.isFullScreen);
    })

    recvMessage(EVENT_UPDATE_MEDIA_ELEMENT_SRC,(msg,data)=>{
      const arrayNewElements = this.getSceneElements();
      const element = arrayNewElements.find(p=>p.customData?.generationData?.id == data.data.id);
      if (element){
        const nIndex = arrayNewElements.findIndex(p=>p.customData?.generationData?.id == data.data.id);
        const newElement:any = {...element};
        newElement.id = generateUUID2();
        newElement.customData.generationData.fileName = data.data.fileName;
        arrayNewElements.splice(nIndex,1,newElement);

        this.updateScene({
          elements:arrayNewElements
        })
      }
    })

    window.getAppFiles = ()=>{
      return this.files;
    }

    /**
     * 添加进图片对象
     * @param file
     */
    window.addImageFile = (file)=>{
      this.addFiles([file])
    }

    window.getAllElement = ()=>{
      return this.getSceneElements();
    }

    window.updateElement = (el,updates)=>{
      const ele = this.getSceneElements().find(p=>p.id == el.id);
      if (ele){
        mutateElement(ele,updates);
      }
    }


    window.updateReplaceElement = (el,updates)=>{
      const arrayNewElements = this.getSceneElements();
      const ele = arrayNewElements.find(p=>p.id == el.id);
      if (ele){
        const newEle = {...ele,...updates};
        const nIndex = arrayNewElements.findIndex(p=>p.id == el.id);
        arrayNewElements.splice(nIndex,1,newEle);
        this.updateScene({
          elements:arrayNewElements
        })
      }
    }

    window.clearSelected = ()=>{
      this.setState({
        selectedElementIds:[]
      })
    }

    //强制更新思维导图强制更新思维导图强制更新思维导图强制更新思维导图强制更新思维导图强制更新思维导图强制更新思维导图强制更新思维导图强制更新思维导图
    window.forceUpdateMindmap = (data)=>{
      let arrayElements = this.getSceneElements();
      var arrayNewEle = [...arrayElements];
      for (let i=0;i<arrayNewEle.length;i++){
        let ele = arrayNewEle[i];
        if (ele.id == data.data.element.id){
          var newEle = {
            ...ele,
            width: data.data.width,
            height: data.data.height,
            data:data.data.data,
            config:data.data.element?.config,
            versionNonce : randomInteger(),
            updated : Date.now()
          }
          arrayNewEle.splice(i,1,newEle);
          this.updateScene({
            elements:arrayNewEle
          })
          // mutateElement(ele,{width:data.data.width,height:data.data.height});
          break;
        }
      }
    }

    /**
     * 全局的刷新。。。
     */
    window.onRefreshContentElement = ()=>{
      if (window.onRefreshElement){
        window.onRefreshElement(this.getSceneElements());
      }
    }

    //强制更新思维导图强制更新思维导图强制更新思维导图强制更新思维导图强制更新思维导图强制更新思维导图强制更新思维导图强制更新思维导图强制更新思维导图
    window.forceUpdateMindmap = (data)=>{
      let arrayElements = this.getSceneElements();
      var arrayNewEle = [...arrayElements];
      for (let i=0;i<arrayNewEle.length;i++){
        let ele = arrayNewEle[i];
        if (ele.id == data.data.element.id){
          // var newEle = {
          //   ...ele,
          //   width: data.data.width,
          //   height: data.data.height,
          //   data:data.data.data,
          //   config:data.data.element?.config,
          //   versionNonce : randomInteger(),
          //   updated : Date.now()
          // }
          // arrayNewEle.splice(i,1,newEle);
          // this.updateScene({
          //   elements:arrayNewEle
          // })
          mutateElement(ele,{width:data.data.width,height:data.data.height});
          break;
        }
      }
    }

    //删除又加上。。。真TM无语了。
    window.forceUpdatMindmapData = (id,data)=>{
      let arrayElements = this.getSceneElements();
      var arrayNewEle = [...arrayElements];
      for (let i=0;i<arrayNewEle.length;i++){
        let ele = arrayNewEle[i];
        if (ele.id == id){
          var newEle = {
            ...ele,
            data:data,
          }
          arrayNewEle.splice(i,1,newEle);
          this.updateScene({
            elements:arrayNewEle
          })
          break;
        }
      }
    }

    /**
     * 刷新元素上的标题----各种地方改--画布上要即时刷新...
     * @param szLibraryId
     */
    window.refreshCanvasElementName = (szLibraryId)=>{
      let arrayElements = this.getSceneElements();
      getCardById(szLibraryId,false).then(res=>{
        if (res){
          for (let i=0;i<arrayElements.length;i++){
            const ele = arrayElements[i];
            if (ele.customData?.generationData?.id == szLibraryId){
              const customData = {...ele.customData};
              customData.generationData.name = res.name;
              mutateElement(ele,{customData:customData,name:res.name});
            }
          }
        }
      })
    }
  }

  public componentWillUnmount() {
    this.renderer.destroy();
    this.scene.destroy();

    this.scene = new Scene();
    this.renderer = new Renderer(this.scene);
    this.files = {};
    this.imageCache.clear();
    this.resizeObserver?.disconnect();
    this.unmounted = true;
    this.removeEventListeners();

    this.library.destroy();
    this.laserPathManager.destroy();
    this.onChangeEmitter.clear();
    ShapeCache.destroy();
    SnapCache.destroy();
    clearTimeout(touchTimeout);
    isSomeElementSelected.clearCache();
    selectGroupsForSelectedElements.clearCache();
    touchTimeout = 0;
    mindMapCache.clear();
    mindMapPngCache.clear();
    mindMapId_Data.clear();
    mindMapUid_Text.clear();
    window.removeEventListener('keydown',this.onWindowKeyDown,{capture:true});
    this.resetHistory();
    releaseCanvasCache();

    unSubscribe(EVENT_COPY_MINDMAP_FROM_ELEMENT);
    unSubscribe(EVENT_FORCE_UPDATE_MINDMAP);
    unSubscribe(EVENT_OPEN_VIDEO_IN_POPUP);
    unSubscribe(EVENT_UPDATE_MEDIA_ELEMENT_SRC);

    //canvas...
    this.releaseCanvas();
    delete window.getAppFiles;
    delete  window.clearSelected;
    delete window.updateElement;
    delete window.onRefreshContentElement;
    delete window.forceUpdateMindmap;
    delete window.getAllElement;
    delete window.forceUpdatMindmapData;
    delete window.refreshCanvasElementName;
    delete window.updateReplaceElement;
    delete window.addImageFile;
  }

  /**
   * 清理
   */
  simpleRelease = ()=>{
    // this.renderer.destroy();
    // this.scene = new Scene();
    // this.renderer = new Renderer(this.scene);
    // this.files = {};
    this.imageCache.clear();
    // this.resizeObserver?.disconnect();
    // this.unmounted = true;
    // this.removeEventListeners();
    // this.scene.destroy();
    this.library.destroy();
    // this.laserPathManager.destroy();
    // this.onChangeEmitter.clear();
    ShapeCache.destroy();
    SnapCache.destroy();
    // clearTimeout(touchTimeout);
    isSomeElementSelected.clearCache();
    selectGroupsForSelectedElements.clearCache();
    // touchTimeout = 0;
    // mindMapCache.clear();
    // mindMapPngCache.clear();
    this.resetHistory();

  }

  /**
   * 释放canvas....
   */
  releaseCanvas = (bClearAll:any = true)=>{
    this.setState({
      videoPlayIds:{}
    })

    const canvas = document.getElementById('canvas');
    if (canvas){
      const ctx = canvas.getContext('2d');
      if (ctx){
        if (bClearAll){
          canvas.width = canvas.height = 1;
        }
        ctx.clearRect(0,0,canvas.width,canvas.height);
        if (bClearAll){
          canvas.remove();
        }
      }
    }
  }

  /**
   * 刷新大小
   */
  onRefreshResize = ()=>{
    this.onResize();
  }

  private onResize = withBatchedUpdates(() => {
    this.scene
      .getElementsIncludingDeleted()
      .forEach((element) => ShapeCache.delete(element));
    this.refreshViewportBreakpoints();
    this.updateDOMRect();
    if (!supportsResizeObserver) {
      this.refreshEditorBreakpoints();
    }
    this.setState({});
  });

  /** generally invoked only if fullscreen was invoked programmatically */
  private onFullscreenChange = () => {
    if (
      // points to the iframe element we fullscreened
      !document.fullscreenElement &&
      this.state.activeEmbeddable?.state === "active"
    ) {
      this.setState({
        activeEmbeddable: null,
      });
    }
  };

  private removeEventListeners() {
    this.onRemoveEventListenersEmitter.trigger();
  }

  private addEventListeners() {
    // remove first as we can add event listeners multiple times
    this.removeEventListeners();

    // -------------------------------------------------------------------------
    //                        view+edit mode listeners
    // -------------------------------------------------------------------------

    if (this.props.handleKeyboardGlobally) {
      this.onRemoveEventListenersEmitter.once(
        addEventListener(document, EVENT.KEYDOWN, this.onKeyDown, false),
      );
    }

    this.onRemoveEventListenersEmitter.once(
      addEventListener(
        this.excalidrawContainerRef.current,
        EVENT.WHEEL,
        this.onWheel,
        { passive: false },
      ),
      addEventListener(window, EVENT.MESSAGE, this.onWindowMessage, false),
      addEventListener(document, EVENT.POINTER_UP, this.removePointer), // #3553
      addEventListener(document, EVENT.COPY, this.onCopy),
      addEventListener(document, EVENT.KEYUP, this.onKeyUp, { passive: true }),
      addEventListener(
        document,
        EVENT.MOUSE_MOVE,
        this.updateCurrentCursorPosition,
      ),
      // rerender text elements on font load to fix #637 && #1553
      addEventListener(document.fonts, "loadingdone", (event) => {
        const loadedFontFaces = (event as FontFaceSetLoadEvent).fontfaces;
        // this.fonts.onFontsLoaded(loadedFontFaces);
      }),
      // Safari-only desktop pinch zoom
      addEventListener(
        document,
        EVENT.GESTURE_START,
        this.onGestureStart as any,
        false,
      ),
      addEventListener(
        document,
        EVENT.GESTURE_CHANGE,
        this.onGestureChange as any,
        false,
      ),
      addEventListener(
        document,
        EVENT.GESTURE_END,
        this.onGestureEnd as any,
        false,
      ),
      addEventListener(window, EVENT.FOCUS, () => {
        this.maybeCleanupAfterMissingPointerUp(null);
      }),
    );

    if (this.state.viewModeEnabled) {
      return;
    }

    // -------------------------------------------------------------------------
    //                        edit-mode listeners only
    // -------------------------------------------------------------------------

    this.onRemoveEventListenersEmitter.once(
      addEventListener(
        document,
        EVENT.FULLSCREENCHANGE,
        this.onFullscreenChange,
      ),
      addEventListener(document, EVENT.PASTE, this.pasteFromClipboard),
      addEventListener(document, EVENT.CUT, this.onCut),
      addEventListener(window, EVENT.RESIZE, this.onResize, false),
      addEventListener(window, EVENT.UNLOAD, this.onUnload, false),
      addEventListener(window, EVENT.BLUR, this.onBlur, false),
      addEventListener(
        this.excalidrawContainerRef.current,
        EVENT.DRAG_OVER,
        this.disableEvent,
        false,
      ),
      addEventListener(
        this.excalidrawContainerRef.current,
        EVENT.DROP,
        this.disableEvent,
        false,
      ),
    );

    if (this.props.detectScroll) {
      this.onRemoveEventListenersEmitter.once(
        addEventListener(
          getNearestScrollableContainer(this.excalidrawContainerRef.current!),
          EVENT.SCROLL,
          this.onScroll,
        ),
      );
    }
  }

  componentDidUpdate(prevProps: AppProps, prevState: AppState) {
    this.updateEmbeddables();
    if (
      !this.state.showWelcomeScreen &&
      !this.scene.getElementsIncludingDeleted().length
    ) {
      this.setState({ showWelcomeScreen: true });
    }

    if (
      prevProps.UIOptions.dockedSidebarBreakpoint !==
      this.props.UIOptions.dockedSidebarBreakpoint
    ) {
      this.refreshEditorBreakpoints();
    }

    const hasFollowedPersonLeft =
      prevState.userToFollow &&
      !this.state.collaborators.has(prevState.userToFollow.socketId);

    if (hasFollowedPersonLeft) {
      this.maybeUnfollowRemoteUser();
    }

    if (
      prevState.zoom.value !== this.state.zoom.value ||
      prevState.scrollX !== this.state.scrollX ||
      prevState.scrollY !== this.state.scrollY
    ) {
      this.props?.onScrollChange?.(
        this.state.scrollX,
        this.state.scrollY,
        this.state.zoom,
      );
      this.onScrollChangeEmitter.trigger(
        this.state.scrollX,
        this.state.scrollY,
        this.state.zoom,
      );
      this.props?.onZoomChanged();

      //这里调整mindmap工具栏
      if (this.state.mindmap?.selected?.node){
        renderNodesToolBar([this.state.mindmap?.selected?.node],this.state.mindmap?.selected?.element?.id);
      }
    }


    if (prevState.userToFollow !== this.state.userToFollow) {
      if (prevState.userToFollow) {
        this.onUserFollowEmitter.trigger({
          userToFollow: prevState.userToFollow,
          action: "UNFOLLOW",
        });
      }

      if (this.state.userToFollow) {
        this.onUserFollowEmitter.trigger({
          userToFollow: this.state.userToFollow,
          action: "FOLLOW",
        });
      }
    }

    if (
      Object.keys(this.state.selectedElementIds).length &&
      isEraserActive(this.state)
    ) {
      this.setState({
        activeTool: updateActiveTool(this.state, { type: "selection" }),
      });
    }
    if (
      this.state.activeTool.type === "eraser" &&
      prevState.theme !== this.state.theme
    ) {
      setEraserCursor(this.interactiveCanvas, this.state.theme);
    }
    // Hide hyperlink popup if shown when element type is not selection
    if (
      prevState.activeTool.type === "selection" &&
      this.state.activeTool.type !== "selection" &&
      this.state.showHyperlinkPopup
    ) {
      this.setState({ showHyperlinkPopup: false });
    }
    if (prevProps.langCode !== this.props.langCode) {
      this.updateLanguage();
    }

    if (prevProps.viewModeEnabled !== this.props.viewModeEnabled) {
      this.setState({ viewModeEnabled: !!this.props.viewModeEnabled });
    }

    if (prevState.viewModeEnabled !== this.state.viewModeEnabled) {
      this.addEventListeners();
      this.deselectElements();
    }

    if (prevState.selectedElementIds !== this.state.selectedElementIds && JSON.stringify(prevState.selectedElementIds) != JSON.stringify(this.state.selectedElementIds)) {
      this.props?.onSelectedElementChanged()?.();
    }

    // if (prevState.selectedElementIds !== this.state.selectedElementIds){
    //   console.log('xxxxx')
    // }

    //判断更新元素数据是否需要保存.....
    // if (this.state.selectedElementIds && Object.keys(this.state.selectedElementIds).length > 0 && JSON.stringify(prevState.selectedElementIds) != JSON.stringify(this.state.selectedElementIds)){
    //   // console.log('xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxupdate.....element....')
    //   if (window.saveNotes){
    //     window.saveNotes();
    //   }
    // }


    if (prevProps.zenModeEnabled !== this.props.zenModeEnabled) {
      this.setState({ zenModeEnabled: !!this.props.zenModeEnabled });
    }

    if (prevProps.theme !== this.props.theme && this.props.theme) {
      this.setState({ theme: this.props.theme });
    }

    if (prevProps.gridModeEnabled !== this.props.gridModeEnabled) {
      this.setState({
        gridSize: this.props.gridModeEnabled ? GRID_SIZE : null,
      });
    }

    if (this.props.name && prevProps.name !== this.props.name) {
      this.setState({
        name: this.props.name,
      });
    }

    this.excalidrawContainerRef.current?.classList.toggle(
      "theme--dark",
      this.state.theme === "dark",
    );

    if (
      this.state.editingLinearElement &&
      !this.state.selectedElementIds[this.state.editingLinearElement.elementId]
    ) {
      // defer so that the commitToHistory flag isn't reset via current update
      setTimeout(() => {
        // execute only if the condition still holds when the deferred callback
        // executes (it can be scheduled multiple times depending on how
        // many times the component renders)
        this.state.editingLinearElement &&
          this.actionManager.executeAction(actionFinalize);
      });
    }

    // failsafe in case the state is being updated in incorrect order resulting
    // in the editingElement being now a deleted element
    if (this.state.editingElement?.isDeleted) {
      this.setState({ editingElement: null });
    }

    if (
      this.state.selectedLinearElement &&
      !this.state.selectedElementIds[this.state.selectedLinearElement.elementId]
    ) {
      // To make sure `selectedLinearElement` is in sync with `selectedElementIds`, however this shouldn't be needed once
      // we have a single API to update `selectedElementIds`
      this.setState({ selectedLinearElement: null });
    }

    const { multiElement } = prevState;
    if (
      prevState.activeTool !== this.state.activeTool &&
      multiElement != null &&
      isBindingEnabled(this.state) &&
      isBindingElement(multiElement, false)
    ) {
      maybeBindLinearElement(
        multiElement,
        this.state,
        this.scene,
        tupleToCoors(
          LinearElementEditor.getPointAtIndexGlobalCoordinates(
            multiElement,
            -1,
          ),
        ),
      );
    }
    this.history.record(this.state, this.scene.getElementsIncludingDeleted());

    // Do not notify consumers if we're still loading the scene. Among other
    // potential issues, this fixes a case where the tab isn't focused during
    // init, which would trigger onChange with empty elements, which would then
    // override whatever is in localStorage currently.
    if (!this.state.isLoading) {
      this.props.onChange?.(
        this.scene.getElementsIncludingDeleted(),
        this.state,
        this.files,
      );
      this.onChangeEmitter.trigger(
        this.scene.getElementsIncludingDeleted(),
        this.state,
        this.files,
      );
    }
  }

  private renderInteractiveSceneCallback = ({
    atLeastOneVisibleElement,
    scrollBars,
    elements,
  }: RenderInteractiveSceneCallback) => {
    if (scrollBars) {
      currentScrollBars = scrollBars;
    }
    const scrolledOutside =
      // hide when editing text
      isTextElement(this.state.editingElement)
        ? false
        : !atLeastOneVisibleElement && elements.length > 0;
    if (this.state.scrolledOutside !== scrolledOutside) {
      this.setState({ scrolledOutside });
    }

    this.scheduleImageRefresh();
  };

  private onScroll = debounce(() => {
    const { offsetTop, offsetLeft } = this.getCanvasOffsets();
    this.setState((state) => {
      if (state.offsetLeft === offsetLeft && state.offsetTop === offsetTop) {
        return null;
      }
      return { offsetTop, offsetLeft };
    });
  }, SCROLL_TIMEOUT);

  // Copy/paste

  private onCut = withBatchedUpdates((event: ClipboardEvent) => {
    const isExcalidrawActive = this.excalidrawContainerRef.current?.contains(
      document.activeElement,
    );
    if (!isExcalidrawActive || isWritableElement(event.target) || event.currentTarget?.activeElement?.className?.indexOf('tiptap ProseMirror') >= 0) {
      return;
    }
    this.actionManager.executeAction(actionCut, "keyboard", event);
    event.preventDefault();
    event.stopPropagation();
  });

  private onCopy = withBatchedUpdates((event: ClipboardEvent) => {
    const isExcalidrawActive = this.excalidrawContainerRef.current?.contains(
      document.activeElement,
    );

    if (textSelectRange) {
      const element = this.scene.getElement(
          textSelectRange.id,
      ) as ExcalidrawTextElement;
      copyTextSelectRange = {
        ...cloneDeep(textSelectRange),
        element: cloneDeep(element),
      };
    }

    const isMindmapRoot = activeNode?.node?.isRoot;
    if (isMindmapRoot){
      const copyEvent = new ClipboardEvent("copy", {
        clipboardData: new DataTransfer(), // 创建一个 DataTransfer 对象来存储剪贴板数据
      });
      this.setState({
        selectedElementIds:{[activeNode?.element?.id]:true},
        draggingElement: null,
        selectionElement: null,
      })
      setTimeout(()=>{
        this.actionManager.executeAction(actionCopy, "keyboard", copyEvent);
      },100)
    }

    if (!isExcalidrawActive || isWritableElement(event.target) || event.currentTarget?.activeElement?.className?.indexOf('tiptap ProseMirror') >= 0) {
      return;
    }
    this.actionManager.executeAction(actionCopy, "keyboard", event);
    event.preventDefault();
    event.stopPropagation();
  });

  private static resetTapTwice() {
    didTapTwice = false;
  }

  private onTouchStart = (event: TouchEvent) => {
    // fix for Apple Pencil Scribble (do not prevent for other devices)
    if (isIOS) {
      event.preventDefault();
    }

    if (!didTapTwice) {
      didTapTwice = true;
      clearTimeout(tappedTwiceTimer);
      tappedTwiceTimer = window.setTimeout(
        App.resetTapTwice,
        TAP_TWICE_TIMEOUT,
      );
      return;
    }
    // insert text only if we tapped twice with a single finger
    // event.touches.length === 1 will also prevent inserting text when user's zooming
    if (didTapTwice && event.touches.length === 1) {
      const touch = event.touches[0];
      // @ts-ignore
      this.handleCanvasDoubleClick({
        clientX: touch.clientX,
        clientY: touch.clientY,
      });
      didTapTwice = false;
      clearTimeout(tappedTwiceTimer);
    }

    if (event.touches.length === 2) {
      this.setState({
        selectedElementIds: makeNextSelectedElementIds({}, this.state),
        activeEmbeddable: null,
        activeStickyNotes: null,
        activeMindMap: null,
      });
    }
  };

  private onTouchEnd = (event: TouchEvent) => {
    this.resetContextMenuTimer();
    if (event.touches.length > 0) {
      this.setState({
        previousSelectedElementIds: {},
        selectedElementIds: makeNextSelectedElementIds(
          this.state.previousSelectedElementIds,
          this.state,
        ),
      });
    } else {
      gesture.pointers.clear();
    }
  };

  /**
   * 添加历史记录
   */
  public pushHistory = ()=>{
    this.history.resumeRecording();
    this.history.record(this.state, this.scene.getElementsIncludingDeleted());
  }

  /**
   * 重命名画布上的元素.
   * @param szElementId
   * @param szName
   */
  public renameElement = async (szElementId:any,szName:any)=>{
    const element = this.getSceneElements().find(p=>p.id == szElementId);
    let isNeedRefresh = false;
    if (isFileVideoElement(element) || isLiveVideoElement(element) || isPdfFileElement(element) || isFileElement(element)){
      const otherElements = this.getSceneElements().filter(p=>p.customData?.generationData?.id == element.customData?.generationData?.id);
      for (let i=0;i<otherElements.length;i++){
        const ele = otherElements[i];
        const customData = {...ele.customData};
        customData.generationData.name = szName;
        mutateElement(ele,{name:szName,customData:customData});//
        await renameCardById(ele.customData.generationData.id,szName);
      }

      isNeedRefresh = true;
    }else if (isFrameElement(element) || isImageElement(element)){
      mutateElement(element,{name:szName})
    }
    if (isNeedRefresh){
      window.globalRefreshList('refreshLibraryList_');
    }
  }


  /**
   * 从剪切板粘贴从剪切板粘贴从剪切板粘贴从剪切板粘贴从剪切板粘贴从剪切板粘贴
   */
  public pasteFromClipboard = withBatchedUpdates(
    async (event: ClipboardEvent) => {
      const isPlainPaste = !IS_PLAIN_PASTE;

      //对话框显示了导入字幕，这里就不要处理了。。。
      if (global.g_showDialogs.dialogImportTranscript)return;

      //postmessage
      if (event?.clipboardData?.files.length > 0){
        postMessage(EVENT_PASTE_FILE_MESSAGE,{file:event?.clipboardData?.files[0]})
      }

      if (event?.target?.tagName == 'TEXTAREA' || event?.taget?.tagName == 'INPUT' || window.getDialogCardCount() > 0 || document.activeElement?.className?.includes('tiptap')){

      }else{
        setTimeout(()=>{
          this.focusContainer();
        },500)
      }


        // #686--不用判断了，直接粘!20250523  ctrl+c复制完了文字直接ctrl+v 粘贴不上去，需要额外激活一下canvas
      const target = document.activeElement;
      // const isExcalidrawActive =
      //   this.excalidrawContainerRef.current?.contains(target);
      // if (event && !isExcalidrawActive) {
      //   return;
      // }

      const elementUnderCursor = document.elementFromPoint(
        this.lastViewportPosition.x,
        this.lastViewportPosition.y,
      );
      if (
        event &&
        (!(elementUnderCursor instanceof HTMLCanvasElement) ||
          isWritableElement(target)) && !global.g_pasteAs
      ) {
        return;
      }

      const { x: sceneX, y: sceneY } = viewportCoordsToSceneCoords(
        {
          clientX: this.lastViewportPosition.x,
          clientY: this.lastViewportPosition.y,
        },
        this.state,
      );

      // must be called in the same frame (thus before any awaits) as the paste
      // event else some browsers (FF...) will clear the clipboardData
      // (something something security)
      let file = event?.clipboardData?.files[0];
      let data = await parseClipboard(event, isPlainPaste,copyTextSelectRange,);
        if (!data?.text && global.g_pasteAs == 'text'){
            if (global.g_pasteEventData.data){
                data = global.g_pasteEventData.data;
            }
        }else{
            global.g_pasteEventData.data = {...data};
        }

        //mindmap-----to
        if (global.g_showDialogs.dialogImportMindmapFromMd)return;
        {
            try {
                const mindmapElement = this.createMindmapElement({
                    sceneX,
                    sceneY,
                    addToFrameUnderCursor: false,
                });
                //这里必须要重新取一下纯文本。不然HTML有些粘贴html的会有问题。太多情况了。粘贴和各种东西.
                let dataOnlyTextPlain = await parseClipboard(event, isPlainPaste,copyTextSelectRange,true);
                if (!dataOnlyTextPlain?.text){
                    if (global.g_pasteEventData.text){
                        dataOnlyTextPlain = global.g_pasteEventData.text;
                    }
                }else{
                    global.g_pasteEventData.text = {...dataOnlyTextPlain};
                }

                const mdText = await transformMarkdownTo(dataOnlyTextPlain.text,mindmapElement.id);
                if (mdText?.children?.length && mdText.children.length > 0) {

                    if (!global.g_pasteAs){
                        //这里要返回了，显示个菜单，然后才做下一步的事件...
                        postMessage(EVENT_PASTE_MINDMAP_PLAINT_TEXT,{sceneX:this.lastViewportPosition.x,sceneY:this.lastViewportPosition.y});
                        return;
                    }

                    if (global.g_pasteAs == 'mindmap'){
                        global.g_pasteAs = '';
                        global.g_pasteEventData = {};
                        this.scene.addNewElement(mindmapElement);
                        setTimeout(() => {
                            const mindMap = createMindMap(
                                mindmapElement,
                                this.createMindMapCallBack,
                                this.props.onLinkOpen,
                                false,
                                this.state
                            );
                          // Markdown -> Mindmap 粘贴属于结构变化：标记 dirty，确保扩容 + 对齐，
                          // 避免首次渲染时顶部/大量子节点被裁切。
                          try {
                            (mindMap as any).__excalidrawDataDirty = true;
                            (mindMap as any).__excalidrawMaybeSelectionRender = false;
                          } catch {}
                            mindMap.setData(mdText);
                        }, 500);
                        return;
                    }
                }
            } catch (error) {
                console.log(error);
            }
        }

        //mindmap-----end----

        global.g_pasteAs = '';
        global.g_pasteEventData = {};

      if (data?.elements){
        for (let i=0;i<data.elements.length;i++){
          data.elements[i].hasUpdate = true;
        }
      }
      if (!file && !isPlainPaste) {
        if (data.mixedContent) {

          return this.addElementsFromMixedContentPaste(data.mixedContent, {
            isPlainPaste,
            sceneX,
            sceneY,
          });

        } else if (data.text) {
          const string = data.text.trim();
          if (string.startsWith("<svg") && string.endsWith("</svg>")) {
            // ignore SVG validation/normalization which will be done during image
            // initialization
            file = SVGStringToFile(string);
          }
        }
      }

      // prefer spreadsheet data over image file (MS Office/Libre Office)
      if (isSupportedImageFile(file) && !data.spreadsheet) {
        if (!this.isToolSupported("image")) {
          this.setState({ errorMessage: t("errors.imageToolNotSupported") });
          return;
        }

        let szFileName = getFileNameWithoutExt(file.name);
        if (szFileName.toLowerCase() == 'image'){
          szFileName = "Screenshot_"+formatDate(new Date(),"yyyyMMddhhmmss");
        }

        //导入模式
        let szImportModel = localStorage.getItem(KEY_FILE_IMPORT_MODE) || IMPORT_FILE_MODE_ASKME;

        if (!file.path){//没有路径，使用导入模式
          szImportModel = IMPORT_FILE_MODE_IMPORTMODE;
        }
        //粘贴的图片又不需要显示这个对话框了，全部使用导入模式!
        szImportModel = IMPORT_FILE_MODE_IMPORTMODE;
        if (szImportModel == IMPORT_FILE_MODE_ASKME){
          //弹窗..
          window.showDialogFileImportDrag(async (szAction)=>{
            const imageElement = this.createImageElement({ sceneX, sceneY,name:szFileName});
            await this.insertImageElement(imageElement, file,false,false,szAction);
            this.initializeImageDimensions(imageElement);
            this.setState({
              selectedElementIds: makeNextSelectedElementIds(
                  {
                    [imageElement.id]: true,
                  },
                  this.state,
              ),
            });
          })
        }else{
          const imageElement = this.createImageElement({ sceneX, sceneY,name:szFileName});
          await this.insertImageElement(imageElement, file,false,false,szImportModel);
          this.initializeImageDimensions(imageElement);
          this.setState({
            selectedElementIds: makeNextSelectedElementIds(
                {
                  [imageElement.id]: true,
                },
                this.state,
            ),
          });
        }

        return;
      }

      if (this.props.onPaste) {
        try {
          if ((await this.props.onPaste(data, event)) === false) {
            return;
          }
        } catch (error: any) {
          console.error(error);
        }
      }

      if (data.errorMessage) {
        this.setState({ errorMessage: data.errorMessage });
      } else if (data.spreadsheet && !isPlainPaste) {
        this.setState({
          pasteDialog: {
            data: data.spreadsheet,
            shown: true,
          },
        });
      } else if (data.elements) {
        const elements = (
          data.programmaticAPI
            ? convertToExcalidrawElements(
                data.elements as ExcalidrawElementSkeleton[],
              )
            : data.elements
        ) as readonly ExcalidrawElement[];

        // const nFrameCount = getSelectedElements().filter(p=>p.type == 'frame').length;
        // for (let i=0;i<elements.length;i++){
        //   if (elements[i].type == 'frame'){
        //     elements[i].
        //   }
        // }
        // TODO remove formatting from elements if isPlainPaste
        //这里如果是选择到frame里面并且带着frame,会重复选择元素，根据ID先去重.
        let arrayDistinctElements:any = [];
        elements.forEach(p=>{
          if (!arrayDistinctElements.some(pppp=>pppp.id == p.id)){
            arrayDistinctElements.push(p);
          }
        })
        this.addElementsFromPasteOrLibrary({
          elements:arrayDistinctElements,
          files: data.files || null,
          position: "cursor",
          retainSeed: isPlainPaste,
        });

        //刷新frame...
        postMessage(EVENT_UPDATE_FRAME_LIST);

      } else if (data.text) {
        const regex = /<span[^>]*>([\s\S]*?)<\/span>/gi;
        let szText = (data.text.replace(regex,'$1'));
        const nonEmptyLines = normalizeEOL(szText)
          .split(/\n+/)
          .map((s) => s.trim())
          .filter(Boolean);

        const embbeddableUrls = nonEmptyLines
          .map((str) => maybeParseEmbedSrc(str))
          .filter((string) => {
            return (
              embeddableURLValidator(string, this.props.validateEmbeddable) &&
              (/^(http|https):\/\/[^\s/$.?#].[^\s]*$/.test(string) ||
                getEmbedLink(string)?.type === "video")
            );
          });

        if (
          !IS_PLAIN_PASTE &&
          embbeddableUrls.length > 0 &&
          // if there were non-embeddable text (lines) mixed in with embeddable
          // urls, ignore and paste as text
          embbeddableUrls.length === nonEmptyLines.length
        ) {
          const embeddables: NonDeleted<ExcalidrawEmbeddableElement>[] = [];
          let arrayElements = null;
          for (const url of embbeddableUrls) {

            // if (isSupportVideoAnnoUrl(url)){//加个判断，只有支持的url默认为embed方式。别的使用文本，弹出右键供用户选择
              const prevEmbeddable: ExcalidrawEmbeddableElement | undefined =
                  embeddables[embeddables.length - 1];

              const embeddable = await this.insertEmbeddableElement({
                sceneX: prevEmbeddable
                    ? prevEmbeddable.x + prevEmbeddable.width + 20
                    : sceneX,
                sceneY,
                link: normalizeLink(url),
                embedType: "text",
              });
              if (embeddable) {
                embeddables.push(embeddable);
              }
            // }
            // else{
            //   arrayElements = this.addTextFromPaste(data.text, isPlainPaste, null);
            // }
          }
          if (embeddables.length) {
            this.setState({
              selectedElementIds: Object.fromEntries(
                embeddables.map((embeddable) => [embeddable.id, true]),
              ),
            });
          }
          if (arrayElements?.length > 0){
            postMessage(EVENT_PASTE_URL,{elements:arrayElements});
          }


          return;
        }
        // @ts-ignore
        this.addTextFromPaste(szText, isPlainPaste, data.richText);
      }
      this.setActiveTool({ type: "selection" });
      event?.preventDefault();
    },
  );

  onUpdateElement = (element,updates)=>{
    // mutateElement(element,updates);

    let arrayElements = this.getSceneElements();
    var arrayNew = [...arrayElements];
    let lastItem:any = null;

    let nIndex = arrayNew.findIndex(p=>p.id == element.id);
    if (nIndex != -1){
      const item = arrayNew[nIndex];
      lastItem = {
        ...item,
        ...updates,
        version:item.version+1,
        versionNonce:randomInteger(),
        updated:getUpdatedTimestamp(),
      }
      arrayNew.splice(nIndex,1);
    }

    //之前的写法....
    // for (let i=0;i<arrayElements.length;i++){
    //   let item = arrayElements[i];
    //   if (arrayElements[i].id == element.id){
    //     item = {
    //       ...item,
    //       ...updates,
    //     }
    //     lastItem = item;
    //   }else{
    //     arrayNew.push(item);
    //   }
    // }
    //end....

    //添加到最后一个
    if (lastItem){
      arrayNew.push(lastItem);
    }

    this.updateScene({
      elements:arrayNew
    })
  }


  onUpdateNoteElement = (element,updates)=>{
    // mutateElement(element,updates);

    let arrayElements = this.getSceneElements();
    var arrayNew = [];
    let lastItem:any = null;

    for (let i=0;i<arrayElements.length;i++) {
      const ele = arrayElements[i];
      if (ele.customData?.generationData?.id == element.customData?.generationData?.id) {
        arrayNew.push({
          ...ele,
          ...updates
        })
      } else {
        arrayNew.push(ele);
      }
    }

    let nIndex = arrayNew.findIndex(p=>p.id == element.id);
    if (nIndex != -1){
      const item = arrayNew[nIndex];
      lastItem = {
        ...item,
        ...updates
      }
      arrayNew.splice(nIndex,1);
    }

    //添加到最后一个
    if (lastItem){
      arrayNew.push(lastItem);
    }

    this.updateScene({
      elements:arrayNew
    })
  }


  /**
   * 标记更新元素
   * @param szElementId
   * @param updates
   */
  signUpdateElement = (szElementId:any,updates:any)=>{
    if (!szElementId)return;
    let element = this.getSceneElements().find(p=>p.id == szElementId);
    if (element){
      mutateElement(element,updates);
    }
  }

  /**
   * 设置所有note element 不允许编辑...
   */
  onResetNoteEditElement = ()=>{
    let arrayElements = this.getSceneElements();
    var arrayNew = [];
    let isNeedUpdate = false;
    for (let i=0;i<arrayElements.length;i++){
      let item = arrayElements[i];
      if (isNoteElement(item)){
        if (item.customData.generationData.isEditable){
          isNeedUpdate = true;
          item.customData.generationData.isEditable = false;
        }
      }
      arrayNew.push(item);
    }
    if (isNeedUpdate){
      this.updateScene({
        elements:arrayNew
      })
    }
  }

  isValidateLink = (element)=>{
    if (isStringNullOrEmpty(element.link)) return false;

    try{
      const objLink = JSON.parse(element.link);

      if ('canvas' in Global.g_currentPageData){
        for (let i=0;i<Global.g_currentPageData.canvas.length;i++){
          const item = Global.g_currentPageData.canvas[i];
          if (item.isFolder){
            for (let j=0;j<item.children.length;j++){
              const subItem = item.children[j];
              if (objLink.id === subItem.key){
                return true;
              }
            }
          }else{
            if (objLink.id === item.key){
              return true;
            }
          }
        }
      }
      else if('notes' in Global.g_currentPageData){
        for (let i=0;i<Global.g_currentPageData.notes.length;i++){
          const item = Global.g_currentPageData.notes[i];
          if (item.isFolder){
            for (let j=0;j<item.children.length;j++){
              const subItem = item.children[j];
              if(objLink.id === item.subItem){
                return true;
              }
            }
          }else{
            if(objLink.id === item.key){
              return true;
            }
          }
        }
      }
    }catch (err){
      console.log('isvalidate link error....')
    }


    return false;
  }

  recursionMindmapData = async (data:any,callback:any)=>{
    if (data.children?.length > 0){
      for (let i=0;i<data.children.length;i++){
        await this.recursionMindmapData(data.children[i],callback);
      }
    }else{
      await callback(data.data);
    }
  }

  //粘贴粘贴粘贴粘贴粘贴粘贴粘贴粘贴粘贴粘贴粘贴粘贴粘贴粘贴粘贴粘贴粘贴粘贴粘贴粘贴粘贴粘贴粘贴粘贴粘贴粘贴粘贴粘贴粘贴粘贴粘贴粘贴粘贴粘贴粘贴粘贴粘贴粘贴粘贴粘贴粘贴粘贴粘贴粘贴粘贴粘贴粘贴粘贴粘贴粘贴粘贴粘贴粘贴
  addElementsFromPasteOrLibrary = async (opts: {
    elements: readonly ExcalidrawElement[];
    files: BinaryFiles | null;
    position: { clientX: number; clientY: number } | "cursor" | "center";
    retainSeed?: boolean;
    fitToContent?: boolean;
  }) => {
    const elements = restoreElements(opts.elements, null, undefined);
    const [minX, minY, maxX, maxY] = getCommonBounds(elements);

    const elementsCenterX = distance(minX, maxX) / 2;
    const elementsCenterY = distance(minY, maxY) / 2;

    const clientX =
      typeof opts.position === "object"
        ? opts.position.clientX
        : opts.position === "cursor"
        ? this.lastViewportPosition.x
        : this.state.width / 2 + this.state.offsetLeft;
    const clientY =
      typeof opts.position === "object"
        ? opts.position.clientY
        : opts.position === "cursor"
        ? this.lastViewportPosition.y
        : this.state.height / 2 + this.state.offsetTop;

    const { x, y } = viewportCoordsToSceneCoords(
      { clientX, clientY },
      this.state,
    );

    const dx = x - elementsCenterX;
    const dy = y - elementsCenterY;

    const [gridX, gridY] = getGridPoint(dx, dy, this.state.gridSize);

      const restoredElements = restoreElements(opts.elements, null, undefined);
      const elementsToDuplicate = restoredElements.map((element) => {
          return newElementWith(element, {
              x: element.x + gridX - minX,
              y: element.y + gridY - minY,
          });
      });

      const newElements = duplicateElements(elementsToDuplicate, {
          randomizeSeed: !opts.retainSeed,
      });

      // Copy/paste needs the same binding rewiring as duplicate:
      // - connectors should re-bind to the pasted widgets
      // - boundElements on pasted widgets should only reference pasted connectors
      const oldIdToDuplicatedId = new Map<
          ExcalidrawElement["id"],
          ExcalidrawElement["id"]
      >();
      restoredElements.forEach((el, idx) => {
          const dup = newElements[idx];
          if (dup) {
              oldIdToDuplicatedId.set(el.id, dup.id);
          }
      });


    //修改note...
    var isCopyedNote = false;
    let isSameBoard = false;
    for (let i=0;i<newElements.length;i++){
      let item = newElements[i];
      // if (isFileElement(item)){
      //   item.customData.generationData.id = item.id;
      // }
      if (item.type == 'iframe' && !isFileElement(item)){
        if (isNoteElement(item)){
          // let szHtml = replaceNoteNameFromElementHtml(item.customData.generationData.name,item.customData.generationData.html);
          // szHtml = createNoteElementByNote(item.customData.generationData.name,item.customData.generationData.html);//replaceOriginNoteDocHtml(item.customData.generationData.name,item.customData.generationData.html);

          const selectIds = sessionStorage.getItem(Global.g_currentBoardId)
          if (selectIds){
            if (selectIds.includes(item.originId)){
              isSameBoard = true;
            }
          }

          // if (isSameBoard){
          //   item.customData.generationData.id = item.id;
          //   item.customData.generationData.name += `(${i18next.t('Copy')})`;
          // }
          //创建新卡片并保存到库...
          const newUUID = generateUUID2();
          getCardById(item.customData?.generationData?.id).then(async (res)=>{
            if (res){
              res.id = newUUID;
              updateCard(res);
            }
          })
          item.customData.generationData.id = newUUID;

          isCopyedNote = true;
        }
      }else if (isPdfFileElement(item)){
        //pdf文件，要复制注记
        // ipcInvoke('copy-annotations',{originFileId:item.originId,newFileId:item.id})
      }
      else if (isMindmapElement(item)){
        //查找card,分离...
        var arrayNewNotes:any = [];
        await this.recursionMindmapData(item.data,async (data:any)=>{
          if (data.extra){
            if (data.extra.type == 'card'){
              //复制这个笔记到这里....
              const note = await window.methodGetNoteById(data.extra.id);
              if (note){
                //生成新的id
                data.extra.id = generateUUID2();
                arrayNewNotes.push({
                  ...note,
                  id:data.extra.id
                });
              }
            }
          }
        })
        //添加笔记到左侧栏
        if (arrayNewNotes.length > 0){
          postMessage(EVENT_ADD_NOTE_BY_CARD,arrayNewNotes);
        }
      }
    }

    if (isCopyedNote){
      //newElement中的name 被不见了？？？？
      const szElements = JSON.stringify(newElements)
      postMessage(EVENT_COPY_DOC_NOTE_FROM_ELEMENT, {elements:JSON.parse(szElements),isSameBoard});
      // message.info(i18next.t("Copyed"));
    }

    let nCopyCount = newElements.length;
    //移除已经存在的注记--20251126 所有的都可以重复，不需要管这个了。随便加。
    // for (let i=newElements.length-1;i>=0;i--){
    //   const item = newElements[i];
    //   if (this.getSceneElements().some(p=>p.id == item.id)){
    //     newElements.splice(i,1);
    //     if (nCopyCount == 1){
    //       message.error(i18next.t("AnnoExist"));
    //     }
    //   }
    // }
    if (newElements.length == 0)return;

      const nextSceneElements = [
          ...this.scene.getElementsIncludingDeleted(),
          ...newElements,
      ];

      bindTextToShapeAfterDuplication(
          nextSceneElements,
          restoredElements,
          oldIdToDuplicatedId,
      );
      fixBindingsAfterDuplication(
          nextSceneElements,
          restoredElements,
          oldIdToDuplicatedId,
      );
      bindElementsToFramesAfterDuplication(
          nextSceneElements,
          restoredElements,
          oldIdToDuplicatedId,
      );

      // After rewiring ids, ensure pasted connectors are snapped to pasted widgets.
      // This fixes cases where arrowheads (especially on polylines) end up with
      // wrong orientation due to stale/incorrect binding endpoints.
      const elementsById = new Map<ExcalidrawElement["id"], ExcalidrawElement>();
      nextSceneElements.forEach((el) => elementsById.set(el.id, el));
      newElements.forEach((el) => {
          if (!isLinearElement(el)) {
              return;
          }
          const startId = el.startBinding?.elementId;
          const endId = el.endBinding?.elementId;
          if (startId) {
              const startEl = elementsById.get(startId);
              if (startEl && isBindableElement(startEl)) {
                  bindLinearElement(el, startEl, "start");
              }
          }
          if (endId) {
              const endEl = elementsById.get(endId);
              if (endEl && isBindableElement(endEl)) {
                  bindLinearElement(el, endEl, "end");
              }
          }
      });



      this.scene.replaceAllElements(nextSceneElements);

      // keep existing behavior below
      const nextElements = nextSceneElements;

    newElements.forEach((newElement) => {
      if (isTextElement(newElement) && isBoundToContainer(newElement)) {
        const container = getContainerElement(newElement);
        redrawTextBoundingBox(newElement, container);
      }
      if (isMindmapElement(newElement)) {
        const dataNew = newElement.data;
        dataNew.data.uid = newElement.id;
        mutateElement(newElement,{data:dataNew});

        setTimeout(() => {
          const container = document.getElementById(
              `excalidraw__mindmap__outer${newElement.id}`,
          )!;
          if (!container) {
            return;
          }

          mindMapCache.forEach((value, key) => {
            value.renderer.clearActiveNode();
          });

          const mindMap = createMindMap(
              newElement,
              this.createMindMapCallBack,
              this.props.onLinkOpen,
              false,
              this.state
          );
          mindMap.setData(newElement.data);
        }, 50);
      }

      // 复制cardlink的元素
      if (isEmbeddableElement(newElement)) {
        setTimeout(() => {
          const node = document.getElementById(`${newElement.id}-card`)!;
          cardLink([node],newElement);
        }, 10);
      }
    });

    if (opts.files) {
      this.files = { ...this.files, ...opts.files };
    }

    this.history.resumeRecording();

    const nextElementsToSelect =
      excludeElementsInFramesFromSelection(newElements);

    setTimeout(() => {
      this.setState(
          {
            ...this.state,
            // keep sidebar (presumably the library) open if it's docked and
            // can fit.
            //
            // NoteItem, we should close the sidebar only if we're dropping items
            // from library, not when pasting from clipboard. Alas.
            openSidebar:
                this.state.openSidebar &&
                this.device.editor.canFitSidebar &&
                jotaiStore.get(isSidebarDockedAtom)
                    ? this.state.openSidebar
                    : null,
            ...selectGroupsForSelectedElements(
                {
                  editingGroupId: null,
                  selectedElementIds: nextElementsToSelect.reduce(
                      (acc: Record<ExcalidrawElement["id"], true>, element) => {
                          // 粘贴后：形状/便利贴/文本默认保持选中，但连接线保持未选中
                          if (!isBoundToContainer(element) && !isLinearElement(element)) {
                              acc[element.id] = true;
                          }
                        return acc;
                      },
                      {},
                  ),
                },
                this.scene.getNonDeletedElements(),
                this.state,
                this,
            ),
          },
          () => {
            if (opts.files) {
              this.addNewImagesToImageCache();
            }
          },
      );
      this.setActiveTool({ type: "selection" });

      if (opts.fitToContent) {
        this.scrollToContent(newElements, {
          fitToContent: true,
        });
      }
    },100)
  };

  // TODO rewrite this to paste both text & images at the same time if
  // pasted data contains both
  private async addElementsFromMixedContentPaste(
    mixedContent: PastedMixedContent,
    {
      isPlainPaste,
      sceneX,
      sceneY,
    }: { isPlainPaste: boolean; sceneX: number; sceneY: number },
  ) {
    if (
      !isPlainPaste &&
      mixedContent.some((node) => node.type === "imageUrl") &&
      this.isToolSupported("image")
    ) {
      const imageURLs = mixedContent
        .filter((node) => node.type === "imageUrl")
        .map((node) => node.value);
      const responses = await Promise.all(
        imageURLs.map(async (url) => {
          try {
            return { file: await ImageURLToFile(url) };
          } catch (error: any) {
            return { errorMessage: error.message as string };
          }
        }),
      );
      let y = sceneY;
      let firstImageYOffsetDone = false;
      const nextSelectedIds: Record<ExcalidrawElement["id"], true> = {};
      for (const response of responses) {
        if (response.file) {
          const imageElement = this.createImageElement({
            sceneX,
            sceneY: y,
          });

          const initializedImageElement = await this.insertImageElement(
            imageElement,
            response.file,
          );
          if (initializedImageElement) {
            // vertically center first image in the batch
            if (!firstImageYOffsetDone) {
              firstImageYOffsetDone = true;
              y -= initializedImageElement.height / 2;
            }
            // hack to reset the `y` coord because we vertically center during
            // insertImageElement
            mutateElement(initializedImageElement, { y }, false);

            y = imageElement.y + imageElement.height + 25;

            nextSelectedIds[imageElement.id] = true;
          }
        }
      }

      this.setState({
        selectedElementIds: makeNextSelectedElementIds(
          nextSelectedIds,
          this.state,
        ),
      });

      const error = responses.find((response) => !!response.errorMessage);
      if (error && error.errorMessage) {
        this.setState({ errorMessage: error.errorMessage });
      }
    } else {
      const textNodes = mixedContent.filter((node) => node.type === "text");
      if (textNodes.length) {
        this.addTextFromPaste(
          textNodes.map((node) => node.value).join("\n\n"),
          isPlainPaste,
        );
      }
    }
  }

  private addTextFromPaste(text: string, isPlainPaste = false,richText?: RichText[],) {
    const { x, y } = viewportCoordsToSceneCoords(
      {
        clientX: this.lastViewportPosition.x,
        clientY: this.lastViewportPosition.y,
      },
      this.state,
    );

    //直接显示embed模式，20241218
    if (isDeepLink(text) || isCardBlockDeeplink(text)){
      const deepLink = decodeDeeplink(text);
      if (deepLink.isFolder){
        message.error(i18next.t('ThisOperationIsNotSupported'));
        return;
      }
      if ((deepLink.type == 'library') && (deepLink.childType == 'pdf' ||
          deepLink.childType == 'video' ||
          deepLink.childType == 'card' ||
          deepLink.childType == 'audio' ||
          deepLink.childType == 'file' ||
          deepLink.childType == 'journal' ||
          deepLink.childType == 'image' ||
          deepLink.childType == 'bilibili' || deepLink.childType == 'youtube' || deepLink.childType == 'baidu' ||
          isAnnoType(deepLink.childType) || deepLink.childType == 'highlight')){
        getCardById(deepLink.id).then(res=>{
          //带上原始的元素的ID，复制的时候把背景也带上....
          postMessage(EVENT_TEST_ONDRAG_NOTE,{...res,x:this.lastViewportPosition.x,y:this.lastViewportPosition.y,deeplink:deepLink});
          // if (res.type == 'card'){
          //   message.info(i18next.t("Copyed"));
          // }
        })
        return;
      }

      if (deepLink.type == 'highlight' && deepLink.childType == 'videoNote'){
        dbGetHighlight(deepLink.id).then(res=>{
          const videoNote = res.data.find(p=>p.id == deepLink.childId);
          postMessage(EVENT_TEST_ONDRAG_NOTE,{type:'videoAnno',id:videoNote.id,libraryId:deepLink.id,name:res.name,videoData:videoNote,x:this.lastViewportPosition.x,y:this.lastViewportPosition.y})
        })
        return;
      }
    //   if (deepLink.type == 'library' && deepLink.childType == 'pdfAnno'){
    //     //粘贴pdf注记
    //     postMessage(EVENT_TEST_ONDRAG_NOTE,{type:'pdfAnno',id:deepLink.id,x:this.lastViewportPosition.x,y:this.lastViewportPosition.y});
    //     return;
    //   }
    }

      let hasBorder = (localStorage.getItem(KEY_TEXT_HAS_BORDER) || 'true') == 'true';
      const fontFamily = getSafeInt(localStorage.getItem(KEY_DEFAULT_TEXT_FONT_FAMILY)) || FONT_FAMILY.Helvetica;

      const textElementProps = {
      id:generateUUID2(),
      x,
      y,
      strokeColor: this.state.currentItemStrokeColor,
      backgroundColor: this.state.currentItemBackgroundColor,
      fillStyle: this.state.currentItemFillStyle,
      strokeWidth: this.state.currentItemStrokeWidth,
      strokeStyle: this.state.currentItemStrokeStyle,
      roundness: null,
      roughness: this.state.currentItemRoughness,
      opacity: this.state.currentItemOpacity,
      text,
      fontSize: this.state.currentItemFontSize,
      fontFamily: fontFamily,//this.state.currentItemFontFamily,
      textAlign: this.state.currentItemTextAlign,
      verticalAlign: DEFAULT_VERTICAL_ALIGN,
      locked: false,
      creation: "addTextFromPaste", // 创建文本的类似，此处是复制创建的
    };

    const LINE_GAP = 10;
    let currentY = y;

      let textElements:any = [];
      if (HAS_ENABLE_TIPTAP_TEXT){
          var szLink = null;
          var bUnderlink = false;
          var szColor:string = isDarkMode()?COLOR_PALETTE.white:COLOR_PALETTE.black;
          if (isDeepLink(text) || isCardBlockDeeplink(text)){
              szLink = JSON.stringify({type:LINK_INFO_TYPE_URL,url:text});
              bUnderlink = true;
              szColor = '#3670F7'
          }
          if (isSupportLink(text)){
              szLink = text;
              szColor = '#3670F7'
          }

          const topLayerFrame = this.getTopLayerFrameAtSceneCoords({
              x,
              y: currentY,
          });
          let szTxt = text;
          if (isHTML(szTxt)){
              szTxt = parseHtmlFromPasteTiptapWithDownload(text,textElementProps.id,(szId)=>{
                  window.globalRefreshList('refresh_paste_html_'+szId,szId);
              })
          }else{
            szTxt = getTiptapHandleText(text);
          }
          const element = newTextElement({
              ...textElementProps,
              x,
              y: currentY,
              text:szTxt,
              link:szLink,
              richText:null,
        height:DEFAULT_TEXT_HEIGHT,
            backgroundColor:localStorage.getItem(KEY_DEFAULT_TEXT_BACKGROUND_COLOR) || COLOR_PALETTE.transparent,
              customData:{generationData:{isEditable:true,isShowBorder:hasBorder}},
              frameId: topLayerFrame ? topLayerFrame.id : null,
              initialized: true, // 以及初始化了，文本元素有文字内容了，
          });
          textElements.push(element);
      }else{
          const lines = isPlainPaste ? [text] : text.split("\n");
          const textElements = lines.reduce(
              (acc: ExcalidrawTextElement[], line, idx) => {
                  const text = line.trim();

                  const lineHeight = getDefaultLineHeight(textElementProps.fontFamily);
                  if (text.length) {
                      const topLayerFrame = this.getTopLayerFrameAtSceneCoords({
                          x,
                          y: currentY,
                      });

                      var szLink = null;
                      var bUnderlink = false;
                      var szColor:string = isDarkMode()?COLOR_PALETTE.white:COLOR_PALETTE.black;
                      if (isDeepLink(text)){
                          szLink = JSON.stringify({type:LINK_INFO_TYPE_URL,url:text});
                          bUnderlink = true;
                          szColor = '#3670F7'
                      }
                      if (isSupportLink(text)){
                          szLink = text;
                          szColor = '#3670F7'
                      }

                      const insertText = text.replace(/\r\n?/g, "\n");
                      const characters = insertText.split("");
                      const emojiValues: string[] = [];
                      let containsEmoji = false;
                      for (const character of insertText) {
                          const parts = character.split("");
                          if (parts.length > 1) {
                              containsEmoji = true;
                              parts.forEach(() => {
                                  emojiValues.push(character);
                              });
                              continue;
                          }
                          emojiValues.push(character);
                      }

                      const arr = characters.map((character, index) => {
                          if (!containsEmoji) {
                              return character;
                          }

                          const currentEmojiValue = emojiValues[index];
                          if (currentEmojiValue.length === 1) {
                              return character;
                          }

                          return {
                              character,
                              emoji: {
                                  length: currentEmojiValue.length,
                                  indice: currentEmojiValue
                                      .split("")
                                      .findIndex((part) => part === character),
                                  value: currentEmojiValue,
                              },
                          };
                      }) as unknown as RichText[];

                      const element = newTextElement({
                          ...textElementProps,
                          x,
                          y: currentY,
                          text,
                          link:szLink,
                          lineHeight,
                          richText:
                              richText ||
                              arr.map((item) => {
                                  const base: any = {
                                      value: item,
                                      fontFamily: this.state.currentItemFontFamily,
                                      fontSize: this.state.currentItemFontSize,
                                      color: szColor,
                                      bold: false,
                                      italic: false,
                                      underline: bUnderlink,
                                      strikeout: false,
                                  };
                                  if (item.emoji) {
                                      base.value = item.character;
                                      base.emoji = item.emoji;
                                  }
                                  return base;
                              }),
                          frameId: topLayerFrame ? topLayerFrame.id : null,
                          initialized: true, // 以及初始化了，文本元素有文字内容了，
                      });
                      acc.push(element);
                      currentY += element.height + LINE_GAP;
                  } else {
                      const prevLine = lines[idx - 1]?.trim();
                      // add paragraph only if previous line was not empty, IOW don't add
                      // more than one empty line
                      if (prevLine) {
                          currentY +=
                              getLineHeightInPx(textElementProps.fontSize, lineHeight) +
                              LINE_GAP;
                      }
                  }

                  return acc;
              },
              [],
          );
      }

    if (textElements.length === 0) {
      return;
    }

    const frameId = textElements[0].frameId;

    if (frameId) {
      this.scene.insertElementsAtIndex(
        textElements,
        this.scene.getElementIndex(frameId),
      );
    } else {
      this.scene.replaceAllElements([
        ...this.scene.getElementsIncludingDeleted(),
        ...textElements,
      ]);
    }

    this.setState({
      selectedElementIds: makeNextSelectedElementIds(
        Object.fromEntries(textElements.map((el) => [el.id, true])),
        this.state,
      ),
    });

    if (
      !isPlainPaste &&
      textElements.length > 1 &&
      PLAIN_PASTE_TOAST_SHOWN === false &&
      !this.device.editor.isMobile
    ) {
      this.setToast({
        message: t("toast.pasteAsSingleElement", {
          shortcut: getShortcutKey("CtrlOrCmd+Shift+V"),
        }),
        duration: 5000,
      });
      PLAIN_PASTE_TOAST_SHOWN = true;
    }

    this.history.resumeRecording();

    //粘贴完了文本....这里，自动弹出链接选项
    global.g_pasteDeeplinkElement = null;
    if (isDeepLink(text)){
      global.g_pasteDeeplinkElement = textElements[0];
      const ele = textElements[0];
      if (isPdfFileElement(ele) || isFileVideoElement(ele) || isNoteElement(ele) || isAudioFileElement(ele)){

      }else{
        postMessage(EVENT_PASTE_DEEP_LINK, {element:textElements[0]});
      }
    }

    return textElements;
  }

  setAppState: React.Component<any, AppState>["setState"] = (
    state,
    callback,
  ) => {
    this.setState(state, callback);
  };

  removePointer = (event: React.PointerEvent<HTMLElement> | PointerEvent) => {
    if (touchTimeout) {
      this.resetContextMenuTimer();
    }

    gesture.pointers.delete(event.pointerId);
  };

  toggleLock = (source: "keyboard" | "ui" = "ui") => {
    if (!this.state.activeTool.locked) {
      trackEvent(
        "toolbar",
        "toggleLock",
        `${source} (${this.device.editor.isMobile ? "mobile" : "desktop"})`,
      );
    }
    this.setState((prevState) => {
      return {
        activeTool: {
          ...prevState.activeTool,
          ...updateActiveTool(
            this.state,
            prevState.activeTool.locked
              ? { type: "selection" }
              : prevState.activeTool,
          ),
          locked: !prevState.activeTool.locked,
        },
      };
    });
  };

  updateFrameRendering = (
    opts:
      | Partial<AppState["frameRendering"]>
      | ((
          prevState: AppState["frameRendering"],
        ) => Partial<AppState["frameRendering"]>),
  ) => {
    this.setState((prevState) => {
      const next =
        typeof opts === "function" ? opts(prevState.frameRendering) : opts;
      return {
        frameRendering: {
          enabled: next?.enabled ?? prevState.frameRendering.enabled,
          clip: next?.clip ?? prevState.frameRendering.clip,
          name: next?.name ?? prevState.frameRendering.name,
          outline: next?.outline ?? prevState.frameRendering.outline,
        },
      };
    });
  };

  togglePenMode = (force: boolean | null) => {
    this.setState((prevState) => {
      return {
        penMode: force ?? !prevState.penMode,
        penDetected: true,
      };
    });
  };

  onHandToolToggle = () => {
    this.actionManager.executeAction(actionToggleHandTool);
  };

  /**
   * Zooms on canvas viewport center
   */
  zoomCanvas = (
    /** decimal fraction between 0.1 (10% zoom) and 30 (3000% zoom) */
    value: number,
  ) => {
    this.setState({
      ...getStateForZoom(
        {
          viewportX: this.state.width / 2 + this.state.offsetLeft,
          viewportY: this.state.height / 2 + this.state.offsetTop,
          nextZoom: getNormalizedZoom(value),
        },
        this.state,
      ),
    });
  };

  private cancelInProgressAnimation: (() => void) | null = null;

  scrollToContent = (
    target:
      | ExcalidrawElement
      | readonly ExcalidrawElement[] = this.scene.getNonDeletedElements(),
    opts?:
      | {
          fitToContent?: boolean;
          fitToViewport?: never;
          viewportZoomFactor?: never;
          animate?: boolean;
          duration?: number;
          offsetX?:number;
          isTopLeft?:boolean;
        }
      | {
          fitToContent?: never;
          fitToViewport?: boolean;
          /** when fitToViewport=true, how much screen should the content cover,
           * between 0.1 (10%) and 1 (100%)
           */
          viewportZoomFactor?: number;
          animate?: boolean;
          duration?: number;
          offsetX?:number;
        isTopLeft?:boolean;
        },
  ) => {
    this.cancelInProgressAnimation?.();

    // convert provided target into ExcalidrawElement[] if necessary
    const targetElements = Array.isArray(target) ? target : [target];

    let zoom = this.state.zoom;
    let scrollX = this.state.scrollX;
    let scrollY = this.state.scrollY;
    let nOffset = opts?.offsetX || 0;
    nOffset = document.body.offsetWidth*0.5 - nOffset;
    nOffset /= zoom.value;

    if (opts?.fitToContent || opts?.fitToViewport) {
      const { appState } = zoomToFit({
        targetElements,
        appState: this.state,
        fitToViewport: !!opts?.fitToViewport,
        viewportZoomFactor: opts?.viewportZoomFactor,
      });
      zoom = appState.zoom;
      scrollX = appState.scrollX;
      scrollY = appState.scrollY;
    } else {
      // compute only the viewport location, without any zoom adjustment
      let scroll = calculateScrollCenter(targetElements, this.state);
      if (opts?.isTopLeft){
        scroll = calculateScrollTopLeft(targetElements,this.state);
        scroll.scrollX -= nOffset;
        let yOffset = 40;
        if (!isMac()){
          yOffset = 70;
        }
        scroll.scrollY -= (document.body.offsetHeight*0.5 - HEADER_HEIGHT - yOffset)/zoom.value;
      }
      scrollX = scroll.scrollX;
      scrollY = scroll.scrollY;
    }

    // when animating, we use RequestAnimationFrame to prevent the animation
    // from slowing down other processes
    if (opts?.animate) {
      const origScrollX = this.state.scrollX;
      const origScrollY = this.state.scrollY;
      const origZoom = this.state.zoom.value;

      const cancel = easeToValuesRAF({
        fromValues: {
          scrollX: origScrollX,
          scrollY: origScrollY,
          zoom: origZoom,
        },
        toValues: { scrollX, scrollY, zoom: zoom.value },
        interpolateValue: (from, to, progress, key) => {
          // for zoom, use different easing
          if (key === "zoom") {
            return from * Math.pow(to / from, easeOut(progress));
          }
          // handle using default
          return undefined;
        },
        onStep: ({ scrollX, scrollY, zoom }) => {
          this.setState({
            scrollX,
            scrollY,
            zoom: { value: zoom },
          });
        },
        onStart: () => {
          this.setState({ shouldCacheIgnoreZoom: true });
        },
        onEnd: () => {
          this.setState({ shouldCacheIgnoreZoom: false });
        },
        onCancel: () => {
          this.setState({ shouldCacheIgnoreZoom: false });
        },
        duration: opts?.duration ?? 500,
      });

      this.cancelInProgressAnimation = () => {
        cancel();
        this.cancelInProgressAnimation = null;
      };
    } else {
      this.setState({ scrollX, scrollY, zoom });
    }
  };

  scrollToElementXY = (el:ExcalidrawElement)=>{
    const {x, y} = sceneCoordsToViewportCoords(
        {sceneX: el.x - el.width, sceneY: el.y- el.height},
        this.state,
    );

    // const { x, y } = viewportCoordsToSceneCoords(
    //     { clientX:el.x, clientY:el.y },
    //     this.state,
    // );

    this.setState({
      scrollX:x,
      scrollY:y
    })
  }

  scrollToElementBottom = (el,nNewHeight)=>{
    let nNewY = el.y + nNewHeight * this.state.zoom.value;
    let nMarginBottom = (this.state.height - this.state.scrollY) - nNewY;
    if (nMarginBottom < 10){
      this.setState({
        scrollY:((this.state.scrollY + nMarginBottom))
      })
    }
  }

  private maybeUnfollowRemoteUser = () => {
    if (this.state.userToFollow) {
      this.setState({ userToFollow: null });
    }
  };

  /** use when changing scrollX/scrollY/zoom based on user interaction */
  private translateCanvas: React.Component<any, AppState>["setState"] = (
    state,
  ) => {
    this.cancelInProgressAnimation?.();
    this.maybeUnfollowRemoteUser();
    this.setState(state);
  };

  setToast = (
    toast: {
      message: string;
      closable?: boolean;
      duration?: number;
    } | null,
  ) => {
    this.setState({ toast });
  };

  restoreFileFromShare = async () => {
    try {
      const webShareTargetCache = await caches.open("web-share-target");

      const response = await webShareTargetCache.match("shared-file");
      if (response) {
        const blob = await response.blob();
        const file = new File([blob], blob.name || "", { type: blob.type });
        this.loadFileToCanvas(file, null);
        await webShareTargetCache.delete("shared-file");
        window.history.replaceState(null, APP_NAME, window.location.pathname);
      }
    } catch (error: any) {
      this.setState({ errorMessage: error.message });
    }
  };

  /** adds supplied files to existing files in the appState */
  public addFiles: ExcalidrawImperativeAPI["addFiles"] = withBatchedUpdates(
    (files) => {
      const filesMap = files.reduce((acc, fileData) => {
        acc.set(fileData.id, fileData);
        return acc;
      }, new Map<FileId, BinaryFileData>());

      this.files = { ...this.files, ...Object.fromEntries(filesMap) };

      this.scene.getNonDeletedElements().forEach((element) => {
        if (
          isInitializedImageElement(element) &&
          filesMap.has(element.fileId)
        ) {
          //update  取消删除图片的缓存，避免链接切换的闪动。虽然会消耗内存。。。。。。。。
          // this.imageCache.delete(element.fileId);
          // ShapeCache.delete(element);
        }
      });
      this.scene.informMutation();

      this.addNewImagesToImageCache();
    },
  );

  public updateScene = withBatchedUpdates(
    <K extends keyof AppState>(sceneData: {
      elements?: SceneData["elements"];
      appState?: Pick<AppState, K> | null;
      collaborators?: SceneData["collaborators"];
      commitToHistory?: SceneData["commitToHistory"];
    }) => {

      if (sceneData.commitToHistory) {
        this.history.resumeRecording();
      }

      if (sceneData.appState) {
        this.setState(sceneData.appState);
      }

      if (sceneData.elements) {
        const elements = sceneData.elements;
        const arrayMindmaps = sceneData.elements.filter(element => element.type == 'mindmap' && !element.data);
        for (let i=0;i<arrayMindmaps.length;i++){
          const mindmap = arrayMindmaps[i];
          let cacheData = mindMapId_Data.get(mindmap.id);
          if (cacheData){
            mindmap.data = cacheData;
          }
        }
        this.scene.replaceAllElements(elements);
      }

      if (sceneData.collaborators) {
        this.setState({ collaborators: sceneData.collaborators });
      }
    },
  );

  /**
   * 设置透明度，设置元素透明度
   * @param nAlpha
   * @private
   */
  private updateEmbedAlpha(nAlpha:number){
    this.scene.mapElements((element) => {
      if ((element.type == 'embeddable' && element.frameId == null) || ((isFileElement(element) || isNoteElement(element)) && element.frameId == null)) {
        return newElementWith(element, {
          opacity:nAlpha
        });
      }
      return element;
    });
  }

  public getFrameElementCount(){
    const frames = this.scene.getNonDeletedFramesLikes();
    return (frames.length);
  }

  /**
   * frame预览
   * 预览frame预览预览预览
   * 预览
   * @param isFullScreen
   * @param nLeftOffset
   */
  public updateDisplaySize = (isFullScreen:boolean = false,nLeftOffset:number=0)=>{
    let w = screen.width;
    let h = screen.height;
    if (!isFullScreen){
      w = document.body.clientWidth// - nLeftOffset;
      h = document.body.clientHeight// - 58//58是顶部状态栏占用
    }

    this.setState({
      width: w,
      height: h,
    });

    setTimeout(()=>{
      // alert(this.state.width)
      const frames = this.state.sortArrayFrames//this.scene.getNonDeletedFramesLikes();
      this.scrollToContent(frames[SELECTED_FRAME_INDEX], {
        fitToViewport: true,
        viewportZoomFactor: 1,
      });
    },100);

  }

  /**
   * frame的预览------frame的预览------frame的预览------frame的预览------frame的预览------frame的预览------frame的预览------frame的预览------frame的预览------frame的预览------frame的预览------frame的预览------frame的预览------frame的预览------frame的预览------frame的预览------frame的预览------frame的预览------frame的预览------frame的预览------frame的预览------frame的预览------frame的预览------frame的预览------frame的预览------frame的预览------frame的预览------frame的预览------frame的预览------frame的预览------
   */
  public updateDisplayMode = withBatchedUpdates( (displayMode: DisplayMode) => {
    if (displayMode === "preview") {
      let frames:any = this.state.sortArrayFrames;//this.scene.getNonDeletedFramesLikes();
      if (!frames.length) {
        this.props.onUpdateDisplayMode?.("edit");
        this.setToast({
          message: "No frame exists",
          closable: true,
          duration: 3000,
        });

        return false;
      }

      // @ts-ignore todo 调整至外面
      // const screen = window.ipcRenderer.sendSync("get-screen-size");
      let w = screen.width;
      let h = screen.height;

      //默认不是全屏的。。。。
      // w = document.body.clientWidth;
      // h = document.body.clientHeight;

      // 保存当前编辑模式的数据快照
      // 将背景变成黑色
      // 改变canvas尺寸
      const appStateSnapshot = cloneDeep(this.state);
      this.setState({
        appStateSnapshot,
        viewBackgroundColor: COLOR_PALETTE.black,
        displayMode,
        width: w,
        height: h,
      });
      // 取消选中的元素
      this.setState({ selectedElementIds: {} });
      //将embed透明度置0  frame透明度
      this.updateEmbedAlpha(0);

      // 禁止交互行为
      // 获取frame元素
      //将第一个frame元素铺满全窗口.并将frame元素居中展示
      setTimeout(() => {
        this.changePreviewFrame({
          frame: frames[0],
          frameIndex: SELECTED_FRAME_INDEX,
        });
      }, 10);

      return true;
    }

    this.updateEmbedAlpha(100);

    SELECTED_FRAME_INDEX = 0;
    const appStateSnapshot = this.state.appStateSnapshot || {};
    this.setState({
      width:appStateSnapshot.width,
      height:appStateSnapshot.height,
      displayMode:'edit',
    });
    this.props.onUpdateDisplayMode?.("edit");
    return true;
  });

  public backupAppState(){
    const appStateSnapshot = cloneDeep(this.state);
    this.setState({
      appStateSnapshot
    });
  }
  public restoreAppState(){
    const appStateSnapshot = this.state.appStateSnapshot || {};
    this.setState(appStateSnapshot);
  }

  public changePreviewFrame = withBatchedUpdates(
      ({
         frame,
         frameIndex,
       }: {
        frame: ExcalidrawFrameLikeElement;
        frameIndex: number;
      }) => {
        SELECTED_FRAME_INDEX = frameIndex;
        // frame = this.getSceneElements().find(p=>p.id == frame.id);
        //将当前frame元素铺满全窗口.并将frame元素居中展示
        this.scrollToContent(frame, {
          fitToViewport: true,
          viewportZoomFactor: 1,
        });

        setTimeout(()=>{
          this.setState({
            originZoomValue:this.state.zoom.value
          })
        },200)
      },
  );

  public closeEyeDropper = ()=>{
  }

  getVideoScale = async (url)=> {
    const video = document.createElement('video');
    video.setAttribute("width", "10");
    video.setAttribute("height", "10");
    video.setAttribute("style", "position: absolute;opacity: 0;z-index: -5;top: 0;");
    video.setAttribute("src", url);

    document.body.appendChild(video);

    const p = new Promise(function(resolve, reject) {
      video.addEventListener('canplay', function(e) {
        resolve({videoWidth:e.target.videoWidth, videoHeight:e.target.videoHeight})
      })
      video.addEventListener('error',function (e) {
        reject(e);
      })
    })
    try{
      const res = await p;
      document.body.removeChild(video);
      return res
    }catch (e){
      console.log(e);
    }
    return 0;
  }

  public setSelectElements = (element)=>{
    this.setState({
      selectedElementIds:{[element.id]:true}
    })
  }

  public setSelectElementsById = (szId,callback)=>{
    this.setState({
      selectedElementIds:{[szId]:true}
    },()=>{
      if (callback){
        callback();
      }
    })
  }

  public insertImgElement = (file:any,bWithTips=true,fileImportMode:any)=>{
    this.insertMultiplyImage([file],null,bWithTips,fileImportMode);
  }

  /**
   * 在这个点插入note
   * @param sceneX
   * @param sceneY
   */
  public insertNoteElement = (sceneX:number,sceneY:number,card:any = null,bInsertCanvas = false)=> {
    const element = this.createCardElement(sceneX, sceneY,card);
    //添加到侧边栏
    postMessage(EVENT_ADD_NOTE_BY_CARD, [{id: element.customData.generationData.id, name: card?.name || '', hasTitle:card?.hasTitle??true,type: 'card'}])
    if (!card || bInsertCanvas) {//外面添加
      this.scene.addNewElement(element);
      setTimeout(() => {
        this.setState({
          selectedElementIds: {[element.id]: true}
        })
      }, 100)
    }

    return element;

  }

  public createCardElement = (sceneX:number,sceneY:number,card:any)=>{
    const width = DEFAULT_NOTE_WIDTH;
    const height = DEFAULT_NOTE_HEIGHT_1;
    let x = sceneX;
    let y = sceneY;

    const szDefaultStyle = localStorage.getItem(KEY_CARD_ELEMENT_DEFAULT_STYLE);
    let defaultStyle = {strokeWidth:2,strokeColor:'transparent',backgroundColor:getNoteElementBackgroundColor()};
    if (szDefaultStyle){
      const jsonDefaultStyle = JSON.parse(szDefaultStyle);

      if (jsonDefaultStyle.backgroundColor){
        defaultStyle.backgroundColor = jsonDefaultStyle.backgroundColor;
      }
      if (jsonDefaultStyle.strokeWidth){
        defaultStyle.strokeWidth = jsonDefaultStyle.strokeWidth;
      }
      if (jsonDefaultStyle.strokeColor){
        defaultStyle.strokeColor = jsonDefaultStyle.strokeColor;
      }
      if (jsonDefaultStyle.corner){
        defaultStyle.corner = jsonDefaultStyle.corner;
      }
    }

    const topLayerFrame = this.getTopLayerFrameAtSceneCoords({
      x: sceneX,
      y: sceneY,
    });

    var element = {
      type: "iframe",
      version: 1,
      isDeleted: false,
      id: generateUUID2(),
      strokeWidth: defaultStyle.strokeWidth,
      strokeStyle: "solid",
      roughness: 0,
      roundness: {
        type: ROUNDNESS.ADAPTIVE_RADIUS,
        value: defaultStyle.corner || 8
      },
      opacity: 100,
      angle: 0,
      locked: false,
      x: x,
      y: y,
      created:Date.now(),
      backgroundColor: defaultStyle.backgroundColor,//getNoteElementBackgroundColor(),
      strokeColor: defaultStyle.strokeColor,//getNoteElementStrokeColor(),
      fillStyle: 'solid',
      width: width,
      height: height,
      groupIds: [],
      frameId:topLayerFrame?topLayerFrame.id:null,
      customData: {
        generationData: {
          status: 'done',
          html: card?.doc || '',
          name: card?.name || '',
          type: 'note',
          id: card?.id || generateUUID2(),
          isEditable: card?false:true,
          isExpand: true
        }
      }
    }
    return element;
  }

  /**
   * 插入项目的Link
   * @param sceneX
   * @param sceneY
   */
  public insertProjectLink= (sceneX:number,sceneY:number,projectId:any,boardId:any,deeplink)=>{
    const element:ExcalidrawElement = this.createProjectLink(sceneX,sceneY,projectId,boardId,deeplink);
    this.scene.addNewElement(element);
    setTimeout(()=>{
      this.setState({
        selectedElementIds: {[element.id]:true}
      })

      postMessage(EVENT_CANVAS_REFRESH_ELEMENT_COUNT);
    },100)
  }

  /**
   * 创建项目链接
   * @param sceneX
   * @param sceneY
   * @param projectId
   * @param boardId
   */
  public createProjectLink = (sceneX:number,sceneY:number,projectId:any,boardId:any,deeplink:any,outHeight:any)=>{
    const width = DEFAULT_NOTE_WIDTH;
    let height = width*PROJECT_THUMB_W_H;//没有图的时候是159
    if (outHeight){
      height = outHeight;
    }
    let x = sceneX;
    let y = sceneY;
    let nStrokeWidth = 1;
    let szStrokeColor = 'transparent';
    let szBackgroundColor = getProjectLinkElementBackground();
    if (deeplink){
      if (deeplink.backgroundColor){
        szBackgroundColor = deeplink.backgroundColor;
      }
      // if (deeplink.strokeColor){
      //   nStrokeWidth = DEFAULT_ELEMENT_STROKE_WITHD;
      //   szStrokeColor = deeplink.strokeColor;
      // }
    }
    var element = {
      type: "projectLink",
      version: 1,
      isDeleted: false,
      id: generateUUID2(),
      strokeWidth: nStrokeWidth,
      strokeStyle: "solid",
      roughness:0,
      roundness: {
        type:ROUNDNESS.ADAPTIVE_RADIUS,
        value:8
      },
      created:Date.now(),
      opacity: 100,
      angle: 0,
      locked:false,
      x: x,
      y: y,
      backgroundColor:szBackgroundColor,
      strokeColor:szStrokeColor,
      fillStyle:'solid',
      width: width,
      height: height,
      groupIds: [],
      customData:{
        projectId:projectId,
        boardId:boardId
      }
    }
    return element;
  }

  /**
   * 插入sticknotes....
   * @param x
   * @param y
   */
  public insertStickyNotes(x,y,callback:any){
    const stickyNotesElement = this.createStickyNotesElement({
      sceneX: x,
      sceneY: y,
      addToFrameUnderCursor: false,
      backgroundColor: localStorage.getItem(KEY_DEFAULT_STICKYNOTE_BACKGROUND_COLOR) || DEFAULT_STICKYNOTE_BACKGROUND_COLOR,
    });

    this.scene.addNewElement(stickyNotesElement);
    this.setState({
      selectedElementIds: { [stickyNotesElement.id]: true },
      activeStickyNotes: { element: stickyNotesElement, state: "active" },
    });
    if (callback){
      callback(stickyNotesElement);
    }
  }

    /**
     * 插入sticknotes....
     * @param x
     * @param y
     */
    public insertTextTiptap(x,y,callback:any){
        const stickyNotesElement = this.createTextTiptapElement({
            sceneX: x,
            sceneY: y,
            addToFrameUnderCursor: true,
            backgroundColor: localStorage.getItem(KEY_DEFAULT_TEXT_BACKGROUND_COLOR) || COLOR_PALETTE.transparent,
        });

        this.scene.addNewElement(stickyNotesElement);
        this.setState({
            selectedElementIds: { [stickyNotesElement.id]: true },
            activeStickyNotes: { element: stickyNotesElement, state: "active" },
        });
        if (callback){
            callback(stickyNotesElement);
        }
    }

  /**
   * 从library中插入图片
   * @param library
   * @param point
   */
  public async insertLibrayImage(library:any,point:any,isAddCanvas:any = true,deeplink){
    let ptX = point.x;
    let ptY = point.y;
    let szOriBase64 = await ipcInvoke('read-file-base64',{filePath:(library.isAbsolutePath?'':localStorage.getItem(KEY_FILE_DIR))+library.fileName});
    let szBase64 = 'data:image/'+getFileNameExt(library.fileName)+';base64,'+szOriBase64;
    if (isSvg(library.fileName)){
      szBase64 = 'data:image/svg+xml;base64,'+szOriBase64;
    }
    const imgSize = await getBase64ImageSize(szBase64);
    // console.log('get pdf thumb.....end...')
    let w = DEFAULT_NOTE_WIDTH;
    let h = w*(imgSize?.height/imgSize?.width);
    if (!h){
      h = w*1.4;
    }

    let szStrokeColor = "#00000000";
    let strokeWidth =  0.001;
    if (deeplink){
      if (deeplink.strokeColor && deeplink.strokeColor != 'transparent'){
        szStrokeColor = deeplink.strokeColor;
        strokeWidth = DEFAULT_ELEMENT_STROKE_WITHD;//默认4
      }
    }

    var element = {
      type: "image",
      version: 1,
      isDeleted: false,
      id: generateRandomPageKey(),
      fileId:getFileNameWithoutExt(library.fileName),//这里ID直接用没有扩展名的实际文件名称
      strokeWidth: strokeWidth,
      strokeStyle: "solid",
      opacity: 100,
      angle: 0,
      locked:false,
      x: ptX-130,
      y: ptY-h/2,
      strokeColor: szStrokeColor,
      backgroundColor: "transparent",
      fillStyle:'solid',
      roundness: null,//{type:ROUNDNESS.ADAPTIVE_RADIUS,value:8},
      width: w,
      height: h,
      hasUpdate:true,
      groupIds: [],
      isLockRatio:true,
      name:library?.name,
      customData:{generationData: {id: library.id, name: library.name,type:'image',infoNotes:library.infoNotes,infoUrl:library.infoUrl}}
    }
    const arraySplits = szBase64.split(',');
    const szMimeType = arraySplits[0].replace("data:","").replace(";base64","");
    await this.addNewImageFiles(szMimeType,element.fileId,szBase64,library.isAbsolutePath?library.fileName:'');
    if (isAddCanvas){
      this.updateScene({
        elements:[...this.getSceneElements(),element]
      })
    }

    return element;
  }

   public async insertBase64ImageElement(szBase64:string,szImportMode:any,fullPath:any){
    const {x,y}= this.getCurrentCenterPoint();
    let ptX = x;
    let ptY = y;

    const imgSize = await getBase64ImageSize(szBase64);
    // console.log('get pdf thumb.....end...')
    let w = DEFAULT_NOTE_WIDTH;
    let h = w*(imgSize?.height/imgSize?.width);
    if (!h){
      h = w*1.4;
    }

    var element = {
      type: "image",
      version: 1,
      isDeleted: false,
      id: generateRandomPageKey(),
      fileId:generateUUID2(),
      strokeWidth: 0.0000001,
      strokeStyle: "solid",
      roughness:0,
      opacity: 100,
      angle: 0,
      locked:false,
      x: ptX-130,
      y: ptY-h/2,
      strokeColor: "white",
      backgroundColor: "white",
      fillStyle:'solid',
      width: w,
      height: h,
      groupIds: [],
      isLockRatio:true,
    }
    const arraySplits = szBase64.split(',');
    const szMimeType = arraySplits[0].replace("data:","").replace(";base64","");
    //保存到文件
     if (szImportMode == IMPORT_FILE_MODE_IMPORTMODE){
       fullPath = '';
       await ipcInvoke('write-base64-image',{fileName:element.fileId+'.'+extFromMineType(szMimeType),imgBase64:szBase64});
     }
     var item = {
       mimeType: szMimeType,
       id: element.fileId,
       dataURL: '',
       created: Date.now(),
       lastRetrieved: Date.now(),
       fullPath
     }
    this.addFiles([item]);
    this.updateScene({
      elements:[...this.getSceneElements(),element]
    })
  }

  public insertImageElementWithCursor(imageFile:any){
    const clientX = this.state.width / 2 + this.state.offsetLeft;
    const clientY = this.state.height / 2 + this.state.offsetTop;

    const { x, y } = viewportCoordsToSceneCoords(
        { clientX, clientY },
        this.state,
    );

    const imageElement = this.createImageElement({
      sceneX: x,
      sceneY: y,
      addToFrameUnderCursor: false,
    });

    this.setState(
        {
          pendingImageElementId: imageElement.id,
        },
        () => {
          this.insertImageElement(
              imageElement,
              imageFile,
              /* showCursorImagePreview */ true,
          );
        },
    );
  }

  public insertFileElement = async (szFileName:string,szCardId:any,point=undefined,isHidden=false,file:any,elementId:any = undefined,url:any = undefined,isSaveToCard = true,isAbsolutePath:boolean = false,szName:any = '',isAddToCanvas = true,deeplink = null)=>{
    let szImgBase64:any = '';
    const {x,y}= this.getCurrentCenterPoint();
    let ptX = x;
    let ptY = y;
    let isInCenter = true;
    if (global.g_insertPosition && global.g_insertPosition.x && global.g_insertPosition.y){
      isInCenter = false;
      ptX = global.g_insertPosition.x;
      ptY = global.g_insertPosition.y;

      //用过，不要了.
      global.g_insertPosition = null;
    }

    global.g_importFileCount ++;

    if (global.g_importFileCount > 5){
      global.g_importFileCount = global.g_importFileCount%5;
    }
    if (global.g_importFileCount){
      ptX += (global.g_importFileCount*20)
      ptY += (global.g_importFileCount*20)
    }
    if (point){
      isInCenter = false;
      ptX = point.x;
      ptY = point.y;
    }
    let w = DEFAULT_NOTE_WIDTH,h = 210;
    if (isVideo(szFileName)){

      let wh:any = 0;
      if (isWebApp() && file){
        const fileUrl = URL.createObjectURL(file);
        wh = await this.getVideoScale(fileUrl);
        URL.revokeObjectURL(fileUrl);
      }else{
        wh = await this.getVideoScale(getLocalFilePath(szFileName,isAbsolutePath));
      }

      w = DEFAULT_NOTE_WIDTH;
      h = w*(wh.videoHeight/wh.videoWidth);
    }else if (isPdf(szFileName)){
      // const res = await ipcInvoke('read-file-absolute-path',{filePath:localStorage.getItem(KEY_FILE_DIR)+szFileName});
      // const blob = new Blob([res]);
      // const url = URL.createObjectURL(blob);
      // console.log('get pdf thumb.....')

      if (isWebApp() && file){
        const fileUrl = URL.createObjectURL(file);
        szImgBase64 = await getPdfImageThumb(fileUrl);
        URL.revokeObjectURL(fileUrl);
      }else{
        szImgBase64 = await getPdfImageThumb(getLocalFilePath(szFileName,isAbsolutePath));
      }

      const imgSize = await getBase64ImageSize(szImgBase64);
      // console.log('get pdf thumb.....end...')
      w = DEFAULT_NOTE_WIDTH;
      h = (w*imgSize?.height/imgSize?.width);
      if (!h){
        h = w*1.4;
      }
      // h = w*1.4;
    }else if (isAudio(szFileName)){
      //音频
      w = DEFAULT_AUDIO_WIDTH;
      h = DEFAULT_AUDIO_HEIGHT;
    }

    let nOpacity = 100;
    if (isHidden){
      nOpacity = 0;
      h = w = 0.0001;
    }

    let id = generateRandomPageKey();
    if (elementId){
      id = elementId;
    }

    let highlight = await dbGetHighlight(szCardId);
    let videoNotes;
    if (highlight){
      videoNotes = highlight.data;
      videoCache.set(id,videoNotes);
    }

    //缩略图保存成图片保存在本地
    let szSrc = '';
    if (szImgBase64){
      const szFileName = szCardId??id + ".png";
      await ipcInvoke('write-base64-image',{fileName:szFileName,imgBase64:szImgBase64});
      szSrc = getScheme()+szFileName;
    }

    let nStrokeWidth = 0.0000001;
    let szStrokeColor = 'transparent';
    if (deeplink){
      if (deeplink.strokeColor){
        nStrokeWidth = DEFAULT_ELEMENT_STROKE_WITHD;
        szStrokeColor = deeplink.strokeColor;
      }
    }

    var element = {
      type: "iframe",
      version: 1,
      isDeleted: false,
      id: generateUUID2(),
      strokeWidth: nStrokeWidth,
      strokeStyle: "solid",
      roughness:0,
      roundness: {
        type:ROUNDNESS.ADAPTIVE_RADIUS,
        value:8
      },
      opacity: nOpacity,
      angle: 0,
      locked:false,
      x: isInCenter?ptX-130:ptX,
      y: isInCenter?ptY-h/2:ptY,
      strokeColor: szStrokeColor,
      backgroundColor: "white",
      fillStyle:'solid',
      width: w,
      height: h,
      groupIds: [],
      name:szName || getFileNameWithoutExt(szFileName),
      isLockRatio:true,
      isHidden:isHidden,
      videoNotes:videoNotes,
      created:Date.now(),
      customData: {
        generationData: {
          createTime:Date.now(),
          status: 'done',
          html: createFileElementHtml(id,isWebApp()?url:szFileName,getFileNameWithoutExt(szFileName),szSrc),
          type:'file',
          fileName:isWebApp()?url:szFileName,
          showName:getFileNameWithoutExt(szFileName),
          isShowName:true,
          name:getFileNameWithoutExt(szFileName),
          id:szCardId??id,
          isAbsolutePath:isAbsolutePath
        }
      }
    }


    if (!szCardId && isSaveToCard /*&& !isAbsolutePath*/){//没有card id 的插入新的....
      //保存到card library
      const card:any = {
        id:id,
        name:element.customData?.generationData?.showName,
        fileName:element.customData?.generationData?.fileName,
        isAbsolutePath:isAbsolutePath
      }
      let szType = 'file';
      if (isPdf(szFileName)){
        szType = 'pdf';
        if (isWebApp()){
          // szImgBase64 = await this.resizeBase64Image(szVideoThumb);
        }
        card.thumbImage = szSrc;//szImgBase64;
      }else if (isVideo(szFileName)){
        szType = 'video';
        let fileUrl = null;
        if (file){
          fileUrl = URL.createObjectURL(file);
        }
        let szVideoThumb = await getVideoImageThumb((isWebApp() && fileUrl)?fileUrl:getLocalFilePath(szFileName,isAbsolutePath));
        URL.revokeObjectURL(fileUrl);
        if (isWebApp()){
          szVideoThumb = await this.resizeBase64Image(szVideoThumb);
        }
        //保存到本地
        const szLocalFileName = card.id + '.png';
        await ipcInvoke('write-base64-image',{fileName:szLocalFileName,imgBase64:szVideoThumb});

        card.thumbImage = getScheme()+szLocalFileName;
      }else if (isAudio(szFileName)){
        szType = 'audio';
      }
      card.type = szType;
      updateCard(card);
    }

    //刷新文字....
    setTimeout(()=>{
      postMessage(EVENT_CANVAS_REFRESH_ELEMENT_COUNT);
    },10)

    if (isAddToCanvas){
      this.updateScene({
        elements:[...this.getSceneElements(),element]
      })
    }

    return element;
  }

  /**
   * 根据图片base64 添加到图片集合，并且添加到缓存.
   * @param mimeType
   * @param id
   * @param szBase64
   */
  public async addNewImageFiles(szMimeType:string,szId:string,szBase64:string,fullPath){
    var item:any = {
      mimeType: szMimeType,
      id: szId,
      dataURL: isSvg(szMimeType)?szBase64:'',//szBase64,
      created: Date.now(),
      lastRetrieved: Date.now(),
      fullPath:fullPath
    }
    await this.createImageToCache(szId,szBase64,szMimeType,fullPath)
    this.addFiles([item]);
  }
  /**
   * 调整图片大小
   * @param szImgBase64
   */
  private async resizeBase64Image(szImgBase64:string){
    //缩略图搞小点
    try{
      let file = new File([base64ToBlob(szImgBase64,MIME_TYPES.png)],'preview',{ type: MIME_TYPES.png });
      const newImageFile = await resizeImageFile(file,
          {
            outputType: MIME_TYPES.jpg,
            maxWidthOrHeight: 260,
          });
      let szImgBase64 = await fileToBase64(newImageFile);
      return szImgBase64;
    }catch (e){
      console.log('thumb resize err...'+e);
    }
    return '';
  }


  private onSceneUpdated = () => {
    this.setState({});
  };

  /**
   * @returns whether the menu was toggled on or off
   */
  public toggleSidebar = ({
    name,
    tab,
    force,
  }: {
    name: SidebarName;
    tab?: SidebarTabName;
    force?: boolean;
  }): boolean => {
    let nextName;
    if (force === undefined) {
      nextName = this.state.openSidebar?.name === name ? null : name;
    } else {
      nextName = force ? name : null;
    }
    this.setState({ openSidebar: nextName ? { name: nextName, tab } : null });

    return !!nextName;
  };

  private updateCurrentCursorPosition = withBatchedUpdates(
    (event: MouseEvent) => {
      this.lastViewportPosition.x = event.clientX;
      this.lastViewportPosition.y = event.clientY;
    },
  );

  // Input handling
  private onKeyDown = withBatchedUpdates(
    async (event: React.KeyboardEvent | KeyboardEvent) => {
      // normalize `event.key` when CapsLock is pressed #2372

      if (
        "Proxy" in window &&
        ((!event.shiftKey && /^[A-Z]$/.test(event.key)) ||
          (event.shiftKey && /^[a-z]$/.test(event.key)))
      ) {
        event = new Proxy(event, {
          get(ev: any, prop) {
            const value = ev[prop];
            if (typeof value === "function") {
              // fix for Proxies hijacking `this`
              return value.bind(ev);
            }
            return prop === "key"
              ? // CapsLock inverts capitalization based on ShiftKey, so invert
                // it back
                event.shiftKey
                ? ev.key.toUpperCase()
                : ev.key.toLowerCase()
              : value;
          },
        });
      }

      // if (this.state.displayMode === "preview" && event.key === KEYS.ESCAPE) {
      //   this.updateDisplayMode("edit");
      //   return;
      // }

      if (event[KEYS.CTRL_OR_CMD] && event.key.toLowerCase() === KEYS.V) {
        IS_PLAIN_PASTE = event.shiftKey;
        clearTimeout(IS_PLAIN_PASTE_TIMER);
        // reset (100ms to be safe that we it runs after the ensuing
        // paste event). Though, technically unnecessary to reset since we
        // (re)set the flag before each paste event.
        IS_PLAIN_PASTE_TIMER = window.setTimeout(() => {
          IS_PLAIN_PASTE = false;
        }, 100);
      }

      // prevent browser zoom in input fields
      if (event[KEYS.CTRL_OR_CMD] && isWritableElement(event.target)) {
        if (event.code === CODES.MINUS || event.code === CODES.EQUAL) {
          event.preventDefault();
          return;
        }
      }

      // bail if
      if (
        // inside an input
        (isWritableElement(event.target) &&
          // unless pressing escape (finalize action)
          event.key !== KEYS.ESCAPE) ||
        // or unless using arrows (to move between buttons)
        (isArrowKey(event.key) && isInputLike(event.target))
      ) {
        return;
      }

      if (event.key === KEYS.QUESTION_MARK) {
        // this.setState({
        //   openDialog: { name: "help" },
        // });
        // return;
      } else if (
        event.key.toLowerCase() === KEYS.E &&
        event.shiftKey &&
        event[KEYS.CTRL_OR_CMD]
      ) {
        // event.preventDefault();
        // this.setState({ openDialog: { name: "imageExport" } });
        return;
      }

      if (event.key === KEYS.PAGE_UP || event.key === KEYS.PAGE_DOWN) {
        let offset =
          (event.shiftKey ? this.state.width : this.state.height) /
          this.state.zoom.value;
        if (event.key === KEYS.PAGE_DOWN) {
          offset = -offset;
        }
        if (event.shiftKey) {
          this.translateCanvas((state) => ({
            scrollX: state.scrollX + offset,
          }));
        } else {
          this.translateCanvas((state) => ({
            scrollY: state.scrollY + offset,
          }));
        }
      }

      if (
          event[KEYS.CTRL_OR_CMD] &&
          event.key.toLowerCase() === KEYS.Z &&
          previousMindMapElement &&
          previousMindMapElementIndex
      ) {
        const mindMap = mindMapCache.get(previousMindMapElement.id);
        mindMap?.execCommand("BACK");
        return;
      }

      // if (this.state.activeTool.type != 'laser'){
      //   if (this.actionManager.handleKeyDown(event)) {
      //     // return;
      //   }
      // }


      if (
          (event.key === KEYS.BACKSPACE || event.key === KEYS.DELETE) &&
          !event[KEYS.CTRL_OR_CMD] &&
          (event.nativeEvent.isActiveNodeRoot || activeNode?.node?.isRoot)
      )
      {
        window.showMessageBox(i18next.t("Tips"),i18next.t("DeleteTheMindmap"),i18next.t("Cancel"),i18next.t("Delete"),null,async ()=>{
          // this.history.undoOnce();

          const mindMap = mindMapCache.get(activeNode.element.id);
          mindMap?.destroy();
          mindMapCache.delete(activeNode.element.id);
          hiddenToolbar();
          this.setState(
              {
                selectedElementIds: { [activeNode.element.id]: true },
              },
              () => {
                this.actionManager.executeAction(actionDeleteSelected);
              },
          );
          //
          // const deletingElements  = this.state.mindmapOperations?.deletingElements;
          //
          // if (deletingElements?.length) {
          //   deletingElements.forEach((id) => {
          //     const mindMap = mindMapCache.get(id);
          //     mindMap?.destroy();
          //     mindMapCache.delete(id);
          //     this.setState(
          //         {
          //           mindmapOperations: {
          //             deletingElements: [],
          //             status: "done",
          //           },
          //         },
          //         () => {
          //           this.setState(
          //               {
          //                 selectedElementIds: {
          //                   [id]: true,
          //                 },
          //               },
          //               () => {
          //                 this.actionManager.executeAction(actionDeleteSelected);
          //                 this.setState({
          //                   mindmapOperations: {
          //                     deletingElements: [],
          //                     status: "init",
          //                   },
          //                 });
          //               },
          //           );
          //         },
          //     );
          //   });
          // }
        });
        /*
        Modal.info({
          centered:true,
          title: "Deleting nested item",
          content: (
              <div>
                1 nested item will be deleted. <br />
                This action cannot be undone.
              </div>
          ),
          onOk: () => {
            this.history.undoOnce();

            const deletingElements  = this.state.mindmapOperations?.deletingElements;

            if (deletingElements?.length) {
              deletingElements.forEach((id) => {
                const mindMap = mindMapCache.get(id);
                mindMap?.destroy();
                mindMapCache.delete(id);
                this.setState(
                    {
                      mindmapOperations: {
                        deletingElements: [],
                        status: "done",
                      },
                    },
                    () => {
                      this.setState(
                          {
                            selectedElementIds: {
                              [id]: true,
                            },
                          },
                          () => {
                            this.actionManager.executeAction(actionDeleteSelected);
                            this.setState({
                              mindmapOperations: {
                                deletingElements: [],
                                status: "init",
                              },
                            });
                          },
                      );
                    },
                );
              });

              return;
            }

            const mindMapIds = this.scene
                .getNonDeletedElements()
                .filter((el) => isMindmapElement(el))
                .map((el) => el.id);

            mindMapIds.forEach((id) => {
              const mindMap = mindMapCache.get(id);
              mindMap?.destroy();
              mindMapCache.delete(id);
              this.setState(
                  {
                    mindmapOperations: {
                      deletingElements: [],
                      status: "done",
                    },
                  },
                  () => {
                    this.setState(
                        {
                          selectedElementIds: {
                            [id]: true,
                          },
                        },
                        () => {
                          this.actionManager.executeAction(actionDeleteSelected);
                          this.setState({
                            mindmapOperations: {
                              deletingElements: [],
                              status: "init",
                            },
                          });
                        },
                    );
                  },
              );
            });
          },
          okCancel: true,
          okText: "Delete",
          okType: "danger",
        });
        */
        return;
      }

      const selectedElements = this.scene.getSelectedElements({
        selectedElementIds: this.state.selectedElementIds,
        includeBoundTextElement: true,
        includeElementsInFrames: false,
      });
      //如果选择的是mindmap
      const hasMindmap = selectedElements.some(p=>isMindmapElement(p));
      const hasImage = selectedElements.some(p=>isImageElement(p));
      if ((event.key == KEYS.BACKSPACE || event.key == KEYS.DELETE) && hasMindmap){
        window.showMessageBox(i18next.t("Tips"),i18next.t("DeleteTheMindmap"),i18next.t("Cancel"),i18next.t("Delete"),null,async ()=>{
          if (this.actionManager.handleKeyDown(event)) {
            return;
          }
        });
      }else if ((event.key == KEYS.BACKSPACE || event.key == KEYS.DELETE) && hasImage){
        const szTrue = localStorage.getItem(KEY_DONOT_SHOW_REMOVE_IMAGE_TIP) || 'false';
        if (szTrue !== 'true'){
          const arraySelectedElements = this.getSelectedElements();
          const nImageCount = arraySelectedElements.filter(p=>isImageElement(p)).length;
          window.showMessageBox(i18next.t("Remove image"),nImageCount>1?i18next.t("SureToRemoveImages"):i18next.t("SureToRemoveImage"),i18next.t("Cancel"),i18next.t("Remove"),null,()=>{
            if (this.actionManager.handleKeyDown(event)) {
              return;
            }
          });
        }else{
          if (this.actionManager.handleKeyDown(event)) {
            return;
          }
        }
      }
      else{
        if (this.actionManager.handleKeyDown(event)) {
          return;
        }
      }

      if (this.state.viewModeEnabled) {
        return;
      }

      if (event[KEYS.CTRL_OR_CMD] && this.state.isBindingEnabled) {
        this.setState({ isBindingEnabled: false });
      }


      if (isArrowKey(event.key)) {
        const step =
          (this.state.gridSize &&
            (event.shiftKey
              ? ELEMENT_TRANSLATE_AMOUNT
              : this.state.gridSize)) ||
          (event.shiftKey
            ? ELEMENT_SHIFT_TRANSLATE_AMOUNT
            : ELEMENT_TRANSLATE_AMOUNT);

        let offsetX = 0;
        let offsetY = 0;

        if (event.key === KEYS.ARROW_LEFT) {
          offsetX = -step;
        } else if (event.key === KEYS.ARROW_RIGHT) {
          offsetX = step;
        } else if (event.key === KEYS.ARROW_UP) {
          offsetY = -step;
        } else if (event.key === KEYS.ARROW_DOWN) {
          offsetY = step;
        }

        selectedElements.forEach((element) => {
          mutateElement(element, {
            x: element.x + offsetX,
            y: element.y + offsetY,
          });

          updateBoundElements(element, {
            simultaneouslyUpdated: selectedElements,
          });
        });

        this.maybeSuggestBindingForAll(selectedElements);

        event.preventDefault();
      } else if (event.key === KEYS.ENTER) {
        const selectedElements = this.scene.getSelectedElements(this.state);
        if (selectedElements.length === 1) {
          const selectedElement = selectedElements[0];
          if (event[KEYS.CTRL_OR_CMD]) {
            if (isLinearElement(selectedElement)) {
              if (
                !this.state.editingLinearElement ||
                this.state.editingLinearElement.elementId !==
                  selectedElements[0].id
              ) {
                this.history.resumeRecording();
                this.setState({
                  editingLinearElement: new LinearElementEditor(
                    selectedElement,
                    this.scene,
                  ),
                });
              }
            }
          } else if (
            isTextElement(selectedElement) ||
            isValidTextContainer(selectedElement)
          ) {
            let container;
            if (!isTextElement(selectedElement)) {
              container = selectedElement as ExcalidrawTextContainer;
            }
            const midPoint = getContainerCenter(selectedElement, this.state);
            const sceneX = midPoint.x;
            const sceneY = midPoint.y;
            this.startTextEditing({
              sceneX,
              sceneY,
              container,
            });
            event.preventDefault();
            return;
          } else if (isFrameLikeElement(selectedElement)) {
            this.setState({
              editingFrame: selectedElement.id,
            });
          }
        }
      } else if (
        !event.ctrlKey &&
        !event.altKey &&
        !event.metaKey &&
        this.state.draggingElement === null
      ) {
        if (event.key == KEYS.T || event.key == KEYS.L || event.key == KEYS.F || event.key == KEYS.R){
          if (event.key == KEYS.L){
            global.g_forceStraight = true;
          }
          this.forceSetCursor(CURSOR_TYPE.CROSSHAIR);
        }
        const shape = await findShapeByKey(event.key);
        if (shape) {
          if (this.state.activeTool.type !== shape) {
            trackEvent(
              "toolbar",
              shape,
              `keyboard (${
                this.device.editor.isMobile ? "mobile" : "desktop"
              })`,
            );
          }
          //放在外面处理.....
          this.setActiveTool({ type: shape });
          // event.stopPropagation();
        } else if (event.key === KEYS.Q) {
          this.toggleLock("keyboard");
          event.stopPropagation();
        }
      }
      if (event.key === KEYS.SPACE && gesture.pointers.size === 0) {
        isHoldingSpace = true;
        setCursor(this.interactiveCanvas, CURSOR_TYPE.GRAB);
        event.preventDefault();
      }

      if (
        (event.key === KEYS.G || event.key === KEYS.S) &&
        !event.altKey &&
        !event[KEYS.CTRL_OR_CMD]
      ) {
        const selectedElements = this.scene.getSelectedElements(this.state);
        if (
          this.state.activeTool.type === "selection" &&
          !selectedElements.length
        ) {
          return;
        }

        if (
          event.key === KEYS.G &&
          (hasBackground(this.state.activeTool.type) ||
            selectedElements.some((element) => hasBackground(element.type)))
        ) {
          this.setState({ openPopup: "elementBackground" });
          event.stopPropagation();
        }
        if (event.key === KEYS.S) {
          this.setState({ openPopup: "elementStroke" });
          event.stopPropagation();
        }
      }

      // if (event.key === KEYS.K && !event.altKey && !event[KEYS.CTRL_OR_CMD]) {
      //   if (this.state.activeTool.type === "laser") {
      //     this.setActiveTool({ type: "selection" });
      //   } else {
      //     this.setActiveTool({ type: "laser" });
      //   }
      //   return;
      // }

      if (
        event[KEYS.CTRL_OR_CMD] &&
        (event.key === KEYS.BACKSPACE || event.key === KEYS.DELETE)
      ) {
        // jotaiStore.set(activeConfirmDialogAtom, "clearCanvas");
        postMessage(EVENT_CLEAR_CANVAS)
      }

      // eye dropper
      // -----------------------------------------------------------------------
      const lowerCased = event.key.toLocaleLowerCase();
      const isPickingStroke = lowerCased === KEYS.S && event.shiftKey;
      const isPickingBackground =
        event.key === KEYS.I || (lowerCased === KEYS.G && event.shiftKey);

      if (isPickingStroke || isPickingBackground) {
        this.openEyeDropper({
          type: isPickingStroke ? "stroke" : "background",
        });
      }
      // -----------------------------------------------------------------------
    },
  );

  private onWheel = withBatchedUpdates((event: WheelEvent) => {
    // prevent browser pinch zoom on DOM elements
    if (!(event.target instanceof HTMLCanvasElement) && event.ctrlKey) {
      event.preventDefault();
    }
  });

  private onKeyUp = withBatchedUpdates((event: KeyboardEvent) => {
    if (event.key === KEYS.SPACE) {
      if (this.state.viewModeEnabled) {
        setCursor(this.interactiveCanvas, CURSOR_TYPE.GRAB);
      } else if (this.state.activeTool.type === "selection") {
        resetCursor(this.interactiveCanvas);
      } else {
        setCursorForShape(this.interactiveCanvas, this.state);
        this.setState({
          selectedElementIds: makeNextSelectedElementIds({}, this.state),
          selectedGroupIds: {},
          editingGroupId: null,
          activeEmbeddable: null,
          activeStickyNotes: null,
          activeMindMap: null,
        });
      }
      isHoldingSpace = false;
    }
    if (!event[KEYS.CTRL_OR_CMD] && !this.state.isBindingEnabled) {
      this.setState({ isBindingEnabled: true });
    }
    if (isArrowKey(event.key)) {
      const selectedElements = this.scene.getSelectedElements(this.state);
      isBindingEnabled(this.state)
        ? bindOrUnbindSelectedElements(selectedElements)
        : unbindLinearElements(selectedElements);
      this.setState({ suggestedBindings: [] });
    }
  });

  // We purposely widen the `tool` type so this helper can be called with
  // any tool without having to type check it
  private isToolSupported = <T extends ToolType | "custom">(tool: T) => {
    return (
      this.props.UIOptions.tools?.[
        tool as Extract<T, keyof AppProps["UIOptions"]["tools"]>
      ] !== false
    );
  };

  setActiveTool = (
    tool: (
      | (
          | { type: Exclude<ToolType, "image"> }
          | {
              type: Extract<ToolType, "image">;
              insertOnCanvasDirectly?: boolean;
            }
        )
      | { type: "custom"; customType: string }
    ) & { locked?: boolean },
  ) => {
    if (!this.isToolSupported(tool.type)) {
      console.warn(
        `"${tool.type}" tool is disabled via "UIOptions.canvasActions.tools.${tool.type}"`,
      );
      return;
    }

    const nextActiveTool = updateActiveTool(this.state, tool);
    if (nextActiveTool.type === "hand") {
      setCursor(this.interactiveCanvas, CURSOR_TYPE.GRAB);
    } else if (!isHoldingSpace) {
      setCursorForShape(this.interactiveCanvas, this.state);
    }
    if (isToolIcon(document.activeElement)) {
      this.focusContainer();
    }
    if (!isLinearElementType(nextActiveTool.type)) {
      this.setState({ suggestedBindings: [] });
    }
    if (nextActiveTool.type === "image") {
      this.onImageAction({
        insertOnCanvasDirectly:
          (tool.type === "image" && tool.insertOnCanvasDirectly) ?? false,
      });
    }

    if (nextActiveTool.type === "mindmap") {
      this.onMindmapAction({});
    }

    if (nextActiveTool.type === "stickyNotes") {
      // @ts-ignore
      this.onStickyNotesAction({});
    }


    this.setState((prevState) => {
      const commonResets = {
        snapLines: prevState.snapLines.length ? [] : prevState.snapLines,
        originSnapOffset: null,
        activeEmbeddable: null,
        activeStickyNotes: null,
        activeMindMap: null,
      } as const;
      if (nextActiveTool.type !== "selection") {
        return {
          ...prevState,
          activeTool: { ...nextActiveTool, activeToolFirstClicked: true },
          selectedElementIds: makeNextSelectedElementIds({}, prevState),
          selectedGroupIds: makeNextSelectedElementIds({}, prevState),
          editingGroupId: null,
          multiElement: null,
          ...commonResets,
        };
      }
      return {
        ...prevState,
        activeTool: nextActiveTool,
        ...commonResets,
      };
    });
  };

  setOpenDialog = (dialogType: AppState["openDialog"]) => {
    this.setState({ openDialog: dialogType });
  };

  private setCursor = (cursor: string) => {
    setCursor(this.interactiveCanvas, cursor);
  };

  private resetCursor = () => {
    resetCursor(this.interactiveCanvas);
  };
  /**
   * returns whether user is making a gesture with >= 2 fingers (points)
   * on o touch screen (not on a trackpad). Currently only relates to Darwin
   * (iOS/iPadOS,MacOS), but may work on other devices in the future if
   * GestureEvent is standardized.
   */
  private isTouchScreenMultiTouchGesture = () => {
    // we don't want to deselect when using trackpad, and multi-point gestures
    // only work on touch screens, so checking for >= pointers means we're on a
    // touchscreen
    return gesture.pointers.size >= 2;
  };

  // fires only on Safari
  private onGestureStart = withBatchedUpdates((event: GestureEvent) => {
    event.preventDefault();

    // we only want to deselect on touch screens because user may have selected
    // elements by mistake while zooming
    if (this.isTouchScreenMultiTouchGesture()) {
      this.setState({
        selectedElementIds: makeNextSelectedElementIds({}, this.state),
        activeEmbeddable: null,
        activeStickyNotes: null,
        activeMindMap: null,
      });
    }
    gesture.initialScale = this.state.zoom.value;
  });

  // fires only on Safari
  private onGestureChange = withBatchedUpdates((event: GestureEvent) => {
    event.preventDefault();

    // onGestureChange only has zoom factor but not the center.
    // If we're on iPad or iPhone, then we recognize multi-touch and will
    // zoom in at the right location in the touchmove handler
    // (handleCanvasPointerMove).
    //
    // On Macbook trackpad, we don't have those events so will zoom in at the
    // current location instead.
    //
    // As such, bail from this handler on touch devices.
    if (this.isTouchScreenMultiTouchGesture()) {
      return;
    }

    const initialScale = gesture.initialScale;
    if (initialScale) {
      this.setState((state) => ({
        ...getStateForZoom(
          {
            viewportX: this.lastViewportPosition.x,
            viewportY: this.lastViewportPosition.y,
            nextZoom: getNormalizedZoom(initialScale * event.scale),
          },
          state,
        ),
      }));
    }
  });

  // fires only on Safari
  private onGestureEnd = withBatchedUpdates((event: GestureEvent) => {
    event.preventDefault();
    // reselect elements only on touch screens (see onGestureStart)
    if (this.isTouchScreenMultiTouchGesture()) {
      this.setState({
        previousSelectedElementIds: {},
        selectedElementIds: makeNextSelectedElementIds(
          this.state.previousSelectedElementIds,
          this.state,
        ),
      });
    }
    gesture.initialScale = null;
  });

  private handleTextWysiwyg(
    element: ExcalidrawTextElement,
    {
      isExistingElement = false,
    }: {
      isExistingElement?: boolean;
    },
  ) {
    const updateElement = (
      text: string,
      originalText: string,
      isDeleted: boolean,
      type?: string,
    ) => {
      this.scene.replaceAllElements([
        ...this.scene.getElementsIncludingDeleted().map((_element) => {
          if (_element.id === element.id && isTextElement(_element)) {
            return updateTextElement(_element, {
              text,
              isDeleted,
              originalText,
              type,
            });
          }
          return _element;
        }),
      ]);
    };

    if (!element.containerId) {
      textWysiwyg({
        id: element.id,
        canvas: this.canvas,
        getViewportCoords: (x, y) => {
          const { x: viewportX, y: viewportY } = sceneCoordsToViewportCoords(
              {
                sceneX: x,
                sceneY: y,
              },
              this.state,
          );
          return [
            viewportX - this.state.offsetLeft,
            viewportY - this.state.offsetTop,
          ];
        },
        onSelect: withBatchedUpdates(({ range }) => {
          // console.log("onSelect", range);
          textSelectRange = range;
        }),
        onChange: withBatchedUpdates(
            ({ text, data }: { text: string; data: Op[]; id: string }) => {
              let retain = 0;
              const insertAction = data.every(
                  (item) =>
                      item.hasOwnProperty("insert") || item.hasOwnProperty("retain"),
              );
              const insertItems = data.filter((item) =>
                  item.hasOwnProperty("insert"),
              );
              const multipleInsert = insertAction && insertItems.length > 1;
              //插入多行数据时候，有多个insert，0个或1个retain
              if (multipleInsert) {
                retain = (data[0]?.retain as number) || 0;
                let eachText: any[] = [];
                data.forEach((item) => {
                  if (!item.insert) {
                    return;
                  }
                  const insertText = item.insert as string;
                  const characters = insertText.split("") as Array<string>;
                  const emojiValues: string[] = [];
                  let containsEmoji = false;
                  for (const character of insertText) {
                    const parts = character.split("");
                    if (parts.length > 1) {
                      containsEmoji = true;
                      parts.forEach(() => {
                        emojiValues.push(character);
                      });
                      continue;
                    }
                    emojiValues.push(character);
                  }
                  const arr = characters.map((character, index) => {
                    if (!containsEmoji) {
                      const value = character === "\n" ? "\r" : character;
                      return {
                        value,
                        ...(item.attributes || {}),
                      };
                    }
                    const currentEmojiValue = emojiValues[index];
                    if (currentEmojiValue.length === 1) {
                      const value = character === "\n" ? "\r" : character;
                      return {
                        value,
                        ...(item.attributes || {}),
                      };
                    }
                    return {
                      character,
                      emoji: {
                        length: currentEmojiValue.length,
                        indice: currentEmojiValue
                            .split("")
                            .findIndex((part) => part === character),
                        value: currentEmojiValue,
                      },
                    };
                  }) as unknown as RichText[];

                  eachText = eachText.concat(arr);
                });

                element.richText?.splice(
                    retain,
                    0,
                    ...eachText.map((item: any) => {
                      const baseItem: any = {
                        value: item.value,
                        fontFamily: this.state.currentItemFontFamily,
                        fontSize: this.state.currentItemFontSize,
                        color: item.color || COLOR_PALETTE.black,
                        bold:
                            (item.bold && typeof item.bold === "boolean") || false,
                        italic: item.italic || false,
                        underline: item.underline || false,
                        strikeout: item.strike || false,
                      };
                      if (item.emoji) {
                        baseItem.value = item.character;
                        baseItem.emoji = item.emoji;
                      }
                      return baseItem;
                    }),
                );
              } else {
                let insertItem: Op = {};
                let deletedCount = 0;

                data.forEach((item) => {
                  if (item.retain) {
                    retain = item.retain as number;
                  }
                  if (item.insert) {
                    insertItem = item;
                  }
                  if (item.delete) {
                    deletedCount = item.delete;
                  }
                });

                if (deletedCount) {
                  element.richText?.splice(retain, deletedCount);
                }
                if (insertItem.insert) {
                  const insertText = insertItem.insert as string;
                  const characters = insertText.split("") as Array<string>;

                  const emojiValues: string[] = [];
                  let containsEmoji = false;

                  for (const character of insertText) {
                    const parts = character.split("");
                    if (parts.length > 1) {
                      containsEmoji = true;
                      parts.forEach(() => {
                        emojiValues.push(character);
                      });
                      continue;
                    }
                    emojiValues.push(character);
                  }

                  const arr = characters.map((character, index) => {
                    const baseAttributes = {
                      value: character === "\n" ? "\r" : character,
                      ...(insertItem.attributes || {}),
                    };

                    if (!containsEmoji) {
                      return baseAttributes;
                    }

                    const currentEmojiValue = emojiValues[index];
                    if (currentEmojiValue.length === 1) {
                      return baseAttributes;
                    }

                    // Current character is part of an emoji
                    return {
                      ...baseAttributes,
                      emoji: {
                        length: currentEmojiValue.length,
                        indice: currentEmojiValue
                            .split("")
                            .findIndex((part) => part === character),
                        value: currentEmojiValue,
                      },
                    };
                  });

                  const previousText = element.richText?.[retain - 1] || null;

                  element.richText?.splice(
                      retain,
                      0,
                      ...arr.map((item: any) => {
                        const baseItem: any = {
                          value: item.value,
                          fontFamily:
                              previousText?.fontFamily ||
                              this.state.currentItemFontFamily,
                          fontSize:
                              previousText?.fontSize ||
                              this.state.currentItemFontSize,
                          color:
                              item.color || previousText?.color || (isDarkMode()?COLOR_PALETTE.white:COLOR_PALETTE.black),
                          bold:
                              (item.bold && typeof item.bold === "boolean") ||
                              previousText?.bold ||
                              false,
                          italic: item.italic || previousText?.italic || false,
                          underline:
                              item.underline || previousText?.underline || false,
                          strikeout:
                              item.strike || previousText?.strikeout || false,
                        };
                        if (item.emoji) {
                          baseItem.emoji = item.emoji;
                        }
                        return baseItem;
                      }),
                  );
                }
              }
              //富文本元素剔除手动添加的换行符号
              const str =
                  element.richText
                      ?.map((item) => {
                        if (item.value === "\n") {
                          return "";
                        }

                        return item.value;
                      })
                      .join("") || "";
              // text 是展示时候手动添加的换行符号，str是原始的输入的文字
              // 如输入：啧啧啧啧啧啧自知则知之咋咋咋咋咋咋咋咋咋咋咋咋咋咋咋咋咋咋咋咋咋咋咋咋咋咋咋咋咋咋咋咋咋咋咋咋咋咋咋咋咋咋，
              // 则展示超过宽度的时候会多换行符，需要添加换行符
              updateElement(text, str, false);

              if (isNonDeletedElement(element)) {
                updateBoundElements(element);
              }
            },
        ),
        onSubmit: withBatchedUpdates(({ text, viaKeyboard, id }) => {
          textSelectRange = null;
          const isDeleted = !text.trim();
          // 手动取一下originalText，放置取数不正确
          const textElement = this.scene.getElement(
              id,
          ) as ExcalidrawTextElement;
          mutateElement(textElement, {
            initialized: true,
          });
          updateElement(text, textElement.originalText, isDeleted);
          // select the created text element only if submitting via keyboard
          // (when submitting via click it should act as signal to deselect)
          if (!isDeleted && viaKeyboard) {
            const elementIdToSelect = element.containerId
                ? element.containerId
                : element.id;
            this.setState((prevState) => ({
              selectedElementIds: makeNextSelectedElementIds(
                  {
                    ...prevState.selectedElementIds,
                    [elementIdToSelect]: true,
                  },
                  prevState,
              ),
            }));
          }
          if (isDeleted) {
            fixBindingsAfterDeletion(this.scene.getNonDeletedElements(), [
              element,
            ]);
          }
          if (!isDeleted || isExistingElement) {
            this.history.resumeRecording();
          }

          this.setState({
            draggingElement: null,
            editingElement: null,
          });
          if (this.state.activeTool.locked) {
            setCursorForShape(this.interactiveCanvas, this.state);
          }

          if (document.activeElement?.tagName != 'INPUT'){
            this.focusContainer();
          }
        }),
        element,
        excalidrawContainer: this.excalidrawContainerRef.current,
        app: this,
      });
    } else {
      // 形状内的文字编辑保持原状
      textAreaWysiwyg({
        id: element.id,
        canvas: this.canvas,
        getViewportCoords: (x, y) => {
          const { x: viewportX, y: viewportY } = sceneCoordsToViewportCoords(
              {
                sceneX: x,
                sceneY: y,
              },
              this.state,
          );
          return [
            viewportX - this.state.offsetLeft,
            viewportY - this.state.offsetTop,
          ];
        },
        onChange: withBatchedUpdates((text) => {
          updateElement(text, text, false);
          if (isNonDeletedElement(element)) {
            updateBoundElements(element);
          }
        }),
        onSubmit: withBatchedUpdates(({ text, viaKeyboard, originalText }) => {
          const isDeleted = !text.trim();
          updateElement(text, originalText, isDeleted);
          // select the created text element only if submitting via keyboard
          // (when submitting via click it should act as signal to deselect)
          if (!isDeleted && viaKeyboard) {
            const elementIdToSelect = element.containerId
                ? element.containerId
                : element.id;
            this.setState((prevState) => ({
              selectedElementIds: makeNextSelectedElementIds(
                  {
                    ...prevState.selectedElementIds,
                    [elementIdToSelect]: true,
                  },
                  prevState,
              ),
            }));
          }
          if (isDeleted) {
            fixBindingsAfterDeletion(this.scene.getNonDeletedElements(), [
              element,
            ]);
          }
          if (!isDeleted || isExistingElement) {
            this.history.resumeRecording();
          }

          this.setState({
            draggingElement: null,
            editingElement: null,
          });
          if (this.state.activeTool.locked) {
            setCursorForShape(this.interactiveCanvas, this.state);
          }

          this.focusContainer();
        }),
        element,
        excalidrawContainer: this.excalidrawContainerRef.current,
        app: this,
      });
    }

    // deselect all other elements when inserting text
    this.deselectElements();

    // do an initial update to re-initialize element position since we were
    // modifying element's x/y for sake of editor (case: syncing to remote)
    // 删除重复的updateElement
  }

  private deselectElements() {
    this.setState({
      selectedElementIds: makeNextSelectedElementIds({}, this.state),
      selectedGroupIds: {},
      editingGroupId: null,
      activeEmbeddable: null,
      activeStickyNotes: null,
      activeMindMap: null,
    });
  }

  private getTextElementAtPosition(
    x: number,
    y: number,
  ): NonDeleted<ExcalidrawTextElement> | null {
    const element = this.getElementAtPosition(x, y, {
      includeBoundTextElement: true,
    });
    if (element && isTextElement(element) && !element.isDeleted) {
      return element;
    }
    return null;
  }

  private getElementAtPosition0(
    x: number,
    y: number,
    opts?: {
      /** if true, returns the first selected element (with highest z-index)
        of all hit elements */
      preferSelected?: boolean;
      includeBoundTextElement?: boolean;
      includeLockedElements?: boolean;
      filterFrameBackgroundImage?: boolean;
      isDOM?: boolean; // canvas上获取的位置
    },
  ): NonDeleted<ExcalidrawElement> | null {
    let allHitElements = this.getElementsAtPosition(
      x,
      y,
      opts?.includeBoundTextElement,
      opts?.includeLockedElements,
    );

    // if (!opts?.isDOM) {
    //   const frameElements = allHitElements.filter(
    //       (item) => item.type === "frame",
    //   );
    //   const elementsInFrames: {
    //     elementsInFrame: ExcalidrawElement[];
    //     frameId: string;
    //   }[] = [];
    //   frameElements.forEach((frame) => {
    //     const elementsInFrame = getFrameChildren(
    //         this.scene.getNonDeletedElements(),
    //         frame.id,
    //     ).filter(
    //         (element) =>
    //             element.type !== "image" || !element.isFrameBackgroundImage,
    //     );
    //     elementsInFrames.push({ elementsInFrame, frameId: frame.id });
    //   });
    //
    //   // 点击的存在其它非frame元素
    //   if (frameElements.length !== allHitElements.length) {
    //     allHitElements = allHitElements.filter((item) => item.type !== "frame");
    //   } else {
    //     //多个frame内部有别的元素
    //     elementsInFrames.forEach((frame) => {
    //       if (frame.elementsInFrame.length) {
    //         allHitElements = allHitElements.filter(
    //             (item) => item.id !== frame.frameId,
    //         );
    //       }
    //     });
    //   }
    // }

    if (opts?.filterFrameBackgroundImage) {
      allHitElements = allHitElements.filter(
          (item) => item.type !== "image" || !item.isFrameBackgroundImage,
      );
    }

    if (allHitElements.length > 1) {
      if (opts?.preferSelected) {
        for (let index = allHitElements.length - 1; index > -1; index--) {
          if (this.state.selectedElementIds[allHitElements[index].id]) {
            return allHitElements[index];
          }
        }
      }
      const elementWithHighestZIndex =
        allHitElements[allHitElements.length - 1];
      // If we're hitting element with highest z-index only on its bounding box
      // while also hitting other element figure, the latter should be considered.
      return isHittingElementBoundingBoxWithoutHittingElement(
        elementWithHighestZIndex,
        this.state,
        this.frameNameBoundsCache,
        x,
        y,
      )
        ? allHitElements[allHitElements.length - 2]
        : elementWithHighestZIndex;
    }
    if (allHitElements.length === 1) {
      return allHitElements[0];
    }
    return null;
  }

  private getElementAtPosition(
      x: number,
      y: number,
      opts?: {
        /** if true, returns the first selected element (with highest z-index)
         of all hit elements */
        preferSelected?: boolean;
        includeBoundTextElement?: boolean;
        includeLockedElements?: boolean;
        filterFrameBackgroundImage?: boolean;
        isDOM?: boolean; // canvas上获取的位置
      },
  ): NonDeleted<ExcalidrawElement> | null {
    let allHitElements = this.getElementsAtPosition(
        x,
        y,
        opts?.includeBoundTextElement,
        opts?.includeLockedElements,
    );

    if (!opts?.isDOM) {
      const frameElements = allHitElements.filter(
          (item) => item.type === "frame",
      );
      const elementsInFrames: {
        elementsInFrame: ExcalidrawElement[];
        frameId: string;
      }[] = [];
      frameElements.forEach((frame) => {
        const elementsInFrame = getFrameChildren(
            this.scene.getNonDeletedElements(),
            frame.id,
        ).filter(
            (element) =>
                element.type !== "image" || !element.isFrameBackgroundImage,
        );
        elementsInFrames.push({ elementsInFrame, frameId: frame.id });
      });

      // 如果frame位置上有别的元素，排除frame
      if (frameElements.length !== allHitElements.length) {
        allHitElements = allHitElements.filter((item) => item.type !== "frame");
      }
    }

    if (opts?.filterFrameBackgroundImage) {
      allHitElements = allHitElements.filter(
          (item) => item.type !== "image" || !item.isFrameBackgroundImage,
      );
    }

    if (allHitElements.length > 1) {
      if (opts?.preferSelected) {
        for (let index = allHitElements.length - 1; index > -1; index--) {
          if (this.state.selectedElementIds[allHitElements[index].id]) {
            return allHitElements[index];
          }
        }
      }
      // const elementWithHighestZIndex =
      //     allHitElements[allHitElements.length - 1];
      const elementWithHighestZIndex =
          allHitElements[allHitElements.length - 1];
      // If we're hitting element with highest z-index only on its bounding box
      // while also hitting other element figure, the latter should be considered.
      return isHittingElementBoundingBoxWithoutHittingElement(
          elementWithHighestZIndex,
          this.state,
          this.frameNameBoundsCache,
          x,
          y,
      )
          ? allHitElements[allHitElements.length - 2]
          : elementWithHighestZIndex;
    }
    if (allHitElements.length === 1) {
      return allHitElements[0];
    }
    return null;
  }

  private getElementsAtPosition(
    x: number,
    y: number,
    includeBoundTextElement: boolean = false,
    includeLockedElements: boolean = false,
  ): NonDeleted<ExcalidrawElement>[] {
    const elements =
      includeBoundTextElement && includeLockedElements
        ? this.scene.getNonDeletedElements()
        : this.scene
            .getNonDeletedElements()
            .filter(
              (element) =>
                (includeLockedElements || !element.locked) &&
                (includeBoundTextElement ||
                  !(isTextElement(element) && element.containerId)),
            );

    return getElementsAtPosition(elements, (element) =>
      hitTest(element, this.state, this.frameNameBoundsCache, x, y),
    ).filter((element) => {
      // hitting a frame's element from outside the frame is not considered a hit
      // const containingFrame = getContainingFrame(element);
      // return containingFrame &&
      //   this.state.frameRendering.enabled &&
      //   this.state.frameRendering.clip
      //   ? isCursorInFrame({ x, y }, containingFrame)
      //   : true;
      return true;
    });
  }

  private startTextEditing = ({
    sceneX,
    sceneY,
    insertAtParentCenter = true,
    container,
  }: {
    /** X position to insert text at */
    sceneX: number;
    /** Y position to insert text at */
    sceneY: number;
    /** whether to attempt to insert at element center if applicable */
    insertAtParentCenter?: boolean;
    container?: ExcalidrawTextContainer | null;
  }) => {
    const szDoubleClickElement:any = localStorage.getItem(KEY_GLOBAL_DOUBLE_CLICK_ELEMENT) || 'card';
      if (szDoubleClickElement == 'text' || isShapeElement(container)){
      this.startTextEditing0({sceneX,sceneY,insertAtParentCenter,container});
      return;
    }

    let shouldBindToContainer = false;
    let parentCenterPosition =
      insertAtParentCenter &&
      this.getTextWysiwygSnappedToCenterPosition(
        sceneX,
        sceneY,
        this.state,
        container,
      );
    if (container && parentCenterPosition) {
      const boundTextElementToContainer = getBoundTextElement(container);
      if (!boundTextElementToContainer) {
        shouldBindToContainer = true;
      }
    }
    let existingTextElement: NonDeleted<ExcalidrawTextElement> | null = null;

    const selectedElements = this.scene.getSelectedElements(this.state);

    if (selectedElements.length === 1) {
      if (selectedElements[0].locked)return;

      if (isTextElement(selectedElements[0])) {
        existingTextElement = selectedElements[0];
      } else if (container) {
        existingTextElement = getBoundTextElement(selectedElements[0]);
      } else {
        existingTextElement = this.getTextElementAtPosition(sceneX, sceneY);
      }
    } else {
      existingTextElement = this.getTextElementAtPosition(sceneX, sceneY);
    }

    const fontFamily =
      existingTextElement?.fontFamily || this.state.currentItemFontFamily;

    const lineHeight =
      existingTextElement?.lineHeight || getDefaultLineHeight(fontFamily);
    let fontSize = this.state.currentItemFontSize;

    if (
      !existingTextElement &&
      shouldBindToContainer &&
      container &&
      !isArrowElement(container)
    ) {
      const fontString = {
        fontSize,
        fontFamily,
      };
      const minWidth = getApproxMinLineWidth(
        getFontString(fontString),
        lineHeight,
      );
      const minHeight = getApproxMinLineHeight(fontSize, lineHeight);
      let newHeight = Math.max(container.height, minHeight);
      let newWidth = Math.max(container.width, minWidth);

      // if the container is too small, we'll set the font size to the minimum
      if (minHeight > container.height) {
        fontSize = getApproxFontSizeByContainerHeight(
            container.height,
            lineHeight,
            1,
        );

        newHeight = container.height;
        newWidth = container.width;
      } else {
        mutateElement(container, { height: newHeight, width: newWidth });
      }

      // const newHeight = Math.max(container.height, minHeight);
      // const newWidth = Math.max(container.width, minWidth);
      // mutateElement(container, { height: newHeight, width: newWidth });
      sceneX = container.x + newWidth / 2;
      sceneY = container.y + newHeight / 2;
      if (parentCenterPosition) {
        parentCenterPosition = this.getTextWysiwygSnappedToCenterPosition(
          sceneX,
          sceneY,
          this.state,
          container,
        );
      }
    }

    const topLayerFrame = this.getTopLayerFrameAtSceneCoords({
      x: sceneX,
      y: sceneY,
    });

      let element;
      if (HAS_ENABLE_TIPTAP_TEXT && szDoubleClickElement=='text'){

      }else{
          if (existingTextElement){
              this.setState({
                  editingElement: existingTextElement,
              });

              this.handleTextWysiwyg(existingTextElement, {
                  isExistingElement: !!existingTextElement,
              });
              return;
          }else{
              //创建个正常的文本。。。。。。
              element = newTextElement({
                  x: parentCenterPosition
                      ? parentCenterPosition.elementCenterX
                      : sceneX,
                  y: parentCenterPosition
                      ? parentCenterPosition.elementCenterY
                      : sceneY,
                  strokeColor: this.state.currentItemFontColor,//this.state.currentItemStrokeColor,
                  backgroundColor: this.state.currentItemBackgroundColor,
                  fillStyle: this.state.currentItemFillStyle,
                  strokeWidth: this.state.currentItemStrokeWidth,
                  strokeStyle: this.state.currentItemStrokeStyle,
                  roughness: this.state.currentItemRoughness,
                  opacity: this.state.currentItemOpacity,
                  text: "",
                  fontSize,
                  fontFamily,
                  bold: false,
                  italic: false,
                  underline: false,
                  strikeout: false,
                  textAlign: parentCenterPosition
                      ? "center"
                      : this.state.currentItemTextAlign,
                  verticalAlign: parentCenterPosition
                      ? VERTICAL_ALIGN.MIDDLE
                      : DEFAULT_VERTICAL_ALIGN,
                  containerId: shouldBindToContainer ? container?.id : undefined,
                  groupIds: container?.groupIds ?? [],
                  lineHeight,
                  angle: container?.angle ?? 0,
                  frameId: topLayerFrame ? topLayerFrame.id : null,
                  richText: [],
              });
          }

      if (shouldBindToContainer && container) {
        mutateElement(container, {
          boundElements: (container.boundElements || []).concat({
            type: "text",
            id: element.id,
          }),
        });

        const containerIndex = this.scene.getElementIndex(container.id);
        this.scene.insertElementAtIndex(element, containerIndex + 1);

        this.setState({ editingElement: element });
        this.handleTextWysiwyg(element, {
          isExistingElement: !!existingTextElement,
        });
      }else{
        //是新建card
        if (szDoubleClickElement == 'card'){
          if (!hasLicense() && !isFreeCreateCardMode() && !hasDesktopBaseLicense()){
            getCardCount().then(nCount=>{
              if (nCount > MAX_CARD_COUNT){
                window.showMessageBox(i18next.t("Upgrade required"),i18next.t("YouNeedToUpgradeCreateCard"),i18next.t("Cancel"),i18next.t("Learn more"),null,()=>{
                  if (isZh()) {
                    ipcSend('open-url',import.meta.env.VITE_HTTP_PURCHASE_PRICE_ZH)
                  }else {
                    ipcSend('open-url', import.meta.env.VITE_HTTP_PURCHASE_PRICE);
                  }
                })
              }else{
                this.insertNoteElement(sceneX,sceneY)
              }
            })
          }else{
            this.insertNoteElement(sceneX,sceneY)
          }
        }else if (szDoubleClickElement == 'stickyNote'){
          this.insertStickyNotes(sceneX,sceneY)
        }else if (szDoubleClickElement == 'none'){
          //nothing....
        }else if (szDoubleClickElement == 'text'){
            this.insertTextTiptap(sceneX,sceneY)
        }
      }
    }
  };



  //region 原来的双击直接插入文本内容部分....
  private startTextEditing0 = ({
                                sceneX,
                                sceneY,
                                insertAtParentCenter = true,
                                container,
                              }: {
    /** X position to insert text at */
    sceneX: number;
    /** Y position to insert text at */
    sceneY: number;
    /** whether to attempt to insert at element center if applicable */
    insertAtParentCenter?: boolean;
    container?: ExcalidrawTextContainer | null;
  }) => {
      if ((HAS_ENABLE_TIPTAP_TEXT && !container) || this.state.activeTool.type == 'text'){
          //这里插入新的tiptap的text
          this.insertTextTiptap(sceneX,sceneY,null);
          return;
      }

    let shouldBindToContainer = false;

    let parentCenterPosition =
        insertAtParentCenter &&
        this.getTextWysiwygSnappedToCenterPosition(
            sceneX,
            sceneY,
            this.state,
            container,
        );
    if (container && parentCenterPosition) {
      const boundTextElementToContainer = getBoundTextElement(container);
      if (!boundTextElementToContainer) {
        shouldBindToContainer = true;
      }
    }
    let existingTextElement: NonDeleted<ExcalidrawTextElement> | null = null;

    const selectedElements = this.scene.getSelectedElements(this.state);

    if (selectedElements.length === 1) {
      if (selectedElements[0].locked)return;

      if (isTextElement(selectedElements[0])) {
        existingTextElement = selectedElements[0];
      } else if (container) {
        existingTextElement = getBoundTextElement(selectedElements[0]);
      } else {
        existingTextElement = this.getTextElementAtPosition(sceneX, sceneY);
      }
    } else {
      existingTextElement = this.getTextElementAtPosition(sceneX, sceneY);
    }

    const fontFamily =
        existingTextElement?.fontFamily || this.state.currentItemFontFamily;

    const lineHeight =
        existingTextElement?.lineHeight || getDefaultLineHeight(fontFamily);
    let fontSize = this.state.currentItemFontSize;

    if (
        !existingTextElement &&
        shouldBindToContainer &&
        container &&
        !isArrowElement(container)
    ) {
      const fontString = {
        fontSize,
        fontFamily,
      };
      const minWidth = getApproxMinLineWidth(
          getFontString(fontString),
          lineHeight,
      );
      const minHeight = getApproxMinLineHeight(fontSize, lineHeight);
      let newHeight = Math.max(container.height, minHeight);
      let newWidth = Math.max(container.width, minWidth);

      // if the container is too small, we'll set the font size to the minimum
      if (minHeight > container.height) {
        fontSize = getApproxFontSizeByContainerHeight(
            container.height,
            lineHeight,
            1,
        );

        newHeight = container.height;
        newWidth = container.width;
      } else {
        mutateElement(container, { height: newHeight, width: newWidth });
      }

      // const newHeight = Math.max(container.height, minHeight);
      // const newWidth = Math.max(container.width, minWidth);
      // mutateElement(container, { height: newHeight, width: newWidth });
      sceneX = container.x + newWidth / 2;
      sceneY = container.y + newHeight / 2;
      if (parentCenterPosition) {
        parentCenterPosition = this.getTextWysiwygSnappedToCenterPosition(
            sceneX,
            sceneY,
            this.state,
            container,
        );
      }
    }

    const topLayerFrame = this.getTopLayerFrameAtSceneCoords({
      x: sceneX,
      y: sceneY,
    });

    const element = existingTextElement
        ? existingTextElement
        : newTextElement({
          x: parentCenterPosition
              ? parentCenterPosition.elementCenterX
              : sceneX,
          y: parentCenterPosition
              ? parentCenterPosition.elementCenterY
              : sceneY,
          strokeColor: this.state.currentItemFontColor,//this.state.currentItemStrokeColor,
          backgroundColor: this.state.currentItemBackgroundColor,
          fillStyle: this.state.currentItemFillStyle,
          strokeWidth: this.state.currentItemStrokeWidth,
          strokeStyle: this.state.currentItemStrokeStyle,
          roughness: this.state.currentItemRoughness,
          opacity: this.state.currentItemOpacity,
          text: "",
          fontSize,
          fontFamily,
          bold: false,
          italic: false,
          underline: false,
          strikeout: false,
          textAlign: parentCenterPosition
              ? "center"
              : this.state.currentItemTextAlign,
          verticalAlign: parentCenterPosition
              ? VERTICAL_ALIGN.MIDDLE
              : DEFAULT_VERTICAL_ALIGN,
          containerId: shouldBindToContainer ? container?.id : undefined,
          groupIds: container?.groupIds ?? [],
          lineHeight,
          angle: container?.angle ?? 0,
          frameId: topLayerFrame ? topLayerFrame.id : null,
          richText: [],
        });

    if (!existingTextElement && shouldBindToContainer && container) {
      mutateElement(container, {
        boundElements: (container.boundElements || []).concat({
          type: "text",
          id: element.id,
        }),
      });
    }
    this.setState({ editingElement: element });

    if (!existingTextElement) {
      if (container && shouldBindToContainer) {
        const containerIndex = this.scene.getElementIndex(container.id);
        this.scene.insertElementAtIndex(element, containerIndex + 1);
      } else {
        this.scene.addNewElement(element);
      }
    }

    this.setState({
      editingElement: element,
    });

    this.handleTextWysiwyg(element, {
      isExistingElement: !!existingTextElement,
    });
  };

  //endregion

  private createMindMapCallBack = ({
                                     type,
                                     element,
                                     event,
                                     node,
                                     mindMap,
                                     id,
                                     delta,
                                   }: {
    type: "mousedown" | "mousemove" | "node_contextmenu" | "node_mousedown" | "node_active" | "keydown" | "back_forward" | "destory"
        | "zoom"| "add_link" | "reset_selected_element" | "push_history" | "get_real_element" | "Control+c" | "mouse_wheel";
    element: ExcalidrawMindmapElement;
    event: React.PointerEvent<HTMLElement>;
    node: any;
    mindMap: any;
    id: any;
    delta?: any;
  }) => {
    if (type == 'mouse_wheel'){
      this.handleWheel(event);
      return;
    }
    if (type == 'getSelectElements'){
      return this.getSelectedElements();
    }
    if (type === "Control+c") {
      this.setState(
          {
            selectedElementIds: { [id]: true },

            draggingElement: null,
            selectionElement: null,
          },
          () => {
            const copyEvent = new ClipboardEvent("copy", {
              clipboardData: new DataTransfer(), // 创建一个 DataTransfer 对象来存储剪贴板数据
            });
            this.actionManager.executeAction(actionCopy, "keyboard", copyEvent);
          },
      );
      return;
    }

    if (type == "push_history"){
      // this.pushHistory();
      return;
    }
    if (type == 'get_real_element'){
      return this.getSceneElements().find(p=>p.id == element.id);
    }
    if (type === "add_link") {
      // this.setState({
      //   mindmap: {
      //     ...this.state.mindmap,
      //     modal: {
      //       visible: true,
      //       type: "link",
      //       action: "text",
      //       delta,
      //     },
      //   },
      // });
      postMessage(EVENT_MINDMAP_ADD_LINK,{linkInfo:node?.nodeData?.data.hyperlink});
      return;
    }

    if (type == 'node_mousedown'){
      //刷新当前mindmap选中的菜单。。。多个和单个不一样...
      this.setState({
        isMindmapNodeMultiply:(mindMapCache.get(element?.id)?.renderer?.activeNodeList?.length > 1)
      })
      return;
    }

    if (type == 'reset_selected_element'){
      // const elements = this.getSelectedElements();
      // for (let i=0;i<elements.length;i++){
      //   const ele = elements[i];
      //   if (isStickyNotesElement(ele)){
      //
      //   }
      // }
      this.setState({
        selectedElementIds:{},
        isMindmapEditing:true,
      });
      return;
    }

    if (type == 'hide_text_edit'){
      this.setState({
        isMindmapEditing:false
      })
      return;
    }

    if (type === "zoom") {
      return this.state.zoom.value;
    }

    if (type === "keydown") {
      this.setState(
          {
            selectedElementIds: { [element.id]: true },
          },
          () => {
            const event = new KeyboardEvent("keydown", {
              key: "c",
              code: "KeyC",
              ctrlKey: true, // 设置 Ctrl 键为按下状态
              bubbles: true, // 事件是否冒泡
              cancelable: true, // 事件是否可以被取消
            });
            // console.log(event, "createMindMapCallBack");
            this.excalidrawContainerRef.current!.dispatchEvent(event);
          },
      );
      return;
    }

    if (type == 'mousedown'){
      // console.log("createMindMapCallBack mousedown");
      if (event.button == 2){
        this.handleCanvasPointerDown(event);
      }

      return;
    }

    if (type == 'mouseup'){
      // setTimeout(()=>{
      //   if (this.state.mindmap?.selected?.node){
      //     renderNodesToolBar([this.state.mindmap?.selected?.node],this.state.mindmap?.selected?.element?.id);
      //   }else{
      //     hiddenToolbar();
      //   }
      // },200)

      return;
    }

    if (type === "mousemove") {
      // console.log('*******   mousemove  *******')
      // hiddenToolbar();
      // If multiple elements are already selected and this mindmap is one of
      // them, preserve the full selection so all selected elements drag together.
      const existingSelectedIds = this.state.selectedElementIds;
      const isPartOfMultiSelect =
          Object.keys(existingSelectedIds).length > 1 &&
          existingSelectedIds[element.id];
      const nextSelectedIds = isPartOfMultiSelect
          ? existingSelectedIds
          : { [element.id]: true };
      // Build the list of all selected elements for the pointerDownState so
      // the drag handler moves every selected element, not just this one.
      const allSelected = isPartOfMultiSelect
          ? this.scene.getNonDeletedElements().filter(
              (el) => existingSelectedIds[el.id],
          )
          : [element];

      this.setState(
          {
            selectedElementIds: nextSelectedIds,
            draggingElement: element,
            selectionElement: null,
            // viewBackgroundColor:randomColor(),
          },
          () => {
            // console.log('*******   createMindMapCallBack mousemove  *******')
            const pointerDownState: PointerDownState = {
              ...this.initialPointerDownState(event),

              hit: {
                element,
                allHitElements: allSelected,
                wasAddedToSelection: true,
                hasBeenDuplicated: false,
                hasHitCommonBoundingBoxOfSelectedElements: isPartOfMultiSelect,
              },
            };

            const onPointerMove =
                this.onPointerMoveFromPointerDownHandler(pointerDownState);

            const onPointerUp =
                this.onPointerUpFromPointerDownHandler(pointerDownState);

            window.addEventListener(EVENT.POINTER_MOVE, onPointerMove);
            window.addEventListener(EVENT.POINTER_UP, onPointerUp);

            pointerDownState.eventListeners.onMove = onPointerMove;
            pointerDownState.eventListeners.onUp = onPointerUp;
            pointerDownState.boxUnSelection.onUp = true;
          },
      );

      return;
    }
    if (type === "node_contextmenu") {
        event.stopPropagation();
      const container = this.excalidrawContainerRef.current!;
      const { top: offsetTop, left: offsetLeft } =
          container.getBoundingClientRect();
      const height = document.body.clientHeight;
      const left = event.clientX - offsetLeft;
      let top = event.clientY - offsetTop;
      const arrayItems = this.getContextMenuItems("node_contextmenu",node);
      // for (let i=0;i<arrayItems.length;i++){
      //   if (arrayItems[i].contextItemLabel){
      //     arrayItems[i].contextItemLabel = i18next.t(arrayItems[i].contextItemLabel)
      //   }
      // }
      const menuHeight = arrayItems.filter(p=>p != CONTEXT_MENU_SEPARATOR).length * 34 + 64;
      if ((top + menuHeight) > height){
        top = height - menuHeight;
      }
      setTimeout(()=>{
        this.setState({
          contextMenu: {
            top,
            left,
            items: arrayItems,
          },
        });
      },100);

      return;
    }

    if (type === "node_active") {
      this.setState({
        mindmap: {
          ...this.state.mindmap,
          selected: {
            node,
            element,
          },
        },
        // 只有真正选中节点时才清除画布元素选中状态；
        // node 为 null 时是取消选中（由 clearMindmapActiveNode 触发），不应清除刚选中的画布元素
        ...(node ? { selectedElementIds: {} } : {}),
      });

    }
    // if (type === "node_active") {
    //   if (node == null)return;
    //
    //   let szIds = null;
    //   if (node?.isRoot){
    //     szIds = [element.id];
    //   }
    //   this.setState({
    //     mindmapOperations:{
    //       deletingElements:szIds
    //     },
    //     mindmap: {
    //       ...this.state.mindmap,
    //       selected: {
    //         node,
    //         element,
    //       },
    //     },
    //     selectedElementIds: {},
    //     draggingElement: null,
    //     selectionElement: null,
    //   });
    //   return;
    // }

    if (type === "destory") {
      Modal.info({
        title: "Deleting nested item",
        content: (
            <div>
              1 nested item will be deleted. <br />
              This action cannot be undone.
            </div>
        ),
        onOk: () => {
          mindMap?.destroy();
          mindMapCache.delete(id);
          const toolbar = document.getElementById(
              `excalidraw__mindmap__outer__toolbar`,
          )!;
          toolbar.style.display = "none";
          this.setState(
              {
                selectedElementIds: { [id]: true },
              },
              () => {
                this.actionManager.executeAction(actionDeleteSelected);
              },
          );
        },
        okCancel: true,
        okText: "Delete",
        okType: "danger",
      });
    }
  };


  private replaceImage = async ({
                            element,
                            croppedImage: { src, croppedCanvas, croppedImageSize },
                          }: {
    element: InitializedExcalidrawImageElement;
    croppedImage: {
      src: string;
      croppedCanvas: HTMLCanvasElement;
      croppedImageSize: CroppedImageSize;
    };
  }) => {
    const imageCache = this.imageCache.get(element.fileId);
    if (!imageCache) {
      return;
    }
    const image = imageCache.image as HTMLImageElement;
    const croppedImage = new Image();
    const fileId = nanoid() as FileId;
    //保存
    const szFileName = fileId + '.png';
    await ipcInvoke('write-base64-image',{fileName:szFileName,imgBase64:src});
    croppedImage.src = getScheme()+szFileName;
      const imageSize:any = await getBase64ImageSize(src);
    this.imageCache.set(fileId, {
      ...imageCache,
      image: croppedImage,
    });

    // 将裁剪后的图片存入文件
    this.files = {
      ...this.files,
      [fileId]: {
        mimeType: IMAGE_MIME_TYPES.png,
        id: fileId,
        dataURL: '',//src as DataURL,
        created: Date.now(),
        lastRetrieved: Date.now(),
      },
    };

    // 计算裁剪的图片尺寸
    const scaledNewRectSize = calculateScaledRectSize(croppedCanvas, {
      widthScale: image.width / element.width,
      heightScale: image.height / element.height,
    });
    croppedImage.onload = () => {
        const nRealHeight = scaledNewRectSize.width*imageSize?.height / imageSize?.width;
        const customData = {...element.customData};
        customData.generationData.originHeight = nRealHeight;
        customData.generationData.whRate = imageSize?.width/imageSize?.height;
        mutateElement(element, {
            ...scaledNewRectSize,
            fullImageFileId: element.fullImageFileId || element.fileId,
            fileId,
            hasUpdate:true,
            croppedImageSize, // 记忆裁剪图片得尺寸
            customData:customData,
            width:scaledNewRectSize.width,
        });
    };
  };

  private setCardLink = (
      type: string,
      element: ExcalidrawEmbeddableElement,
  ) => {
    if (type === "text") {
      const node = document.getElementById(`${element.id}_div`)!;
      node.innerHTML = "";
      const aElement = document.createElement("a");
      // 设置a元素的属性
      aElement.id = `${element.id}-card`;
      aElement.href = element.link || "#"; // 如果el.link为空，则链接指向#
      aElement.target = "_blank";
      aElement.rel = "noreferrer";
      aElement.textContent = element.link; // 设置链接文本为el.link
      node.appendChild(aElement);
      const span = document.createElement("span");
      span.textContent = element.link;
      document.body.appendChild(span);
      const rect = span.getBoundingClientRect();
      const height = rect.height;
      const width = rect.width;
      document.body.removeChild(span);
      mutateElement(element, {
        embedType: "text",
        width,
        height,
      });

      return;
    }
    if (type === "card") {
      mutateElement(element, {
        embedType: "card",
        width: 350,
        height: 95,
      });

      const nodeDiv = document.getElementById(`${element.id}_div`)!;
      nodeDiv.style.display = "block";

      nodeDiv.innerHTML = "";
      const aElement = document.createElement("a");
      // 设置a元素的属性
      aElement.id = `${element.id}-card`;
      aElement.href = element.link || "#"; // 如果el.link为空，则链接指向#
      aElement.target = "_blank";
      aElement.rel = "noreferrer";
      aElement.textContent = element.link; // 设置链接文本为el.link
      nodeDiv.appendChild(aElement);
      setTimeout(() => {
        const node = document.getElementById(`${element.id}-card`)!;
        cardLink([node],element);
      }, 10);
      return;
    }
    if (type === "page") {
      const node = document.getElementById(`${element.id}_div`)!;
      node.innerHTML = "";
      const embedLink = getEmbedLink(element.link)!;
      mutateElement(element, {
        embedType: "page",
        width: embedLink.intrinsicSize.w,
        height: embedLink.intrinsicSize.h,
      });
    }
  };

  private delaySaved = async ()=>{
    return new Promise(resolve => {
      setTimeout(async()=>{
        if (window.saveNotes){
          await window.saveNotes();
          resolve(true);
        }else{
          resolve(false);
        }
      },100)
    })
  }

  /**
   * 插入embedable链接
   * @param initX
   * @param initY
   * @param item
   */
  insertEmbedElement = async (initX:any,initY:any,item:any)=>{
    const {x,y} = this.getCurrentCenterPoint();
    let nPosX = x-190;
    let nPosY = y-130;
    if (initX){
      nPosX = initX;
    }
    if (initY) {
      nPosY = initY;
    }

    let width = DEFAULT_EMBEDABLE_ELEMENT_WIDTH;
    let height = DEFAULT_EMBEDABLE_ELEMENT_HEIGHT;
    if (isSupportVideoAnnoUrl(item.fileName) && !isBilibiliUrl(item.fileName) && !isBaiduVideoUrl(item.fileName)){
      width = DEFAULT_NOTE_WIDTH;
      height = width*height / DEFAULT_EMBEDABLE_ELEMENT_WIDTH
    }

    var element = {
      type: "embeddable",
      version: 1,
      isDeleted: false,
      id: item.id,
      strokeWidth: 1,
      strokeStyle: "solid",
      roughness:0,
      roundness: {
        type:ROUNDNESS.ADAPTIVE_RADIUS,
        value:12
      },
      link:item.fileName,
      opacity: 100,
      angle: 0,
      x: nPosX,
      y: nPosY,
      strokeColor: "#F0EFF5",
      backgroundColor: getBookmarkBackgroundColor(),
      fillStyle:'solid',
      width: width,
      height:height,
      locked:false,
      groupIds: [],
      embedType:'page'
    }

    //解析name---
    fetchPageTitle(item.fileName).then(res=>{
      mutateElement(element,{name:res});
    })

    return element;
  }


  /**
   * 创建视频注记元素
   * @param noteData
   */
  createVideoAnnoElement = async (libraryId:any,videoData:any,positionX,positionY,videoName,type) => {

    let position=this.getCurrentCenterPoint();
    let x = position.x - 100;
    let y = position.y - 150;
    if (positionX != undefined){
      x = positionX;
    }
    if (positionY != undefined){
      y = positionY;
    }

    let height = await calcVideoAnnoHeight(videoData,DEFAULT_NOTE_WIDTH);

    var element = {
      type: "iframe",
      version: 1,
      isDeleted: false,
      id: videoData.id,
      strokeWidth: 1,
      strokeStyle: "solid",
      roughness:0,
      roundness: {
        type:ROUNDNESS.ADAPTIVE_RADIUS,
        value:8
      },
      opacity: 100,
      angle: 0,
      locked:false,
      x: x,
      y: y,
      backgroundColor:getNoteElementBackgroundColor(),
      strokeColor:getNoteElementStrokeColor(),
      fillStyle:'solid',
      width: DEFAULT_NOTE_WIDTH,
      height: height,
      groupIds: [],
      customData: {
        generationData: {
          // annoHeight:elementHeight,//注记本身高度
          status: 'done',
          html: '',//szHtml,
          type: type || 'videoAnno',
          id:libraryId,
          videoData:videoData,
          videoName:videoName
        }
      }
    }
    return element;
  }

  /**
   * 创建笔记元素
   * @param noteData
   */
  createPdfAnnoElement = async (pdfData:any,positionX,positionY,type) => {
    let position = this.getCurrentCenterPoint();
    let x = position.x - 100;
    let y = position.y - 150;
    if (positionX != undefined){
      x = positionX;
    }
    if (positionY != undefined){
      y = positionY;
    }

    const szHtml = createPdfAnnoTextElement(pdfData);

    let height = 210;
    let elementHeight = height;
    if (pdfData.type == 'image' || pdfData.annoType == 'image'){
      let szBase64Image = pdfData.image;
      if (!pdfData.image && pdfData.thumbImage){
        const filePath = localStorage.getItem(KEY_FILE_DIR)+pdfData.id+'.png';
        szBase64Image = 'data:image/png;base64,' + await ipcInvoke('read-file-base64',{filePath:filePath});
      }
      const size = await getBase64ImageSize(szBase64Image);
      height = (size.height/size.width) * (DEFAULT_NOTE_WIDTH);
      if (height > 200){
        height = 200;
      }
      height += 58;
      elementHeight = height;//不包含注记的高度
      height += calcTextAreaHeight((DEFAULT_NOTE_WIDTH - (2 + 8 + 6)*2),pdfData.comment);
    }else{
      //文本，计算下高度.
      const hhhhhhiiiigggggg = calcTextHeight((DEFAULT_NOTE_WIDTH - (8 + 8 + 6) * 2), szHtml);
      const nCommentHeight = calcTextAreaHeight((DEFAULT_NOTE_WIDTH - (2 + 8 + 6)*2),pdfData.comment);
      height = hhhhhhiiiigggggg  + nCommentHeight;//(2 + 8 + 6)这个是所有的padding.
      elementHeight = height;//不包含注记的高度.
    }

    const card = await getCardById(pdfData.libraryId);
    let fileName = pdfData?.name?.replace('.pdf','')+'.pdf'
    if (card){
      fileName = card.fileName;
    }

    var element = {
      type: "iframe",
      version: 1,
      isDeleted: false,
      id: pdfData.id,
      strokeWidth: 1,
      strokeStyle: "solid",
      roughness:0,
      roundness: {
        type:ROUNDNESS.ADAPTIVE_RADIUS,
        value:8
      },
      opacity: 100,
      angle: 0,
      locked:false,
      x: x,
      y: y,
      backgroundColor:getNoteElementBackgroundColor(),
      strokeColor:getNoteElementStrokeColor(),
      fillStyle:'solid',
      width: DEFAULT_NOTE_WIDTH,
      height: height,
      groupIds: [],
      customData: {
        generationData: {
          annoHeight:elementHeight,//注记本身高度
          status: 'done',
          html: szHtml,
          type:type || 'pdfAnno',
          id:pdfData.pdfId,
          pdfData:pdfData,
          fileName:fileName
        }
      }
    }
    return element;
  }

  /**
   * 插入library中的数据到元素...
   * @param item
   */
  private onInsertLibraryToElement = async (item:any,x:any,y:any,direction:any)=>{
    const arrayElements = this.getSceneElementsOrigin();
    let resultElement = null;

    if (item.type == 'card' || item.type == 'journal') {
      //添加到左侧栏
      // leftMenuRef.current?.onAddOrUpdateDatas(card.id, card.name, card.doc);
      resultElement = this.createCardElement(x,y,item);
    } else if (item.type == 'video' || item.type == 'pdf' || item.type == 'audio' || item.type == 'file') {
      if (arrayElements.some(p => p.type == 'iframe' && p.customData?.generationData?.id == item.id && (p.customData?.generationData?.type == 'file' || p.customData?.generationData?.type == 'note'))) {
        message.info(i18next.t("CardIsAlreadOn").replace('{0}', i18next.t(firstLetterUpper(item.type == 'pdf' ? 'PDF' : item.type))));
        return;
      }

      resultElement = await this.insertFileElement(item.fileName, item.id, {x, y},false,null,null,null,false,false,item.name,false);
    }else if (item.type == 'youtube' || item.type == 'bilibili'){
      //判断是否已添加过.
      if (arrayElements.some(p => p.id == item.id)) {
        message.info(i18next.t("CardIsAlreadOn").replace("{0}",i18next.t("Video")));
        return;
      }
      resultElement = await this.insertEmbedElement(x,y,item);
    }
    else if (item.type == 'image') {
      resultElement = this.insertLibrayImage(item, {x, y},false)
    }else if (item.type == 'videoAnno' || item.type == 'audioAnno'){
      //region 视频注记
      if (arrayElements.some(p => p.type == 'iframe' && p.id == item.id && p.customData?.generationData?.type == 'videoAnno')) {
        message.info(i18next.t("VideoAnnoExist"));
        return;
      }
      const videoAnno:any = await getCardById(item.id);
      if (!videoAnno){
        message.error(i18next.t('HighlightNotFoundInLibrary'))
        return;
      }
      if (videoAnno && !videoAnno.libraryId){
        videoAnno.libraryId = item.libraryId;
        updateCard(videoAnno);
      }
      let szName = item.name;
      if (!szName){
        const library = await getCardById(videoAnno.libraryId);
        if (library && library.name){
          szName = library.name;
        }else{
          //去hight表找找看
          const highlight = await dbGetHighlightByLibraryId(videoAnno.libraryId);
          if (highlight && highlight.name){
            szName = highlight.name;
          }
        }
      }
      //处理注记的插入
      resultElement = await this.createVideoAnnoElement(videoAnno.libraryId,videoAnno, x, y,szName,item.type)
      //endregion
    }else if (item.type == 'pdfAnno' || item.type == 'cardAnno'){
      //pdf注记拖动过来的.
      if (arrayElements.some(p => p.type == 'iframe' && p.customData?.generationData?.id == item.id && p.customData?.generationData?.type == 'pdfAnno')) {
        let szMsg = i18next.t("CardAnnoExist");
        if (item.type == 'pdfAnno'){
          szMsg = i18next.t("PdfAnnoExist");
        }
        message.info(szMsg);
        return;
      }
      const pdfAnno:any = item;
      const library:any = await getCardById(pdfAnno.libraryId);
      pdfAnno.pdfId = pdfAnno.libraryId;
      //处理注记的插入
      resultElement = await this.createPdfAnnoElement(pdfAnno, x, y,item.type);
    }else if (item.type == 'webLink'){
      if (arrayElements.some(p => p.type == 'embeddable' && p.id == item.id)) {
        message.info(i18next.t("Bookmark exist"));
        return;
      }
      //插入bookmark
      resultElement = await this.insertEmbedElement(item.fileName,item.id,x,y,'text');
    }

    return resultElement;
  }

  /**
   * 连接线创建新的元素
   * 连接线
   * hover
   * @param type
   */
  private addElementByHover = async (type: ToolType | "pin" | "card" | 'newBoard' | 'existCard' | 'newText') => {

    const hoverModel = document.getElementById("hoverModel")!;
    let x = Number(hoverModel.getAttribute("x"));
    let y = Number(hoverModel.getAttribute("y"));
    let originX = x,originY = y;
    hoverModel.setAttribute("type", type);
    if (type === "pin") {
      hoverModel.style.display = "none";
      hoverModel.setAttribute("type", "");
      return;
    }

    let bOver = false;
    if (type == 'card' || type == 'newText' || type == 'stickyNotes' || type === "roundedRectangle"){
      if (await hasBeyondTextLimit()){
        bOver = true;
      }
    }else if (type == 'newBoard'){
      if (await hasBeyondBoardLimit()){
        bOver = true;
      }
    }

    if (bOver){
      hoverModel.style.display = "none";
      hoverModel.setAttribute("type", "");
      //删除这条线
      this.removeElementById(this.state.hoveredElement.linearElement.id);
      return;
    }

    const direction = this.state.hoveredElement.direction;
    const sourceElement = this.state.hoveredElement.sourceElement!;
    const linearElement = this.state.hoveredElement.linearElement!;
    let { width, height } = sourceElement;
    if (type === "stickyNotes") {
      width = DEFAULT_STICK_NOTE_WIDTH;
      height = DEFAULT_STICK_NOTE_HEIGHT;
    }else if (type === "card"){
      width = DEFAULT_NOTE_WIDTH;
      height = DEFAULT_NOTE_HEIGHT_1;
    }else if (type === "newText"){
      width = DEFAULT_TEXT_WIDTH;
      height = DEFAULT_TEXT_HEIGHT;
    }else if (type == 'newBoard'){
      width = DEFAULT_NOTE_WIDTH;
      height = PROJECT_THUMB_DEFAULT_HEIGHT;//没有图的时候是159
    }
    switch (direction) {
      case "top":
        x = x - width / 2;
        y = y - height;
        break;
      case "right":
        y = y - height / 2;
        break;
      case "bottom":
        x = x - width / 2;
        break;
      case "left":
        x = x - width;
        y = y - height / 2;
        break;
      default:
        break;
    }
    let resultElement:any = null;
    if (type === "stickyNotes") {
      resultElement = this.createStickyNotesElement({
        sceneX: x,
        sceneY: y,
        addToFrameUnderCursor: false,
        backgroundColor: localStorage.getItem(KEY_DEFAULT_STICKYNOTE_BACKGROUND_COLOR) || DEFAULT_STICKYNOTE_BACKGROUND_COLOR,
      });
      setTimeout(()=>{
        this.setState({
          activeStickyNotes:{
            element:resultElement,
            state:'active'
          }
        })
      },100)
    } else if (type === "roundedRectangle") {
      // @ts-ignore
      const element = this.createGenericElementOnPointerDown(type, {
        origin: {
          x,
          y,
        },
      },false);
      mutateElement(element, {
        width: sourceElement.width,
        height: sourceElement.height,
      });
      resultElement = element;

      //自动进入编辑状态-----
      this.setState({
        selectedElementIds:{[resultElement.id]:true}
      })

      setTimeout(()=>{
        let sceneX = x;
        let sceneY = y;
        const container = getTextBindableContainerAtPosition(
            this.scene.getNonDeletedElements(),
            this.state,
            sceneX,
            sceneY,
        );

        if (container) {
          if (
              hasBoundTextElement(container) ||
              !isTransparent(container.backgroundColor) ||
              isHittingElementNotConsideringBoundingBox(
                  container,
                  this.state,
                  this.frameNameBoundsCache,
                  [sceneX, sceneY],
              )
          ) {
            const midPoint = getContainerCenter(container, this.state);

            sceneX = midPoint.x;
            sceneY = midPoint.y;
          }
        }

        //这里 双击画布 开始编辑文本....
        this.startTextEditing({
          sceneX,
          sceneY,
          insertAtParentCenter: true,
          container,
        });

        this.props.onClickElement(element,true);
      },100)

    }else if (type === "card"){
      resultElement = this.createCardElement(x,y,null);
      //添加到左侧栏
      postMessage(EVENT_ADD_NOTE_BY_CARD, [{id: resultElement.customData.generationData.id, name: '', type: 'card'}]);
    }else if (type == 'newBoard'){
      //创建一个新board....
      if (window.onAddNewCanvas){
        const newBoard = window.onAddNewCanvas();
        //要保存.--要延迟一下保存.
        await this.delaySaved();

        resultElement = this.createProjectLink(x,y,PROJECT_ID,newBoard.key,null,PROJECT_THUMB_DEFAULT_HEIGHT);
      }
    }else if (type == 'existCard'){
      //已存在的card...
      window.showDialogLink(null, 'card', async (szDeeplink) => {
        //根据这个类型创建个element????  --- 搞得复杂了...
        const deeplink = decodeDeeplink(szDeeplink);
        if (deeplink.type == 'library'){
          const item = await getCardById(deeplink.id);
          resultElement = await this.onInsertLibraryToElement(item,x,y,direction);
        }else if (deeplink.type == 'project'){
          resultElement = this.createProjectLink(x,y,deeplink.id,deeplink.childId,null,PROJECT_THUMB_DEFAULT_HEIGHT);
        }else if (deeplink.type == 'highlight'){
          const item = await getCardById(deeplink.childId);
          resultElement = await this.onInsertLibraryToElement(item,x,y,direction);
        }

        // 恢复历史记录的记录
        this.history.resumeRecording();
        // 设置当前活动工具为选择工具
        this.setActiveTool({ type: "selection" });
        // let szFixedDirection =
        if (resultElement){
          width = resultElement.width;
          height = resultElement.height;
          switch (direction) {
            case "top":
              x = originX - width / 2;
              y = originY - height;
              resultElement.forceDirection = "bottom";
              break;
            case "right":
              y = originY - height / 2;
              resultElement.forceDirection = "left";
              break;
            case "bottom":
              x = originX - width / 2;
              resultElement.forceDirection = "top";
              break;
            case "left":
              x = originX - width;
              y = originY - height / 2;
              resultElement.forceDirection = "right";
              break;
            default:
              break;
          }
          resultElement.x = x;
          resultElement.y = y;

          this.scene.addNewElement(resultElement!);

          bindOrUnbindLinearElement(
              linearElement,
              sourceElement as ExcalidrawBindableElement,
              resultElement as ExcalidrawBindableElement,
              'left'
          );
          this.setState({
            editingGroupId: null,
            selectedElementIds: {
              [resultElement!.id]: true,
            },
          });

          this.setState({
            hoveredElement: {
              element: null,
              direction: null,
              status: null,
              sourceElement: null,
              linearElement: null,
            },
          });
        }else{
          //删除这个箭头
          this.actionManager.executeAction(actionDeleteSelected);
        }
      },{from:'linkExistCard'})
    }else if (type == 'newText'){
        resultElement = this.createTextTiptapElement({
            sceneX: x,
            sceneY: y,
            addToFrameUnderCursor: false,
        });

      // Set forceDirection based on the source direction
      // The connector should attach to the opposite side of where it came from
      switch (direction) {
        case "top":
          resultElement.forceDirection = "bottom";
          break;
        case "right":
          resultElement.forceDirection = "left";
          break;
        case "bottom":
          resultElement.forceDirection = "top";
          break;
        case "left":
          resultElement.forceDirection = "right";
          break;
        default:
          break;
      }

        setTimeout(()=>{
            this.setState({
                activeStickyNotes:{
                    element:resultElement,
                    state:'active'
                }
            })
        },100)
    }

    hoverModel.style.display = "none";
    if (resultElement){
      // 恢复历史记录的记录
      this.history.resumeRecording();
      // 设置当前活动工具为选择工具
      this.setActiveTool({ type: "selection" });
      this.scene.addNewElement(resultElement!);
      // For newText type, pass the forceDirection to ensure correct connection
      const fixedDirection = type === 'newText' ? resultElement.forceDirection : null;

      bindOrUnbindLinearElement(
          linearElement,
          sourceElement as ExcalidrawBindableElement,
          resultElement as ExcalidrawBindableElement,
          fixedDirection,
      );
      this.setState({
        editingGroupId: null,
        selectedElementIds: {
          [resultElement!.id]: true,
        },
      });

      this.setState({
        hoveredElement: {
          element: null,
          direction: null,
          status: null,
          sourceElement: null,
          linearElement: null,
        },
      });
    }
  };

  private onVideoNotes = (videoNotes: VideoNotesType) => {
    this.setState({
      videoNotes,
    });
  };

  private searchElementsContent = (
      type: "select" | "deselect" = "select",
      elements: NonDeletedExcalidrawElement[],
  ) => {
    // 取消选择元素
    if (type === "deselect") {
      this.setState({ selectedElementIds: {} });
      return;
    }

    // 将搜索的元素至于中间
    this.setAppState((appState) => ({
      ...calculateScrollCenter(elements, appState),
    }));
    // 选择搜索到的元素
    const elementsWithinSelection = elements;

    const firstElement = elementsWithinSelection[0];
    // 单选mindMap节点
    if (
        elementsWithinSelection.length === 1 &&
        isMindmapElement(firstElement)
    ) {
      const currentMindMap = mindMapCache.get(firstElement.id);
      // 搜索某个导图子节点内容无法定位展开
      // @ts-ignore
      currentMindMap.renderer.expandToNodeUid(firstElement.uid, () => {
        setTimeout(() => {
          // @ts-ignore
          const node = currentMindMap.renderer.findNodeByUid(firstElement.uid);
          node.active();
        }, 100);
      });
      return;
    }

    this.setState((prevState) => {
      const nextSelectedElementIds = {
        ...elementsWithinSelection.reduce(
            (acc: Record<ExcalidrawElement["id"], true>, element) => {
              acc[element.id] = true;
              return acc;
            },
            {},
        ),
      };

      prevState = {
        ...prevState,
        selectedGroupIds: {},
        editingGroupId: null,
      };

      return {
        ...selectGroupsForSelectedElements(
            {
              editingGroupId: prevState.editingGroupId,
              selectedElementIds: nextSelectedElementIds,
            },
            this.scene.getNonDeletedElements(),
            prevState,
            this,
        ),
        // select linear element only when we haven't box-selected anything else
        selectedLinearElement:
            elementsWithinSelection.length === 1 &&
            isLinearElement(elementsWithinSelection[0])
                ? new LinearElementEditor(elementsWithinSelection[0], this.scene)
                : null,
        showHyperlinkPopup:
            elementsWithinSelection.length === 1 &&
            (elementsWithinSelection[0].link ||
                isEmbeddableElement(elementsWithinSelection[0]))
                ? "info"
                : false,
      };
    });
  };

  private handleCanvasDoubleClick = (
    event: React.MouseEvent<HTMLCanvasElement>,
  ) => {
    if (this.state.displayMode === "preview") {
      return;
    }
    // case: double-clicking with arrow/line tool selected would both create
    // text and enter multiElement mode
    if (this.state.multiElement) {
      return;
    }
    // we should only be able to double click when mode is selection
    if (this.state.activeTool.type !== "selection") {
      return;
    }

    const selectedElements = this.scene.getSelectedElements(this.state);

    if (selectedElements.length === 1 && isLinearElement(selectedElements[0])) {
      if (
        event[KEYS.CTRL_OR_CMD] &&
        (!this.state.editingLinearElement ||
          this.state.editingLinearElement.elementId !== selectedElements[0].id)
      ) {
        this.history.resumeRecording();
        this.setState({
          editingLinearElement: new LinearElementEditor(
            selectedElements[0],
            this.scene,
          ),
        });
        return;
      } else if (
        this.state.editingLinearElement &&
        this.state.editingLinearElement.elementId === selectedElements[0].id
      ) {
        return;
      }
    }

    resetCursor(this.interactiveCanvas);


    let { x: sceneX, y: sceneY } = viewportCoordsToSceneCoords(
      event,
      this.state,
    );

    this.props?.onDoubleClick?.(this.state.activeTool,selectedElements);

    let isSelectMindmap = false;
    if(selectedElements.length == 1){
      const firstElement = selectedElements[0];
      if (isIframeElement(firstElement)){
        return;
      }
      isSelectMindmap = isMindmapElement(firstElement);
    }
    const selectedGroupIds = getSelectedGroupIds(this.state);

    if (selectedGroupIds.length > 0) {
      const hitElement = this.getElementAtPosition(sceneX, sceneY);

      const selectedGroupId =
        hitElement &&
        getSelectedGroupIdForElement(hitElement, this.state.selectedGroupIds);

      if (selectedGroupId) {
        this.setState((prevState) => ({
          ...prevState,
          ...selectGroupsForSelectedElements(
            {
              editingGroupId: selectedGroupId,
              selectedElementIds: { [hitElement!.id]: true },
            },
            this.scene.getNonDeletedElements(),
            prevState,
            this,
          ),
        }));
        return;
      }
    }

    resetCursor(this.interactiveCanvas);
    if (!event[KEYS.CTRL_OR_CMD] && !this.state.viewModeEnabled) {
      let hitElement = this.getElementAtPosition(sceneX, sceneY);

      if (isIframeLikeElement(hitElement)) {
        this.setState({
          activeEmbeddable: { element: hitElement, state: "active" },
        });
        return;
      }

      if (isStickyNotesElement(hitElement)) {
        this.setState({
          activeStickyNotes: { element: hitElement, state: "active" },
        });
        return;
      }


      const container = getTextBindableContainerAtPosition(
        this.scene.getNonDeletedElements(),
        this.state,
        sceneX,
        sceneY,
      );

      if (container) {
        if (
          hasBoundTextElement(container) ||
          !isTransparent(container.backgroundColor) ||
          isHittingElementNotConsideringBoundingBox(
            container,
            this.state,
            this.frameNameBoundsCache,
            [sceneX, sceneY],
          )
        ) {
          const midPoint = getContainerCenter(container, this.state);

          sceneX = midPoint.x;
          sceneY = midPoint.y;
        }
      }

      //这里 双击画布 开始编辑文本....
      hitElement = this.getElementAtPosition(sceneX, sceneY,{includeLockedElements:true});
      if (!isImageElement(hitElement) && !isSelectMindmap){
        this.startTextEditing({
          sceneX,
          sceneY,
          insertAtParentCenter: !event.altKey,
          container,
        });
      }
    }
  };

  private getElementLinkAtPosition = (
    scenePointer: Readonly<{ x: number; y: number }>,
    hitElement: NonDeletedExcalidrawElement | null,
  ): ExcalidrawElement | undefined => {
    // Reversing so we traverse the elements in decreasing order
    // of z-index
    const elements = this.scene.getNonDeletedElements().slice().reverse();
    let hitElementIndex = Infinity;

    return elements.find((element, index) => {
      if (hitElement && element.id === hitElement.id) {
        hitElementIndex = index;
      }
      return (
        element.link &&
        index <= hitElementIndex &&
        isPointHittingLink(
          element,
          this.state,
          [scenePointer.x, scenePointer.y],
          this.device.editor.isMobile,
        )
      );
    });
  };

  private getElementVideoAtPosition = (
      scenePointer: Readonly<{ x: number; y: number }>,
      hitElement: NonDeletedExcalidrawElement | null,
  ): ExcalidrawElement | undefined => {
    // Reversing so we traverse the elements in decreasing order
    // of z-index
    const elements = this.scene.getNonDeletedElements().slice().reverse();

    let hitElementIndex = Infinity;

    return elements.find((element, index) => {
      if (hitElement && element.id === hitElement.id) {
        hitElementIndex = index;
      }
      return (
          // element.comments.status !== "uncreated" &&
          index <= hitElementIndex &&
          isPointHittingVideoNotes(
              element,
              this.state,
              [scenePointer.x, scenePointer.y],
              this.device.editor.isMobile,
          )
      );
    });
  };

  private redirectToLink = (
    event: React.PointerEvent<HTMLCanvasElement>,
    isTouchScreen: boolean,
  ) => {
    const draggedDistance = distance2d(
      this.lastPointerDownEvent!.clientX,
      this.lastPointerDownEvent!.clientY,
      this.lastPointerUpEvent!.clientX,
      this.lastPointerUpEvent!.clientY,
    );
    if (
      !this.hitLinkElement ||
      // For touch screen allow dragging threshold else strict check
      (isTouchScreen && draggedDistance > DRAGGING_THRESHOLD) ||
      (!isTouchScreen && draggedDistance !== 0)
    ) {
      return;
    }
    const lastPointerDownCoords = viewportCoordsToSceneCoords(
      this.lastPointerDownEvent!,
      this.state,
    );
    const lastPointerDownHittingLinkIcon = isPointHittingLink(
      this.hitLinkElement,
      this.state,
      [lastPointerDownCoords.x, lastPointerDownCoords.y],
      this.device.editor.isMobile,
    );
    const lastPointerUpCoords = viewportCoordsToSceneCoords(
      this.lastPointerUpEvent!,
      this.state,
    );
    const lastPointerUpHittingLinkIcon = isPointHittingLink(
      this.hitLinkElement,
      this.state,
      [lastPointerUpCoords.x, lastPointerUpCoords.y],
      this.device.editor.isMobile,
    );
    if (lastPointerDownHittingLinkIcon && lastPointerUpHittingLinkIcon) {
      let url = this.hitLinkElement.link;
      if (url) {
        url = normalizeLink(url);
        let customEvent;
        if (this.props.onLinkOpen) {
          customEvent = wrapEvent(EVENT.EXCALIDRAW_LINK, event.nativeEvent);
          this.props.onLinkOpen(
            {
              ...this.hitLinkElement,
              link: url,
            },
            customEvent,
          );
        }
        if (!customEvent?.defaultPrevented) {
          const target = isLocalLink(url) ? "_self" : "_blank";
          const newWindow = window.open(undefined, target);
          // https://mathiasbynens.github.io/rel-noopener/
          if (newWindow) {
            newWindow.opener = null;
            newWindow.location = url;
          }
        }
      }
    }
  };

  private getTopLayerFrameAtSceneCoords = (sceneCoords: {
    x: number;
    y: number;
  }) => {
    const frames = this.scene
      .getNonDeletedFramesLikes()
      .filter((frame): frame is ExcalidrawFrameLikeElement =>
        isCursorInFrame(sceneCoords, frame),
      );

    return frames.length ? frames[frames.length - 1] : null;
  };

  /**
   * 重置所有笔记的hover状态
   * @param hoverdElement
   */
  private resetNoteElementHover = (hoverdElement:any)=>{
    const arrayElements = this.getSceneElements();
    let arrayNewElements = [];
    for (let i=0;i<arrayElements.length;i++){
      const element = arrayElements[i];
      if (isNoteElement(element)){
        element.customData.generationData.isHoverd = false;
        if (element.id == hoverdElement?.id){
          element.customData.generationData.isHoverd = true;
        }
      }
      arrayNewElements.push(element);
    }
    this.updateScene({
      elements:arrayNewElements
    })
  }

  private handleCanvasPointerMove = (
    event: React.PointerEvent<HTMLCanvasElement>,
  ) => {
    // console.log('handleCanvasPointerMovehandleCanvasPointerMove');

    this.props?.onPointerMove?.(event);

    let lastX = this.lastPointerMoveEvent?.clientX;
    let lastY = this.lastPointerMoveEvent?.clientY;
    let isMoving = Math.abs(event.clientX - lastX) > 5 || Math.abs(event.clientY - lastY)>5;
    if (this.mIsMouseDown){
      this.mIsMouseMoved = true;
    }
    if (this.mIsMouseDown && isMoving){
      this.mIsMouseMoving = true;
    }else{
      this.mIsMouseMoving = false;
    }

    global.g_DrawEventXY = {x:event.clientX,y:event.clientY};

    if (mIsPointerRightDown){
      mIsPointerRightMove = true;
      setCursor(this.interactiveCanvas, CURSOR_TYPE.GRABBING);
    }

    this.savePointer(event.clientX, event.clientY, this.state.cursorButton);
    this.lastPointerMoveEvent = event.nativeEvent;

    if (gesture.pointers.has(event.pointerId)) {
      gesture.pointers.set(event.pointerId, {
        x: event.clientX,
        y: event.clientY,
      });
    }

    const initialScale = gesture.initialScale;
    if (
      gesture.pointers.size === 2 &&
      gesture.lastCenter &&
      initialScale &&
      gesture.initialDistance
    ) {
      const center = getCenter(gesture.pointers);
      const deltaX = center.x - gesture.lastCenter.x;
      const deltaY = center.y - gesture.lastCenter.y;
      gesture.lastCenter = center;

      const distance = getDistance(Array.from(gesture.pointers.values()));
      const scaleFactor =
          (this.state.activeTool.type === "freedraw" && this.state.activeTool.type === "highlighter") && this.state.penMode
          ? 1
          : distance / gesture.initialDistance;

      const nextZoom = scaleFactor
        ? getNormalizedZoom(initialScale * scaleFactor)
        : this.state.zoom.value;

      this.setState((state) => {
        const zoomState = getStateForZoom(
          {
            viewportX: center.x,
            viewportY: center.y,
            nextZoom,
          },
          state,
        );

        this.translateCanvas({
          zoom: zoomState.zoom,
          scrollX: zoomState.scrollX + deltaX / nextZoom,
          scrollY: zoomState.scrollY + deltaY / nextZoom,
          shouldCacheIgnoreZoom: true,
        });
      });
      this.resetShouldCacheIgnoreZoomDebounced();
    } else {
      gesture.lastCenter =
        gesture.initialDistance =
        gesture.initialScale =
          null;
    }

    const scenePointer = viewportCoordsToSceneCoords(event, this.state);
    const { x: scenePointerX, y: scenePointerY } = scenePointer;

    let hitElement = this.getElementAtPosition(
        scenePointer.x,
        scenePointer.y,
        { isDOM: !(event.target instanceof HTMLCanvasElement) },
    );
    // if (hitElement && !this.state.selectedElementIds[hitElement?.id]){
    //   const arraySelecteds = this.getSelectedElements();
    //   if (arraySelecteds.length > 0){
    //     hitElement = arraySelecteds[0];
    //   }
    // }
    mMoveHitElement = hitElement;
    //*******video element---play......
    //todo 是否需要非video时关闭所有video的事件..触发
    if (hitElement) {
      if (isFileVideoElement(hitElement)){
        let dragging = {};
        this.setState(
            {
              activeEmbeddable: { element: hitElement, state: "hover" },
              ...dragging
            },
            () => {
              const container = document.getElementById(
                  `excalidraw__embeddable__outer${hitElement.id}`,
              );

              if (container) {
                if (this.state.draggingElement){
                  container.style.pointerEvents = "none";
                }else{
                  container.style.pointerEvents = "all";
                }

              }
            },
        );
      }
    }
    //*******video element---play end......

    //不能搞在这里。移动元素的时候影响太大了。。。
    // this.resetNoteElementHover(hitElement);

    if (
      isHoldingSpace ||
      isPanning ||
      isDraggingScrollBar ||
      isHandToolActive(this.state)
    ) {
      return;
    }

    const isPointerOverScrollBars = isOverScrollBars(
      currentScrollBars,
      event.clientX - this.state.offsetLeft,
      event.clientY - this.state.offsetTop,
    );
    const isOverScrollBar = isPointerOverScrollBars.isOverEither;
    if (!this.state.draggingElement && !this.state.multiElement) {
      if (isOverScrollBar) {
        resetCursor(this.interactiveCanvas);
      } else {
        setCursorForShape(this.interactiveCanvas, this.state);
      }
    }

    if (
      !this.state.draggingElement &&
      isActiveToolNonLinearSnappable(this.state.activeTool.type)
    ) {
      const { originOffset, snapLines } = getSnapLinesAtPointer(
        this.scene.getNonDeletedElements(),
        this.state,
        {
          x: scenePointerX,
          y: scenePointerY,
        },
        event,
      );

      this.setState((prevState) => {
        const nextSnapLines = updateStable(prevState.snapLines, snapLines);
        const nextOriginOffset = prevState.originSnapOffset
          ? updateStable(prevState.originSnapOffset, originOffset)
          : originOffset;

        if (
          prevState.snapLines === nextSnapLines &&
          prevState.originSnapOffset === nextOriginOffset
        ) {
          return null;
        }
        return {
          snapLines: nextSnapLines,
          originSnapOffset: nextOriginOffset,
        };
      });
    } else if (!this.state.draggingElement) {
      this.setState((prevState) => {
        if (prevState.snapLines.length) {
          return {
            snapLines: [],
          };
        }
        return null;
      });
    }

    if (
      this.state.editingLinearElement &&
      !this.state.editingLinearElement.isDragging
    ) {
      const editingLinearElement = LinearElementEditor.handlePointerMove(
        event,
        scenePointerX,
        scenePointerY,
        this.state,
      );

      if (
        editingLinearElement &&
        editingLinearElement !== this.state.editingLinearElement
      ) {
        // Since we are reading from previous state which is not possible with
        // automatic batching in React 18 hence using flush sync to synchronously
        // update the state. Check https://github.com/excalidraw/excalidraw/pull/5508 for more details.
        flushSync(() => {
          this.setState({
            editingLinearElement,
          });
        });
      }
      if (editingLinearElement?.lastUncommittedPoint != null) {
        this.maybeSuggestBindingAtCursor(scenePointer);
      } else {
        // causes stack overflow if not sync
        flushSync(() => {
          this.setState({ suggestedBindings: [] });
        });
      }
    }

    if (isBindingElementType(this.state.activeTool.type)) {
      // Hovering with a selected tool or creating new linear element via click
      // and point
      const { draggingElement } = this.state;
      if (isBindingElement(draggingElement, false)) {
        this.maybeSuggestBindingsForLinearElementAtCoords(
          draggingElement,
          [scenePointer],
          this.state.startBoundElement,
        );
      } else {
        this.maybeSuggestBindingAtCursor(scenePointer);
      }
    }

    if (this.state.multiElement) {
      const { multiElement } = this.state;
      const { x: rx, y: ry } = multiElement;

      const { points, lastCommittedPoint } = multiElement;
      const lastPoint = points[points.length - 1];

      setCursorForShape(this.interactiveCanvas, this.state);

      if (lastPoint === lastCommittedPoint) {
        // if we haven't yet created a temp point and we're beyond commit-zone
        // threshold, add a point
        if (
          distance2d(
            scenePointerX - rx,
            scenePointerY - ry,
            lastPoint[0],
            lastPoint[1],
          ) >= LINE_CONFIRM_THRESHOLD
        ) {
          mutateElement(multiElement, {
            points: [...points, [scenePointerX - rx, scenePointerY - ry]],
          });
        } else {
          setCursor(this.interactiveCanvas, CURSOR_TYPE.POINTER);
          // in this branch, we're inside the commit zone, and no uncommitted
          // point exists. Thus do nothing (don't add/remove points).
        }
      } else if (
        points.length > 2 &&
        lastCommittedPoint &&
        distance2d(
          scenePointerX - rx,
          scenePointerY - ry,
          lastCommittedPoint[0],
          lastCommittedPoint[1],
        ) < LINE_CONFIRM_THRESHOLD
      ) {
        setCursor(this.interactiveCanvas, CURSOR_TYPE.POINTER);
        mutateElement(multiElement, {
          points: points.slice(0, -1),
        });
      } else {
        const [gridX, gridY] = getGridPoint(
          scenePointerX,
          scenePointerY,
          event[KEYS.CTRL_OR_CMD] ? null : this.state.gridSize,
        );

        const [lastCommittedX, lastCommittedY] =
          multiElement?.lastCommittedPoint ?? [0, 0];

        let dxFromLastCommitted = gridX - rx - lastCommittedX;
        let dyFromLastCommitted = gridY - ry - lastCommittedY;

        if (shouldRotateWithDiscreteAngle(event)) {
          ({ width: dxFromLastCommitted, height: dyFromLastCommitted } =
            getLockedLinearCursorAlignSize(
              // actual coordinate of the last committed point
              lastCommittedX + rx,
              lastCommittedY + ry,
              // cursor-grid coordinate
              gridX,
              gridY,
            ));
        }

        if (isPathALoop(points, this.state.zoom.value)) {
          setCursor(this.interactiveCanvas, CURSOR_TYPE.POINTER);
        }
        // update last uncommitted point
        mutateElement(multiElement, {
          points: [
            ...points.slice(0, -1),
            [
              lastCommittedX + dxFromLastCommitted,
              lastCommittedY + dyFromLastCommitted,
            ],
          ],
        });
      }

      return;
    }

    const hasDeselectedButton = Boolean(event.buttons);
    if (
      hasDeselectedButton ||
      (this.state.activeTool.type !== "selection" &&
        this.state.activeTool.type !== "text" &&
        this.state.activeTool.type !== "eraser")
    ) {
      return;
    }

    const elements = this.scene.getNonDeletedElements();

    const selectedElements = this.scene.getSelectedElements(this.state);
    if (
      selectedElements.length === 1 &&
      !isOverScrollBar &&
      !this.state.editingLinearElement
    ) {
      // this.elementWithQuickFlowHandle = getElementWithQuickFlowHandles({
      //   element: selectedElements[0],
      //   appState: this.state,
      //   scenePointerX,
      //   scenePointerY,
      //   zoom: this.state.zoom,
      //   pointerType: event.pointerType,
      // });
      //
      // if (this.elementWithQuickFlowHandle) {
      //   this.setState({
      //     quickFlowConfig: {
      //       // @ts-ignore
      //       activeHandle: this.elementWithQuickFlowHandle.direction,
      //     },
      //   });
      //   setCursor(this.interactiveCanvas, CURSOR_TYPE.POINTER);
      //   return;
      // }
      // this.setState({
      //   quickFlowConfig: {
      //     activeHandle: null,
      //   },
      // });

        let element = null;
        if (selectedElements.length >0){
            element = selectedElements[0];
        }

        const elementWithTransformHandleType = getElementWithTransformHandleType(
        elements,
        this.state,
        scenePointerX,
        scenePointerY,
        this.state.zoom,
        event.pointerType,
      );
      if (
        elementWithTransformHandleType &&
        elementWithTransformHandleType.transformHandleType
      ) {
        setCursor(
          this.interactiveCanvas,
          getCursorForResizingElement(elementWithTransformHandleType),
            element
        );
        return;
      }
      else{
          if (isStickyNotesElement(element)){
              const div = document.getElementById(`stickynote-container-${element.id}`);
              if (div){
                  div.style.cursor = 'pointer';
              }
          }else if (isTextElement(element)){
              const div = document.getElementById(`texttiptap-container-${element.id}`);
              if (div){
                  div.style.cursor = 'pointer';
              }
          }
      }
    } else if (selectedElements.length > 1 && !isOverScrollBar) {
      const transformHandleType = getTransformHandleTypeFromCoords(
        getCommonBounds(selectedElements),
        scenePointerX,
        scenePointerY,
        this.state.zoom,
        event.pointerType,
      );
      if (transformHandleType) {
        setCursor(
          this.interactiveCanvas,
          getCursorForResizingElement({
            transformHandleType,
          }),
        );
        return;
      }
    }

    /**
     *  擦除功能
     */
    if (isEraserActive(this.state)) {
      return;
    }

    // 如果hover到元素上，并且到控件上，则高亮控件

    if (this.state.hoveredElement?.element && this.state.hoveredElement?.element?.height > HOVER_CIRCLE_ELEMENT_SIZE && this.state.hoveredElement?.element?.width > HOVER_CIRCLE_ELEMENT_SIZE) {
      this.elementWithHoverHandle = getElementWithHoverHandles({
        element: this.state.hoveredElement.element,
        appState: this.state,
        scenePointerX,
        scenePointerY,
      });
      if (this.elementWithHoverHandle) {
        this.setState({
          hoveredElement: {
            ...this.state.hoveredElement,
            status: "highlight",
          },
        });
        setCursor(this.interactiveCanvas, CURSOR_TYPE.POINTER);
        return;
      }
    }

    const bindingDirections: { [key: string]: string } = {};
    // 如果选中一个元素，并且是线性元素
    if (selectedElements.length === 1 && isLinearElement(selectedElements[0])) {
      const [selectedElement] = selectedElements;
      const connectedElementIds = [
        selectedElement.startBinding,
        selectedElement.endBinding,
      ].reduce((ids, binding) => {
        return binding?.elementId ? { ...ids, [binding.elementId]: true } : ids;
      }, {});

      const bindingElements =
          connectedElementIds &&
          this.scene.getSelectedElements({
            selectedElementIds: connectedElementIds,
          });

      bindingElements?.forEach((element) => {
        (
            ["startConnectionDirection", "endConnectionDirection"] as (
                | "startConnectionDirection"
                | "endConnectionDirection"
                )[]
        ).forEach((connection) => {
          if (element[connection]?.[selectedElement.id]) {
            // @ts-ignore
            bindingDirections[element.id] =
                // @ts-ignore
                element[connection][selectedElement.id];
          }
        });
      });
    }

    if (
        !!hitElement &&
        this.getElementsAtPosition(scenePointer.x, scenePointer.y).some(
            (element) => isHoverAbleElement(element),
        )
    ) {
      const element = this.getElementsAtPosition(
          scenePointer.x,
          scenePointer.y,
      ).find((element) => isHoverAbleElement(element))!;

      const { x, y, width, height } = element;
      const { x: scenePointerX, y: scenePointerY } = scenePointer;
      const [pointerX, pointerY] = rotate(
          scenePointerX,
          scenePointerY,
          element.x + element.width / 2,
          element.y + element.height / 2,
          -element.angle,
      );

      // 定义边界的阈值，用于确定指针是否位于边界附近
      const threshold = 10; // 可以根据需要调整这个值

      // 初始化direction变量
      let direction: "top" | "bottom" | "left" | "right" | null = null;

      // 计算指针与hitElement的四个边界的距离
      const distanceTop = Math.abs(y - pointerY);
      const distanceBottom = Math.abs(y + height - pointerY);
      const distanceLeft = Math.abs(x - pointerX);
      const distanceRight = Math.abs(x + width - pointerX);

      // 检查指针是否在顶部边界附近
      if (distanceTop <= threshold && pointerX >= x && pointerX <= x + width) {
        direction = "top";
      }
      // 检查指针是否在底部边界附近
      else if (
          distanceBottom <= threshold &&
          pointerX >= x &&
          pointerX <= x + width
      ) {
        direction = "bottom";
      }
      // 检查指针是否在左侧边界附近
      else if (
          distanceLeft <= threshold &&
          pointerY >= y &&
          pointerY <= y + height
      ) {
        direction = "left";
      }
      // 检查指针是否在右侧边界附近
      else if (
          distanceRight <= threshold &&
          pointerY >= y &&
          pointerY <= y + height
      ) {
        direction = "right";
      }
      if (bindingDirections[element.id] !== direction) {
        // 如果hover到元素上，则显示hover控件
        this.setState({
          hoveredElement: {
            ...this.state.hoveredElement,
            element,
            direction,
            status: "show",
          },
        });
      }
    } else {
      this.setState({
        hoveredElement: {
          ...this.state.hoveredElement,
          element: null,
        },
      });
    }

    this.hitLinkElement = this.getElementLinkAtPosition(
      scenePointer,
      hitElement,
    );
    // if (isEraserActive(this.state)) {
    //   return;
    // }

    // 视频笔记
    this.hitVideoNotesElement = this.getElementVideoAtPosition(
        scenePointer,
        hitElement,
    );

    if (this.hitVideoNotesElement) {
      setCursor(this.interactiveCanvas, CURSOR_TYPE.POINTER);
    }

    if (
      this.hitLinkElement &&
      !this.state.selectedElementIds[this.hitLinkElement.id]
    ) {
      setCursor(this.interactiveCanvas, CURSOR_TYPE.POINTER);
      showHyperlinkTooltip(this.hitLinkElement, this.state);
    } else {
      hideHyperlinkToolip();
      if (this.state.displayMode === "preview" && this.state.activeTool.type != 'laser') {
        return;
      }

      if (
        hitElement &&
        (hitElement.link || isEmbeddableElement(hitElement)) &&
        this.state.selectedElementIds[hitElement.id] &&
        !this.state.contextMenu &&
        !this.state.showHyperlinkPopup
      ) {
        this.setState({ showHyperlinkPopup: "info" });
      } else if (this.state.activeTool.type === "text") {
        setCursor(
          this.interactiveCanvas,
          isTextElement(hitElement) ? CURSOR_TYPE.TEXT : CURSOR_TYPE.CROSSHAIR,
        );
      } else if (this.state.viewModeEnabled) {
        setCursor(this.interactiveCanvas, CURSOR_TYPE.GRAB);
      } else if (isOverScrollBar) {
        setCursor(this.interactiveCanvas, CURSOR_TYPE.AUTO);
      } else if (this.state.selectedLinearElement) {
        this.handleHoverSelectedLinearElement(
          this.state.selectedLinearElement,
          scenePointerX,
          scenePointerY,
        );
      } else if (
        // if using cmd/ctrl, we're not dragging
        !event[KEYS.CTRL_OR_CMD]
      ) {
        if (
          (hitElement ||
            this.isHittingCommonBoundingBoxOfSelectedElements(
              scenePointer,
              selectedElements,
            )) &&
          !hitElement?.locked
        ) {
          setCursor(this.interactiveCanvas, CURSOR_TYPE.MOVE);
          if (this.state.activeEmbeddable?.state === "hover") {
            this.setState({ activeEmbeddable: null });
          }
          if (!hitElement) {
            return;
          }
          if (isFrameElement(hitElement)) {
            setCursor(this.interactiveCanvas, CURSOR_TYPE.POINTER);
            return;
          }
          if (
              isIframeLikeElement(hitElement) &&
              this.isIframeLikeElementCenter(
                  hitElement,
                  event,
                  scenePointerX,
                  scenePointerY,
              )
          ) {
            this.setState({
              activeEmbeddable: { element: hitElement, state: "hover" },
            });
            setCursor(this.interactiveCanvas, CURSOR_TYPE.POINTER);
            return;
          }
          //stickyNotes
          if (isStickyNotesElement(hitElement)) {
            setCursor(
                this.interactiveCanvas,
                this.isStickyNotesElementCenter(
                    hitElement,
                    event,
                    scenePointerX,
                    scenePointerY,
                )
                    ? CURSOR_TYPE.POINTER
                    : CURSOR_TYPE.MOVE,
            );
            return;
          }
          // mindmap
          if (isMindmapElement(hitElement)) {
            this.setState(
                {
                  activeMindMap: { element: hitElement, state: "hover" },
                },
                () => {
                  //todo 这里，设置为all时   有拖动的元素经过mindmap时，会卡顿。***** 同mindMapSelect中的 一个设置

                  // const container = document.getElementById(
                  //     `excalidraw__mindmap__outer${hitElement.id}`,
                  // );

                  // if (
                  //     container &&
                  //     Object.keys(this.state.selectedElementIds).length === 0
                  // ) {
                  //   container.style.pointerEvents = "all";
                  // }
                },
            );
          }
        } else if (this.state.activeMindMap?.state === "hover") {
          this.setState({ activeMindMap: null});
        }
      } else {
        setCursor(this.interactiveCanvas, CURSOR_TYPE.AUTO);
      }
    }
  };

  private handleEraser = (
    event: PointerEvent,
    pointerDownState: PointerDownState,
    scenePointer: { x: number; y: number },
  ) => {
    const updateElementIds = (elements: ExcalidrawElement[]) => {
      elements.forEach((element) => {
        if (element.locked) {
          return;
        }

        idsToUpdate.push(element.id);
        if (event.altKey) {
          if (
            pointerDownState.elementIdsToErase[element.id] &&
            pointerDownState.elementIdsToErase[element.id].erase
          ) {
            pointerDownState.elementIdsToErase[element.id].erase = false;
          }
        } else if (!pointerDownState.elementIdsToErase[element.id]) {
          pointerDownState.elementIdsToErase[element.id] = {
            erase: true,
            opacity: element.opacity,
          };
        }
      });
    };

    const idsToUpdate: Array<string> = [];

    const distance = distance2d(
      pointerDownState.lastCoords.x,
      pointerDownState.lastCoords.y,
      scenePointer.x,
      scenePointer.y,
    );
    const threshold = 10 / this.state.zoom.value;
    const point = { ...pointerDownState.lastCoords };
    let samplingInterval = 0;
    while (samplingInterval <= distance) {
      const hitElements = this.getElementsAtPosition(point.x, point.y);
      updateElementIds(hitElements);

      // Exit since we reached current point
      if (samplingInterval === distance) {
        break;
      }

      // Calculate next point in the line at a distance of sampling interval
      samplingInterval = Math.min(samplingInterval + threshold, distance);

      const distanceRatio = samplingInterval / distance;
      const nextX =
        (1 - distanceRatio) * point.x + distanceRatio * scenePointer.x;
      const nextY =
        (1 - distanceRatio) * point.y + distanceRatio * scenePointer.y;
      point.x = nextX;
      point.y = nextY;
    }

    const elements = this.scene.getElementsIncludingDeleted().map((ele) => {
      // 画笔pen和荧光笔才能被擦除
      if (!isFreeDrawElementType(ele.type)) {
        return ele;
      }

      const id =
        isBoundToContainer(ele) && idsToUpdate.includes(ele.containerId)
          ? ele.containerId
          : ele.id;
      if (idsToUpdate.includes(id)) {
        if (event.altKey) {
          if (
            pointerDownState.elementIdsToErase[id] &&
            pointerDownState.elementIdsToErase[id].erase === false
          ) {
            return newElementWith(ele, {
              opacity: pointerDownState.elementIdsToErase[id].opacity,
            });
          }
        } else {
          return newElementWith(ele, {
            opacity: ELEMENT_READY_TO_ERASE_OPACITY,
          });
        }
      }
      return ele;
    });

    this.scene.replaceAllElements(elements);

    pointerDownState.lastCoords.x = scenePointer.x;
    pointerDownState.lastCoords.y = scenePointer.y;
  };
  // set touch moving for mobile context menu
  private handleTouchMove = (event: React.TouchEvent<HTMLCanvasElement>) => {
    invalidateContextMenu = true;
  };

  handleHoverSelectedLinearElement(
    linearElementEditor: LinearElementEditor,
    scenePointerX: number,
    scenePointerY: number,
  ) {
    const element = LinearElementEditor.getElement(
      linearElementEditor.elementId,
    );

    const boundTextElement = getBoundTextElement(element);

    if (!element) {
      return;
    }
    if (this.state.selectedLinearElement) {
      let hoverPointIndex = -1;
      let segmentMidPointHoveredCoords = null;
      if (
        isHittingElementNotConsideringBoundingBox(
          element,
          this.state,
          this.frameNameBoundsCache,
          [scenePointerX, scenePointerY],
        )
      ) {
        hoverPointIndex = LinearElementEditor.getPointIndexUnderCursor(
          element,
          this.state.zoom,
          scenePointerX,
          scenePointerY,
        );
        segmentMidPointHoveredCoords =
          LinearElementEditor.getSegmentMidpointHitCoords(
            linearElementEditor,
            { x: scenePointerX, y: scenePointerY },
            this.state,
          );

        if (hoverPointIndex >= 0 || segmentMidPointHoveredCoords) {
          setCursor(this.interactiveCanvas, CURSOR_TYPE.POINTER);
        } else {
          setCursor(this.interactiveCanvas, CURSOR_TYPE.MOVE);
        }
      } else if (
        shouldShowBoundingBox([element], this.state) &&
        isHittingElementBoundingBoxWithoutHittingElement(
          element,
          this.state,
          this.frameNameBoundsCache,
          scenePointerX,
          scenePointerY,
        )
      ) {
        setCursor(this.interactiveCanvas, CURSOR_TYPE.MOVE);
      } else if (
        boundTextElement &&
        hitTest(
          boundTextElement,
          this.state,
          this.frameNameBoundsCache,
          scenePointerX,
          scenePointerY,
        )
      ) {
        setCursor(this.interactiveCanvas, CURSOR_TYPE.MOVE);
      }

      if (
        this.state.selectedLinearElement.hoverPointIndex !== hoverPointIndex
      ) {
        this.setState({
          selectedLinearElement: {
            ...this.state.selectedLinearElement,
            hoverPointIndex,
          },
        });
      }

      if (
        !LinearElementEditor.arePointsEqual(
          this.state.selectedLinearElement.segmentMidPointHoveredCoords,
          segmentMidPointHoveredCoords,
        )
      ) {
        this.setState({
          selectedLinearElement: {
            ...this.state.selectedLinearElement,
            segmentMidPointHoveredCoords,
          },
        });
      }
    } else {
      setCursor(this.interactiveCanvas, CURSOR_TYPE.AUTO);
    }
  }


  private handleCanvasPointerDown = (
    event: React.PointerEvent<HTMLElement>,
  selectElement:any = null) => {

    this.mIsMouseDown = true;

    if (event.button == 2){
      mIsPointerRightDown = true;
      this.setState({
        editingElement:null,
        draggingElement:null,
      })
    }

    updateMindmapCallback(this.createMindMapCallBack);
    //重置所有笔记的编辑状态...
    if (this.state.activeTool.type != 'hand'){
      this.onResetNoteEditElement();
    }

    /* 这里限制了 this.state.displayMode === "preview" 的pan移动...加个限制 ...
    if ((this.state.displayMode === "preview") && this.state.activeTool.type != 'laser') {
      const { x, y } = viewportCoordsToSceneCoords(event, this.state);
      let element = this.getElementAtPosition(x, y, {
        preferSelected: true,
        includeLockedElements: true,
        isDOM: !(event.target instanceof HTMLCanvasElement),
      });
      const pointerDownState = this.initialPointerDownState(event);
      pointerDownState.hit.element = element;

      this.props?.onPointerDown?.(this.state.activeTool, pointerDownState);
      this.onPointerDownEmitter.trigger(
          this.state.activeTool,
          pointerDownState,
          event,
      );
    }*/
    //这里不用限制了。。。。
    // if (this.state.displayMode == 'preview'){
    //   if (this.state.originZoomValue.toFixed(1) >= this.state.zoom.value.toFixed(1)){
    //     return;
    //   }
    // }


    const hoverModel = document.getElementById("hoverModel")!;
    if (hoverModel.style.display !== "none") {
      hoverModel.style.display = "none";
      const hoveredElementType = hoverModel.getAttribute("type");
      if (hoveredElementType !== "pin") {
        this.actionManager.executeAction(actionDeleteSelected);
      }
    }


    this.setState((prevState) => {
      return {
        activeTool: {
          ...prevState.activeTool,
          activeToolFirstClicked: false,
        },
        searchContent: { visible: false },
        quickFlowConfig: {
          activeHandle: null,
        },
      };
    });

    if (event.type != 'input'){
      clearMindmapActiveNode();
    }


    this.maybeCleanupAfterMissingPointerUp(event.nativeEvent);
    this.maybeUnfollowRemoteUser();

    if (this.state.activeTool.type != 'eraser' &&
        this.state.activeTool.type != 'freedraw' &&
        this.state.activeTool.type != 'highlighter' &&
        this.state.activeTool.type != 'laser' &&
        this.state.activeTool.type != 'hand' &&
        !event.shiftKey &&
        Object.keys(this.state.selectedElementIds).length <= 1 &&
        Object.keys(this.state.selectedGroupIds).length == 0){
      const { x, y } = viewportCoordsToSceneCoords(event, this.state);
      // const element = this.getElementAtPosition(x, y, {
      //   preferSelected: true,
      //   includeLockedElements: true,
      //   filterFrameBackgroundImage: true,
      //   isDOM: true, //!(event.target instanceof HTMLCanvasElement),
      // });
      let allHitElements = this.getElementsAtPosition(
          x,
          y,
          false,
          true,
      );
      let element = null;
      if (allHitElements.length > 0){
        if (allHitElements.every(p=>isStickyNotesElement(p))){
          element = allHitElements[allHitElements.length - 1];
          selectElement = element;
        }else{
          if (allHitElements[0].type == 'arrow' || isStickyNotesElement(allHitElements[0]) || (isTextElement(allHitElements[0]) && !allHitElements[0].containerId)){
            element = allHitElements[0];
            selectElement = element;
          }else{
            element = allHitElements[allHitElements.length - 1];
          }
        }
      }

      if (selectElement){
        element = selectElement;
      }

      global.g_hitElement = element;

      //各种地方都要完美，所以这里也要设置一下。因为--新建mindmap后，直接在别的元素上拖拽连接线只能在mindmap的边界上。*/
      if (APP_WEB){
        for (const element of mindMapCache) {
          const dom = document.getElementById(
              `excalidraw__mindmap__outer${element[0]}`,
          )!;
          if (dom){
            dom.style.pointerEvents = "all";
          }
        }
      }else{
        for (const element of mindMapCache) {
          const dom = document.getElementById(
              `excalidraw__mindmap__outer${element[0]}`,
          )!;
          if (dom){
            if (isMindmapElement(element)){
              dom.style.pointerEvents = "all";
            }else{
              dom.style.pointerEvents = "none";
            }
          }
        }
      }


      if (element?.groupIds?.length > 0){

      }else{
        //正在旋转的时候，不能再设置这个....------- 暂时这样判断...
        if (getCursor(this.interactiveCanvas) != CURSOR_TYPE.GRAB){
          //这里要加个判断，当是选中箭头的时候，在端点的时候，不要选择下面这个element!!!!
          if (this.state.activeTool.type == 'selection'){
            this.props?.onClickElement?.(element,false,event);
          }
        }
      }
      if (element){
        if (isNoteElement(element) || isProjectLinkElement(element)){
          //将当前选中的元素置于顶层
          const ele = element;
          setTimeout(()=>{
            actionBringToFront.perform([ele],this.state);
          },50)
        }
      }
    }

    // since contextMenu options are potentially evaluated on each render,
    // and an contextMenu action may depend on selection state, we must
    // close the contextMenu before we update the selection on pointerDown
    // (e.g. resetting selection)
    if (this.state.contextMenu) {
      this.setState({ contextMenu: null });
    }

    if (this.state.snapLines) {
      this.setAppState({ snapLines: [] });
    }

    this.updateGestureOnPointerDown(event);

    // if dragging element is freedraw and another pointerdown event occurs
    // a second finger is on the screen
    // discard the freedraw element if it is very short because it is likely
    // just a spike, otherwise finalize the freedraw element when the second
    // finger is lifted
    if (
      event.pointerType === "touch" &&
      this.state.draggingElement &&
      this.state.draggingElement.type === "freedraw"
    ) {
      const element = this.state.draggingElement as ExcalidrawFreeDrawElement;
      this.updateScene({
        ...(element.points.length < 10
          ? {
              elements: this.scene
                .getElementsIncludingDeleted()
                .filter((el) => el.id !== element.id),
            }
          : {}),
        appState: {
          draggingElement: null,
          editingElement: null,
          startBoundElement: null,
          suggestedBindings: [],
          selectedElementIds: makeNextSelectedElementIds(
            Object.keys(this.state.selectedElementIds)
              .filter((key) => key !== element.id)
              .reduce((obj: { [id: string]: true }, key) => {
                obj[key] = this.state.selectedElementIds[key];
                return obj;
              }, {}),
            this.state,
          ),
        },
      });
      return;
    }

    // remove any active selection when we start to interact with canvas
    // (mainly, we care about removing selection outside the component which
    //  would prevent our copy handling otherwise)
    const selection = document.getSelection();
    if (selection?.anchorNode) {
      selection.removeAllRanges();
    }
    this.maybeOpenContextMenuAfterPointerDownOnTouchDevices(event);

    //fires only once, if pen is detected, penMode is enabled
    //the user can disable this by toggling the penMode button
    if (!this.state.penDetected && event.pointerType === "pen") {
      this.setState((prevState) => {
        return {
          penMode: true,
          penDetected: true,
        };
      });
    }

    if (
      !this.device.isTouchScreen &&
      ["pen", "touch"].includes(event.pointerType)
    ) {
      this.device = updateObject(this.device, { isTouchScreen: true });
    }

    if (isPanning) {
      return;
    }

    this.lastPointerDownEvent = event;


    // we must exit before we set `cursorButton` state and `savePointer`
    // else it will send pointer state & laser pointer events in collab when
    // panning
    if (this.handleCanvasPanUsingWheelOrSpaceDrag(event)) {
      const { x, y } = viewportCoordsToSceneCoords(event, this.state);
      const element = this.getElementAtPosition(x, y, {
        preferSelected: true,
        includeLockedElements: true,
        isDOM: !(event.target instanceof HTMLCanvasElement),
      });
      const pointerDownState = this.initialPointerDownState(event);
      pointerDownState.hit.element = element;
      this.props?.onPointerDown?.(this.state.activeTool, pointerDownState);
      this.onPointerDownEmitter.trigger(
        this.state.activeTool,
        pointerDownState,
        event,
      );
      return;
    }


    this.setState({
      lastPointerDownWith: event.pointerType,
      cursorButton: "down",
    });
    this.savePointer(event.clientX, event.clientY, "down");

    if (
      event.button === POINTER_BUTTON.ERASER &&
      this.state.activeTool.type !== TOOL_TYPE.eraser
    ) {
      this.setState(
        {
          activeTool: updateActiveTool(this.state, {
            type: TOOL_TYPE.eraser,
            lastActiveToolBeforeEraser: this.state.activeTool,
          }),
        },
        () => {
          this.handleCanvasPointerDown(event);
          const onPointerUp = () => {
            unsubPointerUp();
            unsubCleanup?.();
            if (isEraserActive(this.state)) {
              this.setState({
                activeTool: updateActiveTool(this.state, {
                  ...(this.state.activeTool.lastActiveTool || {
                    type: TOOL_TYPE.selection,
                  }),
                  lastActiveToolBeforeEraser: null,
                }),
              });
            }
          };

          const unsubPointerUp = addEventListener(
            window,
            EVENT.POINTER_UP,
            onPointerUp,
            {
              once: true,
            },
          );
          let unsubCleanup: UnsubscribeCallback | undefined;
          // subscribe inside rAF lest it'd be triggered on the same pointerdown
          // if we start erasing while coming from blurred document since
          // we cleanup pointer events on focus
          requestAnimationFrame(() => {
            unsubCleanup =
              this.missingPointerEventCleanupEmitter.once(onPointerUp);
          });
        },
      );
      return;
    }

    // only handle left mouse button or touch
    if (
      event.button !== POINTER_BUTTON.MAIN &&
      event.button !== POINTER_BUTTON.TOUCH &&
      event.button !== POINTER_BUTTON.ERASER
    ) {
      return;
    }

    // don't select while panning
    if (gesture.pointers.size > 1) {
      return;
    }

    // State for the duration of a pointer interaction, which starts with a
    // pointerDown event, ends with a pointerUp event (or another pointerDown)
    const pointerDownState = this.initialPointerDownState(event);
    if (selectElement){
      pointerDownState.hit.element = selectElement;
    }

    this.setState({
      selectedElementsAreBeingDragged: false,
    });

    if (this.handleDraggingScrollBar(event, pointerDownState)) {
      return;
    }

    this.clearSelectionIfNotUsingSelection();
    this.updateBindingEnabledOnPointerMove(event);

    if (this.handleSelectionOnPointerDown(event, pointerDownState)) {
      return;
    }


    const allowOnPointerDown =
      !this.state.penMode ||
      event.pointerType !== "touch" ||
      this.state.activeTool.type === "selection" ||
      this.state.activeTool.type === "text" ||
      this.state.activeTool.type === "image";

    if (!allowOnPointerDown) {
      return;
    }

    if (this.state.activeTool.type === "text") {
      this.handleTextOnPointerDown(event, pointerDownState);
      return;
    } else if (
      this.state.activeTool.type === "arrow" ||
      this.state.activeTool.type === "line" ||
        this.elementWithQuickFlowHandle ||
        this.elementWithHoverHandle
    ) {
      if (
          this.state.activeTool.type === "arrow" ||
          this.state.activeTool.type === "line"
      ) {
        this.handleLinearElementOnPointerDown(
            event,
            this.state.activeTool.type,
            pointerDownState,
        );
      } else if (this.elementWithQuickFlowHandle) {
        setCursor(this.interactiveCanvas, CURSOR_TYPE.CROSSHAIR);
        this.handleLinearElementOnPointerDown(
            event,
            "arrow",
            pointerDownState,
            this.elementWithQuickFlowHandle,
        );
      } else if (this.elementWithHoverHandle) {
        const linearElement = this.handleLinearElementOnPointerDown(
            event,
            "arrow",
            pointerDownState,
            undefined,
            this.elementWithHoverHandle,
        )!;
        // 如果点击到hover高亮的控件，则创建线条
        this.setState({
          hoveredElement: {
            ...this.state.hoveredElement,
            sourceElement: this.state.hoveredElement.element,
            linearElement,
            status: null,
          },
        });
      }
    } else if (this.state.activeTool.type === "image") {
      // reset image preview on pointerdown
      setCursor(this.interactiveCanvas, CURSOR_TYPE.CROSSHAIR);

      // retrieve the latest element as the state may be stale
      const pendingImageElement =
        this.state.pendingImageElementId &&
        this.scene.getElement(this.state.pendingImageElementId);

      if (!pendingImageElement) {
        return;
      }


      this.setState({
        draggingElement: pendingImageElement,
        editingElement: pendingImageElement,
        pendingImageElementId: null,
        multiElement: null,
      });

      const { x, y } = viewportCoordsToSceneCoords(event, this.state);
      const frame = this.getTopLayerFrameAtSceneCoords({ x, y });

      mutateElement(pendingImageElement, {
        x,
        y,
        frameId: frame ? frame.id : null,
        status:''
      });
    }  else if (this.state.activeTool.type === "mindmap") {
      const pendinmindmapElement =
          this.state.pendingMindmapElementId &&
          this.scene.getElement(this.state.pendingMindmapElementId);

      if (!pendinmindmapElement) {
        return;
      }

      // this.setState({
      //   draggingElement: pendinmindmapElement,
      //   editingElement: pendinmindmapElement,
      //   pendingImageElementId: null,
      //   multiElement: null,
      // });

      const { x, y } = viewportCoordsToSceneCoords(event, this.state);

      const frame = this.getTopLayerFrameAtSceneCoords({ x, y });

      mutateElement(pendinmindmapElement, {
        x,
        y,
        frameId: frame ? frame.id : null,
      });
      createMindMap(
          pendinmindmapElement as ExcalidrawMindmapElement,
          this.createMindMapCallBack,
          this.props.onLinkOpen,
          true,
          this.state
      );
    }else if (this.state.activeTool.type === "stickyNotes") {
      const { x, y } = viewportCoordsToSceneCoords(event, this.state);

      const stickyNotesElement = this.createStickyNotesElement({
        sceneX: x,
        sceneY: y,
        addToFrameUnderCursor: true,
        backgroundColor: localStorage.getItem(KEY_DEFAULT_STICKYNOTE_BACKGROUND_COLOR) || DEFAULT_STICKYNOTE_BACKGROUND_COLOR//localStorage.getItem("stickyColor") || "rgb(240, 140, 0)",
      });

      this.scene.addNewElement(stickyNotesElement);
      this.setState({
        selectedElementIds: { [stickyNotesElement.id]: true },
        activeStickyNotes: { element: stickyNotesElement, state: "active" },
      });
    }
    else if (this.state.activeTool.type === "freedraw" || this.state.activeTool.type === "highlighter") {
      this.handleFreeDrawElementOnPointerDown(
        event,
        this.state.activeTool.type,
        pointerDownState,
      );
    } else if (this.state.activeTool.type === "custom") {
      setCursorForShape(this.interactiveCanvas, this.state);
    } else if (
      this.state.activeTool.type === TOOL_TYPE.frame ||
      this.state.activeTool.type === TOOL_TYPE.magicframe
    ) {
      this.createFrameElementOnPointerDown(
        pointerDownState,
        this.state.activeTool.type,
      );
    } else if (this.state.activeTool.type === "laser") {
      this.laserPathManager.startPath(
        pointerDownState.lastCoords.x,
        pointerDownState.lastCoords.y,
      );
    }
    else if (this.state.activeTool.type == 'card'){
      const { x, y } = viewportCoordsToSceneCoords(event, this.state);
      const cardElement:any = this.createCardElement(x,y,null);
      this.scene.addNewElement(cardElement);
    }else if (this.state.activeTool.type == 'project'){
      const { x, y } = viewportCoordsToSceneCoords(event, this.state);
      if (window.onAddNewCanvas){
        const newBoard = window.onAddNewCanvas();

        setTimeout(()=>{
          window.saveNotes().then(res=>{
            const resultElement = this.createProjectLink(x,y,PROJECT_ID,newBoard.key);
            this.scene.addNewElement(resultElement!);
          })
        },200)
      }
    }


    else if (
      this.state.activeTool.type !== "eraser" &&
      this.state.activeTool.type !== "hand" &&
        !this.elementWithQuickFlowHandle &&
        !this.elementWithHoverHandle
    ) {
      this.createGenericElementOnPointerDown(
        this.state.activeTool.type,
        pointerDownState,
      );

      if (this.state.activeTool.type === "selection") {
        const { x, y } = viewportCoordsToSceneCoords(event, this.state);
        mindMapCache.forEach((mindmap) => {
          // @ts-ignore
          mindmap.select.onMousedown(event, { x, y });
        });
      }
    }


    if (/*global.g_isPresentMode && */!pointerDownState.hit.element){
      //bookmark预览中，pointerDownState.hit.element取的是null,所以这里要重新找一下。。不知道为什么
      const { x, y } = viewportCoordsToSceneCoords(event, this.state);
      let element = this.getElementAtPosition(x, y, {
        preferSelected: true,
        includeLockedElements: true,
        isDOM: !(event.target instanceof HTMLCanvasElement),
      });
      if (!global.g_isPresentMode && this.state.displayMode != 'preview' && !isMindmapElement(element)){//不能是mindmap--这个东西。。不然这里创建的时候默认就选中了。要兼容各种情况，各种元素的不同.
        pointerDownState.hit.element = element;
      }
    }


    this.props?.onPointerDown?.(this.state.activeTool, pointerDownState);
    if(mOnPointDownCallback != null){
      mOnPointDownCallback(this.state.activeTool, pointerDownState)
    }
    this.onPointerDownEmitter.trigger(
      this.state.activeTool,
      pointerDownState,
      event,
    );


    const onPointerMove =
      this.onPointerMoveFromPointerDownHandler(pointerDownState);

    const onPointerUp =
      this.onPointerUpFromPointerDownHandler(pointerDownState);

    const onKeyDown = this.onKeyDownFromPointerDownHandler(pointerDownState);
    const onKeyUp = this.onKeyUpFromPointerDownHandler(pointerDownState);

    this.missingPointerEventCleanupEmitter.once((_event) =>
      onPointerUp(_event || event.nativeEvent),
    );

    if (!this.state.viewModeEnabled || this.state.activeTool.type === "laser") {
      window.addEventListener(EVENT.POINTER_MOVE, onPointerMove);
      window.addEventListener(EVENT.POINTER_UP, onPointerUp);
      window.addEventListener(EVENT.KEYDOWN, onKeyDown);
      window.addEventListener(EVENT.KEYUP, onKeyUp);
      pointerDownState.eventListeners.onMove = onPointerMove;
      pointerDownState.eventListeners.onUp = onPointerUp;
      pointerDownState.eventListeners.onKeyUp = onKeyUp;
      pointerDownState.eventListeners.onKeyDown = onKeyDown;
    }
  };

  private handleCanvasPointerUp = (
    event: React.PointerEvent<HTMLCanvasElement>,
  ) => {
    this.mIsMouseDown = false;

    setTimeout(()=>{
      postMessage(EVENT_CANVAS_SELECTION_CHANGED,{selectedElements:this.state.selectedElementIds});
    },100)

    mIsPointerRightDown = false;
    if (event.button == 2 && !mIsPointerRightMove){
      this.handleCanvasContextMenu(event,true);
    }
    mIsPointerRightMove = false;

    if (this.state.resizingElement) {
      if (isNoteElement(this.state.resizingElement) || isStickyNotesElement(this.state.resizingElement) || isTextElement(this.state.resizingElement)) {
        //更新这个以后固定高度了..
        let customData: any = {generationData: {}};
        if (this.state.resizingElement.customData) {
          customData = {...this.state.resizingElement.customData};
        }
        if (!customData.generationData) {
          customData.generationData = {};
        }
        customData.generationData.isFixedSize = true;
        if (isStickyNotesElement(this.state.resizingElement)) {
          mutateElement(this.state.resizingElement, {customData: customData});
          const ele = this.getSelectedElements().find(p=>p.id == this.state.resizingElement.id);
          if (ele){
            mutateElement(ele, {customData: customData});
          }
        } else {
          this.onUpdateElement(this.state.resizingElement, {customData});
        }

        postMessage(EVENT_RESIZING_CARD, {element: this.state.resizingElement})
      } else if (isProjectLinkElement(this.state.resizingElement)) {
        const customData = {...this.state.resizingElement.customData};
        customData.isFixedSize = true;
        this.onUpdateElement(this.state.resizingElement, {customData});
      }
    }
    this.setState({
      isPointerDown:false,
      // draggingElement:null
    })
    this.removePointer(event);
    this.lastPointerUpEvent = event;

    const scenePointer = viewportCoordsToSceneCoords(
      { clientX: event.clientX, clientY: event.clientY },
      this.state,
    );
    const hitElement = this.getElementAtPosition(
        scenePointer.x,
        scenePointer.y,
    );
    this.props?.onPointerUp?.(event,hitElement,this.mIsMouseMoved);

    this.mIsMouseMoved = false;

    const clicklength =
      event.timeStamp - (this.lastPointerDownEvent?.timeStamp ?? 0);
    if (this.device.editor.isMobile && clicklength < 300) {
      if (
          (isIframeLikeElement(hitElement) && !isFileVideoElement(hitElement) &&
        this.isIframeLikeElementCenter(
          hitElement,
          event,
          scenePointer.x,
          scenePointer.y,
        ))
          // || isFileVideoElement(hitElement)
      ) {
        this.handleEmbeddableCenterClick(hitElement);
        return;
      }
    }

    if (this.device.isTouchScreen) {
      const hitElement = this.getElementAtPosition(
        scenePointer.x,
        scenePointer.y,
      );
      this.hitLinkElement = this.getElementLinkAtPosition(
        scenePointer,
        hitElement,
      );
    }

    if (this.state.displayMode === "preview" && this.hitLinkElement) {
      let url = this.hitLinkElement.link;
      if (url) {
        url = normalizeLink(url);
        let customEvent;
        if (this.props.onLinkOpen) {
          customEvent = wrapEvent(EVENT.EXCALIDRAW_LINK, event.nativeEvent);
          this.props.onLinkOpen(
              {
                ...this.hitLinkElement,
                link: url,
              },
              customEvent,
          );
        }
        if (!customEvent?.defaultPrevented) {
          const target = isLocalLink(url) ? "_self" : "_blank";
          const newWindow = window.open(undefined, target);
          // https://mathiasbynens.github.io/rel-noopener/
          if (newWindow) {
            newWindow.opener = null;
            newWindow.location = url;
          }
        }
      }

      return;
    }

    if (this.hitVideoNotesElement) {
      console.log(this.hitVideoNotesElement);
      // 本地视频
      if (isFileVideoElement(this.hitVideoNotesElement)) {
        (
            document.getElementById(
                `video-${this.hitVideoNotesElement.id}`,
            ) as HTMLVideoElement
        )?.pause();
        this.setState({
          videoNotes: {
            visibility: true,
            type: "model",
            element: this.hitVideoNotesElement as any,
          },
        });
        return;
      }
      if (isIframeElement(this.hitVideoNotesElement)) {
        return;
      }
      const iframe = this.getHTMLIFrameElement(
          this.hitVideoNotesElement as ExcalidrawIframeLikeElement,
      );

      if (!iframe?.contentWindow) {
        return;
      }

      if (iframe.src.includes("youtube")) {
        iframe.contentWindow?.postMessage(
            JSON.stringify({
              event: "command",
              func: "pauseVideo",
              args: "",
            }),
            "*",
        );
      }
      this.setState({
        videoNotes: {
          visibility: true,
          type: "model",
          element: this.hitVideoNotesElement as ExcalidrawEmbeddableElement,
        },
      });
      return;
    }

    if (
      this.hitLinkElement &&
      !this.state.selectedElementIds[this.hitLinkElement.id]
    ) {
      if (
        clicklength < 300 &&
        isIframeLikeElement(this.hitLinkElement) &&
        !isPointHittingLinkIcon(this.hitLinkElement, this.state, [
          scenePointer.x,
          scenePointer.y,
        ])
      ) {
        this.handleEmbeddableCenterClick(this.hitLinkElement);
      } else {
        this.redirectToLink(event, this.device.isTouchScreen);
      }
    } else if (this.state.viewModeEnabled) {
      this.setState({
        activeEmbeddable: null,
        activeStickyNotes: null,
        activeMindMap: null,
        selectedElementIds: {},
      });
    }

    //add
    if (this.state.editingElement){
      switch (this.state.activeTool.type){
        case "rectangle":
        case "roundedRectangle":
        case "ellipse":
        case "triangle":
        case "diamond":
        case "bubble":
        case "parallelogram":
        case "pentagram":
        case "thickArrow":
        case "arrow":
        case "line":
        {
          this.props.onClickElement(this.state.editingElement,true)
        }
          break;
      }
    }

  };

  private maybeOpenContextMenuAfterPointerDownOnTouchDevices = (
    event: React.PointerEvent<HTMLElement>,
  ): void => {
    // deal with opening context menu on touch devices
    if (event.pointerType === "touch") {
      invalidateContextMenu = false;

      if (touchTimeout) {
        // If there's already a touchTimeout, this means that there's another
        // touch down and we are doing another touch, so we shouldn't open the
        // context menu.
        invalidateContextMenu = true;
      } else {
        // open the context menu with the first touch's clientX and clientY
        // if the touch is not moving
        touchTimeout = window.setTimeout(() => {
          touchTimeout = 0;
          if (!invalidateContextMenu) {
            this.handleCanvasContextMenu(event);
          }
        }, TOUCH_CTX_MENU_TIMEOUT);
      }
    }
  };

  private resetContextMenuTimer = () => {
    clearTimeout(touchTimeout);
    touchTimeout = 0;
    invalidateContextMenu = false;
  };

  //添加quickflow元素
  private addQuickFlowElement = (
      elementWithQuickFlowHandle: ElementWithQuickFlowHandle,
  ) => {
    const margin = QUICKFLOW_MARGIN;
    const { direction, element } = elementWithQuickFlowHandle;
    if (!direction) {
      return;
    }

    const elementQuickFlow: QuickFlowCount = this.quickFlowCache.get(
        element.id,
    ) || {
      top: { elementIds: [], lineIds: [] },
      right: { elementIds: [], lineIds: [] },
      bottom: { elementIds: [], lineIds: [] },
      left: { elementIds: [], lineIds: [] },
    };

    const allElemmentsIds = this.scene
        .getNonDeletedElements()
        .map((item) => item.id);

    const firstDeletedElementIndex = elementQuickFlow[
        direction
        ].elementIds.findIndex((item) => !allElemmentsIds.includes(item));
    let insertIndex = elementQuickFlow[direction].elementIds.length;
    if (firstDeletedElementIndex !== -1) {
      elementQuickFlow[direction].elementIds.splice(
          firstDeletedElementIndex,
          1,
      );

      const lineElement = this.scene.getElement(
          elementQuickFlow[direction].lineIds[firstDeletedElementIndex],
      ) as ExcalidrawLinearElement;
      mutateElement(lineElement, {
        isDeleted: true,
      });
      elementQuickFlow[direction].lineIds.splice(firstDeletedElementIndex, 1);
      insertIndex = firstDeletedElementIndex;
    }

    let baseOffset = 0;

    let x = element.x;
    let y = element.y;

    // 连接线的坐标
    let lineX = element.x;
    let lineY = element.y;
    const points = [[0, 0]] as Point[];

    switch (direction) {
      case "top":
        baseOffset =
            (element.width + margin) * (0 % 2 ? 1 : -1) * Math.round(0 / 2);
        x = element.x + baseOffset;
        y = element.y - element.height - margin;

        lineX = element.x + element.width / 2;
        lineY = element.y;
        points.push([baseOffset, -margin]);

        break;
      case "right":
        baseOffset =
            (element.height + margin) * (0 % 2 ? 1 : -1) * Math.round(0 / 2);
        x = element.x + element.width + margin;
        y = element.y + baseOffset;

        lineX = element.x + element.width;
        lineY = element.y + element.height / 2;

        points.push([margin, baseOffset]);

        break;
      case "bottom":
        baseOffset =
            (element.width + margin) * (0 % 2 ? 1 : -1) * Math.round(0 / 2);
        x =
            element.x +
            (element.width + margin) * (0 % 2 ? 1 : -1) * Math.round(0 / 2);
        y = element.y + element.height + margin;

        lineX = element.x + element.width / 2;
        lineY = element.y + element.height;
        points.push([baseOffset, -margin]);

        break;
      case "left":
        baseOffset =
            (element.height + margin) * (0 % 2 ? 1 : -1) * Math.round(0 / 2);

        x = element.x - element.width - margin;
        y = element.y + baseOffset;

        lineX = element.x;
        lineY = element.y + element.height / 2;

        points.push([-margin, baseOffset]);

        break;
    }

    // 复制元素并根据计算的位移和网格对齐调整它们的位置
    let customData:any = null;
    if (isNoteElement(element)){
      customData = {};
      customData.generationData = {...element.customData?.generationData};
      //保存到library
      customData.generationData.id = generateUUID2();
      customData.generationData.name = ''//i18next.t("Untitled");
      customData.generationData.html = '';//createNoteElementByNote(customData.generationData.name,'');
      // copyNewLibrary(element.customData.generationData.id,customData.generationData.id);
      //保存到左侧栏....
      getCardById(element.customData?.generationData?.id).then((res:any)=>{
        if (res){
          res.id = customData.generationData.id;
          res.name = '';
          res.doc = '';
          postMessage(EVENT_ADD_NOTE_BY_CARD,[res])
        }
      })
    }
    const newElements = duplicateElements(
        [element].map((element) => {
          return newElementWith(element, {
            x,
            y,
            customData:customData
          });
        }),
        {
          randomizeSeed: true,
        },
    );
    const resultElement = newElements[0];

    // 恢复历史记录的记录
    this.history.resumeRecording();

    this.setState({
      editingGroupId: null,
      selectedElementIds: {
        [resultElement.id]: true,
      },
    });
    // 设置当前活动工具为选择工具
    this.setActiveTool({ type: "selection" });

    // 根据当前元素类型确定箭头样式
    const { currentItemStartArrowhead, currentItemEndArrowhead } = this.state;
    const [startArrowhead, endArrowhead] = [
      currentItemStartArrowhead,
      currentItemEndArrowhead,
    ];

    let connectorType = localStorage.getItem(KEY_GLOBAL_CONNECTOR_TYPE);
    let roundness:any = {
      type: ROUNDNESS.PROPORTIONAL_RADIUS
    }
    if (connectorType == 'straight'){
      roundness = null;
    }else if (connectorType == 'elbow'){
        roundness = {
            type: ROUNDNESS.ELBOW
        }
    }
    // 创建新线性元素并设置其属性
    const lineElement = newLinearElement({
      type: "arrow",
      x: lineX,
      y: lineY,
      strokeColor: isDarkMode()?DEFAULT_DRAW_ST_COLOR:DEFAULT_ARROW_COLOR,//this.state.currentItemStrokeColor,
      backgroundColor: this.state.currentItemBackgroundColor,
      fillStyle: this.state.currentItemFillStyle,
      strokeWidth: this.state.currentItemArrowStrokeWidth,//DEFAULT_ARROW_STROKE_WIDTH,//this.state.currentItemStrokeWidth,
      strokeStyle: this.state.currentItemStrokeStyle,
      roughness: this.state.currentItemRoughness,
      opacity: this.state.currentItemOpacity,
      // roundness:
      //     this.state.currentItemRoundness === "round"
      //         ? { type: ROUNDNESS.PROPORTIONAL_RADIUS }
      //         : null,
      roundness,
      startArrowhead,
      endArrowhead,
      locked: false,
      frameId: null,
      points,
      comments: {
        text: "",
        status: "uncreated",
      },
    });

    // 更新场景中的元素
    const nextElements = [
      ...this.scene.getElementsIncludingDeleted(),
      ...newElements,
      lineElement,
    ];
    this.scene.replaceAllElements(nextElements);

    bindOrUnbindLinearElement(
        lineElement,
        element as ExcalidrawBindableElement,
        resultElement as ExcalidrawBindableElement,
    );

    elementQuickFlow[direction].elementIds.splice(
        insertIndex,
        0,
        resultElement.id,
    );
    elementQuickFlow[direction].lineIds.splice(insertIndex, 0, lineElement.id);

    this.quickFlowCache.set(element.id, elementQuickFlow);

    return resultElement;
  };

  /**
   * pointerup may not fire in certian cases (user tabs away...), so in order
   * to properly cleanup pointerdown state, we need to fire any hanging
   * pointerup handlers manually
   */
  private maybeCleanupAfterMissingPointerUp = (event: PointerEvent | null) => {
    lastPointerUp?.();
    this.missingPointerEventCleanupEmitter.trigger(event).clear();
  };

  // Returns whether the event is a panning
  // 右键移动右键移动右键移动右键移动右键移动右键移动右键移动右键移动右键移动右键移动右键移动   右键  移动
  private handleCanvasPanUsingWheelOrSpaceDrag = (
    event: React.PointerEvent<HTMLElement>,
  ): boolean => {

    if (global.g_isPresentMode && isEmbeddableElement(mMoveHitElement)){
      //!!!!!!!!!这里控制----embed视频，鼠标按下从上面划过时移动，导致鼠标一直是按下状态......
      return false;
    }
    if (
      !(
        gesture.pointers.size <= 1 &&
        (event.button === POINTER_BUTTON.WHEEL ||
          (event.button === POINTER_BUTTON.MAIN && isHoldingSpace) ||
          isHandToolActive(this.state) ||
          this.state.viewModeEnabled)
      ) ||
      isTextElement(this.state.editingElement)
    ) {
      if (event.button != 2){
        return false;
      }
    }
    isPanning = true;
    event.preventDefault();

    let nextPastePrevented = false;
    const isLinux = /Linux/.test(window.navigator.platform);

    if (event.button != 2) {
      //鼠标右键点击不变形状.....
      setCursor(this.interactiveCanvas, CURSOR_TYPE.GRABBING);
    }
    let { clientX: lastX, clientY: lastY } = event;
    const onPointerMove = withBatchedUpdatesThrottled((event: PointerEvent) => {
      const deltaX = lastX - event.clientX;
      const deltaY = lastY - event.clientY;
      lastX = event.clientX;
      lastY = event.clientY;

      /*
       * Prevent paste event if we move while middle clicking on Linux.
       * See issue #1383.
       */
      if (
        isLinux &&
        !nextPastePrevented &&
        (Math.abs(deltaX) > 1 || Math.abs(deltaY) > 1)
      ) {
        nextPastePrevented = true;

        /* Prevent the next paste event */
        const preventNextPaste = (event: ClipboardEvent) => {
          document.body.removeEventListener(EVENT.PASTE, preventNextPaste);
          event.stopPropagation();
        };

        /*
         * Reenable next paste in case of disabled middle click paste for
         * any reason:
         * - right click paste
         * - empty clipboard
         */
        const enableNextPaste = () => {
          setTimeout(() => {
            document.body.removeEventListener(EVENT.PASTE, preventNextPaste);
            window.removeEventListener(EVENT.POINTER_UP, enableNextPaste);
          }, 100);
        };

        document.body.addEventListener(EVENT.PASTE, preventNextPaste);
        window.addEventListener(EVENT.POINTER_UP, enableNextPaste);
      }

      this.translateCanvas({
        scrollX: this.state.scrollX - deltaX / this.state.zoom.value,
        scrollY: this.state.scrollY - deltaY / this.state.zoom.value,
      });
    });
    const teardown = withBatchedUpdates(
      (lastPointerUp = () => {
        lastPointerUp = null;
        isPanning = false;
        if (!isHoldingSpace) {
          if (this.state.viewModeEnabled) {
            setCursor(this.interactiveCanvas, CURSOR_TYPE.GRAB);
          } else {
            setCursorForShape(this.interactiveCanvas, this.state);
          }
        }
        this.setState({
          cursorButton: "up",
        });
        this.savePointer(event.clientX, event.clientY, "up");
        window.removeEventListener(EVENT.POINTER_MOVE, onPointerMove);
        window.removeEventListener(EVENT.POINTER_UP, teardown);
        window.removeEventListener(EVENT.BLUR, teardown);
        onPointerMove.flush();
      }),
    );
    window.addEventListener(EVENT.BLUR, teardown);
    window.addEventListener(EVENT.POINTER_MOVE, onPointerMove, {
      passive: true,
    });
    window.addEventListener(EVENT.POINTER_UP, teardown);
    return true;
  };

  private updateGestureOnPointerDown(
    event: React.PointerEvent<HTMLElement>,
  ): void {
    if (!event.pointerId)return;
    gesture.pointers.set(event.pointerId, {
      x: event.clientX,
      y: event.clientY,
    });

    if (gesture.pointers.size === 2) {
      gesture.lastCenter = getCenter(gesture.pointers);
      gesture.initialScale = this.state.zoom.value;
      gesture.initialDistance = getDistance(
        Array.from(gesture.pointers.values()),
      );
    }
  }

  private initialPointerDownState(
    event: React.PointerEvent<HTMLElement>,
  ): PointerDownState {
    const origin = viewportCoordsToSceneCoords(event, this.state);
    const selectedElements = this.scene.getSelectedElements(this.state);
    const [minX, minY, maxX, maxY] = getCommonBounds(selectedElements);

    return {
      origin,
      withCmdOrCtrl: event[KEYS.CTRL_OR_CMD],
      originInGrid: tupleToCoors(
        getGridPoint(
          origin.x,
          origin.y,
          event[KEYS.CTRL_OR_CMD] ? null : this.state.gridSize,
        ),
      ),
      scrollbars: isOverScrollBars(
        currentScrollBars,
        event.clientX - this.state.offsetLeft,
        event.clientY - this.state.offsetTop,
      ),
      // we need to duplicate because we'll be updating this state
      lastCoords: { ...origin },
      originalElements: this.scene
        .getNonDeletedElements()
        .reduce((acc, element) => {
          acc.set(element.id, deepCopyElement(element));
          return acc;
        }, new Map() as PointerDownState["originalElements"]),
      resize: {
        handleType: false,
        isResizing: false,
        offset: { x: 0, y: 0 },
        arrowDirection: "origin",
        center: { x: (maxX + minX) / 2, y: (maxY + minY) / 2 },
        minX,
        minY,
        maxX,
        maxY,
      },
      hit: {
        element: null,
        allHitElements: [],
        wasAddedToSelection: false,
        hasBeenDuplicated: false,
        hasHitCommonBoundingBoxOfSelectedElements:
          this.isHittingCommonBoundingBoxOfSelectedElements(
            origin,
            selectedElements,
          ),
      },
      drag: {
        hasOccurred: false,
        offset: null,
      },
      eventListeners: {
        onMove: null,
        onUp: null,
        onKeyUp: null,
        onKeyDown: null,
      },
      boxSelection: {
        hasOccurred: false,
      },
      boxUnSelection: {
        onUp: false,
      },
      elementIdsToErase: {},
    };
  }

  // Returns whether the event is a dragging a scrollbar
  private handleDraggingScrollBar(
    event: React.PointerEvent<HTMLElement>,
    pointerDownState: PointerDownState,
  ): boolean {
    if (
      !(pointerDownState.scrollbars.isOverEither && !this.state.multiElement)
    ) {
      return false;
    }
    isDraggingScrollBar = true;
    pointerDownState.lastCoords.x = event.clientX;
    pointerDownState.lastCoords.y = event.clientY;
    const onPointerMove = withBatchedUpdatesThrottled((event: PointerEvent) => {
      const target = event.target;
      if (!(target instanceof HTMLElement)) {
        return;
      }

      this.handlePointerMoveOverScrollbars(event, pointerDownState);
    });
    const onPointerUp = withBatchedUpdates(() => {
      lastPointerUp = null;
      isDraggingScrollBar = false;
      setCursorForShape(this.interactiveCanvas, this.state);
      this.setState({
        cursorButton: "up",
      });
      this.savePointer(event.clientX, event.clientY, "up");
      window.removeEventListener(EVENT.POINTER_MOVE, onPointerMove);
      window.removeEventListener(EVENT.POINTER_UP, onPointerUp);
      onPointerMove.flush();
    });

    lastPointerUp = onPointerUp;

    window.addEventListener(EVENT.POINTER_MOVE, onPointerMove);
    window.addEventListener(EVENT.POINTER_UP, onPointerUp);
    return true;
  }

  private clearSelectionIfNotUsingSelection = (): void => {
    if (this.state.activeTool.type !== "selection") {
      this.setState({
        selectedElementIds: makeNextSelectedElementIds({}, this.state),
        selectedGroupIds: {},
        editingGroupId: null,
        activeEmbeddable: null,
        activeStickyNotes: null,
        activeMindMap: null,
      });
    }
  };

  /**
   * @returns whether the pointer event has been completely handled
   */
  private handleSelectionOnPointerDown = (
    event: React.PointerEvent<HTMLElement>,
    pointerDownState: PointerDownState,
  ): boolean => {
    if (
        this.state.hoveredElement.element &&
        this.state.hoveredElement.status === "highlight"
    ) {
      return false;
    }

    if (this.state.activeTool.type === "selection") {
      const elements = this.scene.getNonDeletedElements();
      const selectedElements = this.scene.getSelectedElements(this.state);

      if (
          selectedElements.length === 1 &&
          isMindmapElement(selectedElements[0])
      ) {
        previousMindMapElement = selectedElements[0];
      } else {
        previousMindMapElement = null;
      }

      if (selectedElements.length === 1 && !this.state.editingLinearElement) {
        const elementWithTransformHandleType =
          getElementWithTransformHandleType(
            elements,
            this.state,
            pointerDownState.origin.x,
            pointerDownState.origin.y,
            this.state.zoom,
            event.pointerType,
          );
        if (elementWithTransformHandleType != null) {
          this.setState({
            resizingElement: elementWithTransformHandleType.element,
          });
          pointerDownState.resize.handleType =
            elementWithTransformHandleType.transformHandleType;
        }
      } else if (selectedElements.length > 1) {
        previousMindMapElement = null;
        pointerDownState.resize.handleType = getTransformHandleTypeFromCoords(
          getCommonBounds(selectedElements),
          pointerDownState.origin.x,
          pointerDownState.origin.y,
          this.state.zoom,
          event.pointerType,
        );
      }
      if (pointerDownState.resize.handleType) {
        pointerDownState.resize.isResizing = true;
        pointerDownState.resize.offset = tupleToCoors(
          getResizeOffsetXY(
            pointerDownState.resize.handleType,
            selectedElements,
            pointerDownState.origin.x,
            pointerDownState.origin.y,
          ),
        );
        if (
          selectedElements.length === 1 &&
          isLinearElement(selectedElements[0]) &&
          selectedElements[0].points.length === 2
        ) {
          pointerDownState.resize.arrowDirection = getResizeArrowDirection(
            pointerDownState.resize.handleType,
            selectedElements[0],
          );
        }
      } else {
        if (this.state.selectedLinearElement) {
          const linearElementEditor =
            this.state.editingLinearElement || this.state.selectedLinearElement;
          const ret = LinearElementEditor.handlePointerDown(
            event,
            this.state,
            this.history,
            pointerDownState.origin,
            linearElementEditor,
          );
          if (ret.hitElement) {
            pointerDownState.hit.element = ret.hitElement;
          }
          if (ret.linearElementEditor) {
            this.setState({ selectedLinearElement: ret.linearElementEditor });

            if (this.state.editingLinearElement) {
              this.setState({ editingLinearElement: ret.linearElementEditor });
            }
          }
          if (ret.didAddPoint) {
            return true;
          }
        }
        // hitElement may already be set above, so check first
        pointerDownState.hit.element =
          pointerDownState.hit.element ??
          this.getElementAtPosition(
            pointerDownState.origin.x,
            pointerDownState.origin.y,
              { isDOM: !(event.target instanceof HTMLCanvasElement) },
          );

        if (pointerDownState.hit.element) {
          // Early return if pointer is hitting link icon
          const hitLinkElement = this.getElementLinkAtPosition(
            {
              x: pointerDownState.origin.x,
              y: pointerDownState.origin.y,
            },
            pointerDownState.hit.element,
          );
          if (hitLinkElement) {
            return false;
          }
        }

        // For overlapped elements one position may hit
        // multiple elements
        pointerDownState.hit.allHitElements = this.getElementsAtPosition(
          pointerDownState.origin.x,
          pointerDownState.origin.y,
        );

        const hitElement = pointerDownState.hit.element;
        const someHitElementIsSelected =
          pointerDownState.hit.allHitElements.some((element) =>
            this.isASelectedElement(element),
          );
        if (
          (hitElement === null || !someHitElementIsSelected) &&
            !(event.shiftKey || event[KEYS.CTRL_OR_CMD]) &&
          !pointerDownState.hit.hasHitCommonBoundingBoxOfSelectedElements
        ) {
          this.clearSelection(hitElement);
        }

        if (this.state.editingLinearElement) {
          this.setState({
            selectedElementIds: makeNextSelectedElementIds(
              {
                [this.state.editingLinearElement.elementId]: true,
              },
              this.state,
            ),
          });
          // If we click on something
        } else if (hitElement != null) {
            // Preserve legacy CMD/CTRL behavior: if the element is currently
            // selected via group selection, CMD/CTRL click should "drill down"
            // to the element (enter group editing) instead of multi-selecting.
            //
            // Otherwise CMD/CTRL is treated as additive selection (same as Shift),
            // and handled by the regular selection flow below.
            const selectedGroupIdForHitElement = getSelectedGroupIdForElement(
                hitElement,
                this.state.selectedGroupIds,
            );
            const hasSelectionOutsideHitGroup = selectedGroupIdForHitElement
                ? Object.keys(this.state.selectedElementIds).some((id) => {
                    const el = this.scene.getElement(id);
                    return el
                        ? !isElementInGroup(el, selectedGroupIdForHitElement)
                        : false;
                })
                : false;

            // on CMD/CTRL, drill down to hit element regardless of groups etc.
            if (
                event[KEYS.CTRL_OR_CMD] &&
                !event.shiftKey &&
                isSelectedViaGroup(this.state, hitElement) &&
                selectedGroupIdForHitElement &&
                !hasSelectionOutsideHitGroup &&
                // Ctrl/Cmd+click on frames should behave as additive multi-select.
                !isFrameLikeElement(hitElement)
            ) {
                if (!this.state.selectedElementIds[hitElement.id]) {
                    pointerDownState.hit.wasAddedToSelection = true;
                }
                if (!isMindmapElement(hitElement)) {
                    // Clear mindmap node selection when selecting another element
                    if (activeNode) {
                        const mindMap = mindMapCache.get(activeNode.element.id);
                        if (mindMap) {
                            (mindMap as any).__excalidrawMaybeSelectionRender = true;
                            mindMap.renderer.clearActiveNode();
                        }
                    }
                    this.setState((prevState) => ({
                        ...editGroupForSelectedElement(prevState, hitElement),
                        previousSelectedElementIds: this.state.selectedElementIds,
                    }));
                }
                // mark as not completely handled so as to allow dragging etc.
                return false;
          }

          // deselect if item is selected
          // if shift is not clicked, this will always return true
          // otherwise, it will trigger selection based on current
          // state of the box
          if (!this.state.selectedElementIds[hitElement.id]) {
            // if we are currently editing a group, exiting editing mode and deselect the group.
            if (
              this.state.editingGroupId &&
              !isElementInGroup(hitElement, this.state.editingGroupId)
            ) {
              this.setState({
                selectedElementIds: makeNextSelectedElementIds({}, this.state),
                selectedGroupIds: {},
                editingGroupId: null,
                activeEmbeddable: null,
                activeStickyNotes: null,
                activeMindMap: null,
              });
            }

            // Add hit element to selection. At this point if we're not holding
            // SHIFT the previously selected element(s) were deselected above
            // (make sure you use setState updater to use latest state)
            // With shift-selection, we want to make sure that frames and their containing
            // elements are not selected at the same time.
            if (
              !someHitElementIsSelected &&
              !pointerDownState.hit.hasHitCommonBoundingBoxOfSelectedElements
            ) {
              this.setState((prevState) => {
                const nextSelectedElementIds: { [id: string]: true } = {
                  ...prevState.selectedElementIds,
                  [hitElement.id]: true,
                };

                const previouslySelectedElements: ExcalidrawElement[] = [];

                Object.keys(prevState.selectedElementIds).forEach((id) => {
                  const element = this.scene.getElement(id);
                  element && previouslySelectedElements.push(element);
                });

                // if hitElement is frame-like, deselect all of its elements
                // if they are selected
                if (isFrameLikeElement(hitElement)) {
                  getFrameChildren(
                    previouslySelectedElements,
                    hitElement.id,
                  ).forEach((element) => {
                    delete nextSelectedElementIds[element.id];
                  });
                  delete nextSelectedElementIds[hitElement.id];
                } else {
                  // hitElement is neither a frame nor an element in a frame
                  // but since hitElement could be in a group with some frames
                  // this means selecting hitElement will have the frames selected as well
                  // because we want to keep the invariant:
                  // - frames and their elements are not selected at the same time
                  // we deselect elements in those frames that were previously selected

                  const groupIds = hitElement.groupIds;
                  const framesInGroups = new Set(
                    groupIds
                      .flatMap((gid) =>
                        getElementsInGroup(
                          this.scene.getNonDeletedElements(),
                          gid,
                        ),
                      )
                      .filter((element) => isFrameLikeElement(element))
                      .map((frame) => frame.id),
                  );

                  if (framesInGroups.size > 0) {
                    previouslySelectedElements.forEach((element) => {
                      if (
                        element.frameId &&
                        framesInGroups.has(element.frameId)
                      ) {
                        // deselect element and groups containing the element
                        delete nextSelectedElementIds[element.id];
                        element.groupIds
                          .flatMap((gid) =>
                            getElementsInGroup(
                              this.scene.getNonDeletedElements(),
                              gid,
                            ),
                          )
                          .forEach((element) => {
                            delete nextSelectedElementIds[element.id];
                          });
                      }
                    });
                  }
                }

                return {
                  ...selectGroupsForSelectedElements(
                    {
                      editingGroupId: prevState.editingGroupId,
                      selectedElementIds: nextSelectedElementIds,
                    },
                    this.scene.getNonDeletedElements(),
                    prevState,
                    this,
                  ),
                  showHyperlinkPopup:
                    hitElement.link || isEmbeddableElement(hitElement)
                      ? "info"
                      : false,
                };
              });
              pointerDownState.hit.wasAddedToSelection = true;
            }
          }
        }

        this.setState({
          previousSelectedElementIds: this.state.selectedElementIds,
        });
      }
    }else {
      previousMindMapElement = null;
    }
    return false;
  };

  private isASelectedElement(hitElement: ExcalidrawElement | null): boolean {
    return hitElement != null && this.state.selectedElementIds[hitElement.id];
  }

  private isHittingCommonBoundingBoxOfSelectedElements(
    point: Readonly<{ x: number; y: number }>,
    selectedElements: readonly ExcalidrawElement[],
  ): boolean {
    if (selectedElements.length < 2) {
      return false;
    }

    // How many pixels off the shape boundary we still consider a hit
    const threshold = 10 / this.state.zoom.value;
    const [x1, y1, x2, y2] = getCommonBounds(selectedElements);
    return (
      point.x > x1 - threshold &&
      point.x < x2 + threshold &&
      point.y > y1 - threshold &&
      point.y < y2 + threshold
    );
  }

  private handleTextOnPointerDown = (
    event: React.PointerEvent<HTMLElement>,
    pointerDownState: PointerDownState,
  ): void => {
    // if we're currently still editing text, clicking outside
    // should only finalize it, not create another (irrespective
    // of state.activeTool.locked)
    if (isTextElement(this.state.editingElement)) {
      return;
    }
    let sceneX = pointerDownState.origin.x;
    let sceneY = pointerDownState.origin.y;

    const element = this.getElementAtPosition(sceneX, sceneY, {
      includeBoundTextElement: true,
      isDOM: !(event.target instanceof HTMLCanvasElement),
    });

    // FIXME
    let container = getTextBindableContainerAtPosition(
      this.scene.getNonDeletedElements(),
      this.state,
      sceneX,
      sceneY,
    );

    if (hasBoundTextElement(element) && this.state.activeTool.type != 'text') {
      container = element as ExcalidrawTextContainer;
      sceneX = element.x + element.width / 2;
      sceneY = element.y + element.height / 2;
    }
    //单击保持原来的....
    this.startTextEditing0({
      sceneX,
      sceneY,
      insertAtParentCenter: !event.altKey,
      container,
    });

    resetCursor(this.interactiveCanvas);
    if (!this.state.activeTool.locked) {
      this.setState({
        activeTool: updateActiveTool(this.state, { type: "selection" }),
      });
    }
  };

  private handleFreeDrawElementOnPointerDown = (
    event: React.PointerEvent<HTMLElement>,
    elementType: ExcalidrawFreeDrawElement["type"],
    pointerDownState: PointerDownState,
  ) => {
    // Begin a mark capture. This does not have to update state yet.
    const [gridX, gridY] = getGridPoint(
      pointerDownState.origin.x,
      pointerDownState.origin.y,
      null,
    );

    let strokeColor = this.state.currentItemDrawStrokeColor;
    // highlighter工具第一次创建点击
    // if (
    //     elementType === "highlighter" &&
    //     !!this.state.activeTool.activeToolFirstClicked
    // ) {
    //   strokeColor = "#FFFF00";
    //
    //   this.setState({ currentItemStrokeColor: strokeColor });
    // }

    const topLayerFrame = this.getTopLayerFrameAtSceneCoords({
      x: gridX,
      y: gridY,
    });

    if (strokeColor.toLowerCase() === 'transparent'){
      strokeColor = '#000000';
      this.setState({
        currentItemStrokeColor:'#000000'
      })
    }

    let nFreeDrawStrokeWidth:any = localStorage.getItem(KEY_FREE_DRAW_STROKE_WIDTH);
    if (nFreeDrawStrokeWidth){
      nFreeDrawStrokeWidth = getSafeInt(nFreeDrawStrokeWidth);
    }else{
      nFreeDrawStrokeWidth = this.state.currentItemDrawStrokeWidth;
    }
    var freeStyle = {
      strokeColor: strokeColor,
      strokeWidth: nFreeDrawStrokeWidth,
    }

    if (elementType === "highlighter"){
      freeStyle = {
        strokeColor: this.state.currentItemHighlightStrokeColor,
        strokeWidth: this.state.currentItemHighlightStrokeWidth,
      }
    }

    const element = newFreeDrawElement({
      type: elementType,
      x: gridX,
      y: gridY,
      backgroundColor: this.state.currentItemBackgroundColor,
      fillStyle: this.state.currentItemFillStyle,
      strokeStyle: this.state.currentItemStrokeStyle,
      roughness: this.state.currentItemRoughness,
      opacity: elementType === "highlighter" ? 50:100,//this.state.currentItemOpacity,
      roundness: null,
      simulatePressure: event.pressure === 0.5,
      locked: false,
      frameId: topLayerFrame ? topLayerFrame.id : null,
      ...freeStyle,
    });

    this.setState((prevState) => {
      const nextSelectedElementIds = {
        ...prevState.selectedElementIds,
      };
      delete nextSelectedElementIds[element.id];
      return {
        selectedElementIds: makeNextSelectedElementIds(
          nextSelectedElementIds,
          prevState,
        ),
      };
    });

    const pressures = element.simulatePressure
      ? element.pressures
      : [...element.pressures, event.pressure];

    mutateElement(element, {
      points: [[0, 0]],
      pressures,
    });

    const boundElement = getHoveredElementForBinding(
      pointerDownState.origin,
      this.scene,
    );
    this.scene.addNewElement(element);
    this.setState({
      draggingElement: element,
      editingElement: element,
      startBoundElement: boundElement,
      suggestedBindings: [],
    });
  };

  public forceSetCursor = (cursor)=>{
    const d = document.getElementById('div-visual-cursor-vvv');
    if (d){
      document.body.removeChild(d);
    }

    //设置鼠标样式.
    const div = document.createElement('div');
    div.id = 'div-visual-cursor-vvv'
    div.style.width = div.style.height = '16px';
    div.style.background = 'transparent';
    div.style.cursor = cursor;
    div.style.position = 'fixed';
    div.style.left = (this.lastPointerMoveEvent?.x - 8)+'px';
    div.style.top = (this.lastPointerMoveEvent?.y - 8) + 'px';
    div.style.zIndex = '99';
    document.body.appendChild(div);
    setTimeout(()=>{
      div.style.pointerEvents = 'none';
    },10)
    setCursor(this.interactiveCanvas,cursor);
  }

  public insertIframeElement = ({
    sceneX,
    sceneY,
    width,
    height,
  }: {
    sceneX: number;
    sceneY: number;
    width: number;
    height: number;
  }) => {
    const [gridX, gridY] = getGridPoint(
      sceneX,
      sceneY,
      this.lastPointerDownEvent?.[KEYS.CTRL_OR_CMD]
        ? null
        : this.state.gridSize,
    );

    const element = newIframeElement({
      type: "iframe",
      x: gridX,
      y: gridY,
      strokeColor: "transparent",
      backgroundColor: "transparent",
      fillStyle: this.state.currentItemFillStyle,
      strokeWidth: this.state.currentItemStrokeWidth,
      strokeStyle: this.state.currentItemStrokeStyle,
      roughness: this.state.currentItemRoughness,
      roundness: this.getCurrentItemRoundness("iframe"),
      opacity: this.state.currentItemOpacity,
      locked: false,
      width,
      height,
    });

    this.scene.replaceAllElements([
      ...this.scene.getElementsIncludingDeleted(),
      element,
    ]);

    return element;
  };

  public insertMindmap = ()=>{
    const { x: sceneX, y: sceneY } = viewportCoordsToSceneCoords(
        this.lastPointerMoveEvent,
        this.state,
    );

    const mindmapElement = newMindmapElement({
      type: "mindmap",
      x: sceneX,
      y: sceneY,
      // strokeColor: this.state.currentItemStrokeColor,
      strokeColor: COLOR_PALETTE.transparent,
      backgroundColor: COLOR_PALETTE.transparent,
      fillStyle: this.state.currentItemFillStyle,
      strokeWidth: this.state.currentItemStrokeWidth,
      strokeStyle: this.state.currentItemStrokeStyle,
      roughness: this.state.currentItemRoughness,
      roundness: null,
      opacity: this.state.currentItemOpacity,
      locked: false,
      frameId: null,
      width: 400,
      height: 10,
    });
    this.scene.addNewElement(mindmapElement);
    setTimeout(()=>{
      const mindMap = createMindMap(
          mindmapElement,
          this.createMindMapCallBack,
          this.props.onLinkOpen,
          true,
          this.state,
      );
    },100)

  }

  //create rectangle element with youtube top left on nearest grid point width / hight 640/360
  public insertEmbeddableElement = async ({
    sceneX,
    sceneY,
    link,
                                      embedType = "page",
      isOriginXY = false
  }: {
    sceneX: number;
    sceneY: number;
    link: string;
    embedType: "text" | "card" | "page";
    isOriginXY:boolean;
  }) => {

    const nVideoCount = await beyondFreeVideoLimit();
    if (!hasLicense() && !hasDesktopBaseLicense() &&  nVideoCount){
      window.showMessageBox(i18next.t("Upgrade required"), i18next.t("NeedUpgradeToImportVideoMore"), i18next.t("Cancel"), i18next.t("Learn more"), ()=>{
        window.closeProgress();
      }, () => {
        window.closeProgress();
        if (isZh()) {
          ipcSend('open-url',import.meta.env.VITE_HTTP_PURCHASE_PRICE_ZH)
        }else {
          ipcSend('open-url', import.meta.env.VITE_HTTP_PURCHASE_PRICE);
        }
      })
      return;
    }

    const [gridX, gridY] = getGridPoint(
      sceneX,
      sceneY,
      this.lastPointerDownEvent?.[KEYS.CTRL_OR_CMD]
        ? null
        : this.state.gridSize,
    );

    let x = gridX;
    let y = gridY;

    if (isOriginXY){
      x = sceneX;
      y = sceneY;
    }

    const frame = this.getTopLayerFrameAtSceneCoords({ x: sceneX, y: sceneY });

    const embedLink = getEmbedLink(link);

    if (!embedLink) {
      return;
    }

    if (embedLink.warning) {
      this.setToast({ message: embedLink.warning, closable: true });
    }

    let width = embedLink.intrinsicSize.w;
    let height = embedLink.intrinsicSize.h;
    const isPageEmbedType = true;//isSupportVideoAnnoUrl(link);
    if (isPageEmbedType) {
      embedType = "page";
    }

    if (embedType === "text") {
      height = DEFAULT_EMBEDABLE_BOOKMARK_HEIGHT;
      width = DEFAULT_EMBEDABLE_BOOKMARK_WIDTH;
    }

    if (isSupportVideoAnnoUrl(link) && !isBilibiliUrl(link) && !isBaiduVideoUrl(link)){
      width = DEFAULT_NOTE_WIDTH;
      height = width*height / DEFAULT_EMBEDABLE_ELEMENT_WIDTH
    }
    let szName = '';
    let szType = '';
    let szImageUrl = '';
    if (isYoutubeUrl(link)){
      szType = 'youtube';
      const src = getEmbedLink(toValidURL(link || ""));
      szImageUrl = getYoutubeThumbUrl(src?.videoId);
    }else if (isBilibiliUrl(link)){
      szType = 'bilibili';
    }else if (isBaiduVideoUrl(link)){
      szType = 'baidu';
      szName = getBaiduVideoNameByUrl(link);
    }

    const element = newEmbeddableElement({
      type: "embeddable",
      x: x,
      y: y,
      strokeColor: "transparent",
      backgroundColor: isSupportVideoAnnoUrl(link)?"transparent":getBookmarkBackgroundColor(),
      fillStyle: this.state.currentItemFillStyle,
      strokeWidth: 0.9,//this.state.currentItemStrokeWidth,
      strokeStyle: this.state.currentItemStrokeStyle,
      roughness: this.state.currentItemRoughness,
      roundness: {type:ROUNDNESS.ADAPTIVE_RADIUS,value:12},//this.getCurrentItemRoundness("embeddable"),
      opacity: this.state.currentItemOpacity,
      locked: false,
      // width: embedLink.intrinsicSize.w,
      // height: embedLink.intrinsicSize.h,
      customData:{generationData:{id:generateUUID2(),type:szType,isShowName:true}},
      width,
      height,
      link,
      validated: null,
      embedType,
      frameId: frame?.id || null,
      name:szName
    });

    this.scene.replaceAllElements([
      ...this.scene.getElementsIncludingDeleted(),
      element,
    ]);


    //如果是youtube链接，保存到库里面.--记得还有drawindex中的通过embed嵌入的url

    if (isSupportVideoAnnoUrl(link)){
      //region support-url
      const arrayElements = this.getSceneElements();

      getCardList(-1,0,'liveVideo',null,null).then((arrayLibrarys:any)=>{
        const item = arrayLibrarys.find(p=>p.fileName == link);
        if (item){
          const customData = {generationData:{...item,id:item.id,type:szType,isShowName:true}};
          const ele:any = arrayElements.find(p=>p.id == element.id);
          ele.name = item.name;
          ele.customData = customData;
          window.updateReplaceElement(ele, {customData: customData, name: item.name});
        }else{
          const card:any = {
            id:element.customData.generationData.id,
            name:szName,
            type:szType,
            fileName:link,
            thumbImage:szImageUrl,
            isDeleted:0,//默认不要显示在库中....
          }
          updateCard(card).then(res=>{
            //粘贴了就切到已经筛选后的 library,项目还没保存好，就开始查找是找不到的。这里先保存下。--其实没必要..
            if (window.saveNotes){
              window.saveNotes();
            }
          });

          if (isBilibiliUrl(link)) {
            apiBilibiliCover(link).then(res => {
              //如果获取到缩略图，这里再更新一下...
              card.name = res.title;
              card.thumbImage = res.pic;
              updateCard(card).then(res=>{
                postMessage(EVENT_REFRESH_LIBRARY_LIST);
              })

              mutateElement(element,{name:card.name})
            })
          }
        }
      });
      //endregion
    }else{
      //同样保存到库中，只是不可见。因为有infonotes这些。。。。
      const card:any = {
        id:element.customData.generationData.id,
        name:szName,
        type:'webLink',
        fileName:link,
        isDeleted:2,//默认不要显示在库中....
      }
      updateCard(card);

      //取标题...
      fetchPageTitle(element.link).then(res=>{
          if (res){
            element.name = res;
            mutateElement(element,{name:res});
            card.name = res;
            updateCard(card);
          }
      })
    }

    // 创建完元素的变成card
    if (embedType === "text") {
      setTimeout(() => {
        const node = document.getElementById(`${element.id}-card`)!;
        cardLink([node], element);
      }, 100);
    }

    return element;
  };


  private convertThemeData = (jsonData)=>{
    let szData = JSON.stringify(jsonData);
    if (isDarkMode()){
      szData = szData?.replaceAll('color: rgb(35, 39, 46)','color: rgb(255, 255, 255)')
          .replaceAll('color: rgb(86, 86, 86)','color: rgb(204, 204, 204)')
          .replaceAll('color: #23272E','color: rgb(255, 255, 255)')
    }else{
      szData = szData?.replaceAll('color: rgb(255, 255, 255)','color: rgb(35, 39, 46)')
          .replaceAll('color: rgb(204, 204, 204)','color: rgb(86, 86, 86)')
          .replaceAll('color: #FFFFFF','color: rgb(35, 39, 46)')
    }
    return JSON.parse(szData);
  }

  public refreshMindmap = ()=>{
    const arrayMindmaps = this.scene.getNonDeletedElements().filter(p=>p.type == 'mindmap');
    arrayMindmaps.forEach(p=>{
      const mindmap = mindMapCache.get(p.id);
      if (mindmap){
        mindmap.setData(this.convertThemeData(p.data));
        //config也要换..
        const newConfig = {...mindmap.themeConfig};
        newConfig.root.color = getMindmapRootTextColor();
        newConfig.second.color = getMindmapSecondTextColor();
        newConfig.node.color = getMindmapSecondTextColor();
        mindmap.setThemeConfig(newConfig);
      }
    })
  }

  /**
   * 加载mindmap
   */
  public loadMindmap = ()=>{
    // resetMindmapCache();
    const arrayMindmaps = this.scene.getNonDeletedElements().filter(p=>p.type == 'mindmap');
    arrayMindmaps.forEach(p=>{
      // setTimeout(()=>{
        const mindmap = createMindMap(
            p,
            this.createMindMapCallBack,
            this.props.onLinkOpen,
            false,
            this.state,
        );
        mindmap.setData(this.convertThemeData(p.data));
      // },50);
    })

    //这里额外处理一下--宽度的问题.
    setTimeout(()=>{
      const arrayMindmapG = document.getElementsByTagName('g');
      for (let i=0;i<arrayMindmapG.length;i++){
        const g = arrayMindmapG[i];
        const width = g.dataset.width;
        if (width){
          const nWidth = parseInt(width,10);
          g.dataset.width = nWidth + 2 + '';
        }
      }
      const arrayMindmapForeignObject = document.getElementsByTagName('foreignObject');
      for (let i=0;i<arrayMindmapForeignObject.length;i++){
        const g = arrayMindmapForeignObject[i];
        const width = g.width;
        if (width.baseVal){
          const nWidth = parseInt(width.baseVal.value,10);
          if (nWidth){
            width.baseVal.value = nWidth + 2 + '';
          }

        }
      }
    },100)

    // reRenderMindmap()

    // this.refresh();
    // this.actionManager.executeAction(actionLoadScene);
  }

  public loadEmbedLink = ()=>{
    const arrayEmbed = this.scene.getNonDeletedElements().filter(p=>p.embedType == 'text');
    let isOk = true;
    arrayEmbed.forEach(p=>{
      const node = document.getElementById(`${p.id}-card`)!;
      if (node){
        cardLink([node],p);
      }
    })
    return isOk;
  }

  /**
   * 隐藏连接线
   */
  public hiddenHoverModel = ()=>{
    const hoverModel = document.getElementById("hoverModel")!;
    if (hoverModel.style.display !== "none") {
      hoverModel.style.display = "none";
      const hoveredElementType = hoverModel.getAttribute("type");
      if (hoveredElementType !== "pin") {
        this.actionManager.executeAction(actionDeleteSelected);
      }
      return true;
    }
    return false;
  }


  private createImageElement = ({
    sceneX,
    sceneY,
    addToFrameUnderCursor = true,
      name = '',
  }: {
    sceneX: number;
    sceneY: number;
    addToFrameUnderCursor?: boolean;
    name?:string
  }) => {
    const [gridX, gridY] = getGridPoint(
      sceneX,
      sceneY,
      this.lastPointerDownEvent?.[KEYS.CTRL_OR_CMD]
        ? null
        : this.state.gridSize,
    );

    const topLayerFrame = addToFrameUnderCursor
      ? this.getTopLayerFrameAtSceneCoords({
          x: gridX,
          y: gridY,
        })
      : null;

    const element = newImageElement({
      type: "image",
      x: gridX,
      y: gridY,
      strokeColor: this.state.currentItemStrokeColor,
      backgroundColor: this.state.currentItemBackgroundColor,
      fillStyle: this.state.currentItemFillStyle,
      strokeWidth: 0.9,//this.state.currentItemStrokeWidth,//为后面修改边框的时候替换为默认的边框粗细!!!
      strokeStyle: this.state.currentItemStrokeStyle,
      roughness: this.state.currentItemRoughness,
      roundness: null,//{type:ROUNDNESS.ADAPTIVE_RADIUS,value:8},
      opacity: 100,//this.state.currentItemOpacity,
      locked: false,
      created:Date.now(),
      frameId: topLayerFrame ? topLayerFrame.id : null,
      isLockRatio:true,
      sort:0,
      hasUpdate:true,
      name:name,
      customData:{generationData:{originName:name,isShowName:false}}
    });

    return element;
  };

  private createMindmapElement = ({
                                    sceneX,
                                    sceneY,
                                    addToFrameUnderCursor = true,
                                  }: {
    sceneX: number;
    sceneY: number;
    addToFrameUnderCursor?: boolean;
  }) => {
    const [gridX, gridY] = getGridPoint(
        sceneX,
        sceneY,
        this.lastPointerDownEvent?.[KEYS.CTRL_OR_CMD]
            ? null
            : this.state.gridSize,
    );

    const topLayerFrame = addToFrameUnderCursor
        ? this.getTopLayerFrameAtSceneCoords({
          x: gridX,
          y: gridY,
        })
        : null;

    const element = newMindmapElement({
      type: "mindmap",
      x: gridX,
      y: gridY,
      // strokeColor: this.state.currentItemStrokeColor,
      strokeColor: COLOR_PALETTE.transparent,
      backgroundColor: COLOR_PALETTE.transparent,

      fillStyle: this.state.currentItemFillStyle,
      strokeWidth: this.state.currentItemStrokeWidth,
      strokeStyle: this.state.currentItemStrokeStyle,
      roughness: this.state.currentItemRoughness,
      roundness: null,
      opacity: this.state.currentItemOpacity,
      locked: false,
      frameId: topLayerFrame ? topLayerFrame.id : null,
      width: 400,
      height: 10,
    });

    return element;
  };

  public createStickyNotesElement = ({
                                        sceneX,
                                        sceneY,
                                        addToFrameUnderCursor = true,
                                        backgroundColor,
                                      }: {
    sceneX: number;
    sceneY: number;
    addToFrameUnderCursor?: boolean;
    backgroundColor: string;
  }) => {
    const [gridX, gridY] = getGridPoint(
        sceneX,
        sceneY,
        this.lastPointerDownEvent?.[KEYS.CTRL_OR_CMD]
            ? null
            : this.state.gridSize,
    );

    const topLayerFrame = addToFrameUnderCursor
        ? this.getTopLayerFrameAtSceneCoords({
          x: gridX,
          y: gridY,
        })
        : null;
    const fontSize = DEFAULT_FONT_SIZE;//this.state.currentItemFontSize;
    const fontFamily = FONT_FAMILY.Helvetica;//this.state.currentItemFontFamily;

        const hasBorder = (localStorage.getItem(KEY_STICKYNOTES_HAS_BORDER) || 'true') == 'true';

    const element = newStickyNotesElement({
      type: "stickyNotes",
      x: gridX,
      y: gridY,
      fontSize,
      fontFamily,
      strokeColor: "#333333",//this.state.currentItemStrokeColor,
      backgroundColor: backgroundColor || COLOR_PALETTE.transparent,
      text: "",
      created:Date.now(),
      fillStyle: this.state.currentItemFillStyle,
      strokeWidth: this.state.currentItemStrokeWidth,
      strokeStyle: this.state.currentItemStrokeStyle,
      roughness: this.state.currentItemRoughness,
      roundness: null,
      opacity: this.state.currentItemOpacity,
      locked: false,
      customData:{generationData:{isShowBorder:hasBorder}},
      frameId: topLayerFrame ? topLayerFrame.id : null,
      width: DEFAULT_STICK_NOTE_WIDTH,
      height:DEFAULT_STICK_NOTE_HEIGHT,
    });
    // mutateElement(element, {
    //   containerId: element.id,
    // });

    return element;
  };

    public createTextTiptapElement = ({
                                          sceneX,
                                          sceneY,
                                          addToFrameUnderCursor = true,
                                          backgroundColor,
                                          value=''
                                      }: {
        sceneX: number;
        sceneY: number;
        addToFrameUnderCursor?: boolean;
        backgroundColor: string;
        value:string
    }) => {
        const [gridX, gridY] = getGridPoint(
            sceneX,
            sceneY,
            this.lastPointerDownEvent?.[KEYS.CTRL_OR_CMD]
                ? null
                : this.state.gridSize,
        );

        const topLayerFrame = addToFrameUnderCursor
            ? this.getTopLayerFrameAtSceneCoords({
                x: gridX,
                y: gridY,
            })
            : null;
        const fontSize = getSafeInt(localStorage.getItem(KEY_DEFAULT_TEXT_FONT_SIZE)) || DEFAULT_FONT_SIZE;////DEFAULT_FONT_SIZE;//this.state.currentItemFontSize;
        const fontFamily = getSafeInt(localStorage.getItem(KEY_DEFAULT_TEXT_FONT_FAMILY)) || FONT_FAMILY.Helvetica;//FONT_FAMILY.Helvetica;//this.state.currentItemFontFamily;
        const hasBorder = (localStorage.getItem(KEY_TEXT_HAS_BORDER) || 'true') == 'true';
        const element:any = newTextTiptapElement({
            type: "text",
            x: gridX,
            y: gridY,
            fontSize,
            fontFamily,
            strokeColor: "#000000",//this.state.currentItemStrokeColor,
            backgroundColor: backgroundColor || COLOR_PALETTE.transparent,
            text: value,
            created:Date.now(),
            fillStyle: this.state.currentItemFillStyle,
            strokeWidth: this.state.currentItemStrokeWidth,
            strokeStyle: this.state.currentItemStrokeStyle,
            roughness: this.state.currentItemRoughness,
            roundness: null,
            opacity: this.state.currentItemOpacity,
            locked: false,
            frameId: topLayerFrame ? topLayerFrame.id : null,
            width: DEFAULT_TEXT_WIDTH,
            height:DEFAULT_TEXT_HEIGHT,
            customData:{generationData: {isShowBorder: hasBorder}}
        });

        // console.log(element)

        return element;
    };

  private handleLinearElementOnPointerDown = (
    event: React.PointerEvent<HTMLElement>,
    elementType: ExcalidrawLinearElement["type"],
    pointerDownState: PointerDownState,
    elementWithQuickFlowHandle?: ElementWithQuickFlowHandle,
    elementWithHoverHandle?: ElementWithHoverHandle,
  ): void  | NonDeleted<ExcalidrawLinearElement> => {
    if (this.state.multiElement) {
      const { multiElement } = this.state;

      // finalize if completing a loop
      if (
        multiElement.type === "line" &&
        isPathALoop(multiElement.points, this.state.zoom.value)
      ) {
        mutateElement(multiElement, {
          lastCommittedPoint:
            multiElement.points[multiElement.points.length - 1],
        });
        this.actionManager.executeAction(actionFinalize);
        return;
      }

      const { x: rx, y: ry, lastCommittedPoint } = multiElement;

      // clicking inside commit zone → finalize arrow
      if (
        multiElement.points.length > 1 &&
        lastCommittedPoint &&
        distance2d(
          pointerDownState.origin.x - rx,
          pointerDownState.origin.y - ry,
          lastCommittedPoint[0],
          lastCommittedPoint[1],
        ) < LINE_CONFIRM_THRESHOLD
      ) {
        this.actionManager.executeAction(actionFinalize);
        return;
      }


      this.setState((prevState) => ({
        selectedElementIds: makeNextSelectedElementIds(
          {
            ...prevState.selectedElementIds,
            [multiElement.id]: true,
          },
          prevState,
        ),
      }));
      // clicking outside commit zone → update reference for last committed
      // point
      mutateElement(multiElement, {
        lastCommittedPoint: multiElement.points[multiElement.points.length - 1],
      });
      setCursor(this.interactiveCanvas, CURSOR_TYPE.POINTER);
    } else {
      const elementHandle =
          elementWithQuickFlowHandle || elementWithHoverHandle;
      // 如果没有正在创建的多元素，则创建新元素
      let [gridX, gridY] = getGridPoint(
        pointerDownState.origin.x,
        pointerDownState.origin.y,
        event[KEYS.CTRL_OR_CMD] ? null : this.state.gridSize,
      );

      if (elementHandle) {
        const { direction, element } = elementHandle;
        switch (direction) {
          case "top":
            gridX = element.x + element.width / 2;
            gridY = element.y;
            break;
          case "right":
            gridX = element.x + element.width;
            gridY = element.y + element.height / 2;
            break;
          case "bottom":
            gridX = element.x + element.width / 2;
            gridY = element.y + element.height;
            break;
          case "left":
            gridX = element.x;
            gridY = element.y + element.height / 2;
            break;
        }
      }

      const topLayerFrame = this.getTopLayerFrameAtSceneCoords({
        x: gridX,
        y: gridY,
      });

      /* If arrow is pre-arrowheads, it will have undefined for both start and end arrowheads.
      If so, we want it to be null for start and "arrow" for end. If the linear item is not
      an arrow, we want it to be null for both. Otherwise, we want it to use the
      values from appState. */

      const { currentItemStartArrowhead, currentItemEndArrowhead } = this.state;
      const [startArrowhead, endArrowhead] =
        elementType === "arrow"
          ? [currentItemStartArrowhead, currentItemEndArrowhead]
          : [null, null];
      let strokeStyle:any = this.state.currentItemStrokeStyle;
      let strokeWidth:any = this.state.currentItemStrokeWidth;
      if (elementType == 'arrow' || elementType == 'line'){
        strokeStyle = 'solid';
      }

      let szRoundness:any = this.state.currentItemRoundness === "round"
          ? { type: ROUNDNESS.PROPORTIONAL_RADIUS }
          : null;

      let szConnectType = localStorage.getItem(KEY_GLOBAL_CONNECTOR_TYPE);

      //if from l/line 直接用直线..
      if (global.g_forceStraight){
        szConnectType = 'straight';
      }
      global.g_forceStraight = false;

      if (elementType == 'line' || szConnectType == 'straight'){
        szRoundness = null;
      }
        if (szConnectType == 'elbow'){
            szRoundness = {type:ROUNDNESS.ELBOW};
        }

      let szStrokeColor = this.state.currentItemStrokeColor;
      if (elementType == 'line'){
        szStrokeColor = DEFAULT_ARROW_COLOR;
      }else if (elementType == 'arrow'){
        szStrokeColor = localStorage.getItem(KEY_DRAW_ARROW_STROKE_COLOR) || DEFAULT_ARROW_COLOR;
        strokeStyle = localStorage.getItem(KEY_DRAW_ARROW_LINE_STYLE) || 'solid';
        let nStrokeWidth = this.state.currentItemArrowStrokeWidth;
        let szStrokeWidth = localStorage.getItem(KEY_DRAW_ARROW_STROKE_WIDTH);
        if (szStrokeWidth){
          nStrokeWidth = getSafeFloat(szStrokeWidth);
        }
        strokeWidth = nStrokeWidth;//DEFAULT_ARROW_STROKE_WIDTH;
      }


      const element = newLinearElement({
        type: elementType,
        x: gridX,
        y: gridY,
        strokeColor: isDarkMode()?DEFAULT_DRAW_ST_COLOR:szStrokeColor,
        backgroundColor: this.state.currentItemBackgroundColor,
        fillStyle: this.state.currentItemFillStyle,
        strokeWidth: strokeWidth,
        strokeStyle: strokeStyle,
        roughness: this.state.currentItemRoughness,
        opacity: this.state.currentItemOpacity,
        roundness: szRoundness,
        startArrowhead,
        endArrowhead,
        locked: false,
        frameId: topLayerFrame ? topLayerFrame.id : null,
      });

      this.setState((prevState) => {
        const nextSelectedElementIds = {
          ...prevState.selectedElementIds,
        };
        delete nextSelectedElementIds[element.id];
        return {
          selectedElementIds: makeNextSelectedElementIds(
            nextSelectedElementIds,
            prevState,
          ),
        };
      });
      mutateElement(element, {
        points: [...element.points, [0, 0]],
      });
      let boundElement:any = getHoveredElementForBinding(
        pointerDownState.origin,
        this.scene,
      );

      this.scene.addNewElement(element);

      if (boundElement == null){
        boundElement = elementHandle?.element;
      }

      if (boundElement){
        const scenePointer = viewportCoordsToSceneCoords(event, this.state);
        const { x, y, width, height } = boundElement;
        const { x: pointerX, y: pointerY } = scenePointer;
        // 定义边界的阈值，用于确定指针是否位于边界附近
        const threshold = 10; // 可以根据需要调整这个值

        // 初始化direction变量
        let direction: "top" | "bottom" | "left" | "right" | null = null;

        // 计算指针与hitElement的四个边界的距离
        const distanceTop = Math.abs(y - pointerY);
        const distanceBottom = Math.abs(y + height - pointerY);
        const distanceLeft = Math.abs(x - pointerX);
        const distanceRight = Math.abs(x + width - pointerX);

        // 检查指针是否在顶部边界附近
        if (distanceTop <= threshold && pointerX >= x && pointerX <= x + width) {
          direction = "top";
        }
        // 检查指针是否在底部边界附近
        else if (
            distanceBottom <= threshold &&
            pointerX >= x &&
            pointerX <= x + width
        ) {
          direction = "bottom";
        }
        // 检查指针是否在左侧边界附近
        else if (
            distanceLeft <= threshold &&
            pointerY >= y &&
            pointerY <= y + height
        ) {
          direction = "left";
        }
        // 检查指针是否在右侧边界附近
        else if (
            distanceRight <= threshold &&
            pointerY >= y &&
            pointerY <= y + height
        ) {
          direction = "right";
        }
        let hoveredElement:any = null;
        if (element.type == 'arrow'){
          hoveredElement = {
            ...this.state.hoveredElement,
            sourceElement: boundElement,
            linearElement:element,
            status: null,
            direction
          }
        }

        setTimeout(() => {
          this.setState({
            draggingElement: element,
            editingElement: element,
            startBoundElement: elementWithHoverHandle ? null : boundElement,
            suggestedBindings: [],
            hoveredElement:hoveredElement
          });
        }, 0);
        if (elementHandle) {
          bindOrUnbindLinearElement(
              element,
              elementHandle.element as ExcalidrawBindableElement,
              null,
          );
          this.setState({
            activeTool: updateActiveTool(this.state, { type: "arrow" }),
          });
        }
      }else{
        this.setState((prevState) => {
          const nextSelectedElementIds = {
            ...prevState.selectedElementIds,
          };
          delete nextSelectedElementIds[element.id];
          return {
            selectedElementIds: makeNextSelectedElementIds(
                nextSelectedElementIds,
                prevState,
            ),
          };
        });
        mutateElement(element, {
          points: [...element.points, [0, 0]],
        });
        const boundElement = getHoveredElementForBinding(
            pointerDownState.origin,
            this.scene,
        );
        this.setState({
          draggingElement: element,
          editingElement: element,
          startBoundElement: boundElement,
          suggestedBindings: [],
        });
      }

      return element;
    }
  };

  private getCurrentItemRoundness(
      elementType:
          | "selection"
          | "rectangle"
          | "roundedRectangle"
          | "ellipse"
          | "triangle"
          | "diamond"
          | "bubble"
          | "parallelogram"
          | "pentagram"
          | "thickArrow"
          | "iframe"
          | "embeddable",
  ) {
    if (["rectangle"].includes(elementType)) {
      return null;
    }
    if (["roundedRectangle", "bubble"].includes(elementType)) {
      return {
        type: ROUNDNESS.ADAPTIVE_RADIUS,
      };
    }

    return this.state.currentItemRoundness === "round"
      ? {
          type: isUsingAdaptiveRadius(elementType)
            ? ROUNDNESS.ADAPTIVE_RADIUS
            : ROUNDNESS.PROPORTIONAL_RADIUS,
        }
      : null;
  }

  /**
   * 修改 矩形，圆形，等各种形状的默认样式....
   * @param elementType
   * @param pointerDownState
   */
  private createGenericElementOnPointerDown = (
    elementType: ExcalidrawGenericElement["type"] | "embeddable",
    pointerDownState: PointerDownState,
    bInsertCanvas: boolean = true,
  ): ExcalidrawElement => {
    const [gridX, gridY] = getGridPoint(
      pointerDownState.origin.x,
      pointerDownState.origin.y,
      this.lastPointerDownEvent?.[KEYS.CTRL_OR_CMD]
        ? null
        : this.state.gridSize,
    );

    const topLayerFrame = this.getTopLayerFrameAtSceneCoords({
      x: gridX,
      y: gridY,
    });

    const baseElementAttributes = {
      x: gridX,
      y: gridY,
      strokeColor: this.state.currentItemStrokeColor,
      backgroundColor: this.state.currentItemBackgroundColor,
      fillStyle: this.state.currentItemFillStyle,
      strokeWidth: this.state.currentItemStrokeWidth,
      strokeStyle: this.state.currentItemStrokeStyle,
      roughness: this.state.currentItemRoughness,
      opacity: this.state.currentItemOpacity,
      roundness: this.getCurrentItemRoundness(elementType),
      locked: false,
      frameId: topLayerFrame ? topLayerFrame.id : null,
    } as const;

    let element;
    if (elementType === "embeddable") {
      element = newEmbeddableElement({
        type: "embeddable",
        validated: null,
        ...baseElementAttributes,
        embedType: "page",
      });
    }
    else {
      let szBackgroundColor = 'TRANSPARENT';
      let strokeWidth = this.state.currentItemStrokeWidth;
      let strokeColor = this.state.currentItemStrokeColor;
      let roundness = undefined;
      switch (elementType) {
        case "rectangle":
        case "roundedRectangle":
        case "ellipse":
        case "triangle":
        case "diamond":
        case "bubble":
        case "parallelogram":
        case "pentagram":
        case "thickArrow":
          szBackgroundColor = getShapeElementBackgroundColor();
          strokeWidth = 1;
          strokeColor = getShapeElementStrokeColor();
          if (elementType == 'roundedRectangle'){
            roundness = {type:ROUNDNESS.ADAPTIVE_RADIUS,value:12}
          }
          break;
      }
      element = newElement({
        type: elementType,
        ...baseElementAttributes,
        backgroundColor:szBackgroundColor,//??????why??????
        strokeWidth:strokeWidth,
        strokeColor:strokeColor,
        roundness:roundness
      });
    }

    if (element.type === "selection") {
      this.setState({
        selectionElement: element,
        draggingElement: element,
      });
    } else {
      if (bInsertCanvas){
        this.scene.addNewElement(element);
        this.setState({
          multiElement: null,
          draggingElement: element,
          editingElement: element,
        });
      }
    }
    return element;
  };

  private createFrameElementOnPointerDown = (
    pointerDownState: PointerDownState,
    type: Extract<ToolType, "frame" | "magicframe">,
  ): void => {
    if (!this.state.currentFrameSize) {
      return;
    }

    const [gridX, gridY] = getGridPoint(
      pointerDownState.origin.x,
      pointerDownState.origin.y,
      this.lastPointerDownEvent?.[KEYS.CTRL_OR_CMD]
        ? null
        : this.state.gridSize,
    );

    const { width, height, type: frameSizeType } = this.state.currentFrameSize;
    const nFrameCount = this.getSceneElements().filter(p=>p.type == "frame").length;

    const constructorOpts = {
      x: gridX,
      y: gridY,
      opacity: this.state.currentItemOpacity,
      locked: false,
      ...FRAME_STYLE,
      width: frameSizeType === "preset" ? 540 : Number(width),
      height:
          frameSizeType === "preset" ? 540 * (height / width) : Number(height),
      name:'Frame '+(nFrameCount+1),
      roundness: {
        type: ROUNDNESS.ADAPTIVE_RADIUS,
        value: 8
      },
    } as const;

    const frame =
      type === TOOL_TYPE.magicframe
        ? newMagicFrameElement(constructorOpts)
        : newFrameElement(constructorOpts);

    this.scene.replaceAllElements([
      ...this.scene.getElementsIncludingDeleted(),
      frame,
    ]);

    this.setState({
      multiElement: null,
      draggingElement: frame,
      editingElement: frame,
    });
  };

  private maybeCacheReferenceSnapPoints(
    event: KeyboardModifiersObject,
    selectedElements: ExcalidrawElement[],
    recomputeAnyways: boolean = false,
  ) {
    if (
      isSnappingEnabled({
        event,
        appState: this.state,
        selectedElements,
      }) &&
      (recomputeAnyways || !SnapCache.getReferenceSnapPoints())
    ) {
      SnapCache.setReferenceSnapPoints(
        getReferenceSnapPoints(
          this.scene.getNonDeletedElements(),
          selectedElements,
          this.state,
        ),
      );
    }
  }

  private maybeCacheVisibleGaps(
    event: KeyboardModifiersObject,
    selectedElements: ExcalidrawElement[],
    recomputeAnyways: boolean = false,
  ) {
    if (
      isSnappingEnabled({
        event,
        appState: this.state,
        selectedElements,
      }) &&
      (recomputeAnyways || !SnapCache.getVisibleGaps())
    ) {
      SnapCache.setVisibleGaps(
        getVisibleGaps(
          this.scene.getNonDeletedElements(),
          selectedElements,
          this.state,
        ),
      );
    }
  }

  private onKeyDownFromPointerDownHandler(
    pointerDownState: PointerDownState,
  ): (event: KeyboardEvent) => void {
    // console.log("onKeyDownFromPointerDownHandler")
    return withBatchedUpdates((event: KeyboardEvent) => {
      if (this.maybeHandleResize(pointerDownState, event)) {
        return;
      }
      this.maybeDragNewGenericElement(pointerDownState, event);
    });
  }

  private onKeyUpFromPointerDownHandler(
    pointerDownState: PointerDownState,
  ): (event: KeyboardEvent) => void {
    return withBatchedUpdates((event: KeyboardEvent) => {
      // Prevents focus from escaping excalidraw tab
      event.key === KEYS.ALT && event.preventDefault();
      if (this.maybeHandleResize(pointerDownState, event)) {
        return;
      }
      this.maybeDragNewGenericElement(pointerDownState, event);
    });
  }

  private onPointerMoveFromPointerDownHandler(
    pointerDownState: PointerDownState,
  ) {
    return withBatchedUpdatesThrottled((event: PointerEvent) => {
      // We need to initialize dragOffsetXY only after we've updated
      // `state.selectedElementIds` on pointerDown. Doing it here in pointerMove
      // event handler should hopefully ensure we're already working with
      // the updated state.
      if (pointerDownState.drag.offset === null) {
        pointerDownState.drag.offset = tupleToCoors(
          getDragOffsetXY(
            this.scene.getSelectedElements(this.state),
            pointerDownState.origin.x,
            pointerDownState.origin.y,
          ),
        );
      }
      const target = event.target;
      if (!(target instanceof HTMLElement)) {
        return;
      }

      if (this.handlePointerMoveOverScrollbars(event, pointerDownState)) {
        return;
      }

      const pointerCoords = viewportCoordsToSceneCoords(event, this.state);

      if (isEraserActive(this.state)) {
        this.handleEraser(event, pointerDownState, pointerCoords);
        return;
      }

      if (this.state.activeTool.type === "laser") {
        this.laserPathManager.addPointToPath(pointerCoords.x, pointerCoords.y);
      }

      const [gridX, gridY] = getGridPoint(
        pointerCoords.x,
        pointerCoords.y,
        event[KEYS.CTRL_OR_CMD] ? null : this.state.gridSize,
      );

      // for arrows/lines, don't start dragging until a given threshold
      // to ensure we don't create a 2-point arrow by mistake when
      // user clicks mouse in a way that it moves a tiny bit (thus
      // triggering pointermove)
      if (
        !pointerDownState.drag.hasOccurred &&
        (this.state.activeTool.type === "arrow" ||
          this.state.activeTool.type === "line")
      ) {
        if (
          distance2d(
            pointerCoords.x,
            pointerCoords.y,
            pointerDownState.origin.x,
            pointerDownState.origin.y,
          ) < DRAGGING_THRESHOLD
        ) {
          return;
        }
      }
      if (pointerDownState.resize.isResizing) {
        pointerDownState.lastCoords.x = pointerCoords.x;
        pointerDownState.lastCoords.y = pointerCoords.y;
        if (this.maybeHandleResize(pointerDownState, event)) {
          resizeHandleType = pointerDownState.resize.handleType;
          return true;
        }
      }

      if (this.state.selectedLinearElement) {
        const linearElementEditor =
          this.state.editingLinearElement || this.state.selectedLinearElement;

        if (
          LinearElementEditor.shouldAddMidpoint(
            this.state.selectedLinearElement,
            pointerCoords,
            this.state,
          )
        ) {
          const ret = LinearElementEditor.addMidpoint(
            this.state.selectedLinearElement,
            pointerCoords,
            this.state,
            !event[KEYS.CTRL_OR_CMD],
          );
          if (!ret) {
            return;
          }

          // Since we are reading from previous state which is not possible with
          // automatic batching in React 18 hence using flush sync to synchronously
          // update the state. Check https://github.com/excalidraw/excalidraw/pull/5508 for more details.

          flushSync(() => {
            if (this.state.selectedLinearElement) {
              this.setState({
                selectedLinearElement: {
                  ...this.state.selectedLinearElement,
                  pointerDownState: ret.pointerDownState,
                  selectedPointsIndices: ret.selectedPointsIndices,
                },
              });
            }
            if (this.state.editingLinearElement) {
              this.setState({
                editingLinearElement: {
                  ...this.state.editingLinearElement,
                  pointerDownState: ret.pointerDownState,
                  selectedPointsIndices: ret.selectedPointsIndices,
                },
              });
            }
          });

          return;
        } else if (
          linearElementEditor.pointerDownState.segmentMidpoint.value !== null &&
          !linearElementEditor.pointerDownState.segmentMidpoint.added
        ) {
          return;
        }

        const didDrag = LinearElementEditor.handlePointDragging(
          event,
          this.state,
          pointerCoords.x,
          pointerCoords.y,
            (element, pointsSceneCoords, selectedPointsIndices) => {
              const suggestedBindings =
                  this.maybeSuggestBindingsForLinearElementAtCoords(
                      element,
                      pointsSceneCoords,
                  );
              // Lock start point to a specific edge midpoint when user drags it
              // Calculate direction and set fixed state together
              if (
                  selectedPointsIndices?.length === 1 &&
                  selectedPointsIndices[0] === 0 &&
                  element.startBinding
              ) {

                const startBindingElement = this.scene.getElement(
                    element.startBinding.elementId,
                )!;

                if (startBindingElement) {
                  const points = element.points;
                  const startPointAbs = {
                    x: element.x + points[0][0],
                    y: element.y + points[0][1],
                  };

                  const {x, y, width, height, angle} = startBindingElement as any;
                  const centerX = x + width / 2;
                  const centerY = y + height / 2;
                  const [px, py] = rotate(
                      startPointAbs.x,
                      startPointAbs.y,
                      centerX,
                      centerY,
                      -angle,
                  );

                  const dTop = Math.abs(py - y);
                  const dRight = Math.abs(x + width - px);
                  const dBottom = Math.abs(y + height - py);
                  const dLeft = Math.abs(px - x);
                  const min = Math.min(dTop, dRight, dBottom, dLeft);
                  const dir =
                      min === dTop
                          ? "top"
                          : min === dRight
                              ? "right"
                              : min === dBottom
                                  ? "bottom"
                                  : "left";

                  mutateElement(startBindingElement, {
                    startConnectionState: {
                      ...(startBindingElement.startConnectionState || {}),
                      [element.id]: "fixed",
                    },
                    startConnectionDirection: {
                      ...(startBindingElement as any).startConnectionDirection,
                      [element.id]: dir,
                    },
                  });
                }
              }

              if (
                  selectedPointsIndices?.length === 1 &&
                  selectedPointsIndices[0] === element.points.length - 1 &&
                  !suggestedBindings?.length
              ) {
                (isBindingEnabled(this.state)
                    ? bindOrUnbindSelectedElements
                    : unbindLinearElements)(
                    this.scene.getSelectedElements(this.state),
                );
              }

              // Mark end point as manually adjusted (fixed) when dragging
              if (
                  selectedPointsIndices?.length === 1 &&
                  selectedPointsIndices[0] === element.points.length - 1 &&
                  element.endBinding
              ) {
                const endBindingElement = this.scene.getElement(
                    element.endBinding.elementId,
                )!;
                if (endBindingElement) {
                  const points = element.points;
                  const endPointAbs = {
                    x: element.x + points[points.length - 1][0],
                    y: element.y + points[points.length - 1][1],
                  };

                  const { x, y, width, height, angle } = endBindingElement as any;
                  const centerX = x + width / 2;
                  const centerY = y + height / 2;
                  const [px, py] = rotate(
                      endPointAbs.x,
                      endPointAbs.y,
                      centerX,
                      centerY,
                      -angle,
                  );

                  const dTop = Math.abs(py - y);
                  const dRight = Math.abs(x + width - px);
                  const dBottom = Math.abs(y + height - py);
                  const dLeft = Math.abs(px - x);
                  const min = Math.min(dTop, dRight, dBottom, dLeft);
                  const dir =
                      min === dTop
                          ? "top"
                          : min === dRight
                              ? "right"
                              : min === dBottom
                                  ? "bottom"
                                  : "left";

                  mutateElement(endBindingElement, {
                    endConnectionState: {
                      ...(endBindingElement.endConnectionState || {}),
                      [element.id]: "fixed",
                    },
                    endConnectionDirection: {
                      ...(endBindingElement as any).endConnectionDirection,
                      [element.id]: dir,
                    },
                  });
                }
              }

              if (
                  // 更新startBindingElement-direction
                  selectedPointsIndices?.length === 1 &&
                  selectedPointsIndices[0] === element.points.length - 1 &&
                  element.startBinding &&
                  element.points.length === 2 &&
                  suggestedBindings
              ) {
                const startBindingElement = this.scene.getElement(
                    element.startBinding.elementId,
                )!;
                const points = element.points;
                const pointSceneCoords = {
                  x: element.x + points[points.length - 1][0],
                  y: element.y + points[points.length - 1][1],
                };
                const { direction } = updateBindingStartDirection({
                  startBindingElement,
                  lineElement: element,
                  sceneCoords: pointSceneCoords,
                  suggestedBindings,
                  hoveredDirection:
                      startBindingElement.startConnectionDirection?.[element.id] ||
                      null,
                });
                updatedraggingElementPoint(
                    startBindingElement,
                    direction,
                    element,
                    points,
                    pointSceneCoords,
                );
              }

            },
            linearElementEditor,
        );
        if (didDrag) {
          pointerDownState.lastCoords.x = pointerCoords.x;
          pointerDownState.lastCoords.y = pointerCoords.y;
          pointerDownState.drag.hasOccurred = true;
          if (
            this.state.editingLinearElement &&
            !this.state.editingLinearElement.isDragging
          ) {
            this.setState({
              editingLinearElement: {
                ...this.state.editingLinearElement,
                isDragging: true,
              },
            });
          }
          if (!this.state.selectedLinearElement.isDragging) {
            this.setState({
              selectedLinearElement: {
                ...this.state.selectedLinearElement,
                isDragging: true,
              },
            });
          }
          return;
        }
      }

      const hasHitASelectedElement = pointerDownState.hit.allHitElements.some(
        (element) => this.isASelectedElement(element),
      );

      const isSelectingPointsInLineEditor =
        this.state.editingLinearElement &&
        event.shiftKey &&
        this.state.editingLinearElement.elementId ===
          pointerDownState.hit.element?.id;
      if (
        (hasHitASelectedElement ||
          pointerDownState.hit.hasHitCommonBoundingBoxOfSelectedElements) &&
        !isSelectingPointsInLineEditor
      ) {
        let selectedElements = this.scene.getSelectedElements(this.state);
        //取最上面的这个。。TEST-----
        //如果是选中frame,然后再点击里面的元素，需要下面这个。
        //如果在frame里面有重叠的2个元素，那个下面这个也需要
        if (selectedElements.length == 1 && pointerDownState.hit.allHitElements.length > 1 && selectedElements[0].frameId){
          selectedElements = [pointerDownState.hit.allHitElements[pointerDownState.hit.allHitElements.length-1]];
        }

        if (selectedElements.every((element) => element.locked)) {
          return;
        }

        const selectedElementsHasAFrame = selectedElements.find((e) =>
          isFrameLikeElement(e),
        );
        const topLayerFrame = this.getTopLayerFrameAtSceneCoords(pointerCoords);
        this.setState({
          frameToHighlight:
            topLayerFrame && !selectedElementsHasAFrame ? topLayerFrame : null,
        });

        // Marking that click was used for dragging to check
        // if elements should be deselected on pointerup
        pointerDownState.drag.hasOccurred = true;
        this.setState({
          selectedElementsAreBeingDragged: true,
        });
        // prevent dragging even if we're no longer holding cmd/ctrl otherwise
        // it would have weird results (stuff jumping all over the screen)
        // Checking for editingElement to avoid jump while editing on mobile #6503
        if (
          selectedElements.length > 0 &&
          !pointerDownState.withCmdOrCtrl &&
          !this.state.editingElement &&
          this.state.activeEmbeddable?.state !== "active"
        ) {
          const dragOffset = {
            x: pointerCoords.x - pointerDownState.origin.x,
            y: pointerCoords.y - pointerDownState.origin.y,
          };

          const originalElements = [
            ...pointerDownState.originalElements.values(),
          ];

          // We only drag in one direction if shift is pressed
          const lockDirection = event.shiftKey;

          if (lockDirection) {
            const distanceX = Math.abs(dragOffset.x);
            const distanceY = Math.abs(dragOffset.y);

            const lockX = lockDirection && distanceX < distanceY;
            const lockY = lockDirection && distanceX > distanceY;

            if (lockX) {
              dragOffset.x = 0;
            }

            if (lockY) {
              dragOffset.y = 0;
            }
          }

          // Snap cache *must* be synchronously popuplated before initial drag,
          // otherwise the first drag even will not snap, causing a jump before
          // it snaps to its position if previously snapped already.
          this.maybeCacheVisibleGaps(event, selectedElements);
          this.maybeCacheReferenceSnapPoints(event, selectedElements);

          const { snapOffset, snapLines } = snapDraggedElements(
            getSelectedElements(originalElements, this.state),
            dragOffset,
            this.state,
            event,
          );

          this.setState({ snapLines });

          // when we're editing the name of a frame, we want the user to be
          // able to select and interact with the text input
          var ret = !this.state.editingFrame &&
            dragSelectedElements(
              pointerDownState,
              selectedElements,
              dragOffset,
              this.state,
              this.scene,
              snapOffset,
              event[KEYS.CTRL_OR_CMD] ? null : this.state.gridSize,
            );

          this.maybeSuggestBindingForAll(selectedElements);

          // We duplicate the selected element if alt is pressed on pointer move
          if (false && event.altKey && !pointerDownState.hit.hasBeenDuplicated) {//!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!不要altKey!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!
            // Move the currently selected elements to the top of the z index stack, and
            // put the duplicates where the selected elements used to be.
            // (the origin point where the dragging started)

            pointerDownState.hit.hasBeenDuplicated = true;

            const nextElements = [];
            const elementsToAppend = [];
            const groupIdMap = new Map();
            const oldIdToDuplicatedId = new Map();
            const hitElement = pointerDownState.hit.element;
            const selectedElementIds = new Set(
              this.scene
                .getSelectedElements({
                  selectedElementIds: this.state.selectedElementIds,
                  includeBoundTextElement: true,
                  includeElementsInFrames: true,
                })
                .map((element) => element.id),
            );

            const elements = this.scene.getElementsIncludingDeleted();

            for (const element of elements) {
              if (
                selectedElementIds.has(element.id) ||
                // case: the state.selectedElementIds might not have been
                // updated yet by the time this mousemove event is fired
                (element.id === hitElement?.id &&
                  pointerDownState.hit.wasAddedToSelection)
              ) {
                const duplicatedElement = duplicateElement(
                  this.state.editingGroupId,
                  groupIdMap,
                  element,
                );
                const origElement = pointerDownState.originalElements.get(
                  element.id,
                )!;
                mutateElement(duplicatedElement, {
                  x: origElement.x,
                  y: origElement.y,
                });

                // put duplicated element to pointerDownState.originalElements
                // so that we can snap to the duplicated element without releasing
                pointerDownState.originalElements.set(
                  duplicatedElement.id,
                  duplicatedElement,
                );

                nextElements.push(duplicatedElement);
                elementsToAppend.push(element);
                oldIdToDuplicatedId.set(element.id, duplicatedElement.id);
              } else {
                nextElements.push(element);
              }
            }
            const nextSceneElements = [...nextElements, ...elementsToAppend];
            bindTextToShapeAfterDuplication(
              nextElements,
              elementsToAppend,
              oldIdToDuplicatedId,
            );
            fixBindingsAfterDuplication(
              nextSceneElements,
              elementsToAppend,
              oldIdToDuplicatedId,
              "duplicatesServeAsOld",
            );
            bindElementsToFramesAfterDuplication(
              nextSceneElements,
              elementsToAppend,
              oldIdToDuplicatedId,
            );
            this.scene.replaceAllElements(nextSceneElements);
            this.maybeCacheVisibleGaps(event, selectedElements, true);
            this.maybeCacheReferenceSnapPoints(event, selectedElements, true);
          }
          return;
        }
      }

      // It is very important to read this.state within each move event,
      // otherwise we would read a stale one!
      const draggingElement = this.state.draggingElement;
      if (!draggingElement) {
        return;
      }

      if (
          draggingElement.type === "freedraw" ||
          draggingElement.type === "highlighter"
      ) {
        const points = draggingElement.points;
        const dx = pointerCoords.x - draggingElement.x;
        const dy = pointerCoords.y - draggingElement.y;

        const lastPoint = points.length > 0 && points[points.length - 1];
        const discardPoint =
          lastPoint && lastPoint[0] === dx && lastPoint[1] === dy;

        if (!discardPoint) {
          const pressures = draggingElement.simulatePressure
            ? draggingElement.pressures
            : [...draggingElement.pressures, event.pressure];

          mutateElement(draggingElement, {
            points: [...points, [dx, dy]],
            pressures,
          });
        }
      } else if (isLinearElement(draggingElement)) {
        pointerDownState.drag.hasOccurred = true;
        this.setState({
          selectedElementsAreBeingDragged: true,
        });
        const points = draggingElement.points;
        let dx = gridX - draggingElement.x;
        let dy = gridY - draggingElement.y;

        if (shouldRotateWithDiscreteAngle(event) && points.length === 2) {
          ({ width: dx, height: dy } = getLockedLinearCursorAlignSize(
            draggingElement.x,
            draggingElement.y,
            pointerCoords.x,
            pointerCoords.y,
          ));
        }

        if (points.length === 1) {
          mutateElement(draggingElement, {
            points: [...points, [dx, dy]],
          });
        } else if (points.length === 2) {
          mutateElement(draggingElement, {
            points: [...points.slice(0, -1), [dx, dy]],
          });
        }

        if (isBindingElement(draggingElement, false)) {
          // When creating a linear element by dragging
          const suggestedBindings =
              this.maybeSuggestBindingsForLinearElementAtCoords(
                  draggingElement,
                  [pointerCoords],
                  this.state.startBoundElement,
              );
          // 只修改end-point
          if (
              points.length === 2 &&
              suggestedBindings &&
              draggingElement.startBinding
          ) {
            const startBindingElement = this.scene.getElement(
                draggingElement.startBinding.elementId,
            )!;
            const pointSceneCoords = {
              x: draggingElement.x + dx,
              y: draggingElement.y + dy,
            };
            const { direction } = updateBindingStartDirection({
              startBindingElement,
              lineElement: draggingElement,
              sceneCoords: pointSceneCoords,
              suggestedBindings,
              hoveredDirection: this.state.hoveredElement.direction,
            });
            updatedraggingElementPoint(
                startBindingElement,
                direction,
                draggingElement,
                points,
                pointSceneCoords,
            );
            this.setState({
              hoveredElement: {
                ...this.state.hoveredElement,
                // @ts-ignore
                direction,
              },
            });
          }
        }
      } else {
        pointerDownState.lastCoords.x = pointerCoords.x;
        pointerDownState.lastCoords.y = pointerCoords.y;
        this.maybeDragNewGenericElement(pointerDownState, event);
      }

      if (this.state.activeTool.type === "selection") {
        pointerDownState.boxSelection.hasOccurred = true;

        const elements = this.scene.getNonDeletedElements();

        // box-select line editor points
        if (this.state.editingLinearElement) {
          LinearElementEditor.handleBoxSelection(
            event,
            this.state,
            this.setState.bind(this),
          );
          // regular box-select
        } else {
          let shouldReuseSelection = true;

          if (!event.shiftKey && isSomeElementSelected(elements, this.state)) {
            if (
              pointerDownState.withCmdOrCtrl &&
              pointerDownState.hit.element
            ) {
              this.setState((prevState) =>
                selectGroupsForSelectedElements(
                  {
                    ...prevState,
                    selectedElementIds: {
                      [pointerDownState.hit.element!.id]: true,
                    },
                  },
                  this.scene.getNonDeletedElements(),
                  prevState,
                  this,
                ),
              );
            } else {
              shouldReuseSelection = false;
            }
          }
          const elementsWithinSelection = getElementsWithinSelection(
            elements,
            draggingElement,
          );

          this.setState((prevState) => {
            const nextSelectedElementIds = {
              ...(shouldReuseSelection && prevState.selectedElementIds),
              ...elementsWithinSelection.reduce(
                (acc: Record<ExcalidrawElement["id"], true>, element) => {
                  acc[element.id] = true;
                  return acc;
                },
                {},
              ),
            };

            if (pointerDownState.hit.element) {
              // if using ctrl/cmd, select the hitElement only if we
              // haven't box-selected anything else
              if (!elementsWithinSelection.length) {
                if (!isFrameElement(pointerDownState.hit.element)) {
                  nextSelectedElementIds[pointerDownState.hit.element.id] =
                      true;
                }
              } else {
                delete nextSelectedElementIds[pointerDownState.hit.element.id];
              }
            }

            prevState = !shouldReuseSelection
              ? { ...prevState, selectedGroupIds: {}, editingGroupId: null }
              : prevState;

            return {
              ...selectGroupsForSelectedElements(
                {
                  editingGroupId: prevState.editingGroupId,
                  selectedElementIds: nextSelectedElementIds,
                },
                this.scene.getNonDeletedElements(),
                prevState,
                this,
              ),
              // select linear element only when we haven't box-selected anything else
              selectedLinearElement:
                elementsWithinSelection.length === 1 &&
                isLinearElement(elementsWithinSelection[0])
                  ? new LinearElementEditor(
                      elementsWithinSelection[0],
                      this.scene,
                    )
                  : null,
              showHyperlinkPopup:
                elementsWithinSelection.length === 1 &&
                (elementsWithinSelection[0].link ||
                  isEmbeddableElement(elementsWithinSelection[0]))
                  ? "info"
                  : false,
            };
          });
        }
      }
    });
  }

  // Returns whether the pointer move happened over either scrollbar
  private handlePointerMoveOverScrollbars(
    event: PointerEvent,
    pointerDownState: PointerDownState,
  ): boolean {
    if (pointerDownState.scrollbars.isOverHorizontal) {
      const x = event.clientX;
      const dx = x - pointerDownState.lastCoords.x;
      this.translateCanvas({
        scrollX: this.state.scrollX - dx / this.state.zoom.value,
      });
      pointerDownState.lastCoords.x = x;
      return true;
    }

    if (pointerDownState.scrollbars.isOverVertical) {
      const y = event.clientY;
      const dy = y - pointerDownState.lastCoords.y;
      this.translateCanvas({
        scrollY: this.state.scrollY - dy / this.state.zoom.value,
      });
      pointerDownState.lastCoords.y = y;
      return true;
    }
    return false;
  }

  /**
   * 边角开始拖动的时候.....
   */
  public hasStartDragging = ()=>{
    const {
      draggingElement,
    } = this.state;
    if (draggingElement){
      return draggingElement?.startBinding && !draggingElement?.endBinding;
    }
    return false;
  }

  private onPointerUpFromPointerDownHandler(
    pointerDownState: PointerDownState,
  ): (event: PointerEvent) => void {
    return withBatchedUpdates((childEvent: PointerEvent) => {
      this.removePointer(childEvent);

      if (this.elementWithQuickFlowHandle) {
        if (!pointerDownState.drag.hasOccurred) {
          this.addQuickFlowElement(this.elementWithQuickFlowHandle);
          // this.addQuickFlowLinkLine(
          //   this.elementWithQuickFlowHandle,
          //   auxiliaryElement,
          // );
          // hideQuickFlowToolip();
          this.elementWithQuickFlowHandle = false;
          this.setState({
            draggingElement: null,
            editingElement: null,
            startBoundElement: null,
            suggestedBindings: [],
          });
          return;
        }
        this.elementWithQuickFlowHandle = false;
        this.setState({
          selectedElementIds: {},
        });
      } else if (this.elementWithHoverHandle) {
        if (!pointerDownState.drag.hasOccurred) {
          this.elementWithHoverHandle = false;
          this.setState({
            draggingElement: null,
            editingElement: null,
            startBoundElement: null,
            suggestedBindings: [],
            // hoveredElement: {//这里会影响card的拖出来的线条 。。。。。。
            //   element: null,
            //   direction: null,
            //   status: null,
            //   sourceElement: null,
            //   linearElement: null,
            // },
          });
          this.setState({
            activeTool: updateActiveTool(this.state, { type: "selection" }),
          });
          return;
        }
        this.elementWithHoverHandle = false;
        this.setState({
          selectedElementIds: {},
        });
      }

      if (pointerDownState.eventListeners.onMove) {
        pointerDownState.eventListeners.onMove.flush();
      }
      const {
        draggingElement,
        resizingElement,
        multiElement,
        activeTool,
        isResizing,
        isRotating,
      } = this.state;

      this.setState((prevState) => ({
        isResizing: false,
        isRotating: false,
        resizingElement: null,
        selectionElement: null,
        frameToHighlight: null,
        elementsToHighlight: null,
        cursorButton: "up",
        // text elements are reset on finalize, and resetting on pointerup
        // may cause issues with double taps
        editingElement:
          multiElement || isTextElement(this.state.editingElement)
            ? this.state.editingElement
            : null,
        snapLines: updateStable(prevState.snapLines, []),

        originSnapOffset: null,
      }));

      mindMapCache.forEach((mindmap) => {
        // @ts-ignore
        mindmap.select.onMouseup();
      });

      SnapCache.setReferenceSnapPoints(null);
      SnapCache.setVisibleGaps(null);

      this.savePointer(childEvent.clientX, childEvent.clientY, "up");

      this.setState({
        selectedElementsAreBeingDragged: false,
      });

      // Handle end of dragging a point of a linear element, might close a loop
      // and sets binding element
      if (this.state.editingLinearElement) {
        if (
          !pointerDownState.boxSelection.hasOccurred &&
          pointerDownState.hit?.element?.id !==
            this.state.editingLinearElement.elementId
        ) {
          this.actionManager.executeAction(actionFinalize);
        } else {
          const editingLinearElement = LinearElementEditor.handlePointerUp(
            childEvent,
            this.state.editingLinearElement,
            this.state,
          );
          if (editingLinearElement !== this.state.editingLinearElement) {
            this.setState({
              editingLinearElement,
              suggestedBindings: [],
            });
          }
        }
      } else if (this.state.selectedLinearElement) {
        if (
          pointerDownState.hit?.element?.id !==
          this.state.selectedLinearElement.elementId
        ) {
          const selectedELements = this.scene.getSelectedElements(this.state);
          // set selectedLinearElement to null if there is more than one element selected since we don't want to show linear element handles
          if (selectedELements.length > 1) {
            this.setState({ selectedLinearElement: null });
          }
        } else {
          const linearElementEditor = LinearElementEditor.handlePointerUp(
            childEvent,
            this.state.selectedLinearElement,
            this.state,
          );

          const { startBindingElement, endBindingElement } =
            linearElementEditor;
          const element = this.scene.getElement(linearElementEditor.elementId);
          if (isBindingElement(element)) {
            bindOrUnbindLinearElement(
              element,
              startBindingElement,
              endBindingElement,
            );

            // Ensure manual endpoint adjustment is persisted even if the endpoint
            // was temporarily unbound during dragging. We apply the "fixed" state
            // on pointerup (after rebind) so subsequent widget moves won't
            // auto-switch the endpoint side.
            //
            // Applies only to non-ELBOW (straight/curve) connectors.
            if (
                pointerDownState.drag.hasOccurred &&
                this.state.selectedLinearElement?.isDragging &&
                element.roundness?.type !== ROUNDNESS.ELBOW
            ) {
              const draggedPointIndex =
                  this.state.selectedLinearElement.pointerDownState.lastClickedPoint;
              const points = element.points;
              const lastIndex = points.length - 1;

              const lockEndpoint = (
                  startOrEnd: "start" | "end",
                  bindingElement: any,
                  pointAbs: { x: number; y: number },
              ) => {
                if (!bindingElement) return;
                const { x, y, width, height, angle } = bindingElement as any;
                const centerX = x + width / 2;
                const centerY = y + height / 2;
                const [px, py] = rotate(
                    pointAbs.x,
                    pointAbs.y,
                    centerX,
                    centerY,
                    -angle,
                );

                const dTop = Math.abs(py - y);
                const dRight = Math.abs(x + width - px);
                const dBottom = Math.abs(y + height - py);
                const dLeft = Math.abs(px - x);
                const min = Math.min(dTop, dRight, dBottom, dLeft);
                const dir =
                    min === dTop
                        ? "top"
                        : min === dRight
                            ? "right"
                            : min === dBottom
                                ? "bottom"
                                : "left";

                const connectionStateKey =
                    startOrEnd === "start"
                        ? "startConnectionState"
                        : "endConnectionState";
                const connectionDirectionKey =
                    startOrEnd === "start"
                        ? "startConnectionDirection"
                        : "endConnectionDirection";

                mutateElement(bindingElement, {
                  [connectionStateKey]: {
                    ...((bindingElement as any)[connectionStateKey] || {}),
                    [element.id]: "fixed",
                  },
                  [connectionDirectionKey]: {
                    ...((bindingElement as any)[connectionDirectionKey] || {}),
                    [element.id]: dir,
                  },
                });
              };

              // start endpoint dragged
              if (draggedPointIndex === 0 && element.startBinding) {
                const bindingEl = this.scene.getElement(
                    element.startBinding.elementId,
                );
                lockEndpoint("start", bindingEl, {
                  x: element.x + points[0][0],
                  y: element.y + points[0][1],
                });
              }

              // end endpoint dragged
              if (draggedPointIndex === lastIndex && element.endBinding) {
                const bindingEl = this.scene.getElement(element.endBinding.elementId);
                lockEndpoint("end", bindingEl, {
                  x: element.x + points[lastIndex][0],
                  y: element.y + points[lastIndex][1],
                });
              }
            }
          }

          if (linearElementEditor !== this.state.selectedLinearElement) {
            this.setState({
              selectedLinearElement: {
                ...linearElementEditor,
                selectedPointsIndices: null,
              },
              suggestedBindings: [],
            });
          }
        }
      }

      this.missingPointerEventCleanupEmitter.clear();

      window.removeEventListener(
        EVENT.POINTER_MOVE,
        pointerDownState.eventListeners.onMove!,
      );
      window.removeEventListener(
        EVENT.POINTER_UP,
        pointerDownState.eventListeners.onUp!,
      );
      window.removeEventListener(
        EVENT.KEYDOWN,
        pointerDownState.eventListeners.onKeyDown!,
      );
      window.removeEventListener(
        EVENT.KEYUP,
        pointerDownState.eventListeners.onKeyUp!,
      );

      if (this.state.pendingImageElementId) {
        this.setState({ pendingImageElementId: null });
      }

      this.onPointerUpEmitter.trigger(
        this.state.activeTool,
        pointerDownState,
        childEvent,
      );

      if (
          draggingElement?.type === "freedraw" ||
          draggingElement?.type === "highlighter"
      ) {
        const pointerCoords = viewportCoordsToSceneCoords(
          childEvent,
          this.state,
        );

        const points = draggingElement.points;
        let dx = pointerCoords.x - draggingElement.x;
        let dy = pointerCoords.y - draggingElement.y;

        // Allows dots to avoid being flagged as infinitely small
        if (dx === points[0][0] && dy === points[0][1]) {
          dy += 0.0001;
          dx += 0.0001;
        }

        const pressures = draggingElement.simulatePressure
          ? []
          : [...draggingElement.pressures, childEvent.pressure];

        mutateElement(draggingElement, {
          points: [...points, [dx, dy]],
          pressures,
          lastCommittedPoint: [dx, dy],
        });

        this.actionManager.executeAction(actionFinalize);

        return;
      }
      if (isImageElement(draggingElement)) {
        const imageElement = draggingElement;
        try {
          this.initializeImageDimensions(imageElement);
          this.setState(
            {
              selectedElementIds: makeNextSelectedElementIds(
                { [imageElement.id]: true },
                this.state,
              ),
            },
            () => {
              this.actionManager.executeAction(actionFinalize);
            },
          );
        } catch (error: any) {
          console.error(error);
          this.scene.replaceAllElements(
            this.scene
              .getElementsIncludingDeleted()
              .filter((el) => el.id !== imageElement.id),
          );
          this.actionManager.executeAction(actionFinalize);
        }
        return;
      }

      if (isLinearElement(draggingElement)) {
        if (draggingElement!.points.length > 1) {
          this.history.resumeRecording();
        }
        const pointerCoords = viewportCoordsToSceneCoords(
          childEvent,
          this.state,
        );

        if (
          !pointerDownState.drag.hasOccurred &&
          draggingElement &&
          !multiElement
        ) {
          mutateElement(draggingElement, {
            points: [
              ...draggingElement.points,
              [
                pointerCoords.x - draggingElement.x,
                pointerCoords.y - draggingElement.y,
              ],
            ],
          });
          this.setState({
            multiElement: draggingElement,
            editingElement: this.state.draggingElement,
          });
        } else if (pointerDownState.drag.hasOccurred && !multiElement) {
          if (
            isBindingEnabled(this.state) &&
            isBindingElement(draggingElement, false)
          ) {
            maybeBindLinearElement(
              draggingElement,
              this.state,
              this.scene,
              pointerCoords,
            );
          }
          this.setState({ suggestedBindings: [], startBoundElement: null });
          if (!activeTool.locked) {
            resetCursor(this.interactiveCanvas);
            this.setState((prevState) => ({
              draggingElement: null,
              activeTool: updateActiveTool(this.state, {
                type: "selection",
              }),
              selectedElementIds: makeNextSelectedElementIds(
                {
                  // ...prevState.selectedElementIds,
                  [draggingElement.id]: true,
                },
                prevState,
              ),
              selectedLinearElement: new LinearElementEditor(
                draggingElement,
                this.scene,
              ),
            }));
          } else {
            this.setState((prevState) => ({
              draggingElement: null,
            }));
          }
        }
        // 如果没有结尾没有绑定元素，咋把线、箭头删除
        setTimeout(() => {
          if (draggingElement.startBinding && !draggingElement.endBinding) {
            // this.actionManager.executeAction(actionDeleteSelected);
            // this.removeElementById(draggingElement.id);

            const hoverModel = document.getElementById("hoverModel")!;
            hoverModel.style.display = "block";
            // this.setState({
            //   isShowHoverdElementMenu:true
            // })
            postMessage(EVENT_HIDDEN_FLOAT_MENU);//隐藏浮动菜单，不确定哪边先出来。这里再隐藏一下.
            const { x, y } = sceneCoordsToViewportCoords(
                { sceneX: pointerCoords.x, sceneY: pointerCoords.y },
                this.state,
            );

            let transX = x + 10 * this.state.zoom.value - this.state.offsetLeft;
            if (transX + 204 > this.state.width){
              //超右边界了...
              transX = this.state.width - 204;
            }
            let transY = y - this.state.offsetTop;
            if (transY + 240 > this.state.height){
              //超下边界
              transY = this.state.height - 240;
            }

            const transform = `translate(${
                transX
            }px, ${transY}px) scale(${
                1.0
            })`;
            hoverModel.setAttribute("x", `${pointerCoords.x}`);
            hoverModel.setAttribute("y", `${pointerCoords.y}`);
            hoverModel.style.transform = transform;
          }
        }, 10);
        return;
      }

      if (
        activeTool.type !== "selection" &&
        draggingElement &&
        isInvisiblySmallElement(draggingElement)
      ) {
        // remove invisible element which was added in onPointerDown
        this.scene.replaceAllElements(
          this.scene
            .getElementsIncludingDeleted()
            .filter((el) => el.id !== draggingElement.id),
        );
        this.setState({
          draggingElement: null,
        });
        return;
      }

      if (draggingElement) {
        if (pointerDownState.drag.hasOccurred) {
          const sceneCoords = viewportCoordsToSceneCoords(
            childEvent,
            this.state,
          );

          // when editing the points of a linear element, we check if the
          // linear element still is in the frame afterwards
          // if not, the linear element will be removed from its frame (if any)
          if (
            this.state.selectedLinearElement &&
            this.state.selectedLinearElement.isDragging
          ) {
            const linearElement = this.scene.getElement(
              this.state.selectedLinearElement.elementId,
            );

            if (linearElement?.frameId) {
              const frame = getContainingFrame(linearElement);

              if (frame && linearElement) {
                if (!elementOverlapsWithFrame(linearElement, frame)) {
                  // remove the linear element from all groups
                  // before removing it from the frame as well
                  mutateElement(linearElement, {
                    groupIds: [],
                  });

                  this.scene.replaceAllElements(
                    removeElementsFromFrame(
                      this.scene.getElementsIncludingDeleted(),
                      [linearElement],
                      this.state,
                    ),
                  );
                }
              }
            }
          } else {
            // update the relationships between selected elements and frames
            const topLayerFrame =
              this.getTopLayerFrameAtSceneCoords(sceneCoords);

            const selectedElements = this.scene.getSelectedElements(this.state);
            let nextElements = this.scene.getElementsIncludingDeleted();

            const updateGroupIdsAfterEditingGroup = (
              elements: ExcalidrawElement[],
            ) => {
              if (elements.length > 0) {
                for (const element of elements) {
                  const index = element.groupIds.indexOf(
                    this.state.editingGroupId!,
                  );

                  mutateElement(
                    element,
                    {
                      groupIds: element.groupIds.slice(0, index),
                    },
                    false,
                  );
                }

                nextElements.forEach((element) => {
                  if (
                    element.groupIds.length &&
                    getElementsInGroup(
                      nextElements,
                      element.groupIds[element.groupIds.length - 1],
                    ).length < 2
                  ) {
                    mutateElement(
                      element,
                      {
                        groupIds: [],
                      },
                      false,
                    );
                  }
                });

                this.setState({
                  editingGroupId: null,
                });
              }
            };

            if (
              topLayerFrame &&
              !this.state.selectedElementIds[topLayerFrame.id]
            ) {
              const elementsToAdd: ExcalidrawElement[] = [];
              const elementsToEdit: ExcalidrawElement[] = [];
              selectedElements.forEach((element) => {
                if (!isElementInFrame(element, nextElements, this.state)) {
                  return;
                }
                if (element.frameId !== topLayerFrame.id) {
                  elementsToAdd.push(element);
                }
                if (element.frameId === topLayerFrame.id) {
                  elementsToEdit.push(element);
                }
              });

              if (this.state.editingGroupId) {
                updateGroupIdsAfterEditingGroup(elementsToAdd);
              }

              nextElements = addElementsToFrame(
                nextElements,
                elementsToAdd,
                topLayerFrame,
              );

              // 新增or调整
              let elementsToMutate: ExcalidrawElement[] = [];
              if (elementsToAdd.length) {
                elementsToMutate = elementsToAdd;
              } else if (elementsToEdit.length) {
                elementsToMutate = elementsToEdit;
              }
              if (elementsToMutate.length) {
                const elementsInFrame = getFrameChildren(
                    elementsToMutate,
                    topLayerFrame.id,
                );
                const [minX, minY, maxX, maxY] =
                    getCommonBounds(elementsInFrame);
                // 如果两个矩形相交
                mutateElement(
                    topLayerFrame,
                    expandRect2ToContainRect1(
                        {
                          x: minX,
                          y: minY,
                          width: maxX - minX,
                          height: maxY - minY,
                        },
                        topLayerFrame,
                    ),
                );
              }

            } else if (!topLayerFrame) {
              if (this.state.editingGroupId) {
                const elementsToRemove = selectedElements.filter(
                  (element) =>
                    element.frameId &&
                    !isElementInFrame(element, nextElements, this.state),
                );

                updateGroupIdsAfterEditingGroup(elementsToRemove);
              }
            }

            nextElements = updateFrameMembershipOfSelectedElements(
              nextElements,
              this.state,
              this,
            );

            this.scene.replaceAllElements(nextElements);
          }
        }

        if (isFrameLikeElement(draggingElement)) {
          const elementsInsideFrame = getElementsInNewFrame(
            this.scene.getElementsIncludingDeleted(),
            draggingElement,
          );

          this.scene.replaceAllElements(
            addElementsToFrame(
              this.scene.getElementsIncludingDeleted(),
              elementsInsideFrame,
              draggingElement,
            ),
          );
        }

        mutateElement(
          draggingElement,
          getNormalizedDimensions(draggingElement),
        );
      }

      if (resizingElement) {
        this.history.resumeRecording();
      }

      if (resizingElement && isInvisiblySmallElement(resizingElement)) {
        this.scene.replaceAllElements(
          this.scene
            .getElementsIncludingDeleted()
            .filter((el) => el.id !== resizingElement.id),
        );
      }

      // handle frame membership for resizing frames and/or selected elements
      if (pointerDownState.resize.isResizing) {
        let nextElements = updateFrameMembershipOfSelectedElements(
          this.scene.getElementsIncludingDeleted(),
          this.state,
          this,
        );

        const selectedFrames = this.scene
          .getSelectedElements(this.state)
          .filter((element): element is ExcalidrawFrameLikeElement =>
            isFrameLikeElement(element),
          );

        for (const frame of selectedFrames) {
          nextElements = replaceAllElementsInFrame(
            nextElements,
            getElementsInResizingFrame(
              this.scene.getElementsIncludingDeleted(),
              frame,
              this.state,
            ),
            frame,
            this.state,
          );
        }

        this.scene.replaceAllElements(nextElements);
      }

      // Code below handles selection when element(s) weren't
      // drag or added to selection on pointer down phase.
      const hitElement = pointerDownState.hit.element;
      if (
        this.state.selectedLinearElement?.elementId !== hitElement?.id &&
        isLinearElement(hitElement)
      ) {
        const selectedELements = this.scene.getSelectedElements(this.state);
        // set selectedLinearElement when no other element selected except
        // the one we've hit
        if (selectedELements.length === 1) {
          this.setState({
            selectedLinearElement: new LinearElementEditor(
              hitElement,
              this.scene,
            ),
          });
        }
      }

      const pointerStart = this.lastPointerDownEvent;
      const pointerEnd = this.lastPointerUpEvent || this.lastPointerMoveEvent;

      if (isEraserActive(this.state) && pointerStart && pointerEnd) {
        const draggedDistance = distance2d(
          pointerStart.clientX,
          pointerStart.clientY,
          pointerEnd.clientX,
          pointerEnd.clientY,
        );

        if (draggedDistance === 0) {
          const scenePointer = viewportCoordsToSceneCoords(
            {
              clientX: pointerEnd.clientX,
              clientY: pointerEnd.clientY,
            },
            this.state,
          );
          const hitElements = this.getElementsAtPosition(
            scenePointer.x,
            scenePointer.y,
          ).filter((ele) => isFreeDrawElementType(ele.type));
          hitElements.forEach(
            (hitElement) =>
              (pointerDownState.elementIdsToErase[hitElement.id] = {
                erase: true,
                opacity: hitElement.opacity,
              }),
          );
        }
        this.eraseElements(pointerDownState);
        return;
      } else if (Object.keys(pointerDownState.elementIdsToErase).length) {
        this.restoreReadyToEraseElements(pointerDownState);
      }

      if (
        hitElement &&
        !pointerDownState.drag.hasOccurred &&
        !pointerDownState.hit.wasAddedToSelection &&
        // if we're editing a line, pointerup shouldn't switch selection if
        // box selected
        (!this.state.editingLinearElement ||
          !pointerDownState.boxSelection.hasOccurred)
      ) {
        // when inside line editor, shift selects points instead
          const isAdditiveSelection =
              childEvent.shiftKey ||
              pointerDownState.withCmdOrCtrl ||
              childEvent[KEYS.CTRL_OR_CMD];
        if (isAdditiveSelection && !this.state.editingLinearElement) {
          if (this.state.selectedElementIds[hitElement.id]) {
            if (isSelectedViaGroup(this.state, hitElement)) {
              this.setState((_prevState) => {
                const nextSelectedElementIds = {
                  ..._prevState.selectedElementIds,
                };

                // We want to unselect all groups hitElement is part of
                // as well as all elements that are part of the groups
                // hitElement is part of
                for (const groupedElement of hitElement.groupIds.flatMap(
                  (groupId) =>
                    getElementsInGroup(
                      this.scene.getNonDeletedElements(),
                      groupId,
                    ),
                )) {
                  delete nextSelectedElementIds[groupedElement.id];
                }

                return {
                  selectedGroupIds: {
                    ..._prevState.selectedElementIds,
                    ...hitElement.groupIds
                      .map((gId) => ({ [gId]: false }))
                      .reduce((prev, acc) => ({ ...prev, ...acc }), {}),
                  },
                  selectedElementIds: makeNextSelectedElementIds(
                    nextSelectedElementIds,
                    _prevState,
                  ),
                };
              });
              // if not dragging a linear element point (outside editor)
            } else if (!this.state.selectedLinearElement?.isDragging) {
              // remove element from selection while
              // keeping prev elements selected

              this.setState((prevState) => {
                const newSelectedElementIds = {
                  ...prevState.selectedElementIds,
                };
                delete newSelectedElementIds[hitElement!.id];
                const newSelectedElements = getSelectedElements(
                  this.scene.getNonDeletedElements(),
                  { selectedElementIds: newSelectedElementIds },
                );

                return {
                  ...selectGroupsForSelectedElements(
                    {
                      editingGroupId: prevState.editingGroupId,
                      selectedElementIds: newSelectedElementIds,
                    },
                    this.scene.getNonDeletedElements(),
                    prevState,
                    this,
                  ),
                  // set selectedLinearElement only if thats the only element selected
                  selectedLinearElement:
                    newSelectedElements.length === 1 &&
                    isLinearElement(newSelectedElements[0])
                      ? new LinearElementEditor(
                          newSelectedElements[0],
                          this.scene,
                        )
                      : prevState.selectedLinearElement,
                };
              });
            }
          } else if (
            hitElement.frameId &&
            this.state.selectedElementIds[hitElement.frameId]
          ) {
            // when hitElement is part of a selected frame, deselect the frame
            // to avoid frame and containing elements selected simultaneously
            this.setState((prevState) => {
              const nextSelectedElementIds: {
                [id: string]: true;
              } = {
                ...prevState.selectedElementIds,
                [hitElement.id]: true,
              };
              // deselect the frame
              delete nextSelectedElementIds[hitElement.frameId!];

              // deselect groups containing the frame
              (this.scene.getElement(hitElement.frameId!)?.groupIds ?? [])
                .flatMap((gid) =>
                  getElementsInGroup(this.scene.getNonDeletedElements(), gid),
                )
                .forEach((element) => {
                  delete nextSelectedElementIds[element.id];
                });

              return {
                ...selectGroupsForSelectedElements(
                  {
                    editingGroupId: prevState.editingGroupId,
                    selectedElementIds: nextSelectedElementIds,
                  },
                  this.scene.getNonDeletedElements(),
                  prevState,
                  this,
                ),
                showHyperlinkPopup:
                  hitElement.link || isEmbeddableElement(hitElement)
                    ? "info"
                    : false,
              };
            });
          } else {
            // add element to selection while keeping prev elements selected
            this.setState((_prevState) => ({
              selectedElementIds: makeNextSelectedElementIds(
                {
                  ..._prevState.selectedElementIds,
                  [hitElement!.id]: true,
                },
                _prevState,
              ),
            }));
          }
        } else {
          this.setState((prevState) => ({
            ...selectGroupsForSelectedElements(
              {
                editingGroupId: prevState.editingGroupId,
                selectedElementIds: { [hitElement.id]: true },
              },
              this.scene.getNonDeletedElements(),
              prevState,
              this,
            ),
            selectedLinearElement:
              isLinearElement(hitElement) &&
              // Don't set `selectedLinearElement` if its same as the hitElement, this is mainly to prevent resetting the `hoverPointIndex` to -1.
              // Future we should update the API to take care of setting the correct `hoverPointIndex` when initialized
              prevState.selectedLinearElement?.elementId !== hitElement.id
                ? new LinearElementEditor(hitElement, this.scene)
                : prevState.selectedLinearElement,
          }));
        }
      }

      if (
        !pointerDownState.drag.hasOccurred &&
        !this.state.isResizing &&
        ((hitElement &&
          isHittingElementBoundingBoxWithoutHittingElement(
            hitElement,
            this.state,
            this.frameNameBoundsCache,
            pointerDownState.origin.x,
            pointerDownState.origin.y,
          )) ||
          (!hitElement &&
            pointerDownState.hit.hasHitCommonBoundingBoxOfSelectedElements))
      ) {
        if (this.state.editingLinearElement) {
          this.setState({ editingLinearElement: null });
        } else {
          // Deselect selected elements
          this.setState({
            selectedElementIds: makeNextSelectedElementIds({}, this.state),
            selectedGroupIds: {},
            editingGroupId: null,
            activeEmbeddable: null,
            activeStickyNotes: null,
            activeMindMap: null,
          });
        }
        return;
      }

      if (
        !activeTool.locked &&
        activeTool.type !== "freedraw" &&
        draggingElement &&
        draggingElement.type !== "selection"
      ) {
        this.setState((prevState) => ({
          selectedElementIds: makeNextSelectedElementIds(
            {
              ...prevState.selectedElementIds,
              [draggingElement.id]: true,
            },
            prevState,
          ),
          showHyperlinkPopup:
            isEmbeddableElement(draggingElement) && !draggingElement.link
              ? "editor"
              : prevState.showHyperlinkPopup,
        }));
      }

      if (
        activeTool.type !== "selection" ||
        isSomeElementSelected(this.scene.getNonDeletedElements(), this.state)
      ) {
        this.history.resumeRecording();
      }

      if (pointerDownState.drag.hasOccurred || isResizing || isRotating) {
        (isBindingEnabled(this.state)
          ? bindOrUnbindSelectedElements
          : unbindLinearElements)(this.scene.getSelectedElements(this.state));
      }

      if (activeTool.type === "laser") {
        this.laserPathManager.endPath();
        return;
      }

      if (
          !activeTool.locked &&
          activeTool.type !== "freedraw" &&
          activeTool.type !== "highlighter"
      ) {
        resetCursor(this.interactiveCanvas);
        this.setState({
          draggingElement: null,
          suggestedBindings: [],
          activeTool: updateActiveTool(this.state, { type: "selection" }),
        });
      } else {
        this.setState({
          draggingElement: null,
          suggestedBindings: [],
        });
      }

      if (
        hitElement &&
        this.lastPointerUpEvent &&
        this.lastPointerDownEvent &&
        this.lastPointerUpEvent.timeStamp -
          this.lastPointerDownEvent.timeStamp <
          300 &&
        gesture.pointers.size <= 1 &&
        isIframeLikeElement(hitElement) &&
        this.isIframeLikeElementCenter(
          hitElement,
          this.lastPointerUpEvent,
          pointerDownState.origin.x,
          pointerDownState.origin.y,
        ) && !isFileVideoElement(hitElement)
      ) {
        this.handleEmbeddableCenterClick(hitElement);
      }

      if (
          hitElement &&
          gesture.pointers.size <= 1 &&
          isFrameElement(hitElement)
      ) {
        const pointerCoords = viewportCoordsToSceneCoords(
            childEvent,
            this.state,
        );

        if (
            distance2d(
                pointerCoords.x,
                pointerCoords.y,
                pointerDownState.origin.x,
                pointerDownState.origin.y,
            ) < DRAGGING_THRESHOLD
        ) {
            const isAdditiveSelection =
                childEvent.shiftKey ||
                pointerDownState.withCmdOrCtrl ||
                childEvent[KEYS.CTRL_OR_CMD];

            // If user is multi-selecting (Ctrl/Cmd or Shift), do not override
            // the selection to just the frame. Let the main selection logic
            // handle additive selection.
            if (!isAdditiveSelection) {
                this.setState({
                    selectedElementIds: { [hitElement!.id]: true },
                });
                return;
            }
        }
      }

      if (pointerDownState.boxUnSelection.onUp) {
        this.setState({
          selectedElementIds: {},
        });
      }

      // const elements = getSelectedElements(this.getSceneElements(),this.state);
      // const { x, y } = viewportCoordsToSceneCoords(event, this.state);
      // const frame = this.getTopLayerFrameAtSceneCoords({ x, y });
      //
      // for (let i=0;i<elements.length;i++){
      //   if (isMindmapElement(elements[i])){
      //     mutateElement(elements[i],{frameId:frame?.id || null})
      //   }
      // }

    });
  }

  private restoreReadyToEraseElements = (
    pointerDownState: PointerDownState,
  ) => {
    const elements = this.scene.getElementsIncludingDeleted().map((ele) => {
      if (
        pointerDownState.elementIdsToErase[ele.id] &&
        pointerDownState.elementIdsToErase[ele.id].erase
      ) {
        return newElementWith(ele, {
          opacity: pointerDownState.elementIdsToErase[ele.id].opacity,
        });
      } else if (
        isBoundToContainer(ele) &&
        pointerDownState.elementIdsToErase[ele.containerId] &&
        pointerDownState.elementIdsToErase[ele.containerId].erase
      ) {
        return newElementWith(ele, {
          opacity: pointerDownState.elementIdsToErase[ele.containerId].opacity,
        });
      } else if (
        ele.frameId &&
        pointerDownState.elementIdsToErase[ele.frameId] &&
        pointerDownState.elementIdsToErase[ele.frameId].erase
      ) {
        return newElementWith(ele, {
          opacity: pointerDownState.elementIdsToErase[ele.frameId].opacity,
        });
      }
      return ele;
    });

    this.scene.replaceAllElements(elements);
  };

  private eraseElements = (pointerDownState: PointerDownState) => {
    const elements = this.scene.getElementsIncludingDeleted().map((ele) => {
      if (!isFreeDrawElementType(ele.type)) {
        return ele;
      }

      if (
        pointerDownState.elementIdsToErase[ele.id] &&
        pointerDownState.elementIdsToErase[ele.id].erase
      ) {
        return newElementWith(ele, { isDeleted: true });
      } else if (
        isBoundToContainer(ele) &&
        pointerDownState.elementIdsToErase[ele.containerId] &&
        pointerDownState.elementIdsToErase[ele.containerId].erase
      ) {
        return newElementWith(ele, { isDeleted: true });
      } else if (
        ele.frameId &&
        pointerDownState.elementIdsToErase[ele.frameId] &&
        pointerDownState.elementIdsToErase[ele.frameId].erase
      ) {
        return newElementWith(ele, { isDeleted: true });
      }
      return ele;
    });

    this.history.resumeRecording();
    this.scene.replaceAllElements(elements);
  };

  private initializeImage = async ({
    imageFile,
    imageElement: _imageElement,
    showCursorImagePreview = false,
                                     isFrameBackgroundImage,
                                     szImportMode = IMPORT_FILE_MODE_IMPORTMODE,
  }: {
    imageFile: File;
    imageElement: ExcalidrawImageElement;
    showCursorImagePreview?: boolean;
    isFrameBackgroundImage?: boolean;
    szImportMode?:any;
  }) => {
    // at this point this should be guaranteed image file, but we do this check
    // to satisfy TS down the line
    if (!isSupportedImageFile(imageFile)) {
      throw new Error(t("errors.unsupportedFileType"));
    }
    const mimeType = imageFile.type;

    setCursor(this.interactiveCanvas, "wait");

    if (mimeType === MIME_TYPES.svg) {
      try {
        imageFile = SVGStringToFile(
          await normalizeSVG(await imageFile.text()),
          imageFile.name,
        );
      } catch (error: any) {
        console.warn(error);
        throw new Error(t("errors.svgImageInsertError"));
      }
    }

    // generate image id (by default the file digest) before any
    // resizing/compression takes place to keep it more portable
    // const fileId = await ((this.props.generateIdForFile?.(
    //   imageFile,
    // ) as Promise<FileId>) || generateIdFromFile(imageFile));
    const fileId = crypto.randomUUID();//直接生成一个uuid，。。。即使一个图片拖进来几次，就创建几次...

    if (!fileId) {
      console.warn(
        "Couldn't generate file id or the supplied `generateIdForFile` didn't resolve to one.",
      );
      throw new Error(t("errors.imageInsertError"));
    }

    const existingFileData = this.files[fileId];
    if (!existingFileData?.dataURL) {
      //update 20240405  图片模糊 -- 取消了图片的压缩。。
      if (isFrameBackgroundImage || imageFile.size > MAX_ALLOWED_FILE_BYTES){
        //只对frame的背景图剪切过的做压缩....
        try {
          imageFile = await resizeImageFile(imageFile, {maxWidthOrHeight: DEFAULT_MAX_IMAGE_WIDTH_OR_HEIGHT,});
        } catch (error: any) {
          console.error(
              "Error trying to resizing image file on insertion",
              error,
          );
        }
      }

      if (imageFile.size > MAX_ALLOWED_FILE_BYTES) {
        throw new Error(
            i18next.t("fileTooBig", {
            maxSize: `${Math.trunc(MAX_ALLOWED_FILE_BYTES / 1024 / 1024)}MB`,
          }),
        );
      }
    }

    if (showCursorImagePreview) {
      const dataURL = this.files[fileId]?.dataURL;
      // optimization so that we don't unnecessarily resize the original
      // full-size file for cursor preview
      // (it's much faster to convert the resized dataURL to File)
      const resizedFile = dataURL && dataURLToFile(dataURL);

      this.setImagePreviewCursor(resizedFile || imageFile);
    }

    const dataURL =
      this.files[fileId]?.dataURL || (await getDataURL(imageFile));

    const imageElement = mutateElement(
      _imageElement,
      {
        fileId,
      },
      false,
    ) as NonDeleted<InitializedExcalidrawImageElement>;

    return new Promise<NonDeleted<InitializedExcalidrawImageElement>>(
      async (resolve, reject) => {
        try {
          let szFullPath = '';
          if (szImportMode != IMPORT_FILE_MODE_IMPORTMODE){
            szFullPath = imageFile.path;
          }
          this.files = {
            ...this.files,
            [fileId]: {
              mimeType,
              id: fileId,
              dataURL:(APP_WEB || isOfflineWeb() || mimeType.includes('/svg'))?dataURL:'',
              created: Date.now(),
              lastRetrieved: Date.now(),
              fullPath:szFullPath//有这个字段，表示使用的是文件的绝对路径....
            },
          };

          const cachedImageData = this.imageCache.get(fileId);
          if (!cachedImageData || isFrameBackgroundImage) {
            //这里保存文件.。
            if (!isSvg(mimeType)){
              const szFileName = fileId + "."+extFromMineType(mimeType);
              if (szImportMode == IMPORT_FILE_MODE_IMPORTMODE){
                await ipcInvoke('write-base64-image',{fileName:szFileName,imgBase64:dataURL});
              }
            }

            this.addNewImagesToImageCache();
            await this.updateImageCache([imageElement]);
          }



          if (cachedImageData?.image instanceof Promise) {
            await cachedImageData.image;
          }
          if (
            this.state.pendingImageElementId !== imageElement.id &&
            this.state.draggingElement?.id !== imageElement.id
          ) {
            this.initializeImageDimensions(imageElement, true,isFrameBackgroundImage);
          }
          resolve(imageElement);
        } catch (error: any) {
          console.error(error);
          reject(new Error(t("errors.imageInsertError")));
        } finally {
          if (!showCursorImagePreview) {
            resetCursor(this.interactiveCanvas);
          }
        }
      },
    );
  };

  /**
   * inserts image into elements array and rerenders
   */
  private insertImageElement = async (
    imageElement0: ExcalidrawImageElement,
    imageFile: File,
    showCursorImagePreview?: boolean,
    isFrameBackgroundImage?: boolean,
    szImportMode?:any,
  ) => {
    // we should be handling all cases upstream, but in case we forget to handle
    // a future case, let's throw here
    if (!this.isToolSupported("image")) {
      this.setState({ errorMessage: t("errors.imageToolNotSupported") });
      return;
    }
    var imageElement = {...imageElement0};
    if (imageFile.type == MIME_TYPES.svg){
      const szText = await imageFile.text();
      let szDefaultColor = getSvgColor(szText);
      let wh:any = {};
      if (imageElement.width == 0 || imageElement.height === 0){
        wh.width = getSvgWidth(szText);
        wh.height = getSvgHeight(szText);
        if (wh.width === 0){
          wh.width = 20;
        }
        if(wh.height === 0){
          wh.height = 20;
        }

        wh.width *= 4;
        wh.height *= 4;

        if (wh.width > 80){
          wh.height = 80/wh.width * wh.height;
          wh.width = 80;
        }
      }

      imageElement = {
        ...imageElement0,
        strokeColor:szDefaultColor,
        ...wh,
        hasUpdate:true,
        extra:{svg:true,svgText:szText}
      }
    }

    //这里上传图片---
    if (isWebApp() && !imageElement?.extra?.svg){
      window.showProgress();
      const szUrl = await uploadS3File(imageElement.id,imageFile,'image');
      imageElement = {
        ...imageElement,
        data:[szUrl]
      }
      window.closeProgress();
    }

    this.scene.addNewElement(imageElement);

    try {
      return await this.initializeImage({
        imageFile,
        imageElement,
        showCursorImagePreview,
        isFrameBackgroundImage,
        szImportMode,
      });
    } catch (error: any) {
      mutateElement(imageElement, {
        isDeleted: true,
      });
      this.actionManager.executeAction(actionFinalize);
      this.setState({
        errorMessage: error.message || t("errors.imageInsertError"),
      });
      return null;
    }
  };

  private setImagePreviewCursor = async (imageFile: File) => {
    // mustn't be larger than 128 px
    // https://developer.mozilla.org/en-US/docs/Web/CSS/CSS_Basic_User_Interface/Using_URL_values_for_the_cursor_property
    const cursorImageSizePx = 96;

    const imagePreview = await resizeImageFile(imageFile, {
      maxWidthOrHeight: cursorImageSizePx,
    });

    let previewDataURL = await getDataURL(imagePreview);

    // SVG cannot be resized via `resizeImageFile` so we resize by rendering to
    // a small canvas
    if (imageFile.type === MIME_TYPES.svg) {
      const img = await loadHTMLImageElement(previewDataURL);

      let height = Math.min(img.height, cursorImageSizePx);
      let width = height * (img.width / img.height);

      if (width > cursorImageSizePx) {
        width = cursorImageSizePx;
        height = width * (img.height / img.width);
      }

      const canvas = document.createElement("canvas");
      canvas.height = height;
      canvas.width = width;
      const context = canvas.getContext("2d")!;

      context.drawImage(img, 0, 0, width, height);

      previewDataURL = canvas.toDataURL(MIME_TYPES.svg) as DataURL;
    }

    if (this.state.pendingImageElementId) {
      if (imageFile.type == MIME_TYPES.gif){
        setCursor(this.interactiveCanvas, `url(${BASE64_GIF}) 4 4, auto`);
      }else{
        setCursor(this.interactiveCanvas, `url(${previewDataURL}) 4 4, auto`);
      }

    }
  };


  private onImageAction = async ({
    insertOnCanvasDirectly,
                                   frameElement,
  }: {
    insertOnCanvasDirectly: boolean;
    frameElement?: ExcalidrawElement;
  }) => {
    try {
      // const arrayFiles = await fileOpen({
      //   description: "Image",
      //   extensions: Object.keys(
      //       IMAGE_MIME_TYPES,
      //   ) as (keyof typeof IMAGE_MIME_TYPES)[],
      //   multiple:true,
      // });
      imageChoosePromise = new Promise(resolve => {
        imageChoosePromiseResolve = resolve;
      });
      this.fileImageInputRef.current?.click();

      const arrayFiles000 = await imageChoosePromise;
      const arrayFiles = [...arrayFiles000];

      this.fileImageInputRef.current.value = '';

      let imageFile:any;
      if (arrayFiles.length == 1){
        imageFile = arrayFiles[0];
      }else{
        if (arrayFiles.length <= 20){
          //mul files...
          var arrayFilesNew :any= [];
          let nTotal = arrayFiles.length;
          // if (nTotal > 10){
          //   nTotal = 10;
          // }
          for (let i=0;i<nTotal;i++){
            const file = arrayFiles[i];
            arrayFilesNew.push(await normalizeFile(file));
          }
          let szImportMode = localStorage.getItem(KEY_FILE_IMPORT_MODE) || IMPORT_FILE_MODE_IMPORTMODE;
          this.insertMultiplyImage(arrayFiles,null,true,szImportMode);
        }else{
          //tips....
          postMessage(EVENT_MAX_DRAG_IMAGE);
        }

        this.setState(
            {
              pendingImageElementId: null,
              editingElement: null,
              activeTool: updateActiveTool(this.state, { type: "selection" }),
            },
            () => {
              this.actionManager.executeAction(actionFinalize);
            },
        );
        return;
      }

      if (frameElement) {
        const base64 = await getDataURL(imageFile);
        this.setState({
          imgCropper: {
            type: "background",
            simpleCropper: {
              croppeAspectRatio:
                  Math.round(frameElement.width) /
                  Math.round(frameElement.height),
              base64,
            },
          },
        });
        return;
      }

      const clientX = this.state.width / 2 + this.state.offsetLeft;
      const clientY = this.state.height / 2 + this.state.offsetTop;

      const { x, y } = viewportCoordsToSceneCoords(
        { clientX, clientY },
        this.state,
      );

      const imageElement = this.createImageElement({
        sceneX: x,
        sceneY: y,
        addToFrameUnderCursor: false,
        name:getFileNameWithoutExt(imageFile.name)
      });

      if (insertOnCanvasDirectly) {
        this.insertImageElement(imageElement, imageFile, false, true);
        this.initializeImageDimensions(
            imageElement,
            false,
        );

        this.setState(
            {
              selectedElementIds: makeNextSelectedElementIds(
                  { [imageElement.id]: true },
                  this.state,
              ),
            },
            () => {
              this.actionManager.executeAction(actionFinalize);
            },
        );
      } else {
        this.setState(
          {
            pendingImageElementId: imageElement.id,
          },
          () => {
            this.insertImageElement(
              imageElement,
              imageFile,
              /* showCursorImagePreview */ true,
                false,
                global.g_insertMode
            );
          },
        );
      }
    } catch (error: any) {
      if (error.name !== "AbortError") {
        console.error(error);
      } else {
        console.warn(error);
      }
      this.setState(
        {
          pendingImageElementId: null,
          // editingElement: null,
          activeTool: updateActiveTool(this.state, { type: "selection" }),
        },
        () => {
          this.actionManager.executeAction(actionFinalize);
        },
      );
    }
  };

  private onMindmapAction = async ({}: {}) => {
    const clientX = this.state.width / 2 + this.state.offsetLeft;
    const clientY = this.state.height / 2 + this.state.offsetTop;

    const { x, y } = viewportCoordsToSceneCoords(
        { clientX, clientY },
        this.state,
    );
    const mindmapElement = this.createMindmapElement({
      sceneX: x,
      sceneY: y,
      addToFrameUnderCursor: false,
    });

    this.setState(
        {
          pendingMindmapElementId: mindmapElement.id,
        },
        () => {
          this.scene.addNewElement(mindmapElement);
          const image = new Image();
          image.src = isDarkMode()?mindmapWhite:mindmap;
          image.onload = () => {
            const canvas = document.createElement("canvas");
            canvas.height = 100;
            canvas.width = 100;
            const context = canvas.getContext("2d")!;
            context.drawImage(image, 0, 0, 100, 100);
            const previewDataURL = canvas.toDataURL();
            setCursor(this.interactiveCanvas, `url(${previewDataURL}) 4 4, auto`);
          };
        },
    );
    // this.setState({
    //   draggingElement: mindmapElement,
    //   editingElement: mindmapElement,
    //   startBoundElement: null,
    //   suggestedBindings: [],
    // });
  };

  private onStickyNotesAction = async ({}: {}) => {
    // const image = new Image();
    // image.src = stickyNotes;
    // image.onload = () => {
    //   const canvas = document.createElement("canvas");
    //   canvas.height = 100;
    //   canvas.width = 100;
    //   const context = canvas.getContext("2d")!;
    //   context.drawImage(image, 0, 0, 100, 100);
    //   const previewDataURL = canvas.toDataURL();
    //   setCursor(this.interactiveCanvas, `url(${previewDataURL}) 4 4, auto`);
    // };
  };

  private initializeImageDimensions = (
    imageElement: ExcalidrawImageElement,
    forceNaturalSize = false,
    isFrameBackgroundImage?: boolean,
  ) => {
    if (isFrameBackgroundImage) {
      return;
    }

    const image =
      isInitializedImageElement(imageElement) &&
      this.imageCache.get(imageElement.fileId)?.image;

    if (!image || image instanceof Promise) {
      if (
        imageElement.width < DRAGGING_THRESHOLD / this.state.zoom.value &&
        imageElement.height < DRAGGING_THRESHOLD / this.state.zoom.value
      ) {
        const placeholderSize = 100 / this.state.zoom.value;
        mutateElement(imageElement, {
          x: imageElement.x - placeholderSize / 2,
          y: imageElement.y - placeholderSize / 2,
          width: placeholderSize,
          height: placeholderSize,
        });
      }

      return;
    }

    if (
      forceNaturalSize ||
      // if user-created bounding box is below threshold, assume the
      // intention was to click instead of drag, and use the image's
      // intrinsic size
      (imageElement.width < DRAGGING_THRESHOLD / this.state.zoom.value &&
        imageElement.height < DRAGGING_THRESHOLD / this.state.zoom.value)
    ) {
      const minHeight = Math.max(this.state.height - 120, 160);
      // max 65% of canvas height, clamped to <300px, vh - 120px>
      const maxHeight = Math.min(
        minHeight,
        Math.floor(this.state.height * 0.5) / this.state.zoom.value,
      );

      if (imageElement.extra?.svg){
        mutateElement(imageElement,{});
      }else{
        // let height = Math.min(image.naturalHeight, maxHeight);
        // let width = height * (image.naturalWidth / image.naturalHeight);
        let width = DEFAULT_NOTE_WIDTH;
        let height = width*(image.naturalHeight / image.naturalWidth);

        // add current imageElement width/height to account for previous centering
        // of the placeholder image
        const x = imageElement.x + imageElement.width / 2 - width / 2;
        const y = imageElement.y + imageElement.height / 2 - height / 2;

        mutateElement(imageElement, { x, y, width, height });
      }


      // if (imageElement.extra?.svg){
      //   height = width = 80;
      // }


    }
  };

  /** updates image cache, refreshing updated elements and/or setting status
      to error for images that fail during <img> element creation */
  private updateImageCache = async (
    elements: readonly InitializedExcalidrawImageElement[],
    files = this.files,
  ) => {
    const { updatedFiles, erroredFiles } = await _updateImageCache({
      imageCache: this.imageCache,
      fileIds: elements.map((element) => element.fileId),
      files,
    });
    if (updatedFiles.size || erroredFiles.size) {
      for (const element of elements) {
        if (updatedFiles.has(element.fileId)) {
          ShapeCache.delete(element);
        }
      }
    }
    if (erroredFiles.size) {
      this.scene.replaceAllElements(
        this.scene.getElementsIncludingDeleted().map((element) => {
          if (
            isInitializedImageElement(element) &&
            erroredFiles.has(element.fileId)
          ) {
            return newElementWith(element, {
              status: "error",
            });
          }
          return element;
        }),
      );
    }

    return { updatedFiles, erroredFiles };
  };

  /** adds new images to imageCache and re-renders if needed */
  private addNewImagesToImageCache = async (
    imageElements: InitializedExcalidrawImageElement[] = getInitializedImageElements(
      this.scene.getNonDeletedElements(),
    ),
    files: BinaryFiles = this.files,
  ) => {
    const fullImageElements: InitializedExcalidrawImageElement[] = [];
    imageElements.forEach((item) => {
      if (item.fullImageFileId) {
        fullImageElements.push({ ...item, fileId: item.fullImageFileId });
      }
    });

    const uncachedImageElements = [
      ...imageElements,
      ...fullImageElements,
    ].filter(
        (element) => !element.isDeleted && !this.imageCache.has(element.fileId),
    );

    // const uncachedImageElements = imageElements.filter(
    //   (element) => !element.isDeleted && !this.imageCache.has(element.fileId),
    // );

    if (uncachedImageElements.length) {
      const { updatedFiles } = await this.updateImageCache(
        uncachedImageElements,
        files,
      );
      if (updatedFiles.size) {
        this.scene.informMutation();
      }
    }
  };

  /** generally you should use `addNewImagesToImageCache()` directly if you need
   *  to render new images. This is just a failsafe  */
  private scheduleImageRefresh = throttle(() => {
    this.addNewImagesToImageCache();
  }, IMAGE_RENDER_TIMEOUT);

  private updateBindingEnabledOnPointerMove = (
    event: React.PointerEvent<HTMLElement>,
  ) => {
    const shouldEnableBinding = shouldEnableBindingForPointerEvent(event);
    if (this.state.isBindingEnabled !== shouldEnableBinding) {
      this.setState({ isBindingEnabled: shouldEnableBinding });
    }
  };

  private maybeSuggestBindingAtCursor = (pointerCoords: {
    x: number;
    y: number;
  }): void => {
    const hoveredBindableElement = getHoveredElementForBinding(
      pointerCoords,
      this.scene,
    );
    this.setState({
      suggestedBindings:
        hoveredBindableElement != null ? [hoveredBindableElement] : [],
    });
  };

  private maybeSuggestBindingsForLinearElementAtCoords = (
    linearElement: NonDeleted<ExcalidrawLinearElement>,
    /** scene coords */
    pointerCoords: {
      x: number;
      y: number;
    }[],
    // During line creation the start binding hasn't been written yet
    // into `linearElement`
    oppositeBindingBoundElement?: ExcalidrawBindableElement | null,
  ): void | NonDeleted<ExcalidrawBindableElement>[] => {
    if (!pointerCoords.length) {
      return;
    }

    const suggestedBindings = pointerCoords.reduce(
      (acc: NonDeleted<ExcalidrawBindableElement>[], coords) => {
        const hoveredBindableElement = getHoveredElementForBinding(
          coords,
          this.scene,
        );
        if (
          hoveredBindableElement != null &&
          !isLinearElementSimpleAndAlreadyBound(
            linearElement,
            oppositeBindingBoundElement?.id,
            hoveredBindableElement,
          )&&
            !isMindmapElement(hoveredBindableElement)
        ) {
          acc.push(hoveredBindableElement);
        }
        return acc;
      },
      [],
    );

    this.setState({ suggestedBindings });
    return suggestedBindings;
  };

  private async insertFrameBackgroundImage(imageFile: File) {
    const frameElement = this.scene.getSelectedElements({
      selectedElementIds: this.state.selectedElementIds,
    })[0];
    // 删除原本存在的元素
    const isBackgroundImageElement = this.scene
        .getNonDeletedElements()
        .find(
            (item) =>
                item.type === "image" &&
                item.isFrameBackgroundImage &&
                item.frameId === frameElement.id,
        );
    // 创建元素
    const imageElement = newImageElement({
      type: "image",
      x: frameElement.x,
      y: frameElement.y,
      strokeColor: this.state.currentItemStrokeColor,
      backgroundColor: this.state.currentItemBackgroundColor,
      fillStyle: this.state.currentItemFillStyle,
      strokeWidth: this.state.currentItemStrokeWidth,
      strokeStyle: this.state.currentItemStrokeStyle,
      roughness: this.state.currentItemRoughness,
      roundness: null,
      opacity: this.state.currentItemOpacity,
      locked: true,
      frameId: frameElement.id,
      width: frameElement.width,
      height: frameElement.height,
      isFrameBackgroundImage: true,
      hasUpdate:true
    });
    // 插入元素
    await this.insertImageElement(imageElement, imageFile, false, true);

    if (isBackgroundImageElement) {
      const elements =
          this.scene.getElementsIncludingDeleted().map((item) => {
            if (
                item.type === "image" &&
                item.id === isBackgroundImageElement.id
            ) {
              return newElementWith(item, {
                isDeleted: true,
                isFrameBackgroundImage: false,
                locked: false,
              });
            }
            return item;
          }) || [];

      this.scene.replaceAllElements(elements);
    }
    // // 强制重新渲染
    const renderStaticScene =
        // @ts-ignore
        this.staticCanvasRef.current?.render();
    renderStaticScene();
  }

  private maybeSuggestBindingForAll(
    selectedElements: NonDeleted<ExcalidrawElement>[],
  ): void {
    if (selectedElements.length > 50) {
      return;
    }
    const suggestedBindings = getEligibleElementsForBinding(selectedElements);
    this.setState({ suggestedBindings });
  }

  private clearSelection(hitElement: ExcalidrawElement | null): void {
    this.setState((prevState) => ({
      selectedElementIds: makeNextSelectedElementIds({}, prevState),
      activeEmbeddable: null,
      activeMindMap: null,
      activeStickyNotes: null,
      selectedGroupIds: {},
        previousSelectedElementIds: prevState.selectedElementIds,
      // Continue editing the same group if the user selected a different
      // element from it
      editingGroupId:
        prevState.editingGroupId &&
        hitElement != null &&
        isElementInGroup(hitElement, prevState.editingGroupId)
          ? prevState.editingGroupId
          : null,
    }));
    // this.setState({
    //   selectedElementIds: makeNextSelectedElementIds({}, this.state),
    //   activeEmbeddable: null,
    //   activeStickyNotes: null,
    //   activeMindMap: null,
    //   previousSelectedElementIds: this.state.selectedElementIds,
    // });

    setTimeout(()=>{
      postMessage(EVENT_CANVAS_SELECTION_CHANGED,{selectedElements:this.state.selectedElementIds});
    },200)
  }

  private handleInteractiveCanvasRef = (canvas: HTMLCanvasElement | null) => {
    // canvas is null when unmounting
    if (canvas !== null) {
      this.interactiveCanvas = canvas;

      // -----------------------------------------------------------------------
      // NOTE wheel, touchstart, touchend events must be registered outside
      // of react because react binds them them passively (so we can't prevent
      // default on them)
      this.interactiveCanvas.addEventListener(EVENT.WHEEL, this.handleWheel);
      this.interactiveCanvas.addEventListener(
        EVENT.TOUCH_START,
        this.onTouchStart,
      );
      this.interactiveCanvas.addEventListener(EVENT.TOUCH_END, this.onTouchEnd);
      // -----------------------------------------------------------------------
    } else {
      this.interactiveCanvas?.removeEventListener(
        EVENT.WHEEL,
        this.handleWheel,
      );
      this.interactiveCanvas?.removeEventListener(
        EVENT.TOUCH_START,
        this.onTouchStart,
      );
      this.interactiveCanvas?.removeEventListener(
        EVENT.TOUCH_END,
        this.onTouchEnd,
      );
    }
  };

  /**
   * 处理拖拽过来的文件文件文件..../////
   * @param file
   * @param importFileMode
   */
  private handleDragFile = async (file,importFileMode)=>{
    if (importFileMode == IMPORT_FILE_MODE_LINKMODE){
      //关闭进度...
      window.closeDialogFileImportLoading();
      setTimeout(()=>{
        this.insertFileElement(file.path,null,undefined,false,null,undefined,null,true,true)
      },1000)
    }else{
      const szExistFileName = await ipcInvoke('cmp-file-md5', {filePath:file.path});
      if (szExistFileName){
        window.showMessageBox(i18next.t("DuplicateFileFound"),i18next.t("TheSameFileAlreadyExists"),i18next.t("CreateNewCopy"),i18next.t("Ok"),()=>{
          //create new copy
          ipcInvoke('copy-file', {filePath: file.path}).then(res => {
            //关闭进度...
            window.closeDialogFileImportLoading();
            //insert file type
            this.insertFileElement(res, null);
          });
        },()=>{
          //关闭进度...
          window.closeDialogFileImportLoading();
          //use exist--得到文件名，创建一个新的...
          // this.insertFileElement(szExistFileName, null,);
          getLibraryByFileName(szExistFileName).then(library=>{
            this.insertFileElement(szExistFileName, library.id,undefined, false, file, null, null,false,false,library.name);
          });
        })
      }else{
        ipcInvoke('copy-file', {filePath: file.path}).then(res => {
          //关闭进度...
          window.closeDialogFileImportLoading();
          //insert file type
          this.insertFileElement(res, null);
        });
      }
    }

  }

  /**
   * 插入多图片
   * @param files
   */
  private insertMultiplyImage = async (files:any,event: React.DragEvent<HTMLDivElement>,bWithTips=true,importFileMode:any,bShowImportLoading=true)=>{
    let xy:any = {};
    if (event != null){
      xy = viewportCoordsToSceneCoords(
          event,
          this.state,
      );
    }else{
      xy = this.getCurrentCenterPoint();
    }
    let bImage = true;

    let nOffset = 0;
    let lastWidth=0,lastHeight = 0,lastX=0,lastY = 0;
    if (importFileMode && bShowImportLoading){
      window.showDialogFileImportLoading(importFileMode);
    }

    for (let i=0;i<files.length;i++){
      nOffset =i*40;
      const file:any = files[i];
      if (!isImage(file.name)){
        bImage = false;
      }
      try {
        // if image tool not supported, don't show an error here and let it fall
        // through so we still support importing scene data from images. If no
        // scene data encoded, we'll show an error then
        if (isSupportedImageFile(file) && this.isToolSupported("image")) {
          // first attempt to decode scene from the image if it's embedded
          // ---------------------------------------------------------------------

          if (file?.type === MIME_TYPES.png || file?.type === MIME_TYPES.svg) {
            try {
              const scene = await loadFromBlob(
                  file,
                  this.state,
                  this.scene.getElementsIncludingDeleted(),
                  null,
              );
              this.syncActionResult({
                ...scene,
                appState: {
                  ...(scene.appState || this.state),
                  isLoading: false,
                },
                replaceFiles: true,
                commitToHistory: true,
              });
              return;
            } catch (error: any) {
              if (error.name !== "EncodingError") {
                throw error;
              }
            }
          }

          // if no scene is embedded or we fail for whatever reason, fall back
          // to importing as regular image
          // ---------------------------------------------------------------------

          const imageElement0 = this.createImageElement({ sceneX:xy.x, sceneY:xy.y,name:getFileNameWithoutExt(file.name) });
          const imageElement = {
            ...imageElement0,
            x:(imageElement0.x+nOffset+lastWidth),
            y:(imageElement0.y+nOffset+lastHeight),
          }

          this.insertImageElement(imageElement, file,false,false,importFileMode);
          this.initializeImageDimensions(imageElement);
          this.setState({
            selectedElementIds: makeNextSelectedElementIds(
                { [imageElement.id]: true },
                this.state,
            ),
          });
          // lastWidth = imageElement.width;
          // lastHeight = imageElement.height;

          continue;
        }
      } catch (error: any) {
        return this.setState({
          isLoading: false,
          errorMessage: error.message,
        });
      }

      const libraryJSON = event?.dataTransfer?.getData(MIME_TYPES.excalidrawlib);
      if (libraryJSON && typeof libraryJSON === "string") {
        try {
          const libraryItems = parseLibraryJSON(libraryJSON);
          this.addElementsFromPasteOrLibrary({
            elements: distributeLibraryItemsOnSquareGrid(libraryItems),
            position: event,
            files: null,
          });
        } catch (error: any) {
          this.setState({ errorMessage: error.message });
        }
        return;
      }

      if (file) {
        // Attempt to parse an excalidraw/excalidrawlib file
        if (isSupportedImageFile(file)){
          await this.loadFileToCanvas(file, fileHandle);
          if (i == files.length - 1){
            //关闭进度...
            window.closeDialogFileImportLoading();
          }
        }else{
          if (hasLicense() || hasDesktopBaseLicense()){
            if (file.size > MAX_SIZE){
              message.error("The maximum file");
            }else{
              if (files.length == 1){
                //只有一个文件的时候才检测文件是否重复...
                this.handleDragFile(file,importFileMode);
              }else{
                if (i == files.length - 1){
                  //关闭进度...
                  window.closeDialogFileImportLoading();
                }
                if (importFileMode == IMPORT_FILE_MODE_LINKMODE){
                  //link mode....
                  this.insertFileElement(file.path,null,undefined,false,null,undefined,null,true,true)
                }else{
                  //按文件的方式导入.....
                  ipcInvoke('copy-file',{filePath:file.path}).then(res=>{
                    //insert file type
                    this.insertFileElement(res,null);
                  });
                }
              }
            }
          }else{
            // todo
            // message.error('请购买license')
            // postMessage(EVENT_SHOW_IMPORT_FILE_VIP,{files:[file]});
          }
        }
      }


      if (event?.dataTransfer?.types?.includes("text/plain")) {
        const text = event.dataTransfer?.getData("text");
        if (
            text &&
            embeddableURLValidator(text, this.props.validateEmbeddable) &&
            (/^(http|https):\/\/[^\s/$.?#].[^\s]*$/.test(text) ||
                getEmbedLink(text)?.type === "video")
        ) {
          const embeddable = await this.insertEmbeddableElement({
            sceneX,
            sceneY,
            link: normalizeLink(text),
            embedType: "page",
          });
          if (embeddable) {
            this.setState({ selectedElementIds: { [embeddable.id]: true } });
          }
        }
      }
    }


    if (!bImage && bWithTips && !hasLicense() && !hasDesktopBaseLicense()){
      postMessage(EVENT_SHOW_IMPORT_FILE_VIP,{files:files,importFileMode:importFileMode});
    }

    //关闭进度...
    window.closeDialogFileImportLoading();
    // postMessage(EVENT_SHOW_IMPORT_FILE_VIP,{files:files});
  }

  /**
   * 拖动文件到这里....
   * 拖动图片
   * 拖动
   * 图片
   * @param event
   */
  private handleAppOnDrop = async (event: React.DragEvent<HTMLDivElement>) => {
    // must be retrieved first, in the same frame
    if (global.g_currentDialogName == 'DialogChooseImage'){
      //别的地方来的，这里不需要处理....
      return;
    }

    // Dragging blocks inside a canvas card (tiptap global drag-handle) should not create
    // new canvas text elements or trigger note-drag detection.
    if (event?.dataTransfer?.types?.includes?.("application/x-render-note-block-drag")) {
      return;
    }

    const szHtml = event.dataTransfer.getData('text/html');
      if (szHtml){
          //这里是网页上的？？？？--下载这个文件
          const uri = event.dataTransfer.getData('text/uri-list');
          if (uri){
              const dom = new DOMParser().parseFromString(szHtml, "text/html");
              const arrayImgs = dom.querySelectorAll('img');
              if (arrayImgs.length > 0){
                  const files = [];
                  for (let i=0;i<arrayImgs.length;i++){
                      const img = arrayImgs[i];
                      const src = img.src;
                      const szBase64 = await downloadImage(src);
                      const szFileName = formatDate(Date.now(),'yyyyMMddhhmmss')+'.jpg';

                      let szFilePath = await ipcInvoke('write-base64-image',{fileName:szFileName,imgBase64:szBase64});
                      szFilePath = localStorage.getItem(KEY_FILE_DIR) + szFilePath;

                      // //在这个位置插入
                      files.push({
                          name:szFileName,
                          path:szFilePath,
                          type:MIME_TYPES.jpg,
                          dataURL:szBase64
                      })
                  }
                  this.insertMultiplyImage(files,event,false,KEY_FILE_IMPORT_MODE,false);
                  return;
              }
          }else{
              if (!global.g_isDragFromCardText) {
                  //当成文字保存进来...
                  const xy = viewportCoordsToSceneCoords(
                      event,
                      this.state,
                  );
                  const element = this.createTextTiptapElement({
                      sceneX: xy.x,
                      sceneY: xy.y,
                      value: parseHtmlFromPasteTiptapWithDownload(szHtml),
                      backgroundColor: localStorage.getItem(KEY_DEFAULT_TEXT_BACKGROUND_COLOR) || COLOR_PALETTE.transparent
                  });
                  //添加进来.
                  this.scene.addNewElement(element);
              }
          }
      }
    //看下是不是笔记拖动过来的.
    postMessage(EVENT_TEST_ONDRAG_NOTE,{x:event.clientX,y:event.clientY});

    //这里检测拖动进来的文字
    const szTxt:any = event.dataTransfer.getData('text/plain');
    if (szTxt && szTxt == global.g_currentSelectedText){
      if (window.globalCardEditSelected){
        window.globalCardEditSelected({x:event.clientX,y:event.clientY});
      }
    }

    const { files, fileHandle } = await getFileFromEvent(event);

    //******这里检测所有的文件，如果是web并且不是图片，不允许拖动进来
    if (isWeb()){
      //web 并且不是image...
      for (let i=0;i<files.length;i++){
        if (!isImage(files[i].name)){
          window.showTipNotSupportWebOfflineVersion();
          return;
        }
      }
    }

    if (files.length >0 && files.length <= MAX_IMPORT_FILE_SIZE){
      let isAllImage = true;
      for (let i=0;i<files.length;i++){
        if (!isImage(files[i].name)){
          isAllImage = false;
          break;
        }
      }

      const fileImportMode = getFileImportMode();

      if (!isAllImage){
        //并不全是Image.....执行带文件的导入操作。。。
        if (fileImportMode == IMPORT_FILE_MODE_ASKME){
          window.showDialogFileImportDrag((szAction)=>{
            this.insertMultiplyImage(files,event,true,szAction);
          })
        }else{
          //如果不全部是文件的话...
          this.insertMultiplyImage(files,event,true,fileImportMode);
        }
      }else{
        //只执行图片的导入...
        if (fileImportMode == IMPORT_FILE_MODE_ASKME){
          window.showDialogFileImportDrag((szAction)=>{
            this.insertMultiplyImage(files,event,true,szAction,false);
          })
        } else{
          this.insertMultiplyImage(files,event,true,fileImportMode,false);
        }
      }
    }
  };


  loadFileToCanvas = async (
    file: File,
    fileHandle: FileSystemHandle | null,
  ) => {
    file = await normalizeFile(file);
    try {
      const ret = await loadSceneOrLibraryFromBlob(
        file,
        this.state,
        this.scene.getElementsIncludingDeleted(),
        fileHandle,
      );
      if (ret.type === MIME_TYPES.excalidraw) {
        this.setState({ isLoading: true });
        this.syncActionResult({
          ...ret.data,
          appState: {
            ...(ret.data.appState || this.state),
            isLoading: false,
          },
          replaceFiles: true,
          commitToHistory: true,
        });
      } else if (ret.type === MIME_TYPES.excalidrawlib) {
        await this.library
          .updateLibrary({
            libraryItems: file,
            merge: true,
            openLibraryMenu: true,
          })
          .catch((error) => {
            console.error(error);
            this.setState({ errorMessage: t("errors.importLibraryError") });
          });
      }
    } catch (error: any) {
      if (
        error instanceof ImageSceneDataError &&
        error.code === "IMAGE_NOT_CONTAINS_SCENE_DATA" &&
        !this.isToolSupported("image")
      ) {
        this.setState({
          isLoading: false,
          errorMessage: t("errors.imageToolNotSupported"),
        });
        return;
      }
      this.setState({ isLoading: false, errorMessage: error.message });
    }
  };

  private handleCanvasContextMenu = (
    event: React.MouseEvent<HTMLElement | HTMLCanvasElement>,
    isShowContextMenu:boolean
  ) => {
    if (!isShowContextMenu)return;
    event.preventDefault();
    if (
      (("pointerType" in event.nativeEvent &&
        event.nativeEvent.pointerType === "touch") ||
        ("pointerType" in event.nativeEvent &&
          event.nativeEvent.pointerType === "pen" &&
          // always allow if user uses a pen secondary button
          event.button !== POINTER_BUTTON.SECONDARY)) &&
      this.state.activeTool.type !== "selection"
    ) {
      return;
    }

    const { x, y } = viewportCoordsToSceneCoords(event, this.state);
    const element = this.getElementAtPosition(x, y, {
      preferSelected: true,
      includeLockedElements: true,
      filterFrameBackgroundImage: true,
      isDOM: true, //!(event.target instanceof HTMLCanvasElement),
    });

    const selectedElements = this.scene.getSelectedElements(this.state);
    const isHittingCommonBoundBox =
      this.isHittingCommonBoundingBoxOfSelectedElements(
        { x, y },
        selectedElements,
      );

    const type = element || isHittingCommonBoundBox ? "element" : "canvas";

    const container = this.excalidrawContainerRef.current!;
    const { top: offsetTop, left: offsetLeft } =
      container.getBoundingClientRect();
    const left = event.clientX - offsetLeft;
    const top = event.clientY - offsetTop;

    // console.log('offsetX:'+offsetLeft + " offsetY:"+offsetTop)
    // console.log('left:'+left + " top:"+top)

    trackEvent("contextMenu", "openContextMenu", type);

    this.setState(
      {
        ...(element && !this.state.selectedElementIds[element.id]
          ? {
              ...this.state,
              ...selectGroupsForSelectedElements(
                {
                  editingGroupId: this.state.editingGroupId,
                  selectedElementIds: { [element.id]: true },
                },
                this.scene.getNonDeletedElements(),
                this.state,
                this,
              ),
              selectedLinearElement: isLinearElement(element)
                ? new LinearElementEditor(element, this.scene)
                : null,
            }
          : this.state),
        showHyperlinkPopup: false,
      },
      () => {
        // this.setState({
        //   contextMenu: { top, left, items: this.getContextMenuItems(type) },
        // });
        //*****update********
        this.props?.onContextMenuOpen?.(left,top,element,x,y);
      },
    );
  };

  private onReadOnlyModeMouseLeft = (
    event: React.MouseEvent<HTMLElement | HTMLCanvasElement>,
  ) => {

    if (
      (("pointerType" in event.nativeEvent &&
          event.nativeEvent.pointerType === "touch") ||
        ("pointerType" in event.nativeEvent &&
          event.nativeEvent.pointerType === "pen" &&
          // always allow if user uses a pen secondary button
          event.button !== POINTER_BUTTON.SECONDARY)) &&
      this.state.activeTool.type !== "selection"
    ) {
      return;
    }

    const { x, y } = viewportCoordsToSceneCoords(event, this.state);
    const element = this.getElementAtPosition(x, y, {
      preferSelected: true,
      includeLockedElements: true,
      isDOM: !(event.target instanceof HTMLCanvasElement),
    });

    const selectedElements = this.scene.getSelectedElements(this.state);
    const isHittingCommonBoundBox =
      this.isHittingCommonBoundingBoxOfSelectedElements(
        { x, y },
        selectedElements,
      );

    const type = element || isHittingCommonBoundBox ? "element" : "canvas";

    const container = this.excalidrawContainerRef.current!;
    const { top: offsetTop, left: offsetLeft } =
      container.getBoundingClientRect();
    const left = event.clientX - offsetLeft;
    const top = event.clientY - offsetTop;

    this.setState(
      {
        ...(element && !this.state.selectedElementIds[element.id]
          ? {
            ...this.state,
            ...selectGroupsForSelectedElements(
              {
                editingGroupId: this.state.editingGroupId,
                selectedElementIds: { [element.id]: true },
              },
              this.scene.getNonDeletedElements(),
              this.state,
              this,
            ),
            selectedLinearElement: isLinearElement(element)
              ? new LinearElementEditor(element, this.scene)
              : null,
          }
          : this.state),
        showHyperlinkPopup: false,
      },
      () => {
        // this.setState({
        //   contextMenu: { top, left, items: this.getContextMenuItems(type) },
        // });
      },
    );
  };

  private maybeDragNewGenericElement = (
    pointerDownState: PointerDownState,
    event: MouseEvent | KeyboardEvent,
  ): void => {
    const draggingElement = this.state.draggingElement;
    const pointerCoords = pointerDownState.lastCoords;
    if (!draggingElement) {
      return;
    }
    if (
      draggingElement.type === "selection" &&
      this.state.activeTool.type !== "eraser"
    ) {
      dragNewElement(
        draggingElement,
        this.state.activeTool.type,
        pointerDownState.origin.x,
        pointerDownState.origin.y,
        pointerCoords.x,
        pointerCoords.y,
        distance(pointerDownState.origin.x, pointerCoords.x),
        distance(pointerDownState.origin.y, pointerCoords.y),
        shouldMaintainAspectRatio(event),
        shouldResizeFromCenter(event),
      );
      const [gridX, gridY] = getGridPoint(
          pointerCoords.x,
          pointerCoords.y,
          null,
      );
      mindMapCache.forEach((mindmap) => {
        // @ts-ignore
        mindmap.select.onMousemove(
            { x: gridX, y: gridY },
            this.state.zoom.value,
        );
        this.setState({
          isMindmapNodeMultiply:mindmap.renderer?.activeNodeList?.length > 1
        })
      });
    } else {
      let [gridX, gridY] = getGridPoint(
        pointerCoords.x,
        pointerCoords.y,
        event[KEYS.CTRL_OR_CMD] ? null : this.state.gridSize,
      );

      const image =
        isInitializedImageElement(draggingElement) &&
        this.imageCache.get(draggingElement.fileId)?.image;
      const aspectRatio =
        image && !(image instanceof Promise)
          ? image.width / image.height
          : null;

      this.maybeCacheReferenceSnapPoints(event, [draggingElement]);

      const { snapOffset, snapLines } = snapNewElement(
        draggingElement,
        this.state,
        event,
        {
          x:
            pointerDownState.originInGrid.x +
            (this.state.originSnapOffset?.x ?? 0),
          y:
            pointerDownState.originInGrid.y +
            (this.state.originSnapOffset?.y ?? 0),
        },
        {
          x: gridX - pointerDownState.originInGrid.x,
          y: gridY - pointerDownState.originInGrid.y,
        },
      );

      gridX += snapOffset.x;
      gridY += snapOffset.y;

      this.setState({
        snapLines,
      });

      dragNewElement(
        draggingElement,
        this.state.activeTool.type,
        pointerDownState.originInGrid.x,
        pointerDownState.originInGrid.y,
        gridX,
        gridY,
        distance(pointerDownState.originInGrid.x, gridX),
        distance(pointerDownState.originInGrid.y, gridY),
        isImageElement(draggingElement)
          ? !shouldMaintainAspectRatio(event)
          : shouldMaintainAspectRatio(event),
        shouldResizeFromCenter(event),
        aspectRatio,
        this.state.originSnapOffset,
      );

      this.maybeSuggestBindingForAll([draggingElement]);

      // highlight elements that are to be added to frames on frames creation
      if (
        this.state.activeTool.type === TOOL_TYPE.frame ||
        this.state.activeTool.type === TOOL_TYPE.magicframe
      ) {
        this.setState({
          elementsToHighlight: getElementsInResizingFrame(
            this.scene.getNonDeletedElements(),
            draggingElement as ExcalidrawFrameLikeElement,
            this.state,
          ),
        });
      }
    }
  };

  private maybeHandleResize = (
    pointerDownState: PointerDownState,
    event: MouseEvent | KeyboardEvent,
  ): boolean => {
    const selectedElements = this.scene.getSelectedElements(this.state);
    const selectedFrames = selectedElements.filter(
      (element): element is ExcalidrawFrameLikeElement =>
        isFrameLikeElement(element),
    );

    const transformHandleType = pointerDownState.resize.handleType;

    if (selectedFrames.length > 0 && transformHandleType === "rotation") {
      return false;
    }

    this.setState({
      // TODO: rename this state field to "isScaling" to distinguish
      // it from the generic "isResizing" which includes scaling and
      // rotating
      isResizing: transformHandleType && transformHandleType !== "rotation",
      isRotating: transformHandleType === "rotation",
      activeEmbeddable: null,
      activeStickyNotes: null,
      activeMindMap: null,
    });
    const pointerCoords = pointerDownState.lastCoords;
    let [resizeX, resizeY] = getGridPoint(
      pointerCoords.x - pointerDownState.resize.offset.x,
      pointerCoords.y - pointerDownState.resize.offset.y,
      event[KEYS.CTRL_OR_CMD] ? null : this.state.gridSize,
    );

    const frameElementsOffsetsMap = new Map<
      string,
      {
        x: number;
        y: number;
      }
    >();
    const elementsInFrames: ExcalidrawElement[][] = [];
    selectedFrames.forEach((frame) => {
      const elementsInFrame = getFrameChildren(
        this.scene.getNonDeletedElements(),
        frame.id,
      );
      elementsInFrames.push(elementsInFrame);
      elementsInFrame.forEach((element) => {
        frameElementsOffsetsMap.set(frame.id + element.id, {
          x: element.x - frame.x,
          y: element.y - frame.y,
        });
      });
    });

    // check needed for avoiding flickering when a key gets pressed
    // during dragging
    if (!this.state.selectedElementsAreBeingDragged) {
      const [gridX, gridY] = getGridPoint(
        pointerCoords.x,
        pointerCoords.y,
        event[KEYS.CTRL_OR_CMD] ? null : this.state.gridSize,
      );

      const dragOffset = {
        x: gridX - pointerDownState.originInGrid.x,
        y: gridY - pointerDownState.originInGrid.y,
      };

      const originalElements = [...pointerDownState.originalElements.values()];

      this.maybeCacheReferenceSnapPoints(event, selectedElements);

      const { snapOffset, snapLines } = snapResizingElements(
        selectedElements,
        getSelectedElements(originalElements, this.state),
        this.state,
        event,
        dragOffset,
        transformHandleType,
      );

      resizeX += snapOffset.x;
      resizeY += snapOffset.y;

      this.setState({
        snapLines,
      });
    }

    var shouldAspectRatio = selectedElements.length === 1 && isImageElement(selectedElements[0]) ?
        !shouldMaintainAspectRatio(event) : shouldMaintainAspectRatio(event);
    if (selectedElements.length == 1){
      var element = selectedElements[0];
      shouldAspectRatio = element.isLockRatio;

      if(isFreeDrawElement(selectedElements[0])){
        shouldAspectRatio = true;
      }
    }

    if (
      transformElements(
        pointerDownState,
        transformHandleType,
        selectedElements,
        pointerDownState.resize.arrowDirection,
        shouldRotateWithDiscreteAngle(event),
        shouldResizeFromCenter(event),
          shouldAspectRatio,
        resizeX,
        resizeY,
        pointerDownState.resize.center.x,
        pointerDownState.resize.center.y,
        this.state,
          elementsInFrames,
      )
    ) {
      const [element] = selectedElements;

      this.maybeSuggestBindingForAll(selectedElements);

      const elementsToHighlight = new Set<ExcalidrawElement>();
      selectedFrames.forEach((frame) => {
        getElementsInResizingFrame(
          this.scene.getNonDeletedElements(),
          frame,
          this.state,
        ).forEach((element) => elementsToHighlight.add(element));
      });

      this.setState({
        elementsToHighlight: [...elementsToHighlight],
      });

      //patch 拖动的时候 实时更新width和height.............
      var arrayEles = this.getSceneElements();
      this.updateScene({
        elements:arrayEles
      })

      return true;
    }
    return false;
  };

  private hasMindmapLink = (szAttachUrl,szHyperlink)=>{
    // return isCardDeeplink(szAttachUrl) ||
    //     isVideoDeeplink(szHyperlink) ||
    //     isFileDeeplink(szHyperlink) ||
    //     isHighlightDeeplink(szHyperlink);
    return szAttachUrl || szHyperlink;
  }

  private getContextMenuItems = (
    type: "canvas" | "element" | "node_contextmenu",
  node:any = null): ContextMenuItems => {
    if (type === "node_contextmenu") {
      const targetMindmapNode = node || this.state.mindmap.selected.node;
      let szAttachUrl = targetMindmapNode.nodeData?.data?.attachmentUrl?.url;
      let szHyperlink = targetMindmapNode.nodeData?.data?.hyperlink;

      if (targetMindmapNode?.isRoot){
        const arrayRootMenus = [
          actionMindmapAddPdf,
          actionMindmapAddCard,
          actionAddImage,
          isStringNullOrEmpty(targetMindmapNode?.nodeData?.data.hyperlink)?actionAddLink:actionEditLink,
          // CONTEXT_MENU_SEPARATOR,
          // actionMoveUpNode,
          // actionMoveDownNode,
          CONTEXT_MENU_SEPARATOR,
          actionMindmapCopylink,
          CONTEXT_MENU_SEPARATOR,
            actionMindmapImportFromMarkdown,
            actionMindmapExportToMarkdown,
            CONTEXT_MENU_SEPARATOR,
          actionDeleteNode,
        ];
        if (this.hasMindmapLink(szAttachUrl,szHyperlink)){
          arrayRootMenus.splice(4,0,actionMindmapRemoveLink);
        }
        return arrayRootMenus;
      }


      const arrayMenus = [
        actionMindmapAddPdf,
        actionMindmapAddCard,
        actionAddImage,
        actionMindmapAddVideo,
        actionMindmapAddFile,
        actionMindmapAddHighlight,
        actionAddLink,
        CONTEXT_MENU_SEPARATOR,
        actionChangeMindmapLineColor,
        actionMoveUpNode,
        actionMoveDownNode,
        CONTEXT_MENU_SEPARATOR,
        actionMindmapCopylink,
        CONTEXT_MENU_SEPARATOR,
        actionDeleteNode,
      ];
      if (this.hasMindmapLink(szAttachUrl,szHyperlink)){
        arrayMenus.splice(7,0,actionMindmapRemoveLink);
      }

      return arrayMenus;
    }

    const options: ContextMenuItems = [];

    // options.push(actionCopyAsPng, actionCopyAsSvg);
    options.push(actionCopyAsPng);

    // canvas contextMenu
    // -------------------------------------------------------------------------

    if (type === "canvas") {
      if (this.state.viewModeEnabled) {
        return [
          ...options,
          actionToggleGridMode,
          actionShowSnapLines,
          actionToggleZenMode,
          actionToggleViewMode,
          actionToggleStats,
        ];
      }

      return [
        actionPaste,
        CONTEXT_MENU_SEPARATOR,
        actionCopyAsPng,
        actionCopyAsSvg,
        copyText,
        // CONTEXT_MENU_SEPARATOR,
        actionSelectAll,
        actionUnlockAllElements,
        CONTEXT_MENU_SEPARATOR,
        actionToggleGridMode,
        actionToggleObjectsSnapMode,
        actionShowSnapLines,
        actionToggleZenMode,
        actionToggleViewMode,
        actionToggleStats,
      ];
    }

    // element contextMenu
    // -------------------------------------------------------------------------

    options.push(copyText);

    if (this.state.viewModeEnabled) {
      return [actionCopy, ...options];
    }

    return [
      actionCut,
      actionCopy,
      actionPaste,
      actionSelectAllElementsInFrame,
      actionRemoveAllElementsFromFrame,
      CONTEXT_MENU_SEPARATOR,
      ...options,
      // CONTEXT_MENU_SEPARATOR,
      // actionCopyStyles,
      // actionPasteStyles,
      // CONTEXT_MENU_SEPARATOR,
        actionCopyAsSvg,
      actionGroup,
      actionUnbindText,
      actionBindText,
      // actionWrapTextInContainer,
      actionUngroup,
      CONTEXT_MENU_SEPARATOR,
      // actionAddToLibrary,
      // CONTEXT_MENU_SEPARATOR,
      actionSendBackward,
      actionBringForward,
      actionSendToBack,
      actionBringToFront,
      CONTEXT_MENU_SEPARATOR,
      actionFlipHorizontal,
      actionFlipVertical,
      CONTEXT_MENU_SEPARATOR,
      actionToggleLinearEditor,
      actionLink,
      // actionReplaceSelection,
      actionDuplicateSelection,
      actionToggleElementLock,
      actionChangeQuickFlow,
      CONTEXT_MENU_SEPARATOR,
      actionDeleteSelected,
        actionReplaceSelection,
        actionChangeBackgroundImage,
    ];
  };

  private handleWheelController = withBatchedUpdates(
      (
          event: WheelEvent | React.WheelEvent<HTMLDivElement | HTMLCanvasElement>,
      ) => {
        event.preventDefault();
        event.stopPropagation();
        requestAnimationFrame(() => {
          this.handleWheel(event);
        });
      },
  );

  private handleWheel = withBatchedUpdates(
    (
      event: WheelEvent | React.WheelEvent<HTMLDivElement | HTMLCanvasElement>,
    ) => {
      // event.preventDefault();

      if (isPanning) {
        return;
      }

      if (this.state.pageSizeValues.type === "custom") {
        this.setToast({
          message: "zoom not supported on fixed-size canvas",
          closable: true,
          duration: 3000,
        });
        return;
      }

      hideMindmapEditBox();

      const { deltaX, deltaY } = event;
      // note that event.ctrlKey is necessary to handle pinch zooming
      if (event.metaKey || event.ctrlKey || localStorage.getItem(KEY_USE_SCROLL_WHEEL_ZOOM)) {
        const sign = Math.sign(deltaY);
        const MAX_STEP = ZOOM_STEP * 100;
        const absDelta = Math.abs(deltaY);
        let delta = deltaY;
        if (absDelta > MAX_STEP) {
          delta = MAX_STEP * sign;
        }

        let newZoom = this.state.zoom.value - delta / 100;
        // increase zoom steps the more zoomed-in we are (applies to >100% only)
        newZoom +=
          Math.log10(Math.max(1, this.state.zoom.value)) *
          -sign *
          // reduced amplification for small deltas (small movements on a trackpad)
          Math.min(1, absDelta / 20);

        if (this.state.displayMode === "preview") {
          //最小比例为整个frame...可放大...
          if (newZoom <= this.state.originZoomValue){
            return;
          }
        }

        this.translateCanvas((state) => ({
          ...getStateForZoom(
            {
              viewportX: this.lastViewportPosition.x,
              viewportY: this.lastViewportPosition.y,
              nextZoom: getNormalizedZoom(newZoom),
            },
            state,
          ),
          shouldCacheIgnoreZoom: true,
        }));
        this.resetShouldCacheIgnoreZoomDebounced();
        return;
      }

      // scroll horizontally when shift pressed
      if (event.shiftKey) {
        this.translateCanvas(({ zoom, scrollX }) => ({
          // on Mac, shift+wheel tends to result in deltaX
          scrollX: scrollX - (deltaY || deltaX) / zoom.value,
        }));
        return;
      }

      this.translateCanvas(({ zoom, scrollX, scrollY }) => ({
        scrollX: scrollX - deltaX / zoom.value,
        scrollY: scrollY - deltaY / zoom.value,
      }));
    },
  );

  private getTextWysiwygSnappedToCenterPosition(
    x: number,
    y: number,
    appState: AppState,
    container?: ExcalidrawTextContainer | null,
  ) {
    if (container) {
      let elementCenterX = container.x + container.width / 2;
      let elementCenterY = container.y + container.height / 2;

      const elementCenter = getContainerCenter(container, appState);
      if (elementCenter) {
        elementCenterX = elementCenter.x;
        elementCenterY = elementCenter.y;
      }
      const distanceToCenter = Math.hypot(
        x - elementCenterX,
        y - elementCenterY,
      );
      const isSnappedToCenter =
        distanceToCenter < TEXT_TO_CENTER_SNAP_THRESHOLD;
      if (isSnappedToCenter) {
        const { x: viewportX, y: viewportY } = sceneCoordsToViewportCoords(
          { sceneX: elementCenterX, sceneY: elementCenterY },
          appState,
        );
        return { viewportX, viewportY, elementCenterX, elementCenterY };
      }
    }
  }

  private savePointer = (x: number, y: number, button: "up" | "down") => {
    if (!x || !y) {
      return;
    }
    const { x: sceneX, y: sceneY } = viewportCoordsToSceneCoords(
      { clientX: x, clientY: y },
      this.state,
    );

    if (isNaN(sceneX) || isNaN(sceneY)) {
      // sometimes the pointer goes off screen
    }

    const pointer: CollaboratorPointer = {
      x: sceneX,
      y: sceneY,
      tool: this.state.activeTool.type === "laser" ? "laser" : "pointer",
    };

    this.props.onPointerUpdate?.({
      pointer,
      button,
      pointersMap: gesture.pointers,
    });
  };

  private resetShouldCacheIgnoreZoomDebounced = debounce(() => {
    if (!this.unmounted) {
      this.setState({ shouldCacheIgnoreZoom: false });
    }
  }, 300);

  private updateDOMRect = (cb?: () => void) => {
    if (this.excalidrawContainerRef?.current) {
      const excalidrawContainer = this.excalidrawContainerRef.current;
      const {
        width,
        height,
        left: offsetLeft,
        top: offsetTop,
      } = excalidrawContainer.getBoundingClientRect();
      const {
        width: currentWidth,
        height: currentHeight,
        offsetTop: currentOffsetTop,
        offsetLeft: currentOffsetLeft,
      } = this.state;

      if (
        width === currentWidth &&
        height === currentHeight &&
        offsetLeft === currentOffsetLeft &&
        offsetTop === currentOffsetTop
      ) {
        if (cb) {
          cb();
        }
        return;
      }

      this.setState(
        {
          width,
          height,
          offsetLeft,
          offsetTop,
        },
        () => {
          cb && cb();
        },
      );
    }
  };

  public refresh = () => {
    this.setState({ ...this.getCanvasOffsets() });
  };

  //刷新
  public refreshElement = (element,updates)=>{
    var newElement:any  = {
     ...element,
     ...updates,
    }
    var arrayNew:any = this.getSceneElements();
    for (let i=0;i<arrayNew.length;i++){
      if(arrayNew[i].id === newElement.id){
        arrayNew.splice(i,1,newElement);
        break;
      }
    }
    // arrayNew.push(newElement);
    this.updateScene({
      elements:arrayNew
    })
  }

  //点击回调
  public onPointDownEvent = (onPointdownCallback)=>{
    mOnPointDownCallback = onPointdownCallback;
  }


  /**
   * 插入SVG图
   * @param element
   * @param svgFile
   */
  public insertSVGIcon = (element,svgFile)=>{
    const clientX = this.state.width / 2 + this.state.offsetLeft + (this.state.leftMenuWidth??0)/2;
    const clientY = this.state.height / 2 + this.state.offsetTop;

    const { x, y } = viewportCoordsToSceneCoords(
        { clientX, clientY },
        this.state,
    );

    var xhr = new XMLHttpRequest();
    xhr.open("GET",svgFile);
    xhr.onload = ()=>{
      if(xhr.status === 200){
        var svgText = xhr.responseText;
        const parser = new DOMParser();
        const xml = parser.parseFromString(svgText,'text/xml');
        try{
          const width = parseInt(xml.firstChild.attributes.width.value);
          const height = parseInt(xml.firstChild.attributes.height.value);
          element.width = width * 4;
          element.height = height * 4;

          if (element.width > 80){
            element.height = 80/element.width * element.height;
            element.width = 80;
          }

        }catch (e){}
        element.x = x - element.width;
        element.y = y - element.height;

        // svgText = changeSvgColor(svgText,element.strokeColor);
        element.extra.svgText = svgText;
        const imageFile = new Blob([svgText],{type:MIME_TYPES.svg});
        this.insertImageElement(element,imageFile as File,true);
      }
    }
    xhr.send();
  }

  /**
   * 当前屏幕中心点坐标
   */
  public getCurrentCenterPoint(){
    const clientX = this.state.width / 2 + this.state.offsetLeft;
    const clientY = this.state.height / 2 + this.state.offsetTop;

    const { x, y } = viewportCoordsToSceneCoords(
        { clientX, clientY },
        this.state,
    );
    return {x,y};
  }



  private getCanvasOffsets(): Pick<AppState, "offsetTop" | "offsetLeft"> {
    if (this.excalidrawContainerRef?.current) {
      const excalidrawContainer = this.excalidrawContainerRef.current;
      const { left, top } = excalidrawContainer.getBoundingClientRect();
      return {
        offsetLeft: left,
        offsetTop: top,
      };
    }
    return {
      offsetLeft: 0,
      offsetTop: 0,
    };
  }

  private async updateLanguage() {
    const currentLang =
      languages.find((lang) => lang.code === this.props.langCode) ||
      defaultLang;
    await setLanguage(currentLang);
    this.setAppState({});
  }
}

// -----------------------------------------------------------------------------
// TEST HOOKS
// -----------------------------------------------------------------------------

// declare global {
//   interface Window {
//     // @ts-ignore
//     h: {
//       elements: readonly ExcalidrawElement[];
//       state: AppState;
//       setState: React.Component<any, AppState>["setState"];
//       app: InstanceType<typeof App>;
//       history: History;
//     };
//   }
// }
//
// if (import.meta.env.MODE === ENV.TEST || import.meta.env.DEV) {
//   window.h = window.h || ({} as Window["h"]);
//
//   Object.defineProperties(window.h, {
//     elements: {
//       configurable: true,
//       get() {
//         return this.app?.scene.getElementsIncludingDeleted();
//       },
//       set(elements: ExcalidrawElement[]) {
//         return this.app?.scene.replaceAllElements(elements);
//       },
//     },
//   });
// }

export default App;
